import { restartWithUpdate } from './restartWithUpdate.js';
import { googleEarthOpenPattern } from './assistWebShortcut';
import { isAssistWorkRequest, isAssistDiscoveryRequest, isAssistInspectionRequest } from './assistInspectionIntent';
import AriaTranscriptTest from './AriaTranscriptTest';
import { prepareTaskTurn } from './assistTaskIntake';
import { voiceTrace } from './ariaVoiceTrace.js';
import './TaskWorkspace.css';
import { upsertStreamMessage, latestWorkspaceIndex } from './ariaWorkspaceMessages.js';
import { createVoiceTurn } from './ariaVoiceTurn';
/**
 * ⚠️  Aria layout is LOCKED. See ./LAYOUT.md before changing anything that
 *     affects the sidebar, header, orb, top-right chat, downloads, or input bar.
 *     Web (:5173) and the Electron desktop app must render IDENTICALLY.
 */
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { flushSync, createPortal } from 'react-dom';
import './App.css';
import ScreenShareView from './ScreenShareView';
import HowToThumbnails from './HowToThumbnails';
import ExploreView from './ExploreView';
import Workstation, { hasArtifact as wsHasArtifact } from './Workstation';
import Notepad from './Notepad';
import TourSpotlight from './TourSpotlight';
import ThreeThings from './ThreeThings';
import ariaHand from './ariaHand';   // her cursor and her keyboard: when she does it, you see her do it (9/5)
import Uses from './Uses';
import JobChip from './JobChip';
import userSync from './userSync';
import ImageGenView from './ImageGenView';
import WorkbenchFrame from './WorkbenchFrame';
import WebsiteBuilderView from './WebsiteBuilderView';
import WriteView from './WriteView';
import TrendsView from './TrendsView';
import ReviewQueue from './ReviewQueue';
import CanvasRenderer, { CanvasProjectList } from './CanvasRenderer';
import CanvasAriaChat from './CanvasAriaChat';
import { audioDataUrlFromArrayBuffer } from './ariaAudioDataUrl';

// ── Project suggestion chip ────────────────────────────────────────────
// Aria sees the user say "I want to start X" and OFFERS to set up a
// dashboard — she doesn't presume. Two modes:
//   action='open' → dashboard already exists. One button: "Open it →"
//   action='offer' → no dashboard yet. Two buttons: "Yes, build it" + "Not now"
// On "Yes, build it" the chip POSTs to /api/aria/canvas with the description
// the backend stashed, then navigates the user into the new canvas.
function ProjectSuggestionChip({ suggestion, userId, onOpen, onDismiss }) {
  const [building, setBuilding] = React.useState(false);
  const [err, setErr] = React.useState(null);

  if (suggestion.action === 'open' && suggestion.id) {
    return (
      <button
        type="button"
        className="aria-project-chip"
        onClick={() => onOpen?.(suggestion.id)}
        title="You already have a dashboard for this — open it"
      >
        <span className="aria-project-chip__icon">📋</span>
        <span className="aria-project-chip__body">
          <span className="aria-project-chip__label">You already have a dashboard for this</span>
          <span className="aria-project-chip__name">{suggestion.name}</span>
        </span>
        <span className="aria-project-chip__cta">Open →</span>
      </button>
    );
  }

  const confirm = async () => {
    if (building) return;
    setBuilding(true);
    setErr(null);
    try {
      // getApiBase() returns '' in dev (proxy) or absolute URL in electron
      const base = (typeof window !== 'undefined' && window.electronAPI) ? 'http://127.0.0.1:3001' : '';
      const r = await fetch(`${base}/api/aria/canvas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({
          name: suggestion.name,
          description: suggestion.description,
          type: suggestion.type,
        }),
      });
      const data = await r.json();
      if (data?.project?.id) {
        onOpen?.(data.project.id);
      } else {
        setErr('Could not create — try again?');
      }
    } catch (e) {
      setErr(e.message || 'Network hiccup');
    } finally {
      setBuilding(false);
    }
  };

  return (
    <div className="aria-project-chip aria-project-chip--offer">
      <span className="aria-project-chip__icon">✨</span>
      <div className="aria-project-chip__body">
        <span className="aria-project-chip__label">Want me to set up a dashboard for this?</span>
        <span className="aria-project-chip__name">{suggestion.name}</span>
        {suggestion.panelHint && (
          <span className="aria-project-chip__hint">I'd start with: {suggestion.panelHint}</span>
        )}
        {err && <span className="aria-project-chip__err">{err}</span>}
      </div>
      <div className="aria-project-chip__actions">
        <button
          type="button"
          className="aria-project-chip__yes"
          onClick={confirm}
          disabled={building}
        >{building ? 'Building…' : 'Yes, build it'}</button>
        <button
          type="button"
          className="aria-project-chip__no"
          onClick={onDismiss}
          disabled={building}
        >Not now</button>
      </div>
    </div>
  );
}

// ── HireOfferCard ──────────────────────────────────────────────────────
// Rendered when conversation response has displayMode='hire_offer'.
// Editable role/pay/location/certs + "Run it" button → POST /api/aria/hire
// triggers the full orchestrator (crew SMS broadcast + Indeed/ZipRecruiter
// posts + Skillit/Trade Hounds cross-check).
function HireOfferCard({ draft, userId, onResult }) {
  const initial = draft || {};
  const [role, setRole] = React.useState(initial.role || '');
  const [payRate, setPayRate] = React.useState(initial.payRate || '');
  const [location, setLocation] = React.useState(initial.location || '');
  const [certsRequired, setCertsRequired] = React.useState((initial.certsRequired || []).join(', '));
  const [posting, setPosting] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState(null);

  const run = async () => {
    if (posting) return;
    setPosting(true); setErr(null);
    try {
      const base = (typeof window !== 'undefined' && window.electronAPI) ? 'http://127.0.0.1:3001' : '';
      const r = await fetch(`${base}/api/aria/hire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({
          userId, persona: 'work', role, payRate, location,
          certsRequired: certsRequired.split(',').map((s) => s.trim()).filter(Boolean),
          // crewPhones intentionally empty here — the user can paste them in
          // a follow-up step. The first version of the card just kicks off
          // the job-board posts + cert-aware screening setup.
          crewPhones: [],
        }),
      });
      const data = await r.json();
      if (data?.ok) { setResult(data); onResult?.(data); }
      else setErr(data?.error || 'Could not post the hire.');
    } catch (e) { setErr(e.message); }
    finally { setPosting(false); }
  };

  if (result) {
    const channels = result.channelResults || {};
    return (
      <div className="aria-action-card aria-action-card--success">
        <div className="aria-action-card__title">Hire posted ✓</div>
        <div className="aria-action-card__body">
          {Object.entries(channels).map(([ch, res]) => (
            <div key={ch} className="aria-action-card__row">
              <span className="aria-action-card__row-label">{ch}</span>
              <span className={`aria-action-card__row-val ${res.ok ? 'ok' : (res.stubbed ? 'stub' : 'err')}`}>
                {res.ok ? '✓ posted' : (res.stubbed ? 'stubbed (add API key)' : `× ${res.error || 'failed'}`)}
              </span>
            </div>
          ))}
          <div className="aria-action-card__hint">
            Hire #{result.hireId}. I'll text you when applicants come in.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aria-action-card aria-action-card--hire">
      <div className="aria-action-card__title">Set up the hire</div>
      <div className="aria-action-card__form">
        <label>Role
          <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Journeyman HVAC tech" />
        </label>
        <label>Pay
          <input value={payRate} onChange={(e) => setPayRate(e.target.value)} placeholder="$28/hr" />
        </label>
        <label>Location
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Austin, TX" />
        </label>
        <label>Certs required (comma-separated)
          <input value={certsRequired} onChange={(e) => setCertsRequired(e.target.value)} placeholder="EPA-608, NATE" />
        </label>
      </div>
      {err && <div className="aria-action-card__err">{err}</div>}
      <div className="aria-action-card__actions">
        <button type="button" className="aria-action-card__primary" onClick={run} disabled={posting || !role}>
          {posting ? 'Posting…' : 'Run it →'}
        </button>
      </div>
    </div>
  );
}

// ── DashboardPreviewCard ───────────────────────────────────────────────
// Rendered when conversation response has displayMode='dashboard_preview'.
// Shows the auto-generated dashboard tiles for the owner's trade — the
// "here's how YOURS would look" moment at the end of onboarding.
function DashboardPreviewCard({ dashboard }) {
  if (!dashboard) return null;
  const tiles = Array.isArray(dashboard.tiles) ? dashboard.tiles : [];
  return (
    <div className="aria-action-card aria-action-card--dashboard">
      <div className="aria-action-card__title">{dashboard.headline || 'Your dashboard preview'}</div>
      {dashboard.subhead && (
        <div className="aria-action-card__hint" style={{ marginBottom: 12 }}>{dashboard.subhead}</div>
      )}
      <div className="aria-dash-grid">
        {tiles.map((t, i) => (
          <div key={t.id || i} className={`aria-dash-tile ${t.badge?.kind ? 'aria-dash-tile--' + t.badge.kind : ''}`}>
            <div className="aria-dash-tile__title">{t.title}</div>
            <div className="aria-dash-tile__value">{t.value ?? '—'}</div>
            {t.unit && <div className="aria-dash-tile__unit">{t.unit}</div>}
            {t.trend && <div className="aria-dash-tile__trend">{t.trend}</div>}
            {t.badge && t.badge.text && (
              <div className={`aria-dash-tile__badge aria-dash-tile__badge--${t.badge.kind || 'info'}`}>
                ⚑ {t.badge.text}
              </div>
            )}
            {t.aria_action && (
              <div className="aria-dash-tile__action">{t.aria_action}</div>
            )}
          </div>
        ))}
      </div>
      <div className="aria-action-card__hint" style={{ marginTop: 14, textAlign: 'center' }}>
        Tell Aria one thing on your plate today and she'll take it.
      </div>
    </div>
  );
}

// ── AriaSurfaceCard ────────────────────────────────────────────────────
// Inline wb-*.html preview next to Aria's reply. surface.url is backend-root-
// relative ('/wb-shopping.html'); in Electron the page is loaded via file://,
// so a relative iframe src resolves against the DRIVE ROOT (file:///C:/...)
// and 404s — that was the black box with none of the pictures. Resolve the
// src against the API origin instead. The card stays hidden until the frame
// actually loads; on error or an 8s timeout it unmounts entirely — a failed
// preview shows NOTHING, never a black slab. The allowlist guards messages
// persisted from before the dead-template cleanup (e.g. 'wb-style').
const KNOWN_SURFACE_TEMPLATES = ['wb-shopping', 'wb-presenter'];
function AriaSurfaceCard({ surface }) {
  const [loaded, setLoaded] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const src = React.useMemo(() => {
    const u = surface && surface.url;
    if (!u || typeof u !== 'string') return null;
    if (/^https?:\/\//i.test(u)) return u;
    return `${getApiBase() || ''}${u.startsWith('/') ? u : `/${u}`}`;
  }, [surface]);
  React.useEffect(() => {
    if (loaded || failed) return undefined;
    const t = setTimeout(() => setFailed(true), 8000);
    return () => clearTimeout(t);
  }, [loaded, failed]);
  if (!src || failed || !KNOWN_SURFACE_TEMPLATES.includes(surface.template)) return null;
  return (
    <div className="aria-bubble-row aria-bubble-row--surface"
         style={{ justifyContent: 'center', display: loaded ? undefined : 'none' }}>
      <div className="aria-surface">
        <div className="aria-surface-h">
          <span className="aria-surface-h-trust">{surface.trustSignal || `Layout: ${surface.template}`}</span>
          <a className="aria-surface-h-open" href={src} target="_blank" rel="noreferrer"
             title="Open this layout in a new tab">Open ↗</a>
        </div>
        {/* NOT loading="lazy": a lazy iframe inside display:none never loads,
            so onLoad never fires and the timeout would kill every card. */}
        <iframe
          className="aria-surface-frame"
          src={src}
          title={surface.taskLabel || surface.template}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
        />
      </div>
    </div>
  );
}

// ── PartsOfferCard ─────────────────────────────────────────────────────
// Rendered when conversation response has displayMode='parts_offer'.
// Two modes: 'scan' shows camera/upload button; 'lookup' shows editable
// query + Run lookup button → POST /api/aria/parts/lookup.
function PartsOfferCard({ draft, userId, onResult }) {
  const initial = draft || {};
  const [mode] = React.useState(initial.action || 'lookup');
  const [query, setQuery] = React.useState(initial.query || '');
  const [urgency, setUrgency] = React.useState(initial.urgency || 'normal');
  const [busy, setBusy] = React.useState(false);
  const [scanResult, setScanResult] = React.useState(null);
  const [lookupResult, setLookupResult] = React.useState(null);
  const [err, setErr] = React.useState(null);

  const apiBase = (typeof window !== 'undefined' && window.electronAPI) ? 'http://127.0.0.1:3001' : '';

  // Camera / file picker for scan mode
  const onPickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr(null);
    try {
      const buf = await f.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
      const r = await fetch(`${apiBase}/api/aria/parts/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({
          userId, persona: 'work', imageBase64: base64, mimeType: f.type || 'image/jpeg',
          tradeContext: initial.tradeContext || null,
        }),
      });
      const data = await r.json();
      if (data?.ok) { setScanResult(data); onResult?.(data); }
      else setErr(data?.error || 'Scan failed.');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const runLookup = async () => {
    if (busy || !query) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${apiBase}/api/aria/parts/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
        body: JSON.stringify({ userId, persona: 'work', query, urgency }),
      });
      const data = await r.json();
      if (data?.ok) { setLookupResult(data); onResult?.(data); }
      else setErr(data?.error || 'Lookup failed.');
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  // Show scan result if we got one
  if (scanResult) {
    return (
      <div className="aria-action-card aria-action-card--success">
        <div className="aria-action-card__title">Identified ✓ {scanResult.partName}</div>
        <div className="aria-action-card__body">
          {scanResult.brand && <div><strong>Brand:</strong> {scanResult.brand}</div>}
          {scanResult.model && <div><strong>Model:</strong> {scanResult.model}</div>}
          {scanResult.spec && <div><strong>Spec:</strong> {scanResult.spec}</div>}
          {scanResult.function && <div className="aria-action-card__hint">{scanResult.function}</div>}
          {scanResult.commonFailures?.length > 0 && (
            <div><strong>Common failures:</strong> {scanResult.commonFailures.join(', ')}</div>
          )}
        </div>
        <div className="aria-action-card__actions">
          <button
            type="button"
            className="aria-action-card__primary"
            onClick={() => { setQuery(scanResult.partName); setScanResult(null); }}
          >Find where to buy →</button>
        </div>
      </div>
    );
  }

  // Show lookup ranked results
  if (lookupResult) {
    const top = (lookupResult.ranked || []).slice(0, 5);
    return (
      <div className="aria-action-card aria-action-card--success">
        <div className="aria-action-card__title">Top picks for "{query}"</div>
        <div className="aria-action-card__body">
          {top.length === 0 ? (
            <div className="aria-action-card__hint">
              No direct results — all 8 supplier adapters are stubbed (add B2B keys to .env to activate).
              {lookupResult.coverage && (
                <div style={{ marginTop: 8 }}>
                  Coverage: {lookupResult.coverage.activeAdapters}/{lookupResult.coverage.totalAdapters} adapters live.
                </div>
              )}
            </div>
          ) : (
            top.map((p, i) => (
              <a key={i} href={p.url || '#'} target="_blank" rel="noopener" className="aria-action-card__pick">
                <div className="aria-action-card__pick-name">{p.name}</div>
                <div className="aria-action-card__pick-meta">
                  {p.source} · ${p.price?.toFixed(2)} · {p.inStock ? `In stock${p.qtyAvailable ? ` (${p.qtyAvailable})` : ''}` : 'Out of stock'}
                  {p.distanceMi != null && ` · ${p.distanceMi.toFixed(1)}mi`}
                  {p.shippingEta && ` · ETA ${p.shippingEta}`}
                </div>
              </a>
            ))
          )}
        </div>
      </div>
    );
  }

  // Scan mode — show camera button
  if (mode === 'scan') {
    return (
      <div className="aria-action-card aria-action-card--parts">
        <div className="aria-action-card__title">Take a photo</div>
        <div className="aria-action-card__hint">
          I'll identify the part and tell you what it does, common failures, and where to buy a replacement.
        </div>
        <label className="aria-action-card__primary" style={{ cursor: 'pointer', display: 'inline-block' }}>
          {busy ? 'Identifying…' : '📷 Snap or upload photo'}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onPickImage}
            style={{ display: 'none' }}
            disabled={busy}
          />
        </label>
        {err && <div className="aria-action-card__err">{err}</div>}
      </div>
    );
  }

  // Lookup mode — editable query + run button
  return (
    <div className="aria-action-card aria-action-card--parts">
      <div className="aria-action-card__title">Look it up</div>
      <div className="aria-action-card__form">
        <label>Part / spec
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="GE Z97F9881 45/5 µF capacitor" />
        </label>
        <label>Urgency
          <select value={urgency} onChange={(e) => setUrgency(e.target.value)}>
            <option value="emergency">Emergency (need now)</option>
            <option value="today">Today</option>
            <option value="normal">Normal</option>
            <option value="planned">Planned</option>
          </select>
        </label>
      </div>
      {err && <div className="aria-action-card__err">{err}</div>}
      <div className="aria-action-card__actions">
        <button
          type="button"
          className="aria-action-card__primary"
          onClick={runLookup}
          disabled={busy || !query}
        >{busy ? 'Searching…' : 'Run lookup →'}</button>
      </div>
    </div>
  );
}

// ── Canvas section wrapper ─────────────────────────────────────────────
// "Dashboard" entry point. Shows the project list when no project is open,
// or the project canvas + Aria chat side-by-side when one is.
//
// Aria is ALWAYS present when a project is open. She lives in a right-rail
// panel, knows the project's context, and helps the user with it. Personality
// is locked — only the *project context* she's aware of changes per project.
function CanvasSection({ userId }) {
  const [openId, setOpenId] = React.useState(() => {
    if (typeof window === 'undefined') return null;
    const m = (window.location.hash || '').match(/^#?canvas\/(.+)$/);
    return m ? m[1] : null;
  });
  // The renderer reports the loaded project up so the chat can see its
  // full LayoutSpec + data and pass context to Aria's API.
  const [openProjectData, setOpenProjectData] = React.useState(null);

  React.useEffect(() => {
    const onHash = () => {
      const m = (window.location.hash || '').match(/^#?canvas\/(.+)$/);
      setOpenId(m ? m[1] : null);
      if (!m) setOpenProjectData(null);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const openProject = (id) => {
    setOpenId(id);
    setOpenProjectData(null);
    try { window.location.hash = `canvas/${id}`; } catch (_) {}
  };
  const goBack = () => {
    setOpenId(null);
    setOpenProjectData(null);
    try { window.location.hash = 'canvas'; } catch (_) {}
  };

  return (
    <div className={`canvas-view${openId ? ' canvas-view--with-chat' : ''}`}>
      {openId ? (
        <>
          <div className="canvas-view__main">
            <button
              className="canvas-list__new"
              onClick={goBack}
              style={{ marginBottom: 24 }}
            >← All projects</button>
            <CanvasRenderer
              projectId={openId}
              userId={userId}
              onProjectLoaded={setOpenProjectData}
            />
          </div>
          <CanvasAriaChat
            projectId={openId}
            project={openProjectData}
            userId={userId}
            onProjectChanged={() => {
              // Aria's chat may have triggered a layout change — bump the
              // renderer to refetch. Simplest: change the key.
              setOpenId((id) => id ? `${id}` : id);
            }}
          />
        </>
      ) : (
        <CanvasProjectList userId={userId} onOpen={openProject} />
      )}
    </div>
  );
}
import { getApiBase, fetchWithTimeout, ariaUserIdJsonBody, rewriteDownloadUrlsToPageOrigin, cannotReachMessage } from './apiBase';
import {
  loadVoiceProfile,
  saveVoiceProfile,
  clearVoiceProfile as clearStoredVoiceProfile,
  addBinsToSum,
  sumToMeanVector,
  cosineSimilarity,
  blendEMA,
  momentumForAdaptationCount,
  DEFAULT_VOICE_MATCH_THRESHOLD,
  VOICE_ENROLL_MS,
  MIN_FRAMES_FOR_MATCH,
} from './voiceProfile';
import { ARIA_PREFERRED_NAME_KEY } from './ariaConstants';

/**
 * Build a voiceRead from an HTTP /api/speech/transcribe response.
 *
 * WHY (2026-08-03): the WebSocket streaming path already builds this object in
 * streamingTranscribe.js and hands it to onFinal, which stores it in
 * lastVoiceReadRef and attaches it to the next turn as body.voiceRead. Every
 * OTHER path - the MediaRecorder fallback that takes over after 3 streaming
 * failures, Electron IPC, and iOS - went through transcribeBlob(), which
 * returned only { text, gate, speech } and binned the acoustic read the server
 * had already computed. Aria was acoustically deaf on those turns and nothing
 * logged it.
 *
 * The SHAPE MUST MATCH streamingTranscribe.js exactly: ariaWhisperReader reads
 * pitch_hz / energy / pace from the TOP LEVEL, not from .prosody, so a nested
 * object here would be silently ignored - the same class of bug one layer up.
 */
function buildVoiceRead(data) {
  if (!data) return null;
  const pros = data.prosody || {};
  // Prefer the server's pace; otherwise derive words-per-second from the
  // transcript and the clip duration, the way the streaming path does.
  let pace = (typeof data.pace === 'number') ? data.pace : null;
  if (pace == null) {
    const wc = String(data.text || '').trim().split(/\s+/).filter(Boolean).length;
    const secs = (Number(data.durationMs) || 0) / 1000;
    pace = secs > 0.5 ? Math.round((wc / secs) * 10) / 10 : null;
  }
  const read = {
    emotion: data.emotion || {},
    prosody: pros,
    quality: (typeof data.quality === 'number') ? data.quality : (pros.quality ?? 0),
    pitch_hz: (typeof pros.pitch_hz === 'number') ? pros.pitch_hz : null,
    energy: (typeof pros.energy === 'number') ? pros.energy : null,
    intonation: pros.intonation || '',
    pace,
    // extras the HTTP path has and the WS path does not - carried, not invented
    loudness_db: (typeof pros.loudness_db === 'number') ? pros.loudness_db : null,
    emotionRead: data.emotionRead || null,
    ttsSpeed: (typeof data.ttsSpeed === 'number') ? data.ttsSpeed : null,
  };
  // Nothing acoustic came back -> return null rather than an empty shell, so
  // downstream can tell "no read" from "a read that says neutral".
  const hasSignal = read.pitch_hz != null || read.energy != null ||
    read.emotionRead != null || Object.keys(read.emotion).length > 0;
  return hasSignal ? read : null;
}
import AccountMenu, { AuthGate } from './AccountMenu';
import UsageMeter from './UsageMeter';
import { FirstRunPreviewBadge } from './firstRunPreview';
import OutOfCreditsModal from './OutOfCreditsModal';
import PaymentSheet from './PaymentSheet';
// isDesktopApp comes in UNDER AN ALIAS. This component declares its own
// `const isDesktopApp = ... window.electronAPI?.onFullScreenChange` further
// down, which shadows the import for the whole function scope — so calling
// it from a hook initializer above that line hit the temporal dead zone,
// threw, and the try/catch quietly turned the first-time button off (8/31).
import { canSendMessage, decrementCredits, getCreditsRemaining, isLoggedIn, isDesktopApp as isDesktopShell, isFirstVisit, FREE_CREDITS_INITIAL } from './freeCredits';
import { playSend, playReceive, startThinking, stopThinking } from './sounds';
import AriaSpeakingAvatar from './AriaSpeakingAvatar';
import AriaOrb from './AriaOrb';
import CompanyView from './CompanyView';
import BooksView from './BooksView';
import SystemsView from './SystemsView';
import { advance as advanceFirstRun, matchAside as matchFirstRunAside, reAsk as reAskFirstRun, lineFor as lineForFirstRun } from './firstRunFlow';
import {
  CAUSE as SPEECH_CAUSE, noteAttempt as noteSpeechAttempt, notePlayed as noteSpeechPlayed,
  noteFailure as noteSpeechFailure, causeFromPlayError, fixForCannotHear,
  matchHeardBut, readHearingAnswer, voiceState,
} from './ariaVoiceHealth';
import * as instantVoice from './ariaInstantVoice';
import { snapshot as ariaSnapshot, validAction as validUiAction } from './ariaScreenState';
import { normalChatGptStreamEnabled, streamNormalChat } from './ariaNormalChatStream';
// First-time onboarding routing (web only, 9/26): while the server has the flow open,
// ordinary turns go to /api/ai/conversation instead of the GPT stream - like a tour beat.
import { isOnboardingActive, setOnboardingActive, applyOnboardingSignal } from './onboardingRoute';
// The First Time button starts the VOICE onboarding (Braeden 9/26). While this is
// on, the old walked tour (startAriaTour) and the 22-second idle tour offer stand
// down so nothing competes with her. Kept, not deleted: flip to false to get them back.
const FIRST_TIME_IS_VOICE_ONBOARDING = true;

// ── THE LATENCY WATERFALL (Braeden 9/26) ────────────────────────────────────
// "instrument the full voice pipeline and measure it step by step." The ear
// mints one turn id when its final lands (streamingTranscribe.js); the request
// carries it in body.clientTiming with the ear's stamps; the backend stamps
// arrival / first token / answered; and the stages only this browser can see
// (reply received, audio decided, tts requested, first audio playing) are posted
// here with THIS clock, never inferred from when a diagnostic post arrives.
// Read back: GET /api/pipeline/turn?turnId=… or /api/pipeline/turns.
let _voiceTurnTiming = null;   // { turnId, <stage>: ms } for the voice turn in flight
function _vtStamp(stage, meta) {
  try {
    const t = _voiceTurnTiming;
    if (!t || !t.turnId || t[stage]) return;   // first occurrence only
    t[stage] = Date.now();
    const base = String(getApiBase() || '').replace(/\/$/, '');
    fetch(`${base}/api/pipeline/stamp`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ turnId: t.turnId, stage, at: t[stage], meta: meta || undefined }) }).catch(() => {});
  } catch (_) {}
}
const _vtParam = () => { try { return _voiceTurnTiming && _voiceTurnTiming.turnId ? `&turnId=${encodeURIComponent(_voiceTurnTiming.turnId)}` : ''; } catch (_) { return ''; } };
function _vtWatchPlay(audio, meta) {
  try { if (audio && _voiceTurnTiming && _voiceTurnTiming.turnId) audio.addEventListener('playing', () => _vtStamp('audio.first_play', meta), { once: true }); } catch (_) {}
}
import AriaCues from './AriaCues';
import TalkingBall from './TalkingBall';
import uiSound from './uiSound';
import AriaVisual from './AriaVisual';
import { decide as decideVisual, resolve as resolveVisual } from './ariaVisualDecide';
import { attach as attachAriaAnalyser, micLevel as busMicLevel, level as busLevel, isLive as busIsLive } from './ariaAudioBus';
import LeadsView from './LeadsView';
import GoogleView from './GoogleView';
import JobsPage from './JobsPage';
import JobsView from './JobsView';
import HomeScreen from './HomeScreen';
import NewProjectModal from './NewProjectModal';
import SetupHelpBox from './SetupHelpBox';
import ThinkingTimer from './ThinkingTimer';
import AriaBusinessOnboarding, { fetchOnboardingStatus } from './AriaBusinessOnboarding';
import AriaDock from './AriaDock';
import AssistChatBox from './AssistChatBox';
import { isIOSApp as isIOSNativeApp, BILLING_WEB_URL as ARIA_BILLING_WEB_URL } from './platformDetect';
import { getChannel as getReleaseChannel } from './releaseChannel'; // release-channel UI gating
import useConversationLog from './hooks/useConversationLog';
import useAudioDucking from './hooks/useAudioDucking';
import useBackendHealth from './hooks/useBackendHealth';
import useDesktopUpdates from './hooks/useDesktopUpdates';
import useVoiceCapture, { computeMicRms, TTS_TAIL_GATE_MS, MIC_SPEAKING_RMS_THRESHOLD } from './hooks/useVoiceCapture';
import { startStreamingTranscribe } from './streamingTranscribe';
import AriaThinkingStrip from './AriaThinkingStrip';

// Tracks the in-flight theme view-transition so rapid Day/Night spamming can't
// clobber the direction classes. Only the LATEST transition cleans up.
let _currentThemeVt = null;

const API_BASE = getApiBase();
const API_TIMEOUT_MS = 15000;
const ARIA_AUDIO_PLAYBACK_RATE = 1.0;
const SpeechRecognitionAPI = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
const ARIA_USER_ID_KEY = 'aria_user_id';
const PREFERRED_MIC_ID_KEY = 'aria_preferred_mic_id';
const MIC_DIAGNOSTIC_PASS_RMS = 0.006;
const MIC_DIAGNOSTIC_SOFT_RMS = 0.003;
// Attention system constants (used by attention effects that remain in App.jsx)
const ATTENTION_IDLE_TIMEOUT_MS = 1800000;
const WAKE_WORD_VARIANTS = ['aria', 'area', 'arya', 'aria', 'ariel', 'hey aria', 'ok aria', 'yo aria'];

function chooseSpeechRate(text) {
  const t = String(text || '').trim();
  if (!t) return ARIA_AUDIO_PLAYBACK_RATE;
  const hasSpellingPattern = /\b[A-Za-z](\s+[A-Za-z]){2,}\b/.test(t);
  if (hasSpellingPattern) return 0.9;
  return ARIA_AUDIO_PLAYBACK_RATE; // always natural speed
}

function responseDelayForTranscript(text) {
  const t = String(text || '').trim();
  if (!t) return 0;
  // Complete sentences: send immediately — user finished their thought.
  if (/[.?!]\s*$/.test(t) && t.length > 20) return 150;
  // Very short fragments may be partial thoughts — brief hold.
  if (t.length < 18) return 400;
  // Mid-sentence: short wait to avoid cutting off multi-part speech.
  return 300;
}

// Minimal silent WAV to unlock audio in the same turn as voice (so Aria's reply can auto-play)
function unlockAudioForReply() {
  try {
    const silentWav = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
    const a = new Audio(silentWav);
    a.volume = 0;
    a.play().catch(() => {});
  } catch (_) {}
}

// Hard rule (set by component when mic toggles): Aria only speaks when the mic
// is on. Mic off = TTS audio plays silently. This module-scope flag is the
// single source of truth so every TTS playback site (there are ~10) gets the
// gate via prepareAriaPlayback() without scattering checks everywhere.
let _ariaMicOn = false;
export function _setAriaMicOn(v) {
  _ariaMicOn = !!v;
  if (!_ariaMicOn) {
    _stopActiveAriaAudio();
    try { instantVoice.stop(); } catch (_) {}
  }
}

// ── ONE-AT-A-TIME TTS (owner spec 2026-06-25) ───────────────────────────────
// Aria must never talk over herself. We track the single active reply clip; when
// a NEW clip is about to play, any prior clip is stopped first so only ONE ever
// sounds. In the no-barge-in design a genuine next-turn reply never overlaps a
// playing reply, so this only collapses duplicate/filler clips — a real sentence
// still plays to its natural end (never cut by the mic).
let _activeAriaAudio = null;
function _stopActiveAriaAudio() {
  const a = _activeAriaAudio;
  _activeAriaAudio = null;
  if (a) {
    try { a.muted = true; } catch (_) {}
    try { a.pause(); } catch (_) {}
    try { a.currentTime = 0; } catch (_) {}
    // Release any phrase queue waiting for this stopped clip to finish.
    try { a.dispatchEvent(new Event('ended')); } catch (_) {}
  }
}

/** Ensure TTS / reply audio is audible (Electron + some browsers leave defaults odd).
 *  Also enforces the mic-gated silence rule + the one-at-a-time TTS rule. */
function prepareAriaPlayback(audio) {
  if (!audio) return;
  // Check at play time as well as preparation: a reply may finish fetching
  // after the user mutes. Rejecting releases callers' existing cleanup paths.
  const nativePlay = audio.play.bind(audio);
  audio.play = () => {
    if (!_ariaMicOn) {
      audio.muted = true;
      return Promise.reject(new DOMException('Voice is muted', 'AbortError'));
    }
    return nativePlay();
  };
  // ONE-AT-A-TIME: stop whatever clip is currently active before this one starts.
  if (_activeAriaAudio && _activeAriaAudio !== audio) {
    try { _activeAriaAudio.pause(); } catch (_) {}
    try { _activeAriaAudio.currentTime = 0; } catch (_) {}
  }
  _activeAriaAudio = audio;
  // Clear the tracker when this clip ends/errors so the NEXT reply isn't mistaken
  // for an overlap (which would otherwise mute her). Both the real 'ended' and
  // the mic-mute gate's dispatched 'ended' fire this.
  try {
    audio.addEventListener('ended', () => { if (_activeAriaAudio === audio) _activeAriaAudio = null; }, { once: true });
    audio.addEventListener('error', () => { if (_activeAriaAudio === audio) _activeAriaAudio = null; }, { once: true });
  } catch (_) {}
  try {
    audio.muted = false;
    audio.volume = 1;
  } catch (_) {}
  // Voice speed (Settings → Voice & Audio). Every Aria clip flows through here,
  // so this one line makes the user's chosen pace apply everywhere — reply,
  // filler, bridge, preview. Clamped to a natural range; default 1.0 (natural).
  try {
    const sp = parseFloat(localStorage.getItem('aria_voice_speed') || '');
    if (sp >= 0.7 && sp <= 1.4) audio.playbackRate = sp;
  } catch (_) {}
  // Mute now means silence, including replies that arrive after the click.
}

// Open the Stripe-hosted billing portal (update card, view invoices, cancel).
// Reads the signed-in user id from localStorage and redirects the tab to Stripe.
// Shared by Settings and the account menu's "Manage billing".
async function openBillingPortal() {
  let userId = '', email = '';
  try {
    userId = localStorage.getItem(ARIA_USER_ID_KEY) || '';
    email = localStorage.getItem('aria_user_email') || localStorage.getItem('aria_session_email') || '';
  } catch (_) {}
  if (!email) { alert('Sign in first to manage your billing.'); return; }
  try {
    const returnUrl = `${window.location.origin}/#ask`;
    const r = await fetchWithTimeout(`${API_BASE}/api/stripe/portal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // The server resolves the Stripe customer from userId/email.
      body: JSON.stringify({ userId, email, returnUrl }),
      timeout: 15000,
    });
    const d = await r.json().catch(() => ({}));
    const url = d && (d.portalUrl || d.url);
    if (url) { window.location.href = url; }
    else if (d && d.error === 'no_customer') { alert("You don't have any billing set up yet — add a payment method or pick a paid plan first."); }
    else { alert("Couldn't open billing right now. Please try again."); }
  } catch (_) {
    alert('Billing portal is unavailable right now. Please try again shortly.');
  }
}

// GlobalCheckout — the ONE subscription surface. Mounted at the app root so the
// payment popup opens over WHATEVER screen the user is on; there is no separate
// subscription page to navigate to. Triggered by the `aria-open-checkout` event
// (from the AuthGate "Add payment", "Go Pro", plan chooser, etc.) or by the
// `aria_intent_subscribe` flag on load (covers a fresh reload after signup).
function GlobalCheckout() {
  const [paySheet, setPaySheet] = useState(null);
  useEffect(() => {
    const openPro = () => setPaySheet({ plan: 'subscription', planLabel: 'HowTo Pro', priceLabel: '$20/month — full access, private', ctaLabel: 'Subscribe — $20/month' });
    const openUltra = () => setPaySheet({ plan: 'ultra', planLabel: 'HowTo Ultra', priceLabel: '$200/month — Aria API for your own software (+ usage)', ctaLabel: 'Subscribe — $200/month' });
    try {
      const flag = localStorage.getItem('aria_intent_subscribe');
      if (flag === '1' || flag === 'ultra') { localStorage.removeItem('aria_intent_subscribe'); flag === 'ultra' ? openUltra() : openPro(); }
    } catch (_) {}
    const onOpen = (e) => { try { localStorage.removeItem('aria_intent_subscribe'); } catch (_) {} (e?.detail?.plan === 'ultra' ? openUltra : openPro)(); };
    window.addEventListener('aria-open-checkout', onOpen);
    return () => window.removeEventListener('aria-open-checkout', onOpen);
  }, []);
  if (!paySheet) return null;
  return (
    <PaymentSheet
      plan={paySheet.plan}
      planLabel={paySheet.planLabel}
      priceLabel={paySheet.priceLabel}
      ctaLabel={paySheet.ctaLabel}
      onClose={() => setPaySheet(null)}
      onSuccess={() => {
        setPaySheet(null);
        // Nudge the AuthGate + any credits UI to re-check entitlement now that
        // payment is on file (the gate also self-clears on its 5s poll).
        try { window.dispatchEvent(new CustomEvent('aria-billing-identity', { detail: {} })); } catch (_) {}
      }}
    />
  );
}

function SubscriptionCreditsBlock() {
  const [plans, setPlans] = useState([]);
  const [credits, setCredits] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeInfo, setStripeInfo] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [userId, setUserIdState] = useState(() => typeof localStorage !== 'undefined' ? localStorage.getItem(ARIA_USER_ID_KEY) || '' : '');
  const [email, setEmail] = useState(() => { try { return localStorage.getItem('aria_user_email') || localStorage.getItem('aria_session_email') || ''; } catch (_) { return ''; } });
  const [loading, setLoading] = useState(false);
  // Embedded (on-site) checkout — when set, renders the Stripe Elements sheet
  // instead of redirecting to a Stripe-hosted page.
  const [paySheet, setPaySheet] = useState(null); // { plan, planLabel, priceLabel, ctaLabel }

  // "Buy" from the signup plan chooser: AccountMenu marks the intent and
  // fires aria-open-checkout — the payment POPUP (card/bank slider inside)
  // opens itself. Also checks the flag on mount (covers a reload landing).
  useEffect(() => {
    const openPro = () => setPaySheet({ plan: 'subscription', planLabel: 'HowTo Pro', priceLabel: '$20/month — full access, private', ctaLabel: 'Subscribe — $20/month' });
    const openUltra = () => setPaySheet({ plan: 'ultra', planLabel: 'HowTo Ultra', priceLabel: '$200/month — Aria API for your own software (+ usage)', ctaLabel: 'Subscribe — $200/month' });
    try {
      const flag = localStorage.getItem('aria_intent_subscribe');
      if (flag === '1' || flag === 'ultra') {
        localStorage.removeItem('aria_intent_subscribe');
        flag === 'ultra' ? openUltra() : openPro();
      }
    } catch (_) {}
    const onOpen = (e) => { try { localStorage.removeItem('aria_intent_subscribe'); } catch (_) {} (e?.detail?.plan === 'ultra' ? openUltra : openPro)(); };
    window.addEventListener('aria-open-checkout', onOpen);
    return () => window.removeEventListener('aria-open-checkout', onOpen);
  }, []);

  const loadPlans = () => {
    fetchWithTimeout(`${API_BASE}/api/subscription/plans`, { timeout: API_TIMEOUT_MS })
      .then((r) => r.json())
      .then((d) => d.ok && d.plans && setPlans(d.plans))
      .catch(() => {});
  };
  const loadCredits = (uid) => {
    if (!uid) return setCredits(null);
    setLoading(true);
    fetchWithTimeout(`${API_BASE}/api/subscription/credits?userId=${encodeURIComponent(uid)}`, { timeout: API_TIMEOUT_MS })
      .then((r) => r.json())
      .then((d) => setCredits(d))
      .catch(() => setCredits(null))
      .finally(() => setLoading(false));
  };
  const loadStripeStatus = () => {
    fetchWithTimeout(`${API_BASE}/api/stripe/status`, { timeout: API_TIMEOUT_MS })
      .then((r) => r.json())
      .then((d) => setStripeStatus(d))
      .catch(() => setStripeStatus({ ok: true, configured: false }));
  };
  const loadStripeInfo = (uid) => {
    if (!uid) return setStripeInfo(null);
    fetchWithTimeout(`${API_BASE}/api/stripe/subscription?userId=${encodeURIComponent(uid)}`, { timeout: API_TIMEOUT_MS })
      .then((r) => r.json())
      .then((d) => d.ok ? setStripeInfo(d) : setStripeInfo(null))
      .catch(() => setStripeInfo(null));
  };

  useEffect(() => { loadPlans(); loadStripeStatus(); }, []);
  useEffect(() => {
    loadCredits(userId || null);
    loadStripeInfo(userId || null);
  }, [userId]);

  // ── Cross-device user-sync (Owner spec 2026-05-06) ─────────────
  // Open WebSocket on sign-in so phone uploads, settings, etc. push
  // here in real time. Auto-disconnect on sign-out.
  useEffect(() => {
    if (!userId) {
      userSync.disconnect();
      return;
    }
    userSync.connect(userId);
    const off = userSync.onSyncEvent((event) => {
      // Fire a window event so any component (file lists, settings panels,
      // etc.) can listen without coupling to App.jsx.
      window.dispatchEvent(new CustomEvent('aria:sync', { detail: event }));
    });
    return () => { off(); };
  }, [userId]);

  // Check for Stripe checkout success/cancel in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_success') === '1') {
      window.history.replaceState({}, '', window.location.pathname + '#subscription');
      loadCredits(userId || null);
      loadStripeInfo(userId || null);
    }
  }, [userId]);

  // Pick up a sign-in that happens WHILE this view is mounted (AccountMenu
  // fires these on auth success). Without this, a user who signs in from the
  // out-of-funds modal would have a userId+email in localStorage but the
  // already-rendered checkout buttons wouldn't appear until a manual reload.
  useEffect(() => {
    const onIdentity = () => {
      try {
        const uid = localStorage.getItem(ARIA_USER_ID_KEY) || '';
        const em = localStorage.getItem('aria_user_email') || localStorage.getItem('aria_session_email') || '';
        if (uid) setUserIdState(uid);
        if (em) setEmail(em);
      } catch (_) {}
    };
    window.addEventListener('aria-billing-identity', onIdentity);
    window.addEventListener('aria-account-id', onIdentity);
    return () => {
      window.removeEventListener('aria-billing-identity', onIdentity);
      window.removeEventListener('aria-account-id', onIdentity);
    };
  }, []);

  const saveUserId = (v) => {
    const val = (v || '').trim();
    setUserIdState(val);
    try {
      if (val) localStorage.setItem(ARIA_USER_ID_KEY, val);
      else localStorage.removeItem(ARIA_USER_ID_KEY);
    } catch (_) {}
    loadCredits(val || null);
    loadStripeInfo(val || null);
  };

  const saveEmail = (v) => {
    const val = (v || '').trim();
    setEmail(val);
    try {
      if (val) localStorage.setItem('aria_user_email', val);
      else localStorage.removeItem('aria_user_email');
    } catch (_) {}
  };

  const stripeConfigured = stripeStatus?.configured === true;

  const startCheckout = async (type) => {
    if (!userId || !email) return;
    setCheckoutError('');
    setCheckoutLoading(type);
    try {
      const endpoint = type === 'subscription'
        ? `${API_BASE}/api/stripe/checkout/subscription`
        : `${API_BASE}/api/stripe/checkout/payg`;
      const successUrl = `${window.location.origin}/?stripe_success=1#subscription`;
      const cancelUrl = `${window.location.origin}/#subscription`;
      const r = await fetchWithTimeout(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, email, successUrl, cancelUrl }),
        timeout: 15000,
      });
      const d = await r.json();
      if (d.url) {
        window.location.href = d.url;
      } else {
        setCheckoutError('Couldn\'t start checkout. Please try again.');
      }
    } catch (e) {
      setCheckoutError('Payment service unavailable. Check your connection and try again.');
    } finally {
      setCheckoutLoading('');
    }
  };

  const openBillingPortal = async () => {
    if (!userId) return;
    setCheckoutError('');
    setCheckoutLoading('portal');
    try {
      const returnUrl = `${window.location.origin}/#subscription`;
      const r = await fetchWithTimeout(`${API_BASE}/api/stripe/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, returnUrl }),
        timeout: 15000,
      });
      const d = await r.json();
      if (d.url) {
        window.location.href = d.url;
      } else {
        setCheckoutError('Couldn\'t open billing portal. Please try again.');
      }
    } catch (e) {
      setCheckoutError('Billing portal unavailable right now. Try again shortly.');
    } finally {
      setCheckoutLoading('');
    }
  };

  // Platform gate: on the iOS native app, App Store guideline 3.1.3 forbids
  // in-app subscription UI that bypasses StoreKit. Plans still show (so users
  // know what tiers exist), but the Subscribe/Upgrade buttons hide and we
  // surface a "sign up on the web" notice.
  const onIOS = isIOSNativeApp();

  return (
    <div className="subscription-credits-block">
      {paySheet && (
        <PaymentSheet
          plan={paySheet.plan}
          planLabel={paySheet.planLabel}
          priceLabel={paySheet.priceLabel}
          ctaLabel={paySheet.ctaLabel}
          onClose={() => setPaySheet(null)}
          onSuccess={() => { setPaySheet(null); loadCredits(userId || null); loadStripeInfo(userId || null); }}
        />
      )}
      <h3 className="subscription-credits-title">Plans</h3>
      {onIOS && (
        <div style={{
          padding: '18px 20px',
          marginBottom: 16,
          borderRadius: 14,
          background: 'linear-gradient(155deg, rgba(0,217,255,0.08), rgba(0,217,255,0.02))',
          border: '0.5px solid rgba(0,217,255,0.32)',
          color: '#fff',
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
            🌐 Subscriptions live on the web
          </div>
          <div style={{ fontSize: 13, color: '#cbd0d6', lineHeight: 1.55 }}>
            Aria is a multi-platform service. To subscribe or change plans, sign in at{' '}
            <span style={{ color: '#00d9ff', fontWeight: 600 }}>howtoaria.com</span>{' '}
            from any browser. Your existing Aria login on this device will pick it up automatically.
          </div>
        </div>
      )}
      {plans.length > 0 && (
        <div className="subscription-plans-grid">
          {plans.map((p) => {
            const cents = p.creditsAllowanceCents;
            const isFree = p.id === 'free';
            const isBasic = p.id === 'basic_20';
            const isPayg = p.id === 'pay_as_you_go';
            const isCurrentPlan = credits?.planId === p.id;
            return (
              <div key={p.id} className={`subscription-plan-card ${isCurrentPlan ? 'subscription-plan-card--active' : ''} ${isBasic ? 'subscription-plan-card--featured' : ''}`}>
                {isBasic && <span className="subscription-plan-badge">Most Popular</span>}
                {isCurrentPlan && <span className="subscription-plan-badge subscription-plan-badge--current">Current Plan</span>}
                <h4 className="subscription-plan-card-name">{p.name}</h4>
                <div className="subscription-plan-card-price">
                  {isFree ? 'Free' : isBasic ? '$20' : 'Usage-based'}
                  {isBasic && <span className="subscription-plan-price-period">/month</span>}
                </div>
                <p className="subscription-plan-card-desc">{p.description}</p>
                <ul className="subscription-plan-features">
                  {isFree && <li>$1.00 of free usage to try Aria</li>}
                  {isFree && <li>Voice, images, conversation</li>}
                  {isFree && <li>No credit card required</li>}
                  {isBasic && <li>$20.00 of usage per month</li>}
                  {isBasic && <li>All AI models included</li>}
                  {isBasic && <li>Premium voice, images, video</li>}
                  {isBasic && <li>Priority support</li>}
                  {isPayg && <li>Pay only for what you use</li>}
                  {isPayg && <li>Metered billing to your card</li>}
                  {isPayg && <li>No monthly commitment</li>}
                  {isPayg && <li>All features included</li>}
                </ul>
                {stripeConfigured && userId && email && !isCurrentPlan && !onIOS && (
                  <div className="subscription-plan-card-action">
                    {isBasic && (
                      <button
                        type="button"
                        className="subscription-checkout-btn subscription-checkout-btn--primary"
                        onClick={() => setPaySheet({ plan: 'subscription', planLabel: 'Aria membership', priceLabel: '$20/month — full access to everything', ctaLabel: 'Subscribe — $20/month' })}
                      >
                        Subscribe Now
                      </button>
                    )}
                    {isPayg && (
                      <button
                        type="button"
                        className="subscription-checkout-btn subscription-checkout-btn--secondary"
                        onClick={() => setPaySheet({ plan: 'payg', planLabel: 'Pay as you go', priceLabel: 'Only pay for what you use — billed to your card', ctaLabel: 'Add card & start' })}
                      >
                        Start Pay as You Go
                      </button>
                    )}
                    {isFree && !credits?.planId && (
                      <button
                        type="button"
                        className="subscription-checkout-btn subscription-checkout-btn--outline"
                        onClick={async () => {
                          try {
                            const r = await fetchWithTimeout(`${API_BASE}/api/subscription/assign`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ userId, planId: 'free' }),
                              timeout: API_TIMEOUT_MS,
                            });
                            const d = await r.json();
                            if (d.ok) loadCredits(userId);
                          } catch (_) {}
                        }}
                      >
                        Start Free Trial
                      </button>
                    )}
                  </div>
                )}
                {!stripeConfigured && (isBasic || isPayg) && (
                  <p className="subscription-plan-stripe-note">Payment processing coming soon</p>
                )}
              </div>
            );
          })}
        </div>
      )}
      {checkoutError && (
        <p className="subscription-checkout-error" role="alert">{checkoutError}</p>
      )}

      {/* Account & Billing */}
      <h3 className="subscription-credits-title">Your Account</h3>
      <div className="subscription-account-fields">
        <label className="subscription-field-label">
          User ID
          <input
            type="text"
            className="subscription-field-input"
            placeholder="e.g. user_abc123"
            value={userId}
            onChange={(e) => saveUserId(e.target.value)}
          />
        </label>
        <label className="subscription-field-label">
          Email (for billing)
          <input
            type="email"
            className="subscription-field-input"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => saveEmail(e.target.value)}
          />
        </label>
      </div>

      {userId && (
        <div className="subscription-credits-status">
          {loading ? (
            <p>Loading...</p>
          ) : credits && credits.ok ? (
            <>
              {credits.billingExempt ? (
                <div className="subscription-credits-card subscription-credits-card--owner">
                  <span className="subscription-credits-card-icon">&#9733;</span>
                  <p>Owner account — unlimited usage, no billing.</p>
                </div>
              ) : credits.planId && credits.allowanceCents != null && !Number.isFinite(credits.allowanceCents) ? (
                <div className="subscription-credits-card">
                  <span className="subscription-credits-card-icon">&#9889;</span>
                  <p>Pay as you go — unlimited usage, billed to your card.</p>
                </div>
              ) : credits.planId ? (
                <div className="subscription-credits-card">
                  <div className="subscription-credits-bar-wrap">
                    <div className="subscription-credits-bar" style={{ width: `${Math.min(100, ((credits.remainingCents || 0) / (credits.allowanceCents || 1)) * 100)}%` }} />
                  </div>
                  <p className="subscription-credits-remaining">
                    <strong>${((credits.remainingCents || 0) / 100).toFixed(2)}</strong> of ${((credits.allowanceCents || 0) / 100).toFixed(2)} usage remaining
                    {credits.periodEnd && <span> — resets {new Date(credits.periodEnd).toLocaleDateString()}</span>}
                  </p>
                </div>
              ) : (
                <div className="subscription-credits-card subscription-credits-card--empty">
                  <p>{credits.message || 'No active plan. Choose a plan above to get started.'}</p>
                </div>
              )}
              {credits.planId && !credits.billingExempt && !credits.hasCredits && (
                <div className="subscription-credits-card subscription-credits-card--warning">
                  <p>You've hit your usage limit this period. {credits.periodEnd ? `Resets ${new Date(credits.periodEnd).toLocaleDateString()}.` : ''} Upgrade to $20/month for more, or switch to pay-as-you-go.</p>
                </div>
              )}
            </>
          ) : (
            <div className="subscription-credits-card subscription-credits-card--empty">
              <p>No plan yet. Enter your User ID and email above, then pick a plan to get started.</p>
            </div>
          )}

          {/* Manage billing — hidden on iOS native (App Store guideline 3.1.3).
              On iOS, the subscription notice above tells users to manage on web. */}
          {stripeConfigured && stripeInfo?.hasSubscription && !onIOS && (
            <button
              type="button"
              className="subscription-manage-btn"
              disabled={!!checkoutLoading}
              onClick={openBillingPortal}
            >
              {checkoutLoading === 'portal' ? 'Opening...' : 'Manage Billing & Payment Methods'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const LAYOUT_STORAGE_KEY = 'aria_sidebar_layout';
const ONBOARDING_DONE_KEY = 'aria_desktop_onboarding_done';
const SCREEN_ACCESS_CONFIRMED_KEY = 'aria_screen_access_confirmed';
const DESKTOP_UIFLAG_ONBOARDING_DONE = 'desktop.onboarding.done';
const DESKTOP_UIFLAG_SCREEN_CONFIRMED = 'desktop.screen_access_confirmed';
const DESKTOP_INTRO_SPOKEN_KEY = 'aria_desktop_intro_spoken';
const DESKTOP_UIFLAG_INTRO_SPOKEN = 'desktop.intro_spoken';
/** Desktop: user explicitly muted — do not auto re-enable the mic. */
const DESKTOP_MIC_USER_MUTED_KEY = 'aria_desktop_mic_user_muted';
/** Session persistence — conversation messages and history. */
const ARIA_SESSION_MESSAGES_KEY = 'aria_session_messages';
const ARIA_SESSION_HISTORY_KEY = 'aria_session_history';
const ARIA_SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const ARIA_CONVERSATION_LOG_KEY = 'aria_conversation_log';
const ARIA_CONVERSATION_ARCHIVE_KEY = 'aria_conversation_archive';
const CONVERSATION_DAY_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Read today's conversation log from localStorage */
function readConversationLog() {
  try {
    const raw = localStorage.getItem(ARIA_CONVERSATION_LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.startedAt) return [];
    // Reset if older than 24 hours
    if (Date.now() - parsed.startedAt > CONVERSATION_DAY_MS) {
      // Archive before clearing
      archiveConversationLog(parsed);
      return [];
    }
    return parsed.entries || [];
  } catch (_) { return []; }
}

/** Write today's conversation log. Signed out, there is no log — see
 *  writePersistedSession for the same rule and why (Braeden 9/4). */
function writeConversationLog(entries) {
  try {
    if (!_keepsState()) {
      try { localStorage.removeItem(ARIA_CONVERSATION_LOG_KEY); } catch (_) {}
      try { localStorage.removeItem(ARIA_CONVERSATION_ARCHIVE_KEY); } catch (_) {}
      return;
    }
  } catch (_) {}
  try {
    const existing = localStorage.getItem(ARIA_CONVERSATION_LOG_KEY);
    let startedAt = Date.now();
    if (existing) {
      const parsed = JSON.parse(existing);
      if (parsed && parsed.startedAt && (Date.now() - parsed.startedAt < CONVERSATION_DAY_MS)) {
        startedAt = parsed.startedAt;
      }
    }
    localStorage.setItem(ARIA_CONVERSATION_LOG_KEY, JSON.stringify({ startedAt, entries }));
  } catch (_) {}
}

/** Archive a day's conversation log for history browsing */
function archiveConversationLog(dayLog) {
  try {
    const raw = localStorage.getItem(ARIA_CONVERSATION_ARCHIVE_KEY);
    const archive = raw ? JSON.parse(raw) : [];
    const dateStr = new Date(dayLog.startedAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    archive.push({ date: dateStr, startedAt: dayLog.startedAt, entries: dayLog.entries || [] });
    // Keep last 30 days max
    while (archive.length > 30) archive.shift();
    localStorage.setItem(ARIA_CONVERSATION_ARCHIVE_KEY, JSON.stringify(archive));
  } catch (_) {}
}

/** Read archived conversation days */
function readConversationArchive() {
  try {
    const raw = localStorage.getItem(ARIA_CONVERSATION_ARCHIVE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) { return []; }
}
/** Read back a persisted session (returns null if stale or missing). */
function readPersistedSession(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.savedAt) return null;
    if (Date.now() - parsed.savedAt > ARIA_SESSION_MAX_AGE_MS) return null;
    return parsed.data ?? null;
  } catch (_) { return null; }
}
// ── THE RULE IS FOR THE WEBSITE, NOT THE DESKTOP APP ─────────────────────
//
// Braeden's rule (9/4) is about a first-time visitor to the SITE. The desktop
// app is his own machine and has no session token, so gating on isLoggedIn
// alone would have wiped his projects and history on every launch. The app
// already carries this exemption elsewhere (the credits gate reads
// `!isLoggedIn() && !isDesktopShell()`); this matches it.
function _keepsState() {
  try { return isLoggedIn() || isDesktopShell(); } catch (_) { return true; }
}

function writePersistedSession(key, data) {
  // Signed out on the web, nothing is written down. readPersistedSession was
  // already gated for history, but the WRITE was not — so an anonymous
  // visitor's session still sat in localStorage afterwards, unread but saved.
  // "Nothing is saved" has to mean nothing is written, not merely nothing is
  // loaded.
  try { if (!_keepsState()) { try { localStorage.removeItem(key); } catch (_) {} return; } } catch (_) {}
  try { localStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), data })); } catch (_) {}
}
const DESKTOP_UIFLAG_MIC_USER_MUTED = 'desktop.mic_user_muted';
const DESKTOP_FIRST_RUN_UX_COMPLETE_KEY = 'aria_desktop_first_run_ux_complete';
const DESKTOP_WAKE_LINE_DONE_KEY = 'aria_desktop_wake_line_done';
const DESKTOP_UIFLAG_FIRST_RUN_UX = 'desktop.first_run_ux_complete';
/** First-run / replay: voice enrollment wizard (desktop). */
const VOICE_ONBOARDING_DONE_KEY = 'aria_voice_onboarding_done';

function readDesktopMicUserMuted() {
  // Always start unmuted on desktop — main.js resets the flag to '0' on each launch.
  // The IPC flags effect will read the authoritative value from config shortly after mount.
  // Returning false here prevents a brief "muted=true" window from stale localStorage.
  return false;
}

function persistDesktopMicUserMuted(muted) {
  try {
    if (muted) localStorage.setItem(DESKTOP_MIC_USER_MUTED_KEY, '1');
    else localStorage.removeItem(DESKTOP_MIC_USER_MUTED_KEY);
  } catch (_) {}
  try {
    window.electronAPI?.setUiFlag?.(DESKTOP_UIFLAG_MIC_USER_MUTED, muted ? '1' : '0');
  } catch (_) {}
}
const ARIA_PREFERRED_NAME_BACKUP_KEY = 'aria_preferred_name_backup';

/** First-meet: normalize spoken or typed name input. */
function cleanNameFromRaw(raw) {
  const t = String(raw || '').trim();
  if (!t) return '';
  let s = t
    .replace(/^i'?m\s+/i, '')
    .replace(/^my name is\s+/i, '')
    .replace(/^this is\s+/i, '')
    .replace(/^it's\s+/i, '')
    .replace(/^call me\s+/i, '')
    .split(/[.,;!?]/)[0]
    .trim()
    .replace(/\s+/g, ' ');
  if (s.length > 48) s = s.slice(0, 48).trim();
  return s;
}

function isLikelyUserName(name) {
  const n = String(name || '').trim();
  if (!n) return false;
  if (!/^[A-Za-z][A-Za-z' -]{0,46}$/.test(n)) return false;
  const words = n.split(/\s+/).filter(Boolean);
  if (words.length < 1 || words.length > 3) return false;
  const lower = n.toLowerCase();
  const banned = /\b(help|what|can|you|with|today|please|need|aria|assistant|hello|hey|good|morning|afternoon|evening)\b/i;
  if (banned.test(lower)) return false;
  return words.every((w) => w.length >= 2 && w.length <= 20);
}

function readAwaitingIntroName() {
  try {
    if (typeof window === 'undefined' || !window.electronAPI) return false;
    return localStorage.getItem(DESKTOP_INTRO_SPOKEN_KEY) === '1' && !(localStorage.getItem(ARIA_PREFERRED_NAME_KEY) || '').trim();
  } catch (_) {
    return false;
  }
}

/** Desktop: when the mic may feed Whisper. Only `name_only` (first name) and `free` (normal). */
function readDesktopVoicePolicy() {
  if (typeof window === 'undefined' || !window.electronAPI) return 'free';
  try {
    if (localStorage.getItem(DESKTOP_FIRST_RUN_UX_COMPLETE_KEY) === '1') return 'free';
    if (localStorage.getItem(DESKTOP_INTRO_SPOKEN_KEY) !== '1') return 'intro_pending';
    if (!(localStorage.getItem(ARIA_PREFERRED_NAME_KEY) || '').trim()) return 'name_only';
    // Intro done + name set → Aria can chat freely.
    // The examples modal may still show, but it should NOT block responses.
    // Auto-complete so returning users are never stuck in 'hold' across restarts.
    try { localStorage.setItem(DESKTOP_FIRST_RUN_UX_COMPLETE_KEY, '1'); } catch (_) {}
    return 'free';
  } catch (_) {
    return 'free';
  }
}

/** Shown when the chat is empty so users see modern examples of what to ask. */
const ASK_HELP_EXAMPLE_PROMPTS = [
  {
    title: 'Screen & computer help',
    description:
      'Walk through something on your computer—like a clear, step-by-step demo of how to finish a task or set up a workflow.',
    prompt:
      "Walk me through how to get something done on my computer—step by step, like you're showing me on screen. Start by asking what I'm trying to do.",
  },
  {
    title: 'Cooking at home',
    description: 'Turn what you already have in the kitchen into a simple meal or recipe plan.',
    prompt:
      'Help me figure out what to cook at home with what I have. Ask me what ingredients I have, then suggest a simple recipe.',
  },
  {
    title: 'Parts & shopping online',
    description: 'Find affordable options that are still reliable—what to compare, where to look, and what to avoid.',
    prompt:
      "Help me find cheap but reliable parts for my project online. Ask what I'm building, then suggest how to compare sellers, read reviews, and spot red flags.",
  },
];

function getStoredLayout() {
  if (typeof localStorage === 'undefined') return { left: true, right: true };
  try {
    const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
    if (!raw) return { left: true, right: true };
    const parsed = JSON.parse(raw);
    return {
      left: parsed.left !== false,
      right: parsed.right !== false,
    };
  } catch {
    return { left: true, right: true };
  }
}

function setStoredLayout(left, right) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify({ left, right }));
  } catch {}
}

function formatInstallerBytes(n) {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return null;
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/**
 * Top-right persistent Aria text bubble.
 * A small pill-shaped surface that always sits in the top-right of the
 * screen so Aria can text the user without speaking.
 * - Shows the latest assistant message (truncated, single line).
 * - Shows "thinking…" while loading, "speaking…" while Aria has voice.
 * - Clicking opens the full conversation overlay.
 * - When idle and there's nothing yet, shows a soft "Aria" placeholder.
 * NOTE: this surface is text-only. Voice never plays from here; voice
 * is fully gated by prepareAriaPlayback() per the strict mic-mute rule.
 */
// Inline pay-as-you-go notice that appears under the "Allow extra usage" toggle
// when it's ON. Has a short headline + a "Read more" expander with the full
// explanation of how overage billing works.
function OverageNotice() {
  const [more, setMore] = useState(false);
  return (
    <div className="overage-notice">
      <div className="overage-notice__head">
        <span className="overage-notice__dot" />
        <strong>Pay-as-you-go is on.</strong>
        <span className="overage-notice__short">
          Whatever you use past your plan gets billed at the end of the month.
        </span>
      </div>
      <button
        type="button"
        className="overage-notice__more-btn"
        onClick={() => setMore(v => !v)}
      >
        {more ? 'Hide details' : 'Click for more'}
      </button>
      {more && (
        <div className="overage-notice__detail">
          <p>
            <strong>How it works:</strong> Your plan includes a monthly allowance of
            usage. Once you've used it up, instead of stopping Aria mid-task,
            we keep her running and meter the extra usage at our metered rates.
          </p>
          <p>
            <strong>Billed when:</strong> Every overage charge is added to your monthly
            invoice — no surprise mid-month charges. You can see exactly what you used
            in <em>Subscription → Usage</em> at any time.
          </p>
          <p>
            <strong>If you turn this off:</strong> Aria will pause when you hit your
            plan cap and ask if you want to top up. Nothing gets billed unless you
            opt in to it.
          </p>
        </div>
      )}
    </div>
  );
}

// Mandatory confirmation before turning ON pay-as-you-go billing.
// Default view is short — most people won't read a wall of text.
// "See details" expands to the full breakdown for those who want it.
function OverageConfirmModal({ onCancel, onConfirm }) {
  const [showDetails, setShowDetails] = useState(false);
  // Portal to <body> — this fixed-position dialog is rendered from inside the
  // Settings sliding track (which carries a transform), and a transform ancestor
  // would trap position:fixed and mis-place it. Portaling escapes that.
  return createPortal(
    <div className="overage-confirm__backdrop" onClick={onCancel}>
      <div className="overage-confirm" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Confirm extra usage">
        <div className="overage-confirm__head">
          <div className="overage-confirm__dot" />
          <h3>Are you sure?</h3>
        </div>
        <div className="overage-confirm__body">
          <p>
            Turning this on means <strong>pay-as-you-go</strong>: any usage past
            your monthly plan gets billed to you at the end of the month.
          </p>

          {showDetails ? (
            <>
              <ul className="overage-confirm__list">
                <li><strong>How it works:</strong> Your plan includes a fixed dollar allowance each month. When you hit it, instead of Aria stopping mid-task, she keeps going at our metered rate.</li>
                <li><strong>Billed when:</strong> Every overage charge lands on your monthly invoice — no surprise mid-month charges.</li>
                <li><strong>You stay in control:</strong> Watch usage anytime in <em>Subscription → Usage</em>. Flip this off and Aria pauses at your plan cap until you top up.</li>
                <li><strong>What it costs:</strong> Most overage turns out to be a few dollars a month for normal use. We surface the running total in Usage so it's never a guess.</li>
              </ul>
              <button
                type="button"
                className="overage-confirm__toggle"
                onClick={() => setShowDetails(false)}
              >
                Show less
              </button>
            </>
          ) : (
            <button
              type="button"
              className="overage-confirm__toggle"
              onClick={() => setShowDetails(true)}
            >
              See details
            </button>
          )}
        </div>
        <div className="overage-confirm__actions">
          <button type="button" className="overage-confirm__cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="overage-confirm__confirm" onClick={onConfirm}>Yes, turn it on</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AriaTopRightBubble({ messages, loading, ariaSpeaking, onClick }) {
  // Find the most recent assistant message (skip pure-tool / pure-data turns).
  const lastAria = (() => {
    if (!Array.isArray(messages)) return null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m && m.role === 'assistant' && typeof m.content === 'string' && m.content.trim()) {
        return m.content.trim();
      }
    }
    return null;
  })();

  let label;
  let muted = false;
  if (ariaSpeaking) {
    label = 'speaking…';
  } else if (loading) {
    label = 'thinking…';
  } else if (lastAria) {
    // Single-line preview; CSS handles the ellipsis but trim long whitespace.
    label = lastAria.replace(/\s+/g, ' ');
  } else {
    label = 'Aria';
    muted = true;
  }

  return (
    <button
      type="button"
      className={'aria-top-bubble' + (muted ? ' aria-top-bubble--muted' : '')}
      onClick={onClick}
      title={lastAria || 'Open conversation'}
      aria-label="Aria — latest message"
    >
      <span className="aria-top-bubble__dot" aria-hidden="true" />
      <span className="aria-top-bubble__text">{label}</span>
    </button>
  );
}

/** Live read-along subtitle — shows on whenever Aria is SPEAKING, lighting up
 *  her words in real time so you can read along. Word progress is synced to the
 *  actual reply audio (currentTime / duration) when available, with a spoken-
 *  rate fallback. Text is captured from the most recent Aria reply on the rising
 *  edge of `ariaSpeaking` so it never flickers mid-sentence. */
function AriaSubtitle({ messages, ariaSpeaking, audioRef }) {
  const [text, setText] = useState('');
  const [spoken, setSpoken] = useState(0);
  const prevSpeakingRef = useRef(false);
  const startRef = useRef(0);
  const rafRef = useRef(null);

  // Capture the spoken text on the rising edge of ariaSpeaking.
  useEffect(() => {
    if (ariaSpeaking && !prevSpeakingRef.current) {
      let t = '';
      if (Array.isArray(messages)) {
        for (let i = messages.length - 1; i >= 0; i--) {
          const m = messages[i];
          if (m && m.role === 'assistant' && typeof m.content === 'string' && m.content.trim()) { t = m.content.trim(); break; }
        }
      }
      setText(t);
      setSpoken(0);
      startRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    }
    prevSpeakingRef.current = ariaSpeaking;
  }, [ariaSpeaking, messages]);

  const words = useMemo(() => (text ? text.split(/\s+/) : []), [text]);

  // Drive the highlight: audio progress if we have it, else ~175 wpm.
  useEffect(() => {
    if (!ariaSpeaking || words.length === 0) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return undefined;
    }
    const tick = () => {
      let frac = 0;
      const a = audioRef && audioRef.current;
      if (a && isFinite(a.duration) && a.duration > 0) {
        frac = Math.min(1, a.currentTime / a.duration);
      } else {
        const elapsed = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startRef.current;
        const totalMs = (words.length / (175 / 60)) * 1000;
        frac = totalMs > 0 ? Math.min(1, elapsed / totalMs) : 1;
      }
      setSpoken(Math.round(frac * words.length));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [ariaSpeaking, words, audioRef]);

  if (!ariaSpeaking || !text) return null;
  return (
    <div className="aria-subtitle" aria-live="polite" aria-atomic="true">
      <div className="aria-subtitle__inner">
        {words.map((w, i) => (
          <span key={i} className={i < spoken ? 'aria-sub-word spoken' : 'aria-sub-word'}>{w} </span>
        ))}
      </div>
    </div>
  );
}

/** Mac + Windows download links. The app is a MEMBERS-ONLY perk — non-members
 *  see an upgrade prompt instead of the buttons, and the backend refuses the
 *  installer without a paid session token. Windows always uses
 *  /download/aria-windows first (streams .exe or redirects to help). */
function DesktopDownloadsSection({ info, variant, member }) {
  // Non-members never see download buttons — the app requires membership.
  if (!member) {
    if (variant === 'banner' || variant === 'links') return null; // membership gate remains unchanged
    return (
      <div className="desktop-downloads desktop-downloads--locked">
        <div className="download-strip download-strip--locked">
          <div className="download-strip-text">
            <span className="download-strip-title">The Aria desktop app is for members</span>
            <span className="download-strip-sub">
              Go HowTo Pro to install Aria on your Mac or Windows PC — computer control, offline access, and the full workbench.
            </span>
          </div>
          <button
            type="button"
            className="download-pill download-pill--upgrade"
            onClick={() => { try { window.dispatchEvent(new CustomEvent('aria-open-checkout', { detail: { plan: 'subscription' } })); } catch (_) {} }}
          >
            <span className="download-pill-label">Go Pro — $20/mo</span>
          </button>
        </div>
      </div>
    );
  }
  // Members: append the session token so the backend membership gate passes.
  const _tok = (() => { try { return localStorage.getItem('aria_session_token') || ''; } catch (_) { return ''; } })();
  const _withTok = (u) => { if (!_tok || !u) return u; try { return u + (u.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(_tok); } catch (_) { return u; } };
  const macUrl = _withTok(info?.macUrl || '/aria-downloads.html#mac');
  const ready = info?.windowsInstallerReady === true;
  /** Same canonical URL on every device — never block the button on /api/desktop/latest (LAN can be slow). */
  const rawWinUrl = info?.winUrl || '/download/aria-windows';
  const winHref = _withTok(rewriteDownloadUrlsToPageOrigin(rawWinUrl));
  const winSizeLabel = formatInstallerBytes(info?.windowsInstallerBytes);
  const winDownloadName =
    ready &&
    info?.windowsInstallerFileName &&
    (() => {
      if (typeof window === 'undefined') return false;
      try {
        const u = new URL(winHref, window.location.href);
        return u.origin === window.location.origin;
      } catch (_) {
        return false;
      }
    })()
      ? info.windowsInstallerFileName
      : undefined;
  const version = info?.version;
  const wrapClass =
    variant === 'subscription'
      ? 'desktop-downloads desktop-downloads--subscription'
      : variant === 'banner'
        ? 'desktop-downloads desktop-downloads--banner'
        : 'desktop-downloads desktop-downloads--sidebar';

  if (variant === 'links') return <div className="research-download-links"><a href={macUrl} target="_blank" rel="noopener noreferrer"><svg viewBox="0 0 24 24" width="23" height="23" aria-hidden="true" focusable="false" fill="currentColor"><path d="M17.05 12.536c.031 3.238 2.841 4.315 2.872 4.329-.024.076-.449 1.535-1.48 3.043-.891 1.304-1.816 2.603-3.273 2.63-1.432.026-1.893-.85-3.53-.85-1.636 0-2.148.824-3.503.876-1.406.053-2.477-1.41-3.376-2.709C2.923 17.2 1.52 12.35 3.403 9.077c.935-1.625 2.607-2.654 4.422-2.68 1.382-.027 2.687.93 3.53.93.842 0 2.422-1.15 4.078-.981.693.029 2.638.28 3.887 2.108-.1.062-2.322 1.352-2.298 4.082M14.36 4.6c.747-.904 1.25-2.163 1.113-3.416-1.077.043-2.379.718-3.151 1.622-.692.8-1.299 2.08-1.135 3.308 1.2.093 2.426-.61 3.173-1.514"/></svg><span>Download for Mac</span></a><a href={winHref}><svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false" fill="currentColor"><path d="M2 2h9v9H2zM13 2h9v9h-9zM2 13h9v9H2zM13 13h9v9h-9z"/></svg><span>Download for Windows</span></a></div>;

  if (variant === 'banner') {
    const DownloadIcon = () => (
      <svg className="download-corner-icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
        <path fill="currentColor" d="M8 1.5a.75.75 0 0 1 .75.75v6.69l2.22-2.22a.75.75 0 1 1 1.06 1.06l-3.5 3.5a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 0 1 1.06-1.06l2.22 2.22V2.25A.75.75 0 0 1 8 1.5ZM2.75 12a.75.75 0 0 1 .75.75v.75h9v-.75a.75.75 0 0 1 1.5 0V14a1 1 0 0 1-1 1h-10a1 1 0 0 1-1-1v-1.25a.75.75 0 0 1 .75-.75Z"/>
      </svg>
    );
    return (
      <div className={wrapClass} title="Download the Aria desktop app">
        <a
          className="download-corner-pill download-corner-pill--mac"
          href={macUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download Aria for Mac"
        >
          <DownloadIcon />
          <span>Download for Mac</span>
        </a>
        <a
          className="download-corner-pill download-corner-pill--win"
          href={winHref}
          {...(winDownloadName ? { download: winDownloadName } : {})}
          aria-label="Download Aria for Windows"
        >
          <DownloadIcon />
          <span>Download for Windows</span>
        </a>
      </div>
    );
  }

  return (
    <div className={wrapClass}>
      <div className="download-strip">
        <div className="download-strip-text">
          <span className="download-strip-title">Get the Aria app</span>
          <span className="download-strip-sub">
            Desktop installer · Mac &amp; Windows{version ? ` · v${version}` : ''}
          </span>
        </div>
        <div className="download-strip-actions">
          <a
            className="download-pill download-pill--mac"
            href={macUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="download-pill-label">Download for Mac</span>
          </a>
          <a
            className="download-pill download-pill--win"
            href={winHref}
            {...(winDownloadName ? { download: winDownloadName } : {})}
          >
            <span className="download-pill-label">Download for Windows (.exe)</span>
          </a>
        </div>
        <p className="download-strip-hint">
          Windows: the installer saves to your <strong>Downloads</strong> folder (same as any app from the web). Run the .exe to install — same Aria app as on Mac, built for Windows.
          {winSizeLabel ? ` Expected download size: ~${winSizeLabel}.` : ''}
        </p>
      </div>
    </div>
  );
}

function ensureAriaUserId() {
  try {
    const existing = localStorage.getItem('aria_user_id');
    if (existing && existing.trim()) return existing.trim();
    const generated = 'aria_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('aria_user_id', generated);
    return generated;
  } catch (_) { return ''; }
}

// Debug panel that shows voice state + last console.log lines containing [Voice] or [VAD]
const _debugLogs = [];
if (typeof window !== 'undefined' && !window.__ariaDebugHooked) {
  window.__ariaDebugHooked = true;
  const origLog = console.log;
  const origWarn = console.warn;
  const origErr = console.error;
  const capture = (level, args) => {
    const msg = args.map(a => typeof a === 'string' ? a : JSON.stringify(a)).join(' ');
    if (msg.includes('[Voice]') || msg.includes('[VAD]') || msg.includes('[Assist]')) {
      _debugLogs.push(`${level} ${msg}`);
      if (_debugLogs.length > 8) _debugLogs.shift();
      // Force re-render of debug panel
      window.__ariaDebugUpdate?.();
    }
  };
  console.log = (...a) => { capture('LOG', a); origLog(...a); };
  console.warn = (...a) => { capture('WRN', a); origWarn(...a); };
  console.error = (...a) => { capture('ERR', a); origErr(...a); };
}

function VoiceDebugPanel({ micOn, hasSpokenDesktopIntro, introLocked, voicePolicy, effectiveSection, voiceRecording, ariaSpeaking, desktopUiFlagsReady, micSpeaking, vadCanRun, voiceError, liveTranscript }) {
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    window.__ariaDebugUpdate = () => forceUpdate(c => c + 1);
    return () => { window.__ariaDebugUpdate = null; };
  }, []);
  return (
    <div style={{ background: 'rgba(0,0,0,0.85)', color: '#0f0', fontFamily: 'monospace', fontSize: '10px', padding: '6px 10px', borderRadius: '6px', margin: '4px 8px', maxHeight: '120px', overflow: 'auto' }}>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
        <span>mic:{micOn?'🟢':'🔴'}</span>
        <span>intro:{hasSpokenDesktopIntro?'🟢':'🔴'}</span>
        <span>locked:{introLocked?'🔴':'🟢'}</span>
        <span>policy:{voicePolicy}</span>
        <span>sec:{effectiveSection}</span>
        <span>rec:{voiceRecording?'🟢':'🔴'}</span>
        <span>aria:{ariaSpeaking?'🟢':'⚫'}</span>
        <span>ui:{desktopUiFlagsReady?'🟢':'🔴'}</span>
        <span>micSpk:{micSpeaking?'🟢':'⚫'}</span>
        <span>vad:{vadCanRun?'🟢':'🔴'}</span>
      </div>
      {voiceError && <div style={{color:'#f44'}}>err: {voiceError}</div>}
      {liveTranscript && <div style={{color:'#ff0'}}>hearing: {liveTranscript}</div>}
      {_debugLogs.map((l, i) => <div key={i} style={{color: l.startsWith('ERR') ? '#f66' : '#0f0', fontSize: '9px'}}>{l}</div>)}
    </div>
  );
}

// ── Action detection for home screen (same patterns as ScreenShareView) ──
//
// ── AN ORDER STARTS WITH THE VERB (Braeden 8/31) ─────────────────────────
//
// Measured on the search audit, on the app and not the API:
//
//   HIM : "how to load a moving truck on youtube"
//   HER : "Opening YouTube."      ...and the backend never heard the turn
//
// Two faults, both in the pattern. "load" was a launch verb — and loading a
// truck is the man's actual trade. And `.*` between the verb and the site
// let ANY sentence containing both words match, however far apart they sat.
//
// So a launch order now has to LOOK like one: the verb at the start of the
// sentence, the site right after it, and nothing else. "open youtube" opens
// YouTube; "how to load a moving truck on youtube" goes to the backend,
// which reads YouTube and hands back the videos.
const _GO = String.raw`^\s*(?:hey\s+|ok(?:ay)?\s+|aria[,\s]+)*(?:can you\s+|could you\s+|please\s+|just\s+)*(?:open|go to|navigate to|launch|pull up|bring up|take me to|visit)\s+(?:up\s+)?(?:the\s+|my\s+|a\s+)?`;
const _END = String.raw`\b(?:\s+(?:please|for me|up|now|real quick))*[\s.!?]*$`;
const _site = (name) => new RegExp(_GO + '(?:' + name + ')' + _END, 'i');

const HOME_ACTION_MAP = [
  // Websites
  { pattern: googleEarthOpenPattern, url: 'https://earth.google.com/web/', label: 'Google Earth' },
  { pattern: _site(String.raw`google\.com|google`), url: 'https://www.google.com', label: 'Google' },
  { pattern: _site(String.raw`youtube`), url: 'https://www.youtube.com', label: 'YouTube' },
  { pattern: _site(String.raw`gmail`), url: 'https://mail.google.com', label: 'Gmail' },
  { pattern: _site(String.raw`twitter`), url: 'https://twitter.com', label: 'Twitter' },
  { pattern: _site(String.raw`reddit`), url: 'https://www.reddit.com', label: 'Reddit' },
  { pattern: _site(String.raw`github`), url: 'https://github.com', label: 'GitHub' },
  { pattern: _site(String.raw`amazon`), url: 'https://www.amazon.com', label: 'Amazon' },
  { pattern: _site(String.raw`netflix`), url: 'https://www.netflix.com', label: 'Netflix' },
  { pattern: _site(String.raw`spotify`), url: 'https://open.spotify.com', label: 'Spotify' },
  { pattern: _site(String.raw`instagram`), url: 'https://www.instagram.com', label: 'Instagram' },
  { pattern: _site(String.raw`discord`), url: 'https://discord.com/app', label: 'Discord' },
  { pattern: _site(String.raw`notion`), url: 'https://www.notion.so', label: 'Notion' },
  { pattern: _site(String.raw`slack`), url: 'https://slack.com', label: 'Slack' },
  // macOS apps
  { pattern: /\b(?:open|launch|start|bring up)\b.*\b(?:chrome|google chrome)\b/i, app: 'Google Chrome', label: 'Chrome' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bsafari\b/i, app: 'Safari', label: 'Safari' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\b(?:finder|files)\b/i, app: 'Finder', label: 'Finder' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\b(?:terminal|command line)\b/i, app: 'Terminal', label: 'Terminal' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\b(?:vscode|vs code|visual studio code)\b/i, app: 'Visual Studio Code', label: 'VS Code' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\b(?:messages|imessage)\b/i, app: 'Messages', label: 'Messages' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bmusic\b/i, app: 'Music', label: 'Music' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bnotes\b/i, app: 'Notes', label: 'Notes' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bphotos\b/i, app: 'Photos', label: 'Photos' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\b(?:settings|system settings|preferences)\b/i, app: 'System Settings', label: 'Settings' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bcalendar\b/i, app: 'Calendar', label: 'Calendar' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bmail\b/i, app: 'Mail', label: 'Mail' },
  { pattern: /\b(?:open|launch|start|bring up)\b.*\bxcode\b/i, app: 'Xcode', label: 'Xcode' },
  // Search
  { pattern: /\b(?:search|look up|google)\b\s+(?:for\s+)?["']?(.{3,})["']?\s*$/i, searchUrl: 'https://www.google.com/search?q=', label: 'Google Search' },
];

// ── Tiny markdown renderer for the text-chat ──────────────────────────────
// Handles: **bold**, lines starting with - or • (bullets), 1. 2. 3. (numbered),
// paragraph breaks (blank line). Deliberately small — no full markdown lib,
// no nested lists, no code blocks. Good enough for canonical reply formatting.
function renderInlineMarkdown(text, keyPrefix = '') {
  // Split on **bold** while preserving the delimiters; output alternating
  // <span> / <strong> React fragments.
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  // KEY NAMESPACE (8/22): these keys were bare indexes, so every line in a
  // multi-line paragraph produced key 0, 1, 2… and React logged
  // "two children with the same key" on every markdown reply — the
  // documented consequence is duplicated or OMITTED children, i.e. text
  // silently missing from an answer.
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**') && p.length >= 4) {
      return <strong key={`${keyPrefix}s${i}`}>{p.slice(2, -2)}</strong>;
    }
    return <span key={`${keyPrefix}t${i}`}>{p}</span>;
  });
}
// AriaExhibit — renders teaching visuals below Aria's bubble.
// Recovered from aria-calibrated-v2.html (2026-05-18). The exhibit is a
// standalone diagram, not part of the message — like an illustration
// in a textbook. Each `type` maps to a hand-built visual, with each
// side wrapped in its own .aria-highlight blue Assist-Mode outline.
// IntegrationsPanel — lists every external service Aria can connect to,
// shows status (not configured / configured-but-not-connected / connected),
// and gives a one-click button that hits the backend OAuth kickoff URL.
//
// Backend exposes /api/integrations/status which tells us which providers
// have credentials in .env (configured: true|false). Each provider has its
// own /api/integrations/<provider>/connect/<userId> that redirects through
// OAuth and on success stores tokens.
function IntegrationsPanel({ userId }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const statusUrl = `/api/integrations/status?userId=${encodeURIComponent(userId || 'default')}`;

  React.useEffect(() => {
    let mounted = true;
    fetch(statusUrl)
      .then(r => r.json())
      .then(d => { if (mounted) setStatus(d); })
      .catch(e => { if (mounted) setError(e.message); });
    return () => { mounted = false; };
  }, [statusUrl]);

  // Refresh when the tab regains focus — covers the case where the user
  // completed the OAuth consent screen in this same tab/window.
  React.useEffect(() => {
    const onFocus = () => fetch(statusUrl).then(r => r.json()).then(setStatus).catch(() => {});
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [statusUrl]);

  if (error) return <div style={{color:'#ff6b6b'}}>Couldn't load integration status: {error}</div>;
  if (!status) return <div style={{opacity:0.6}}>Loading integrations…</div>;

  const providers = [
    {
      id: 'google_calendar',
      name: 'Google Calendar',
      icon: '📅',
      description: 'Aria can see your meetings, suggest scheduling, block focus time.',
      connectUrl: `/api/integrations/google-calendar/connect/${encodeURIComponent(userId || 'default')}`,
      setupNote: 'Set GOOGLE_OAUTH_CLIENT_ID + GOOGLE_OAUTH_CLIENT_SECRET in .env to enable.',
    },
    {
      id: 'gmail',
      name: 'Gmail',
      icon: '✉️',
      description: 'Aria reads your unread inbox, drafts replies, surfaces what needs attention.',
      connectUrl: `/api/integrations/gmail/connect/${encodeURIComponent(userId || 'default')}`,
      setupNote: 'Uses the same Google OAuth client as Calendar. One setup covers both.',
    },
    {
      id: 'quickbooks',
      name: 'QuickBooks',
      icon: '📊',
      description: 'Aria sees real P&L, AR aging, balance sheet — answers "how are we doing?" with actual numbers.',
      connectUrl: `/api/integrations/quickbooks/connect/${encodeURIComponent(userId || 'default')}`,
      setupNote: 'Set QUICKBOOKS_CLIENT_ID + QUICKBOOKS_CLIENT_SECRET in .env (developer.intuit.com).',
    },
  ];

  return (
    <div className="integrations-list">
      {providers.map(p => {
        const s = status[p.id] || {};
        const configured = !!s.configured;
        const connected = !!s.connected;
        return (
          <div key={p.id} className="integration-card" style={{
            background:'rgba(255,255,255,0.04)',
            border:'1px solid rgba(255,255,255,0.1)',
            borderRadius:12,
            padding:'18px 20px',
            marginBottom:14,
            display:'flex',
            gap:16,
            alignItems:'flex-start',
          }}>
            <div style={{fontSize:32, lineHeight:1}}>{p.icon}</div>
            <div style={{flex:1, minWidth:0}}>
              <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:4}}>
                <div style={{fontSize:16, fontWeight:600}}>{p.name}</div>
                <StatusPill configured={configured} connected={connected} />
              </div>
              <p style={{opacity:0.7, fontSize:13.5, margin:'0 0 12px 0', lineHeight:1.5}}>{p.description}</p>
              {!configured && (
                <p style={{opacity:0.5, fontSize:12, fontStyle:'italic', margin:'0 0 12px 0'}}>{p.setupNote}</p>
              )}
              <div style={{display:'flex', gap:10}}>
                <a
                  href={configured ? p.connectUrl : undefined}
                  onClick={configured ? undefined : (e) => e.preventDefault()}
                  style={{
                    display:'inline-block',
                    padding:'8px 16px',
                    borderRadius:8,
                    fontSize:13.5,
                    fontWeight:600,
                    textDecoration:'none',
                    background: configured ? (connected ? 'rgba(252, 211, 77,0.18)' : '#3a9dff') : 'rgba(255,255,255,0.06)',
                    color: configured ? (connected ? 'var(--accent)' : '#fff') : 'rgba(255,255,255,0.35)',
                    cursor: configured ? 'pointer' : 'not-allowed',
                    border: connected ? '1px solid rgba(252, 211, 77,0.35)' : 'none',
                  }}
                >
                  {!configured ? 'Setup needed' : connected ? 'Reconnect' : `Connect ${p.name}`}
                </a>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusPill({ configured, connected }) {
  const style = {
    padding:'2px 10px',
    borderRadius:999,
    fontSize:10.5,
    fontWeight:700,
    letterSpacing:'0.05em',
    textTransform:'uppercase',
  };
  if (connected) return <span style={{...style, background:'rgba(252, 211, 77,0.18)', color:'var(--accent)'}}>Connected</span>;
  if (configured) return <span style={{...style, background:'rgba(120,160,255,0.18)', color:'#8aa9ff'}}>Ready</span>;
  return <span style={{...style, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.45)'}}>Setup</span>;
}

// ────────────────────────────────────────────────────────────────────────────
// DevConsole — meta-tool for working on Aria itself.
// Five panes:
//   1. Live Aria      — chat input → reply + tier/timing badge
//   2. Live Transcription — real-time STT (what YOU are saying right now)
//   3. Aria's thinking — what Aria is doing in response (stage, match, reply)
//   4. Knowledge      — counts of canonicals/scenarios/facts/templates
//   5. Ops            — in-house ratio, integrations, gap-log tail
// Reads /api/dev/summary, /api/dev/gap-log, /api/pipeline/events, /api/ai/conversation.
// ────────────────────────────────────────────────────────────────────────────
function DevConsole({ userId }) {
  const [summary, setSummary] = React.useState(null);
  const [summaryErr, setSummaryErr] = React.useState(null);
  const [gapLog, setGapLog] = React.useState([]);
  const [probeQuery, setProbeQuery] = React.useState('');
  const [probeReply, setProbeReply] = React.useState(null);
  const [probing, setProbing] = React.useState(false);
  // Model preference — same localStorage key the main app uses, so flips
  // here apply everywhere immediately. Defaults to 'auto'.
  const [modelPref, setModelPref] = React.useState(() => {
    try { return localStorage.getItem('aria_model_pref') || 'auto'; } catch (_) { return 'auto'; }
  });
  const [modelMenuOpen, setModelMenuOpen] = React.useState(false);
  const setModelPrefPersist = (v) => {
    setModelPref(v);
    try { localStorage.setItem('aria_model_pref', v); } catch (_) {}
    setModelMenuOpen(false);
  };
  // Close the menu when clicking anywhere else
  React.useEffect(() => {
    if (!modelMenuOpen) return;
    const onDoc = () => setModelMenuOpen(false);
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [modelMenuOpen]);
  // Live transcription state — last partial (in-progress) + recent finals + raw event log
  const [livePartial, setLivePartial] = React.useState('');
  const [recentFinals, setRecentFinals] = React.useState([]); // [{ t, text }]
  const [pipelineEvents, setPipelineEvents] = React.useState([]); // tail of ws.partial / ws.final / convo events
  const lastSeqRef = React.useRef(0);
  // Aria's thinking state — derived from pipeline events as Aria works
  const [ariaThinking, setAriaThinking] = React.useState({
    stage: 'idle',          // idle | listening | matching | generating | speaking | done
    matched: null,           // { source, text, score, file }
    response: null,          // { text, tier, ms }
    timing: { brain: null, llm: null, tts: null, total: null },
    lastUpdateAt: 0,
  });

  const loadSummary = React.useCallback(() => {
    fetch('/api/dev/summary')
      .then(r => r.json())
      .then(d => { setSummary(d); setSummaryErr(null); })
      .catch(e => setSummaryErr(e.message));
    fetch('/api/dev/gap-log?limit=15')
      .then(r => r.json())
      .then(d => setGapLog(d.entries || []))
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    loadSummary();
    const t = setInterval(loadSummary, 15000); // refresh every 15s
    return () => clearInterval(t);
  }, [loadSummary]);

  // Live transcription polling — /api/pipeline/events is a ring buffer of
  // every pipeline step. We filter to ws.partial (in-progress STT),
  // ws.final (completed sentence), and a few key conversation stages.
  // Poll every 400ms so the user sees their words land within a beat.
  React.useEffect(() => {
    let cancelled = false;
    const KEEP_FINALS = 8;
    const KEEP_EVENTS = 30;
    // Wider filter — cover BOTH the local Whisper streaming path
    // (ws.partial/final) AND the cloud STT path (conversation.in / user.match).
    // If local Whisper isn't running, only the cloud path fires.
    const INTERESTING_STAGES = [
      // Local streaming Whisper path
      'ws.partial', 'ws.final', 'transcribe.in', 'transcribe.out',
      // Cloud STT + conversation pipeline (always fires)
      'conversation.in', 'user.match', 'intent.classify',
      'hot.embed', 'hot.miss', 'hot.hit',
      'brain.match', 'brain.miss',
      'teach.start', 'teach.sonnet.start', 'teach.sonnet.end',
      'response.sent',
      'tts.gen.start', 'tts.gen.done',
    ];

    async function tick() {
      try {
        const r = await fetch('/api/pipeline/events?since=' + lastSeqRef.current);
        if (!r.ok) return;
        const { events = [] } = await r.json();
        if (cancelled || events.length === 0) return;
        // Track highest seq we've seen so we don't re-fetch
        for (const e of events) if (e.seq > lastSeqRef.current) lastSeqRef.current = e.seq;

        // Update live partial — most recent ws.partial wins. If local
        // Whisper isn't running, we won't get partials; that's OK.
        for (let i = events.length - 1; i >= 0; i--) {
          if (events[i].stage === 'ws.partial') {
            setLivePartial(events[i].text || '');
            break;
          }
          if (events[i].stage === 'ws.final' || events[i].stage === 'conversation.in') {
            // a final landed — clear the in-progress partial
            setLivePartial('');
            break;
          }
        }

        // Append new finals — accept ws.final (streaming Whisper) OR
        // conversation.in (cloud STT path that bypasses streaming).
        const newFinals = events.filter(e =>
          (e.stage === 'ws.final' || e.stage === 'conversation.in') && e.text
        );
        if (newFinals.length > 0) {
          setRecentFinals(prev => {
            const next = [
              ...prev,
              ...newFinals.map(e => ({ t: e.t, text: e.text, via: e.stage === 'ws.final' ? 'streaming' : 'cloud' })),
            ];
            return next.slice(-KEEP_FINALS);
          });
        }

        // Append interesting events to the raw log
        const interesting = events.filter(e => INTERESTING_STAGES.includes(e.stage));
        if (interesting.length > 0) {
          setPipelineEvents(prev => [...prev, ...interesting].slice(-KEEP_EVENTS));
        }

        // Derive Aria's thinking state from the latest events. We walk
        // events in order and update the "stage" flag based on what just
        // fired. The result is a single status that reflects what Aria's
        // doing RIGHT NOW.
        for (const e of events) {
          if (e.stage === 'ws.partial') {
            setAriaThinking(s => ({ ...s, stage: 'listening', lastUpdateAt: e.t }));
          } else if (e.stage === 'ws.final' || e.stage === 'conversation.in') {
            // Either streaming path landed a final, or cloud path delivered
            // the full transcribed query. Either way, Aria's now matching.
            setAriaThinking(s => ({ ...s, stage: 'matching', lastUpdateAt: e.t }));
          } else if (e.stage === 'intent.classify' || e.stage === 'hot.embed' || e.stage === 'hot.miss' || e.stage === 'brain.miss') {
            setAriaThinking(s => ({ ...s, stage: 'matching', lastUpdateAt: e.t }));
          } else if (e.stage === 'teach.start' || e.stage === 'teach.sonnet.start') {
            setAriaThinking(s => ({ ...s, stage: 'generating', lastUpdateAt: e.t }));
          } else if (e.stage === 'teach.sonnet.end' && /error|invalid|fail/i.test(e.text || '')) {
            // Surface the teach-loop error so it doesn't look like Aria is hung
            setAriaThinking(s => ({
              ...s,
              stage: 'done',
              response: { text: '⚠ Teach loop error: ' + (e.text || ''), tier: 'error', ms: null },
              lastUpdateAt: e.t,
            }));
          } else if (e.stage === 'brain.match') {
            const score = e.meta?.score ?? e.meta?.confidence ?? null;
            setAriaThinking(s => ({
              ...s,
              stage: 'generating',
              matched: {
                source: e.meta?.mode || e.meta?.source || 'brain',
                text: e.text || '',
                score: typeof score === 'number' ? score.toFixed(2) : score,
                file: e.meta?.file || null,
              },
              timing: { ...s.timing, brain: e.meta?.ms ?? s.timing.brain },
              lastUpdateAt: e.t,
            }));
          } else if (e.stage === 'response.sent') {
            const tier = e.meta?.tier || e.meta?.source || null;
            const ms = e.meta?.totalMs || e.meta?.ms || null;
            setAriaThinking(s => ({
              ...s,
              stage: 'speaking',
              response: { text: e.text || '', tier, ms },
              timing: { ...s.timing, total: ms ?? s.timing.total },
              lastUpdateAt: e.t,
            }));
          } else if (e.stage === 'tts.gen.start') {
            setAriaThinking(s => ({ ...s, stage: 'speaking', lastUpdateAt: e.t }));
          } else if (e.stage === 'tts.gen.done') {
            const ms = e.meta?.ms || null;
            setAriaThinking(s => ({
              ...s,
              stage: 'done',
              timing: { ...s.timing, tts: ms ?? s.timing.tts },
              lastUpdateAt: e.t,
            }));
          }
        }
      } catch { /* network blip — just wait for next tick */ }
    }
    tick();
    const id = setInterval(tick, 400);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const runProbe = async () => {
    if (!probeQuery.trim() || probing) return;
    setProbing(true);
    const startedAt = performance.now();
    try {
      const r = await fetch('/api/ai/conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          prompt: probeQuery,
          mode: 'text',
          modelPref, // honored by middleware/ariaModelPref.js
        }),
      });
      const data = await r.json();
      const ms = Math.round(performance.now() - startedAt);
      // Extract a tier label from whichever metadata the server attached.
      let tier = data.tier || data.source || data.responseSource || data.mode || null;
      if (!tier && data._fromBrain) tier = 'brain';
      if (!tier && data.fromCanonical) tier = 'canonical';
      if (!tier && data.fromCache) tier = 'cache';
      if (!tier && data.fromRag) tier = 'facts-rag';
      if (!tier && data.brainMeta?.mode) tier = 'brain · ' + data.brainMeta.mode;
      setProbeReply({
        ms,
        text: data.reply || data.response || data.text || data.question || JSON.stringify(data).slice(0, 400),
        tier,
        model: data.model || data.modelUsed || data.brainMeta?.detectedMode || null,
        citation: data.citation || data.brainMeta?.matchedQuery || null,
        cost: data.costUsd ?? data.cost ?? null,
        score: data.brainMeta?.score != null ? data.brainMeta.score.toFixed(2) : null,
      });
      loadSummary(); // refresh stats so the byTier counter updates
    } catch (e) {
      setProbeReply({ ms: 0, text: 'Error: ' + e.message, tier: 'error' });
    } finally {
      setProbing(false);
    }
  };

  const card = {
    background:'rgba(255,255,255,0.04)',
    border:'1px solid rgba(255,255,255,0.1)',
    borderRadius:12,
    padding:18,
  };
  const label = { fontSize:11, opacity:0.55, letterSpacing:'0.05em', textTransform:'uppercase', marginBottom:6 };
  const big = { fontSize:26, fontWeight:600, lineHeight:1 };

  return (
    <div style={{display:'flex', flexDirection:'column', gap:18}}>

      {/* Pane 1 — Live Aria */}
      <section style={card}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12, gap:12}}>
          <div style={{display:'flex', alignItems:'center', gap:10}}>
            <h2 style={{margin:0, fontSize:15, fontWeight:600}}>Live Aria — probe</h2>
            {/* Model switch — next to "Aria" per owner. Auto/Opus/Sonnet. */}
            <span
              onClick={(e) => { e.stopPropagation(); setModelMenuOpen(v => !v); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setModelMenuOpen(v => !v); } }}
              title="Switch model for this probe. Auto = Aria picks. Opus / Sonnet override."
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3px 10px',
                fontSize: '0.72rem',
                fontWeight: 500,
                color: 'rgba(255,255,255,0.75)',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 999,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <span>{modelPref === 'opus' ? 'Opus Max' : modelPref === 'sonnet' ? 'Sonnet' : 'Auto'}</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4, opacity: 0.7 }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
              {modelMenuOpen && (
                <div
                  role="menu"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 6px)',
                    left: 0,
                    zIndex: 60,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 220,
                    padding: 6,
                    background: '#161922',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 12,
                    boxShadow: '0 14px 40px rgba(0,0,0,0.55)',
                  }}
                >
                  {[
                    { v: 'auto',   name: 'Auto',     sub: "Aria picks the best for the task" },
                    { v: 'opus',   name: 'Opus Max', sub: 'Deepest reasoning. Slower, more $' },
                    { v: 'sonnet', name: 'Sonnet',   sub: 'Fast + smart for most things' },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setModelPrefPersist(opt.v)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 2,
                        padding: '8px 10px',
                        background: modelPref === opt.v ? 'rgba(141,247,180,0.08)' : 'transparent',
                        border: 0,
                        borderRadius: 8,
                        color: '#d9e3ef',
                        cursor: 'pointer',
                        textAlign: 'left',
                        font: 'inherit',
                      }}
                    >
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#fff' }}>{opt.name}</span>
                      <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.55)' }}>{opt.sub}</span>
                    </button>
                  ))}
                </div>
              )}
            </span>
          </div>
          <span style={{fontSize:11, opacity:0.5}}>user: {userId}</span>
        </div>
        <div style={{display:'flex', gap:8, marginBottom:12}}>
          <input
            type="text"
            value={probeQuery}
            onChange={e => setProbeQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') runProbe(); }}
            placeholder='Ask Aria something — e.g. "what is gross margin"'
            style={{
              flex:1,
              background:'rgba(0,0,0,0.3)',
              border:'1px solid rgba(255,255,255,0.12)',
              borderRadius:8,
              color:'#fff',
              padding:'10px 14px',
              fontSize:14,
              outline:'none',
            }}
            disabled={probing}
          />
          <button
            type="button"
            onClick={runProbe}
            disabled={probing || !probeQuery.trim()}
            style={{
              background: probing ? 'rgba(255,255,255,0.08)' : '#3a9dff',
              color:'#fff',
              border:'none',
              borderRadius:8,
              padding:'10px 18px',
              fontSize:14,
              fontWeight:600,
              cursor: probing || !probeQuery.trim() ? 'default' : 'pointer',
            }}
          >{probing ? 'Asking…' : 'Send'}</button>
        </div>
        {probeReply && (
          <div style={{
            background:'rgba(0,0,0,0.25)',
            border:'1px solid rgba(255,255,255,0.08)',
            borderRadius:8,
            padding:14,
            fontSize:14,
            lineHeight:1.55,
          }}>
            <div style={{display:'flex', gap:8, flexWrap:'wrap', marginBottom:10, fontSize:11}}>
              {probeReply.tier && (
                <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(120,160,255,0.18)', color:'#8aa9ff', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em'}}>
                  {probeReply.tier}
                </span>
              )}
              <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.6)'}}>
                {probeReply.ms}ms
              </span>
              {probeReply.model && (
                <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.6)'}}>
                  {probeReply.model}
                </span>
              )}
              {probeReply.cost != null && (
                <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.6)'}}>
                  ${Number(probeReply.cost).toFixed(4)}
                </span>
              )}
              {probeReply.citation && (
                <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.6)'}}>
                  cite: {String(probeReply.citation).slice(0, 40)}
                </span>
              )}
              {probeReply.score && (
                <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.6)'}}>
                  score: {probeReply.score}
                </span>
              )}
            </div>
            <div style={{whiteSpace:'pre-wrap'}}>{probeReply.text}</div>
          </div>
        )}
      </section>

      {/* Pane 2 — Live Transcription (what Aria's STT is picking up RIGHT NOW) */}
      <section style={card}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
          <h2 style={{margin:0, fontSize:15, fontWeight:600}}>Live transcription</h2>
          <span style={{fontSize:11, opacity:0.5}}>polls /api/pipeline/events every 400ms</span>
        </div>
        <p style={{opacity:0.55, fontSize:12, margin:'0 0 12px 0'}}>
          Open the Ask Aria page in another tab, hit the mic button, and talk — partials show here as you speak, finals fall into the list below. If nothing appears, mic isn't streaming.
        </p>

        {/* Current partial — the words Whisper is locking in as you speak */}
        <div style={{
          background:'rgba(0,0,0,0.3)',
          border:'1px solid rgba(120,160,255,0.25)',
          borderRadius:8,
          padding:'14px 16px',
          fontSize:18,
          fontWeight:500,
          minHeight:54,
          marginBottom:14,
          fontFamily:'ui-monospace, Menlo, monospace',
          color: livePartial ? '#8aa9ff' : 'rgba(255,255,255,0.3)',
          fontStyle: livePartial ? 'normal' : 'italic',
        }}>
          {livePartial || '(waiting for speech…)'}
        </div>

        {/* Last few finals */}
        <div style={{...label, marginBottom:8}}>Recent finals (last {recentFinals.length})</div>
        {recentFinals.length === 0 && <div style={{opacity:0.5, fontSize:12, marginBottom:14}}>no finals yet</div>}
        {recentFinals.length > 0 && (
          <div style={{marginBottom:14}}>
            {recentFinals.slice().reverse().map((f, i) => {
              const ts = new Date(f.t).toLocaleTimeString([], { hour12: false });
              return (
                <div key={i} style={{
                  padding:'8px 12px',
                  background: i === 0 ? 'rgba(252, 211, 77,0.10)' : 'rgba(255,255,255,0.03)',
                  borderRadius:6,
                  marginBottom:6,
                  fontSize:14,
                  borderLeft: i === 0 ? '2px solid var(--accent)' : '2px solid transparent',
                }}>
                  <span style={{opacity:0.5, fontSize:11, marginRight:8}}>{ts}</span>
                  {f.text}
                </div>
              );
            })}
          </div>
        )}

        {/* Raw pipeline event log */}
        <div style={{...label, marginBottom:8}}>Pipeline events (last {pipelineEvents.length})</div>
        {pipelineEvents.length === 0 && <div style={{opacity:0.5, fontSize:12}}>no events yet</div>}
        {pipelineEvents.length > 0 && (
          <div style={{
            background:'rgba(0,0,0,0.25)',
            border:'1px solid rgba(255,255,255,0.06)',
            borderRadius:8,
            padding:10,
            fontFamily:'ui-monospace, Menlo, monospace',
            fontSize:11,
            maxHeight:200,
            overflowY:'auto',
            lineHeight:1.5,
          }}>
            {pipelineEvents.slice().reverse().map((e, i) => {
              const ts = new Date(e.t).toLocaleTimeString([], { hour12: false });
              const stageColor = e.stage.startsWith('ws.') ? '#8aa9ff'
                : e.stage.startsWith('brain') ? 'var(--accent)'
                : e.stage.startsWith('tts') ? '#f59e0b'
                : e.stage.startsWith('response') ? '#a78bfa'
                : 'rgba(255,255,255,0.6)';
              return (
                <div key={e.seq || i} style={{padding:'3px 0', borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                  <span style={{opacity:0.5}}>{ts}</span>
                  {'  '}
                  <span style={{color: stageColor, fontWeight:600}}>{e.stage}</span>
                  {e.text && <> · <span style={{opacity:0.85}}>{(e.text || '').slice(0, 80)}</span></>}
                  {e.meta?.ms != null && <> <span style={{opacity:0.5}}>({e.meta.ms}ms)</span></>}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Pane 3 — Aria's thinking (what she's doing in response, in real time) */}
      <section style={card}>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
          <h2 style={{margin:0, fontSize:15, fontWeight:600}}>Aria's thinking</h2>
          {(() => {
            const stage = ariaThinking.stage;
            const stageMeta = {
              idle:       { label:'IDLE',       color:'rgba(255,255,255,0.4)', bg:'rgba(255,255,255,0.05)' },
              listening:  { label:'LISTENING',  color:'#8aa9ff',                bg:'rgba(120,160,255,0.18)' },
              matching:   { label:'MATCHING',   color:'#f59e0b',                bg:'rgba(245,158,11,0.18)' },
              generating: { label:'GENERATING', color:'#a78bfa',                bg:'rgba(167,139,250,0.18)' },
              speaking:   { label:'SPEAKING',   color:'var(--accent)',                bg:'rgba(252, 211, 77,0.18)' },
              done:       { label:'DONE',       color:'rgba(255,255,255,0.55)', bg:'rgba(255,255,255,0.06)' },
            }[stage] || { label: stage.toUpperCase(), color:'#fff', bg:'rgba(255,255,255,0.06)' };
            return (
              <span style={{
                padding:'4px 12px', borderRadius:999, fontSize:11, fontWeight:700,
                letterSpacing:'0.05em', color: stageMeta.color, background: stageMeta.bg,
              }}>● {stageMeta.label}</span>
            );
          })()}
        </div>

        {/* What she matched */}
        <div style={{...label, marginBottom:6}}>Matched (last brain hit)</div>
        {!ariaThinking.matched && <div style={{opacity:0.4, fontSize:12, marginBottom:14}}>no match yet — Aria is idle</div>}
        {ariaThinking.matched && (
          <div style={{
            background:'rgba(0,0,0,0.25)',
            border:'1px solid rgba(245,158,11,0.25)',
            borderRadius:8,
            padding:'10px 14px',
            marginBottom:14,
            fontSize:13,
          }}>
            <div style={{display:'flex', gap:8, fontSize:11, marginBottom:6}}>
              <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(245,158,11,0.18)', color:'#f59e0b', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em'}}>
                {ariaThinking.matched.source}
              </span>
              {ariaThinking.matched.score != null && (
                <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.6)'}}>
                  score: {ariaThinking.matched.score}
                </span>
              )}
              {ariaThinking.matched.file && (
                <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.6)'}}>
                  {String(ariaThinking.matched.file).split('/').pop()}
                </span>
              )}
            </div>
            <div style={{opacity:0.85}}>{ariaThinking.matched.text || '(no preview)'}</div>
          </div>
        )}

        {/* What she's actually about to say */}
        <div style={{...label, marginBottom:6}}>Coming up with</div>
        {!ariaThinking.response && (
          <div style={{
            opacity:0.4, fontSize:12, marginBottom:14,
            fontStyle:'italic',
          }}>
            {ariaThinking.stage === 'generating' ? 'thinking…'
             : ariaThinking.stage === 'matching' ? 'searching for a match…'
             : ariaThinking.stage === 'listening' ? 'waiting for you to finish…'
             : '(nothing yet — say something to Aria)'}
          </div>
        )}
        {ariaThinking.response && (
          <div style={{
            background:'rgba(0,0,0,0.3)',
            border:'1px solid rgba(252, 211, 77,0.25)',
            borderRadius:8,
            padding:'12px 14px',
            marginBottom:14,
            fontSize:14,
            lineHeight:1.5,
          }}>
            <div style={{display:'flex', gap:8, fontSize:11, marginBottom:8}}>
              {ariaThinking.response.tier && (
                <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(252, 211, 77,0.18)', color:'var(--accent)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em'}}>
                  {ariaThinking.response.tier}
                </span>
              )}
              {ariaThinking.response.ms != null && (
                <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.6)'}}>
                  {ariaThinking.response.ms}ms
                </span>
              )}
            </div>
            <div style={{whiteSpace:'pre-wrap'}}>{ariaThinking.response.text}</div>
          </div>
        )}

        {/* Timing breakdown */}
        {(ariaThinking.timing.brain != null || ariaThinking.timing.total != null || ariaThinking.timing.tts != null) && (
          <>
            <div style={{...label, marginBottom:6}}>Timing breakdown</div>
            <div style={{display:'flex', gap:14, flexWrap:'wrap', fontSize:12}}>
              {ariaThinking.timing.brain != null && (
                <div>
                  <span style={{opacity:0.55}}>brain match: </span>
                  <span style={{color:'#f59e0b', fontWeight:600}}>{ariaThinking.timing.brain}ms</span>
                </div>
              )}
              {ariaThinking.timing.total != null && (
                <div>
                  <span style={{opacity:0.55}}>total round-trip: </span>
                  <span style={{color:'#a78bfa', fontWeight:600}}>{ariaThinking.timing.total}ms</span>
                </div>
              )}
              {ariaThinking.timing.tts != null && (
                <div>
                  <span style={{opacity:0.55}}>TTS gen: </span>
                  <span style={{color:'var(--accent)', fontWeight:600}}>{ariaThinking.timing.tts}ms</span>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {/* Pane 4 — Knowledge */}
      <section style={card}>
        <h2 style={{margin:'0 0 14px 0', fontSize:15, fontWeight:600}}>Knowledge inventory</h2>
        {summaryErr && <div style={{color:'#ff6b6b', fontSize:13}}>Couldn't load: {summaryErr}</div>}
        {!summary && !summaryErr && <div style={{opacity:0.5, fontSize:13}}>Loading…</div>}
        {summary && (
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:14}}>
            <div>
              <div style={label}>Canonicals</div>
              <div style={big}>{summary.knowledge.canonicalFiles}</div>
              <div style={{fontSize:11, opacity:0.5, marginTop:4}}>files</div>
            </div>
            <div>
              <div style={label}>Scenarios</div>
              <div style={big}>{summary.knowledge.scenarioFiles}</div>
              <div style={{fontSize:11, opacity:0.5, marginTop:4}}>batch files</div>
            </div>
            <div>
              <div style={label}>Facts</div>
              <div style={big}>{summary.knowledge.factsRag?.totalFacts?.toLocaleString() || '—'}</div>
              <div style={{fontSize:11, opacity:0.5, marginTop:4}}>{summary.knowledge.factsRag?.totalCategories || '—'} categories</div>
            </div>
            <div>
              <div style={label}>Templates</div>
              <div style={big}>{summary.knowledge.templates}</div>
              <div style={{fontSize:11, opacity:0.5, marginTop:4}}>
                {Object.entries(summary.knowledge.templateCategories || {})
                  .map(([k,v]) => `${k}:${v}`).join(' · ')}
              </div>
            </div>
            <div>
              <div style={label}>Seed JSONs</div>
              <div style={big}>{summary.knowledge.totalSeedFiles}</div>
              <div style={{fontSize:11, opacity:0.5, marginTop:4}}>total tracked</div>
            </div>
          </div>
        )}
      </section>

      {/* Pane 5 — Ops */}
      <section style={card}>
        <h2 style={{margin:'0 0 14px 0', fontSize:15, fontWeight:600}}>Ops</h2>
        {summary && (
          <div style={{display:'flex', flexDirection:'column', gap:18}}>

            {/* In-house ratio gauge */}
            <div>
              <div style={{display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:12, opacity:0.7}}>
                <span>In-house ratio · {summary.runtime.totalTurns} turn{summary.runtime.totalTurns === 1 ? '' : 's'} · uptime {summary.backendUptimeMin}m</span>
                <span><b style={{color:'var(--accent)'}}>{summary.runtime.inHousePct}%</b> in-house · {summary.runtime.externalPct}% LLM</span>
              </div>
              <div style={{height:8, background:'rgba(255,255,255,0.06)', borderRadius:999, overflow:'hidden', display:'flex'}}>
                <div style={{width: summary.runtime.inHousePct + '%', background:'var(--accent)'}} />
                <div style={{width: summary.runtime.externalPct + '%', background:'#ff9f1c'}} />
              </div>
              {Object.keys(summary.runtime.byTier || {}).length > 0 && (
                <div style={{marginTop:8, fontSize:11, opacity:0.55, display:'flex', gap:10, flexWrap:'wrap'}}>
                  {Object.entries(summary.runtime.byTier).map(([t, n]) => (
                    <span key={t}>{t}: {n}</span>
                  ))}
                </div>
              )}
              {summary.runtime.forceLocal && (
                <div style={{marginTop:6, fontSize:11, color:'#8aa9ff'}}>ARIA_FORCE_LOCAL=1 (no external LLM calls)</div>
              )}
            </div>

            {/* Integrations row */}
            <div>
              <div style={{...label, marginBottom:8}}>Integrations</div>
              <div style={{display:'flex', gap:14, flexWrap:'wrap'}}>
                {['google_calendar', 'gmail', 'quickbooks'].map(k => {
                  const s = summary.integrations[k] || {};
                  return (
                    <div key={k} style={{display:'flex', alignItems:'center', gap:8, fontSize:13}}>
                      <span style={{textTransform:'capitalize'}}>{k.replace(/_/g, ' ')}</span>
                      <StatusPill configured={!!s.configured} connected={false} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Gap log tail */}
            <div>
              <div style={{...label, marginBottom:8}}>Gap log — last 15 (of {summary && gapLog.length > 0 ? '…many' : '?'})</div>
              {gapLog.length === 0 && <div style={{opacity:0.5, fontSize:12}}>empty</div>}
              {gapLog.length > 0 && (
                <div style={{
                  background:'rgba(0,0,0,0.25)',
                  border:'1px solid rgba(255,255,255,0.06)',
                  borderRadius:8,
                  padding:10,
                  fontFamily:'ui-monospace, Menlo, monospace',
                  fontSize:11,
                  maxHeight:220,
                  overflowY:'auto',
                  lineHeight:1.5,
                }}>
                  {gapLog.map((g, i) => (
                    <div key={i} style={{opacity:0.75, borderBottom:'1px solid rgba(255,255,255,0.04)', padding:'4px 0'}}>
                      <span style={{opacity:0.5}}>{(g.ts || '').slice(11, 19)}</span>
                      {'  '}
                      <span style={{color: g.kind?.includes('rejected') ? '#ff9f1c' : '#8aa9ff'}}>{g.kind || '?'}</span>
                      {g.id && <> · <span>{g.id}</span></>}
                      {g.errors?.[0] && <> · <span style={{opacity:0.55}}>{g.errors[0].slice(0, 60)}</span></>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{fontSize:11, opacity:0.4}}>
              Auto-refreshes every 15s. Last update {summary.generatedAt?.slice(11, 19)}.
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// LiveVoiceMonitor — two huge side-by-side boxes for testing voice live.
//   LEFT  box: what Whisper picked up (partials + finals + raw audio frames).
//   RIGHT box: how Aria answered (step-by-step lifecycle as it happens).
// Polls /api/pipeline/events every 250ms so updates feel real-time.
// ────────────────────────────────────────────────────────────────────────────
function LiveVoiceMonitor({ userId }) {
  const [livePartial, setLivePartial] = React.useState('');
  const [recentFinals, setRecentFinals] = React.useState([]); // [{ t, text, via }]
  const [steps, setSteps] = React.useState([]); // ordered lifecycle events for current/latest turn
  const [currentReply, setCurrentReply] = React.useState(null); // { text, tier, ms }
  const [stage, setStage] = React.useState('idle');
  const lastSeqRef = React.useRef(0);
  const currentTurnRef = React.useRef({ steps: [] });

  React.useEffect(() => {
    let cancelled = false;

    // Pipeline stages we care about, grouped by which side they belong to.
    const WHISPER_STAGES = new Set(['ws.partial', 'ws.final', 'audio.in', 'ws.connect', 'ws.err']);
    const ANSWER_STAGES  = new Set([
      'conversation.in', 'user.match', 'intent.classify',
      'hot.embed', 'hot.miss', 'hot.hit',
      'brain.match', 'brain.miss',
      'teach.start', 'teach.sonnet.start', 'teach.sonnet.end',
      'response.sent',
      'tts.gen.start', 'tts.gen.done', 'tts.stream-url',
    ]);

    // Human-readable label + color for each "answer" stage.
    const STAGE_LABEL = {
      'conversation.in':  { label: '1. Heard you',         color: '#8aa9ff' },
      'user.match':       { label: '2. Recognized you',    color: '#8aa9ff' },
      'intent.classify':  { label: '3. Classified intent', color: '#a78bfa' },
      'hot.embed':        { label: '4. Embedded query',    color: '#a78bfa' },
      'hot.hit':          { label: '5. Found in cache ✓',  color: 'var(--accent)' },
      'hot.miss':         { label: '5. Cache miss',        color: '#f59e0b' },
      'brain.match':      { label: '6. Brain matched ✓',   color: 'var(--accent)' },
      'brain.miss':       { label: '6. Brain missed',      color: '#f59e0b' },
      'teach.start':      { label: '7. Teach loop',        color: '#f59e0b' },
      'response.sent':    { label: '8. Reply sent ✓',      color: 'var(--accent)' },
      'tts.gen.start':    { label: '9. Generating voice…', color: '#a78bfa' },
      'tts.gen.done':     { label: '10. Voice ready ✓',    color: 'var(--accent)' },
      'tts.stream-url':   { label: '10. Streaming audio',  color: 'var(--accent)' },
    };

    async function tick() {
      try {
        const r = await fetch('/api/pipeline/events?since=' + lastSeqRef.current);
        if (!r.ok) return;
        const { events = [] } = await r.json();
        if (cancelled || events.length === 0) return;
        for (const e of events) if (e.seq > lastSeqRef.current) lastSeqRef.current = e.seq;

        // LEFT pane: latest ws.partial wins as current partial.
        // ws.final or conversation.in clears the partial + adds a final.
        for (let i = events.length - 1; i >= 0; i--) {
          const e = events[i];
          if (e.stage === 'ws.partial' && e.text) {
            setLivePartial(e.text);
            setStage('listening');
            break;
          }
          if (e.stage === 'ws.final' || e.stage === 'conversation.in') {
            setLivePartial('');
            break;
          }
        }
        const newFinals = events
          .filter(e => (e.stage === 'ws.final' || e.stage === 'conversation.in') && e.text)
          .map(e => ({ t: e.t, text: e.text, via: e.stage === 'ws.final' ? 'whisper' : 'cloud' }));
        if (newFinals.length > 0) {
          setRecentFinals(prev => [...prev, ...newFinals].slice(-6));
        }

        // RIGHT pane: lifecycle steps for the current turn.
        // We reset the step list whenever a new conversation.in arrives.
        for (const e of events) {
          if (e.stage === 'conversation.in') {
            // New turn — reset steps + clear last reply.
            currentTurnRef.current = { steps: [] };
            setSteps([]);
            setCurrentReply(null);
            setStage('matching');
          }
          if (ANSWER_STAGES.has(e.stage) && STAGE_LABEL[e.stage]) {
            const label = STAGE_LABEL[e.stage];
            const stepEntry = {
              seq: e.seq,
              stage: e.stage,
              label: label.label,
              color: label.color,
              text: e.text || '',
              meta: e.meta || null,
              t: e.t,
            };
            currentTurnRef.current.steps.push(stepEntry);
            setSteps([...currentTurnRef.current.steps]);
          }
          if (e.stage === 'response.sent') {
            setCurrentReply({
              text: e.text || '',
              tier: e.meta?.tier || e.meta?.source || null,
              ms: e.meta?.totalMs || e.meta?.ms || null,
            });
            setStage('speaking');
          }
          if (e.stage === 'tts.gen.done' || e.stage === 'tts.stream-url') {
            setStage('done');
          }
        }
      } catch { /* network blip — wait for next tick */ }
    }
    tick();
    const id = setInterval(tick, 250);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const boxBase = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 14,
    padding: 24,
    minHeight: 580,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  };
  const titleStyle = { margin: 0, fontSize: 18, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 };
  const subtle = { fontSize: 12, opacity: 0.55 };

  // Stage pill style
  const stageMeta = {
    idle:       { label: 'IDLE',       color: 'rgba(255,255,255,0.4)', bg: 'rgba(255,255,255,0.05)' },
    listening:  { label: 'HEARING',    color: '#8aa9ff',                bg: 'rgba(120,160,255,0.18)' },
    matching:   { label: 'THINKING',   color: '#a78bfa',                bg: 'rgba(167,139,250,0.18)' },
    speaking:   { label: 'ANSWERING',  color: 'var(--accent)',                bg: 'rgba(252, 211, 77,0.18)' },
    done:       { label: 'DONE',       color: 'rgba(255,255,255,0.55)', bg: 'rgba(255,255,255,0.06)' },
  }[stage] || { label: stage.toUpperCase(), color: '#fff', bg: 'rgba(255,255,255,0.06)' };

  return (
    <div>
      {/* Top status banner */}
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)',
        borderRadius:10, padding:'10px 18px', marginBottom:16,
      }}>
        <div style={{fontSize:13, opacity:0.7}}>
          user: <span style={{opacity:0.9}}>{userId}</span> · polling /api/pipeline/events every 250ms
        </div>
        <span style={{
          padding:'4px 14px', borderRadius:999, fontSize:11, fontWeight:700,
          letterSpacing:'0.06em', color: stageMeta.color, background: stageMeta.bg,
        }}>● {stageMeta.label}</span>
      </div>

      {/* TWO BIG BOXES SIDE BY SIDE */}
      <div style={{display:'flex', gap:16, flexWrap:'wrap'}}>

        {/* ── LEFT: Whisper transcription ──────────────────────────── */}
        <section style={{...boxBase, borderColor: 'rgba(120,160,255,0.30)'}}>
          <h2 style={titleStyle}>
            <span style={{
              padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:700,
              background:'rgba(120,160,255,0.20)', color:'#8aa9ff',
            }}>🎤 WHISPER</span>
            What Whisper hears
          </h2>
          <div style={subtle}>The PC's GPU running distil-small.en, streaming over LAN.</div>

          {/* Current partial — BIG. */}
          <div style={{
            marginTop:18, marginBottom:18,
            background:'rgba(0,0,0,0.35)',
            border:'1px solid rgba(120,160,255,0.30)',
            borderRadius:10,
            padding:'24px 24px',
            fontSize:24,
            fontWeight:500,
            lineHeight:1.4,
            minHeight:110,
            fontFamily:'ui-monospace, Menlo, monospace',
            color: livePartial ? '#a3c0ff' : 'rgba(255,255,255,0.25)',
            fontStyle: livePartial ? 'normal' : 'italic',
          }}>
            {livePartial || '(speak to see partial transcript here…)'}
          </div>

          {/* Finals list */}
          <div style={{...subtle, marginBottom:8, fontWeight:600, opacity:0.7}}>
            COMPLETED SENTENCES ({recentFinals.length})
          </div>
          <div style={{flex:1, overflowY:'auto'}}>
            {recentFinals.length === 0 && (
              <div style={{opacity:0.35, fontSize:14, fontStyle:'italic'}}>no completed sentences yet</div>
            )}
            {recentFinals.slice().reverse().map((f, i) => {
              const ts = new Date(f.t).toLocaleTimeString([], { hour12: false });
              return (
                <div key={i} style={{
                  padding:'12px 16px',
                  background: i === 0 ? 'rgba(252, 211, 77,0.10)' : 'rgba(255,255,255,0.03)',
                  borderRadius:8,
                  marginBottom:8,
                  fontSize:16,
                  lineHeight:1.4,
                  borderLeft: i === 0 ? '3px solid var(--accent)' : '3px solid transparent',
                }}>
                  <div style={{display:'flex', gap:8, fontSize:10, marginBottom:6, opacity:0.55}}>
                    <span>{ts}</span>
                    <span style={{padding:'1px 6px', borderRadius:999, background:'rgba(255,255,255,0.08)'}}>
                      via {f.via}
                    </span>
                  </div>
                  <div>{f.text}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── RIGHT: How Aria answers ──────────────────────────────── */}
        <section style={{...boxBase, borderColor: 'rgba(252, 211, 77,0.30)'}}>
          <h2 style={titleStyle}>
            <span style={{
              padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:700,
              background:'rgba(252, 211, 77,0.20)', color:'var(--accent)',
            }}>🧠 ARIA</span>
            How Aria answers
          </h2>
          <div style={subtle}>Live step-by-step: heard → classified → searched → matched → speaking.</div>

          {/* Step list */}
          <div style={{marginTop:18, marginBottom:18}}>
            {steps.length === 0 && (
              <div style={{
                background:'rgba(0,0,0,0.25)', borderRadius:10, padding:'24px',
                fontSize:14, fontStyle:'italic', opacity:0.4, textAlign:'center',
              }}>
                (waiting for a query — talk to Aria or use the chat input)
              </div>
            )}
            {steps.map((s, i) => (
              <div key={s.seq || i} style={{
                display:'flex', alignItems:'flex-start', gap:14,
                padding:'10px 14px',
                marginBottom:6,
                background:'rgba(0,0,0,0.20)',
                borderRadius:8,
                borderLeft: `3px solid ${s.color}`,
                fontSize:14,
                lineHeight:1.4,
              }}>
                <div style={{minWidth:170, color: s.color, fontWeight:600, fontSize:13}}>
                  {s.label}
                </div>
                <div style={{flex:1, opacity:0.85, fontFamily:'ui-monospace, Menlo, monospace', fontSize:12.5}}>
                  {(s.text || '').slice(0, 140) || <span style={{opacity:0.4}}>—</span>}
                  {s.meta?.score != null && <span style={{opacity:0.55}}> (score {Number(s.meta.score).toFixed(2)})</span>}
                  {s.meta?.ms != null && <span style={{opacity:0.55}}> · {s.meta.ms}ms</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Final reply — BIG. */}
          {currentReply && (
            <div style={{
              background:'rgba(0,0,0,0.35)',
              border:'1px solid rgba(252, 211, 77,0.40)',
              borderRadius:10,
              padding:'20px 22px',
              fontSize:18,
              fontWeight:500,
              lineHeight:1.5,
              whiteSpace:'pre-wrap',
              marginTop:'auto',
            }}>
              <div style={{display:'flex', gap:8, fontSize:11, marginBottom:10}}>
                <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(252, 211, 77,0.20)', color:'var(--accent)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em'}}>
                  ARIA SAID
                </span>
                {currentReply.tier && (
                  <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.7)'}}>
                    {currentReply.tier}
                  </span>
                )}
                {currentReply.ms != null && (
                  <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.7)'}}>
                    {currentReply.ms}ms
                  </span>
                )}
              </div>
              {currentReply.text}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// WhisperMicTest — bare-bones mic + WebSocket tool. No Aria pipeline.
// Opens its own WS to /ws/transcribe (Vite proxies to PC Whisper). Captures
// mic at 16kHz mono via AudioWorklet, sends raw Int16 PCM frames, shows
// partials in huge monospace text + finals in a list + raw event log.
// Lives inside the Aria desktop app so you don't have to leave to test.
// ────────────────────────────────────────────────────────────────────────────
function WhisperMicTest() {
  const [recording, setRecording] = React.useState(false);
  const [status, setStatus] = React.useState('off'); // off | connecting | on | err | mic-denied
  const [partial, setPartial] = React.useState('');
  const [finals, setFinals] = React.useState([]);
  const [logs, setLogs] = React.useState([{ ts: new Date(), msg: 'Click the button to start.', cls: 'info' }]);
  // Aria's responses to each final transcribed sentence
  const [ariaResponses, setAriaResponses] = React.useState([]); // [{ t, query, reply, source, tier, ms, action, askingMs }]
  const [askAria, setAskAria] = React.useState(true); // toggle: forward finals to Aria automatically
  // Whisper's final can arrive with EMPTY text while the full sentence lives
  // in the last partial (proven 8/12 by streaming real audio through the
  // domain WS: 9 partials built the sentence, final.text === ""). Without
  // this fallback the finals list stays empty and Aria is never asked —
  // "no responses yet" forever, even while partials flow.
  const lastPartialTextRef = React.useRef('');
  const mediaStreamRef = React.useRef(null);
  const audioCtxRef = React.useRef(null);
  const workletRef = React.useRef(null);
  const wsRef = React.useRef(null);
  // recording flag accessible to async callbacks (state lags)
  const recordingRef = React.useRef(false);
  // count of WS reconnections this session (each utterance = new WS)
  const sessionCountRef = React.useRef(0);
  // askAria flag for async callbacks
  const askAriaRef = React.useRef(true);
  React.useEffect(() => { askAriaRef.current = askAria; }, [askAria]);

  const addLog = React.useCallback((msg, cls = 'info') => {
    setLogs(prev => [...prev, { ts: new Date(), msg, cls }].slice(-80));
  }, []);

  // Full stop — tear down EVERYTHING (mic, audio context, WS).
  const stop = React.useCallback(() => {
    recordingRef.current = false;
    setRecording(false);
    setStatus('off');
    if (workletRef.current) { try { workletRef.current.disconnect(); } catch (_) {} workletRef.current = null; }
    if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (_) {} audioCtxRef.current = null; }
    if (mediaStreamRef.current) { try { mediaStreamRef.current.getTracks().forEach(t => t.stop()); } catch (_) {} mediaStreamRef.current = null; }
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      try { wsRef.current.close(); } catch (_) {}
    }
    wsRef.current = null;
  }, []);

  // Forward a Whisper final to Aria and capture her response with all the
  // diagnostic metadata we can extract — which tier matched, score, ms,
  // action (for voice-routing intents), citation, model name.
  const askAriaWithFinal = React.useCallback(async (query) => {
    const askedAt = Date.now();
    const entry = { t: askedAt, query, reply: null, source: '...', tier: null, ms: null, action: null, asking: true };
    setAriaResponses(prev => [...prev, entry].slice(-8));
    addLog(`→ Aria: "${query.slice(0, 80)}"`, 'info');
    try {
      const r = await fetch('/api/ai/conversation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: (typeof localStorage !== 'undefined' && localStorage.getItem('aria_user_id')) || 'default',
          prompt: query,
          mode: 'voice',
        }),
      });
      const data = await r.json();
      const ms = Date.now() - askedAt;
      // Same tier extraction logic as the dev console probe
      let tier = data.tier || data.source || data.responseSource || data.mode || null;
      if (!tier && data._fromBrain) tier = 'brain';
      if (!tier && data.brainMeta?.mode) tier = 'brain · ' + data.brainMeta.mode;
      if (!tier && data.source === 'aria_action_intent') tier = 'action';
      if (!tier && data.source === 'aria_integrations_intent') tier = 'integration · ' + (data.intent || '?');
      const replyText = data.reply || data.response || data.text || data.question || '(empty reply)';
      addLog(`← Aria (${ms}ms, ${tier || 'unknown'}): "${(replyText || '').slice(0, 80)}"`, 'ok');
      setAriaResponses(prev => prev.map(e => e.t === askedAt ? {
        ...e,
        reply: replyText,
        source: data.source || (data._fromBrain ? 'brain-fast-path' : 'conversation'),
        tier,
        ms,
        action: data.action || null,
        asking: false,
      } : e));
    } catch (e) {
      const ms = Date.now() - askedAt;
      addLog(`✗ Aria request failed (${ms}ms): ${e.message}`, 'err');
      setAriaResponses(prev => prev.map(item => item.t === askedAt ? {
        ...item, reply: 'Error: ' + e.message, tier: 'error', ms, asking: false,
      } : item));
    }
  }, [addLog]);

  // Open just the WebSocket. Mic + AudioContext + worklet are persistent
  // across reconnections — we only re-open the WS each utterance because
  // Whisper's protocol closes after each final (one-shot per session).
  const openWebSocket = React.useCallback(() => {
    if (!recordingRef.current) return;
    sessionCountRef.current += 1;
    const sessionNum = sessionCountRef.current;
    // wss on https (8/12): 'ws://' hardcoded meant the mic test only worked in
    // the desktop app / localhost — on https://howtoaria.com the browser
    // blocks insecure ws as mixed content. Match the page scheme.
    const url = (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host + '/ws/transcribe';
    addLog(`[session ${sessionNum}] opening WebSocket: ${url}`, 'info');
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onopen = () => {
      addLog(`[session ${sessionNum}] ✓ WS open — Whisper listening`, 'ok');
      setStatus('on');
    };
    ws.onmessage = (ev) => {
      try {
        const m = JSON.parse(ev.data);
        if (m.type === 'partial') {
          setPartial(m.text || '…');
          if (m.text && m.text.trim()) lastPartialTextRef.current = m.text.trim();
          addLog(`[session ${sessionNum}] partial: "${m.text || ''}"`, 'info');
        } else if (m.type === 'final') {
          // Empty final = whisper's EOU marker; the sentence is the last partial.
          const text = (m.text || '').trim() || lastPartialTextRef.current;
          lastPartialTextRef.current = '';
          setFinals(prev => [...prev, { t: Date.now(), text, session: sessionNum }].slice(-12));
          setPartial('');
          addLog(`[session ${sessionNum}] FINAL: "${text}"`, 'ok');
          // Whisper auto-closes after a final. onclose will trigger reopen.
          // Also forward to Aria if the toggle is on.
          if (askAriaRef.current && text.trim().length > 0) {
            askAriaWithFinal(text.trim());
          }
        } else {
          addLog(`[session ${sessionNum}] msg: ${JSON.stringify(m).slice(0, 200)}`, 'info');
        }
      } catch (_) {
        addLog(`[session ${sessionNum}] non-json: ${String(ev.data).slice(0, 100)}`, 'info');
      }
    };
    ws.onerror = () => { addLog(`[session ${sessionNum}] ✗ WS error`, 'err'); };
    ws.onclose = (e) => {
      addLog(`[session ${sessionNum}] WS closed (code=${e.code})`, 'info');
      wsRef.current = null;
      // FLUSH ON CLOSE (8/12, from Braeden's live session): whisper heard the
      // whole sentence in partials, then the WS closed WITHOUT a final —
      // sentence dropped, "no responses yet" forever. Close = done talking
      // (his Stop click, or the server ending the session), so promote the
      // last partial to the finished sentence. The final handler clears the
      // ref, so a normal final can never double-send.
      const heldText = lastPartialTextRef.current;
      if (heldText && heldText.trim()) {
        lastPartialTextRef.current = '';
        setFinals(prev => [...prev, { t: Date.now(), text: heldText, session: sessionNum }].slice(-12));
        setPartial('');
        addLog(`[session ${sessionNum}] FINAL (flushed on close): "${heldText}"`, 'ok');
        if (askAriaRef.current) askAriaWithFinal(heldText.trim());
      }
      // Reopen if we're still recording — Whisper's one-shot protocol means
      // each utterance needs a fresh session.
      if (recordingRef.current) {
        addLog(`[session ${sessionNum}] auto-reconnecting for next utterance…`, 'info');
        setStatus('connecting');
        setTimeout(openWebSocket, 100); // tiny delay so server can reset
      }
    };
  }, [addLog, askAriaWithFinal]);

  const start = React.useCallback(async () => {
    if (recordingRef.current) return;
    try {
      setStatus('connecting');
      setPartial('');
      sessionCountRef.current = 0;

      // 1a. On macOS Electron, getUserMedia alone is unreliable — it can
      //     return a stream that produces digital silence forever if the
      //     helper subprocess hasn't been TCC-registered first. The proper
      //     fix is to call systemPreferences.askForMediaAccess('microphone')
      //     via the Electron main process FIRST. That pops the real macOS
      //     dialog, registers Aria in System Settings → Privacy → Microphone,
      //     and only then does getUserMedia produce real audio.
      if (typeof window !== 'undefined' && window.electronAPI?.requestMicrophoneAccess) {
        addLog('Asking macOS for mic access via Electron…', 'info');
        try {
          const r = await window.electronAPI.requestMicrophoneAccess();
          addLog(`macOS mic status: ${r?.status || 'unknown'} · granted=${r?.granted}`, r?.granted ? 'ok' : 'err');
          if (!r?.granted) {
            setStatus('mic-denied');
            addLog('macOS denied mic. Click "Open System mic settings" below.', 'err');
            return;
          }
        } catch (e) {
          addLog('Electron mic request failed: ' + e.message + ' (falling back to browser getUserMedia)', 'info');
        }
      }

      // 1b. Now request the actual stream. On macOS Electron, this works
      //     reliably ONLY after the native askForMediaAccess above. On
      //     plain web (non-Electron), this is the only step.
      addLog('Requesting microphone stream…', 'info');
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true },
        });
      } catch (micErr) {
        const name = micErr?.name || 'Error';
        addLog(`✗ Mic ${name}: ${micErr.message}`, 'err');
        if (name === 'NotAllowedError' || /permission|denied/i.test(micErr.message)) {
          setStatus('mic-denied');
        } else {
          setStatus('err');
        }
        return;
      }
      mediaStreamRef.current = stream;
      addLog('✓ Mic stream acquired', 'ok');

      // 1c. Verify the stream is actually producing audio (not digital silence).
      //     If the first audio track is muted or has 0 settings, that's the
      //     TCC-not-registered failure mode the Electron comment warned about.
      const tracks = stream.getAudioTracks();
      if (tracks.length === 0) {
        addLog('✗ Stream has no audio tracks', 'err');
        setStatus('err');
        return;
      }
      const t = tracks[0];
      addLog(`Audio track: label="${t.label}", muted=${t.muted}, enabled=${t.enabled}`, t.muted ? 'err' : 'info');

      // 2. Set up audio context + worklet ONCE (persists across WS reconnects).
      const ctx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      audioCtxRef.current = ctx;
      const workletCode = `
        class PcmEmitter extends AudioWorkletProcessor {
          constructor() { super(); this.buf = []; this.frameSize = 1600; }
          process(inputs) {
            const ch = inputs[0]?.[0];
            if (!ch) return true;
            for (let i = 0; i < ch.length; i++) {
              const s = Math.max(-1, Math.min(1, ch[i]));
              this.buf.push(s < 0 ? s * 32768 : s * 32767);
            }
            while (this.buf.length >= this.frameSize) {
              const slice = this.buf.splice(0, this.frameSize);
              const i16 = new Int16Array(slice);
              this.port.postMessage(i16.buffer, [i16.buffer]);
            }
            return true;
          }
        }
        registerProcessor('pcm-emitter', PcmEmitter);
      `;
      const blob = new Blob([workletCode], { type: 'application/javascript' });
      const blobUrl = URL.createObjectURL(blob);
      await ctx.audioWorklet.addModule(blobUrl);
      const source = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, 'pcm-emitter');
      workletRef.current = node;
      // The worklet emits indefinitely. We forward to whichever WS is alive
      // right now — if it's closed (between utterances), we just drop the frame.
      // ── SILENCE MUST NEVER BE THE ANSWER (Braeden 8/31) ──────────────
      // "it says its hearing me but its not responding." The listening
      // indicator only means the SOCKET opened - it says nothing about
      // whether any audio is reaching it. On a phone the capture graph is
      // the fragile part (worklet support, a suspended context, another app
      // holding the mic), and when it silently emits nothing the UI sits on
      // "listening" forever with no transcript, no turn and no reply.
      //
      // The server side of this was proven working end to end on 8/31 (real
      // speech through the public socket came back "Book the Miller move for
      // Saturday"), so the only thing left to make honest is the client:
      // count what we actually send, and say so if it is nothing.
      let _sentChunks = 0;
      node.port.onmessage = (e) => {
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) { ws.send(e.data); _sentChunks += 1; }
      };
      source.connect(node);
      // A local timer: this lives inside start(), so it needs no ref - and a
      // ref declared in another component would simply throw here (caught,
      // silently killing mic start, which is the opposite of the goal).
      setTimeout(() => {
        if (_sentChunks === 0 && recordingRef.current) {
          addLog('mic watchdog: no audio frames captured in 5s', 'err');
          try {
            setVoiceError("I'm not getting any sound from your mic. Check the mic permission for this site, close any other app using the mic, then tap the orb again.");
          } catch (_) {}
          try { if (ctx.state === 'suspended') ctx.resume(); } catch (_) {}
        }
      }, 5000);
      addLog('✓ Audio capture started @ 16kHz (continuous)', 'ok');

      // 3. Mark recording + open first WS session.
      recordingRef.current = true;
      setRecording(true);
      openWebSocket();
    } catch (e) {
      addLog('start() failed: ' + e.message, 'err');
      setStatus('err');
      stop();
    }
  }, [addLog, openWebSocket, stop]);

  // Mic-permission helpers — Electron exposes IPC channels to open the
  // macOS / Windows privacy panel directly.
  const openMicSettings = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    if (window.electronAPI?.openMicrophoneSettings) {
      window.electronAPI.openMicrophoneSettings();
      addLog('Opening system microphone settings…', 'info');
    } else {
      addLog('No Electron API — open System Settings → Privacy → Microphone manually.', 'err');
    }
  }, [addLog]);

  const requestMicAgain = React.useCallback(async () => {
    if (typeof window === 'undefined') return;
    if (window.electronAPI?.requestMicrophoneAccess) {
      addLog('Requesting mic permission via Electron…', 'info');
      try {
        const r = await window.electronAPI.requestMicrophoneAccess();
        addLog(`Permission result: ${JSON.stringify(r).slice(0, 100)}`, 'ok');
      } catch (e) {
        addLog('Request failed: ' + e.message, 'err');
      }
    }
  }, [addLog]);

  React.useEffect(() => () => stop(), [stop]); // cleanup on unmount

  const statusMeta = {
    off:          { label: 'OFF',           color: 'rgba(255,255,255,0.5)', bg: 'rgba(255,255,255,0.06)' },
    connecting:   { label: 'CONNECTING',    color: '#f59e0b',                bg: 'rgba(245,158,11,0.18)' },
    on:           { label: 'LISTENING',     color: 'var(--accent)',                bg: 'rgba(252, 211, 77,0.18)' },
    err:          { label: 'ERROR',         color: '#ef4444',                bg: 'rgba(239,68,68,0.18)' },
    'mic-denied': { label: 'MIC DENIED',    color: '#ef4444',                bg: 'rgba(239,68,68,0.18)' },
  }[status] || { label: status.toUpperCase(), color:'#fff', bg:'rgba(255,255,255,0.06)' };

  const logCls = (c) => ({
    info: '#a3c0ff',
    ok:   'var(--accent)',
    err:  '#ef4444',
  }[c] || 'rgba(255,255,255,0.7)');

  const panel = {
    background:'rgba(255,255,255,0.04)',
    border:'1px solid rgba(255,255,255,0.12)',
    borderRadius:14,
    padding:20,
    marginTop:16,
  };
  const panelTitle = {
    fontSize:11, letterSpacing:'0.08em', textTransform:'uppercase',
    opacity:0.55, margin:'0 0 12px 0', fontWeight:700,
  };

  return (
    <div>
      <p style={{opacity:0.6, fontSize:14, margin:'0 0 20px 0', lineHeight:1.5}}>
        Bare-bones tool inside Aria desktop. Opens a direct WebSocket to PC Whisper (via Vite proxy at <code style={{background:'rgba(255,255,255,0.08)', padding:'1px 6px', borderRadius:4}}>/ws/transcribe</code>), captures your mic, shows what Whisper transcribes. No Aria pipeline in the way.
      </p>

      <div style={{display:'flex', alignItems:'center', gap:14, marginBottom:8, flexWrap:'wrap'}}>
        <button
          type="button"
          onClick={() => recording ? stop() : start()}
          style={{
            display:'flex', alignItems:'center', gap:10,
            padding:'16px 28px', borderRadius:12,
            background: recording ? '#ef4444' : '#3a9dff',
            color:'#fff', border:'none',
            fontSize:17, fontWeight:600, cursor:'pointer',
            animation: recording ? 'pulse 1.2s ease-in-out infinite' : 'none',
          }}
        >
          {recording ? '■ Stop' : '▶ Start mic test'}
        </button>
        <span style={{
          padding:'4px 14px', borderRadius:999,
          fontSize:11, fontWeight:700, letterSpacing:'0.06em',
          color: statusMeta.color, background: statusMeta.bg,
        }}>● {statusMeta.label}</span>

        {/* Send-to-Aria toggle — when on, each Whisper final POSTs to /api/ai/conversation */}
        <label style={{
          display:'flex', alignItems:'center', gap:8,
          padding:'8px 14px', borderRadius:10,
          background: askAria ? 'rgba(252, 211, 77,0.15)' : 'rgba(255,255,255,0.06)',
          border: askAria ? '1px solid rgba(252, 211, 77,0.35)' : '1px solid rgba(255,255,255,0.15)',
          fontSize:13, fontWeight:600, cursor:'pointer',
          color: askAria ? 'var(--accent)' : 'rgba(255,255,255,0.65)',
        }}>
          <input
            type="checkbox"
            checked={askAria}
            onChange={(e) => setAskAria(e.target.checked)}
            style={{margin:0}}
          />
          Send to Aria
        </label>

        {/* Mic permission rescue path — shown when status === 'mic-denied' */}
        {status === 'mic-denied' && (
          <>
            <button
              type="button"
              onClick={openMicSettings}
              style={{
                padding:'10px 18px', borderRadius:10,
                background:'rgba(239,68,68,0.15)', color:'#ef4444',
                border:'1px solid rgba(239,68,68,0.35)',
                fontSize:13, fontWeight:600, cursor:'pointer',
              }}
            >Open System mic settings</button>
            <button
              type="button"
              onClick={() => { requestMicAgain(); setTimeout(start, 800); }}
              style={{
                padding:'10px 18px', borderRadius:10,
                background:'rgba(255,255,255,0.06)', color:'#fff',
                border:'1px solid rgba(255,255,255,0.20)',
                fontSize:13, fontWeight:600, cursor:'pointer',
              }}
            >Retry mic</button>
          </>
        )}
      </div>

      {/* Inline help when mic is denied — explains the fix */}
      {status === 'mic-denied' && (
        <div style={{
          marginTop:14, padding:'14px 18px', borderRadius:10,
          background:'rgba(239,68,68,0.08)',
          border:'1px solid rgba(239,68,68,0.25)',
          fontSize:13.5, lineHeight:1.55,
        }}>
          <div style={{fontWeight:600, marginBottom:6, color:'#ef4444'}}>macOS blocked microphone access.</div>
          <div style={{opacity:0.85}}>
            <strong>System Settings → Privacy &amp; Security → Microphone</strong> — enable <strong>Aria</strong> (or <strong>Electron</strong> if Aria isn't listed). Then click "Retry mic" above.
            <br/>If Aria isn't in the list at all, fully quit (Cmd+Q) and relaunch — macOS adds it after a fresh mic request.
          </div>
        </div>
      )}

      {/* HUGE partial display */}
      <section style={panel}>
        <div style={panelTitle}>Live partial — what Whisper is locking in as you speak</div>
        <div style={{
          background:'rgba(0,0,0,0.4)',
          border:'2px solid rgba(120,160,255,0.4)',
          borderRadius:10,
          padding:'28px 28px',
          fontFamily:'ui-monospace, Menlo, monospace',
          fontSize: partial ? 28 : 18,
          fontStyle: partial ? 'normal' : 'italic',
          lineHeight:1.4,
          minHeight:110,
          color: partial ? '#a3c0ff' : 'rgba(255,255,255,0.25)',
        }}>
          {partial || (recording ? '(listening — say something)' : '(silence)')}
        </div>
      </section>

      {/* Completed sentences */}
      <section style={panel}>
        <div style={panelTitle}>Completed sentences ({finals.length})</div>
        {finals.length === 0 && (
          <div style={{opacity:0.4, fontSize:14, fontStyle:'italic'}}>none yet</div>
        )}
        {finals.length > 0 && (
          <div style={{display:'flex', flexDirection:'column', gap:8}}>
            {finals.slice().reverse().map((f, i) => {
              const ts = new Date(f.t).toLocaleTimeString();
              return (
                <div key={i} style={{
                  background: i === 0 ? 'rgba(252, 211, 77,0.10)' : 'rgba(255,255,255,0.03)',
                  borderLeft: i === 0 ? '3px solid var(--accent)' : '3px solid transparent',
                  padding:'14px 18px',
                  borderRadius:8,
                  fontSize:17,
                  lineHeight:1.4,
                }}>
                  <div style={{fontSize:10, opacity:0.5, marginBottom:4}}>{ts}</div>
                  <div>{f.text}</div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* How Aria answered — auto-filled when "Send to Aria" is on */}
      <section style={{...panel, borderColor: 'rgba(252, 211, 77,0.35)'}}>
        <div style={{...panelTitle, color:'var(--accent)', opacity:0.85}}>
          🧠 How Aria answered ({ariaResponses.length})
        </div>
        {ariaResponses.length === 0 && (
          <div style={{opacity:0.4, fontSize:14, fontStyle:'italic'}}>
            {askAria
              ? 'no responses yet — say something to Aria'
              : '"Send to Aria" is off — flip it on to see her responses'}
          </div>
        )}
        {ariaResponses.length > 0 && (
          <div style={{display:'flex', flexDirection:'column', gap:12}}>
            {ariaResponses.slice().reverse().map((r, i) => {
              const ts = new Date(r.t).toLocaleTimeString();
              const isLatest = i === 0;
              return (
                <div key={r.t} style={{
                  background: isLatest ? 'rgba(252, 211, 77,0.08)' : 'rgba(255,255,255,0.03)',
                  borderLeft: isLatest ? '3px solid var(--accent)' : '3px solid transparent',
                  padding:'14px 18px',
                  borderRadius:8,
                }}>
                  {/* Header row — query + tier badges + timing */}
                  <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:10, fontSize:11}}>
                    <span style={{opacity:0.5}}>{ts}</span>
                    {r.asking ? (
                      <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(245,158,11,0.18)', color:'#f59e0b', fontWeight:600}}>
                        ASKING…
                      </span>
                    ) : (
                      <>
                        {r.tier && (
                          <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(120,160,255,0.18)', color:'#8aa9ff', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.03em'}}>
                            {r.tier}
                          </span>
                        )}
                        {r.ms != null && (
                          <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.65)'}}>
                            {r.ms}ms
                          </span>
                        )}
                        {r.action && (
                          <span style={{padding:'2px 8px', borderRadius:999, background:'rgba(167,139,250,0.18)', color:'#a78bfa', fontWeight:600}}>
                            action: {r.action.type}
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {/* "You said" row */}
                  <div style={{
                    fontSize:14, opacity:0.65, marginBottom:8,
                    fontFamily:'ui-monospace, Menlo, monospace',
                  }}>
                    <span style={{opacity:0.5, marginRight:6}}>you:</span>
                    {r.query}
                  </div>

                  {/* "Aria said" row — BIG */}
                  {r.asking ? (
                    <div style={{
                      fontSize:16, fontStyle:'italic', opacity:0.5,
                      padding:'8px 0',
                    }}>thinking…</div>
                  ) : (
                    <div style={{
                      fontSize:17,
                      lineHeight:1.5,
                      whiteSpace:'pre-wrap',
                      padding:'8px 0',
                    }}>
                      <span style={{opacity:0.5, marginRight:6, fontSize:14}}>aria:</span>
                      {r.reply}
                    </div>
                  )}

                  {/* Action payload if voice routing fired */}
                  {r.action && (
                    <div style={{
                      marginTop:8,
                      padding:'8px 12px',
                      background:'rgba(167,139,250,0.08)',
                      border:'1px solid rgba(167,139,250,0.25)',
                      borderRadius:6,
                      fontSize:11,
                      fontFamily:'ui-monospace, Menlo, monospace',
                      color:'rgba(255,255,255,0.75)',
                    }}>
                      {JSON.stringify(r.action)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Raw event log */}
      <section style={panel}>
        <div style={panelTitle}>Raw event log</div>
        <div style={{
          background:'rgba(0,0,0,0.3)',
          padding:14,
          borderRadius:8,
          maxHeight:260,
          overflowY:'auto',
          fontFamily:'ui-monospace, Menlo, monospace',
          fontSize:12,
          lineHeight:1.55,
        }}>
          {logs.map((l, i) => (
            <div key={i} style={{
              padding:'3px 0',
              borderBottom:'1px solid rgba(255,255,255,0.05)',
              color: logCls(l.cls),
            }}>
              <span style={{opacity:0.5}}>[{l.ts.toLocaleTimeString()}]</span>{' '}
              {l.msg}
            </div>
          ))}
        </div>
      </section>

      <style>{`@keyframes pulse { 50% { opacity:0.7; } }`}</style>
    </div>
  );
}

function AriaExhibit({ data }) {
  if (!data) return null;
  const type = data.type;

  // LIST — Braeden 8/13: "she pulls up a visual list showing them, heres a
  // list, want me to read it out". Asking what tools she uses should SHOW the
  // list, not make her recite twenty-four items out loud. Groups carry their
  // heading; a live item gets the green dot, one that is waiting says what it
  // waits on.
  if (type === 'list') {
    const groups = Array.isArray(data.groups) ? data.groups : [];
    return (
      <div className="aria-viz-list">
        {data.title && <div className="aria-viz-list-title">{data.title}</div>}
        {groups.map((g, gi) => (
          <div className="aria-viz-list-group" key={gi}>
            {g.name && (
              <div className="aria-viz-list-head">
                <span>{g.name}</span>
                {g.count && <span className="aria-viz-list-count">{g.count}</span>}
              </div>
            )}
            {(g.items || []).map((it, ii) => (
              <div className={`aria-viz-list-row${it.on ? ' is-on' : ''}`} key={ii}>
                <span className="aria-viz-list-dot" aria-hidden />
                <span className="aria-viz-list-label">{it.label}</span>
                {it.note && <span className="aria-viz-list-note">{it.note}</span>}
              </div>
            ))}
          </div>
        ))}
        {data.caption && <div className="aria-viz-list-cap">{data.caption}</div>}
      </div>
    );
  }

  // LLM viz: equations panel → arrow → calculator panel
  if (type === 'llm') {
    const problems = data.problems || ['1 + 1 = ', '1 + 2 = ', '1 + 3 = '];
    const answers = data.answers || ['2', '3', '4'];
    return (
      <div className="aria-viz-llm">
        <div className="aria-highlight">
          <span className="aria-highlight-label">{data.leftLabel || 'Equations'}</span>
          <div className="aria-viz-llm-problems">
            {problems.map((p, i) => (
              <div key={i} className="aria-viz-llm-row">{p}<span className="ans">{answers[i] || '?'}</span></div>
            ))}
          </div>
        </div>
        <div className="aria-viz-llm-arrow">→</div>
        <div className="aria-highlight">
          <span className="aria-highlight-label">{data.rightLabel || 'Calculator'}</span>
          <div className="aria-viz-llm-calc">
            <div className="aria-viz-llm-calc-screen"></div>
            <div className="aria-viz-llm-keys">
              {['7','8','9','4','5','6','1','2','3','0','+','='].map((k, i) => (
                <div key={i} className={`aria-viz-llm-key${'+−='.includes(k) ? ' op' : ''}`}>{k}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // API viz: two boxes with animated pinging line between
  if (type === 'api') {
    return (
      <div className="aria-viz-api" style={{maxWidth: 540, margin: '0 auto'}}>
        <div className="aria-highlight" style={{padding: '12px 16px'}}>
          <span className="aria-highlight-label">{data.leftLabel || 'Your app'}</span>
          <div className="aria-viz-api-box-label">{(data.left || 'Your\napp').split('\n').map((line, i) => <span key={i}>{line}<br/></span>)}</div>
        </div>
        <div className="aria-viz-api-line"></div>
        <div className="aria-highlight" style={{padding: '12px 16px'}}>
          <span className="aria-highlight-label">{data.rightLabel || 'Stripe'}</span>
          <div className="aria-viz-api-box-label">{data.right || 'Stripe'}</div>
        </div>
      </div>
    );
  }

  // Encryption viz: scrambled box → key → readable box
  if (type === 'encryption') {
    return (
      <div className="aria-viz-enc" style={{maxWidth: 560, margin: '0 auto'}}>
        <div className="aria-highlight" style={{flex: 1, padding: 16}}>
          <span className="aria-highlight-label">{data.leftLabel || 'Scrambled'}</span>
          <div className="aria-viz-enc-locked" style={{background: 'transparent', padding: 0}}>{data.scrambled || 'xK#7@p$2!q'}</div>
        </div>
        <div className="aria-viz-enc-key">🔑</div>
        <div className="aria-highlight" style={{flex: 1, padding: 16}}>
          <span className="aria-highlight-label">{data.rightLabel || 'Readable'}</span>
          <div className="aria-viz-enc-unlocked" style={{background: 'transparent', padding: 0}}>{data.unscrambled || 'hello aria'}</div>
        </div>
      </div>
    );
  }

  // Fallback — arbitrary HTML if provided (must be pre-sanitized server-side)
  if (data.html) {
    return <div dangerouslySetInnerHTML={{__html: data.html}} />;
  }
  return null;
}

function MarkdownBlock({ text }) {
  const lines = String(text || '').split('\n');
  // Group lines into blocks: paragraphs, bullet lists, numbered lists.
  const blocks = [];
  let cur = null;
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, ''); // trim trailing ws
    if (!line.trim()) {
      // Blank line — end current block.
      if (cur) blocks.push(cur);
      cur = null;
      continue;
    }
    const bulletMatch = line.match(/^[\s]*[-•]\s+(.*)$/);
    const numberMatch = line.match(/^[\s]*\d+\.\s+(.*)$/);
    if (bulletMatch) {
      if (!cur || cur.type !== 'ul') {
        if (cur) blocks.push(cur);
        cur = { type: 'ul', items: [] };
      }
      cur.items.push(bulletMatch[1]);
    } else if (numberMatch) {
      if (!cur || cur.type !== 'ol') {
        if (cur) blocks.push(cur);
        cur = { type: 'ol', items: [] };
      }
      cur.items.push(numberMatch[1]);
    } else {
      if (!cur || cur.type !== 'p') {
        if (cur) blocks.push(cur);
        cur = { type: 'p', lines: [] };
      }
      cur.lines.push(line);
    }
  }
  if (cur) blocks.push(cur);
  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'ul') {
          return (
            <ul className="aria-md-list" key={i}>
              {b.items.map((it, j) => <li key={j}>{renderInlineMarkdown(it)}</li>)}
            </ul>
          );
        }
        if (b.type === 'ol') {
          return (
            <ol className="aria-md-list" key={i}>
              {b.items.map((it, j) => <li key={j}>{renderInlineMarkdown(it)}</li>)}
            </ol>
          );
        }
        return (
          <p className="aria-md-p" key={i}>
            {b.lines.flatMap((ln, k) => k === 0
              ? renderInlineMarkdown(ln, `l${k}-`)
              : [<br key={`br${k}`} />, ...renderInlineMarkdown(ln, `l${k}-`)])}
          </p>
        );
      })}
    </>
  );
}

// Split a project name into 1 or 2 display lines so long names wrap cleanly:
//   "Action Heating & Cooling"   -> ["Action", "Heating & Cooling"]
//   "Joe's Plumbing & Electric"  -> ["Joe's", "Plumbing & Electric"]
//   "Kitchen Reno — 412 Maple"   -> ["Kitchen Reno", "412 Maple"]   (address kept whole)
//   "Bob & Sons Plumbing"        -> ["Bob & Sons Plumbing"]         ("&" is in the brand)
//   "HowTo" / "Q2 Spring Launch" -> single line (short enough)
function splitProjectName(name) {
  if (!name || typeof name !== 'string') return [String(name || '')];
  const trimmed = name.trim();

  // 1) Em-dash or en-dash separator with spaces — typical "Project — Address" form.
  //    Keep the address whole on the second line; drop the dash itself.
  const dashMatch = trimmed.match(/^(.+?)\s+[—–-]\s+(.+)$/);
  if (dashMatch) {
    return [dashMatch[1].trim(), dashMatch[2].trim()];
  }

  // 2) "[Brand] [Service & Service]" pattern: only split when the "&" is past
  //    word index 2 (so things like "Bob & Sons Plumbing" stay together).
  if (trimmed.length > 16 && / & /.test(trimmed)) {
    const words = trimmed.split(/\s+/);
    const ampIdx = words.indexOf('&');
    if (ampIdx >= 2 && words.length - ampIdx >= 2) {
      const brand = words.slice(0, ampIdx - 1).join(' ');
      const descriptor = words.slice(ampIdx - 1).join(' ');
      if (brand && descriptor) return [brand, descriptor];
    }
  }

  return [trimmed];
}

// ─────────────────────────────────────────────────────────────────────────
// CardVideo — one looped, muted video per showcase card. Each video is a
// REAL recording from inside HowTo (a website Aria built, a video she
// generated, an image she made, a shopping cart she filled). No stock
// photos. No slideshows. If the video file doesn't exist yet, falls back
// to a clean dark placeholder with the card label — never a broken-image
// icon and never a stock photo.
//
// Drop the source files into:
//   /public/cards/videos/build-website.mp4
//   /public/cards/videos/make-video.mp4
//   /public/cards/videos/generate-image.mp4
//   /public/cards/videos/shop-for-me.mp4
// Encode 1080×608 (or 16:9), <2MB each, H.264 + AAC, ~6–10 seconds.
//
// Props:
//   src      — required, path to the .mp4 (or .webm)
//   poster   — optional first-frame .jpg for instant paint while video loads
//   label    — text shown on the dark placeholder if the video is missing
// ─────────────────────────────────────────────────────────────────────────
function CardVideo({ src, poster, label }) {
  const [hasError, setHasError] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const v = ref.current;
    if (!v) return;
    // Autoplay quietly. Browsers require muted+inline; we already set both.
    const tryPlay = () => { try { v.play().catch(() => {}); } catch (_) {} };
    v.addEventListener('loadeddata', tryPlay, { once: true });
    return () => v.removeEventListener('loadeddata', tryPlay);
  }, [src]);

  if (hasError || !src) {
    return (
      <div className="aria-card__video-fallback" aria-label={label}>
        <span>{label}</span>
      </div>
    );
  }

  return (
    <video
      ref={ref}
      className="aria-card__video"
      src={src}
      poster={poster || undefined}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      onError={() => setHasError(true)}
    />
  );
}

// Pop-out chat window (opened on another monitor): render ONLY the Aria chat,
// filling the window. No hooks before this branch → safe.
function PopoutChat() {
  // Transparent page so the floating window shows only the rounded chat card
  // (like the assist overlay) — the window itself is frameless + transparent.
  React.useEffect(() => {
    const prevH = document.documentElement.style.background;
    const prevB = document.body.style.background;
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    document.body.classList.add('popout-chat-body');
    // Follow the app's day/night theme (shared localStorage across windows).
    const applyTheme = () => { try { document.body.classList.toggle('theme-light', localStorage.getItem('howto_theme') === 'day'); } catch (_) {} };
    applyTheme();
    const onStorage = (e) => { if (!e || e.key === 'howto_theme' || e.key === null) applyTheme(); };
    window.addEventListener('storage', onStorage);
    const iv = setInterval(applyTheme, 1500); // storage events don't always cross Electron windows — poll as a backstop
    return () => { document.documentElement.style.background = prevH; document.body.style.background = prevB; document.body.classList.remove('popout-chat-body'); window.removeEventListener('storage', onStorage); clearInterval(iv); };
  }, []);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'transparent', display: 'flex' }}>
      <AriaDock popout />
    </div>
  );
}

function App() {
  if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('popout') === 'chat') {
    return <PopoutChat />;
  }
  return <AppMain />;
}

function AppMain() {
  const isDev = import.meta.env?.DEV || import.meta.env?.MODE === 'development';
  useState(() => ensureAriaUserId());
  const [view, setView] = useState('ask');
  // Model preference (2026-06-02): user can let Aria pick (Auto) or override.
  // Auto = Aria evaluates the task and chooses the best model.
  // Opus Max / Sonnet = manual override for a specific session.
  // Sent as `body.modelPref` on every /api/ai/conversation call (server may
  // honor or ignore per the brain-rule amendment).
  const [modelPref, setModelPref] = useState(() => {
    try { return localStorage.getItem('aria_model_pref') || 'auto'; } catch (_) { return 'auto'; }
  });
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const setModelPrefPersist = (v) => {
    setModelPref(v);
    try { localStorage.setItem('aria_model_pref', v); } catch (_) {}
    setModelMenuOpen(false);
  };

  // Power-user controls (2026-06-02): give users who want control the knobs.
  // Defaults are sensible so casual users never see/touch these.
  const [personaPref, setPersonaPref] = useState(() => {
    try { return localStorage.getItem('aria_persona_pref') || 'personal'; } catch (_) { return 'personal'; }
  });
  const [stakesPref, setStakesPref] = useState(() => {
    try { return localStorage.getItem('aria_stakes_pref') || 'auto'; } catch (_) { return 'auto'; }
  });
  const [approvalPref, setApprovalPref] = useState(() => {
    try { return localStorage.getItem('aria_approval_pref') || 'manual'; } catch (_) { return 'manual'; }
  });
  const [verifyPref, setVerifyPref] = useState(() => {
    try { return localStorage.getItem('aria_verify_pref') !== 'off'; } catch (_) { return true; }
  });
  const [controlsMenuOpen, setControlsMenuOpen] = useState(false);
  const setPersist = (key, val, setter) => {
    setter(val);
    try { localStorage.setItem(key, typeof val === 'boolean' ? (val ? 'on' : 'off') : val); } catch (_) {}
  };
  // Last model that actually answered (set from server response). Shown as
  // a tiny tag in the chat so the user knows which brain produced the reply.
  const [lastModelUsed, setLastModelUsed] = useState('');
  // Ad-hoc workbench opened from Explore (.html-route tiles dispatch
  // 'aria:openWorkbench'). Holds { src, title } and renders in the
  // 'workbench' section. Without this, those tiles opened nothing.
  const [openWorkbench, setOpenWorkbench] = useState(null);
  const [dashboardSection, setDashboardSection] = useState(() => {
    if (typeof window === 'undefined') return 'ask';
    const h = (window.location.hash || '').replace(/^#/, '').toLowerCase();
    if (h === 'imagegen' || h === 'explore' || h === 'screen' || h === 'subscription' ||
        h === 'website' || h === 'video' || h === 'shop' || h === 'trends' || h === 'home') return h;
    // Desktop/wide opens on Ask Aria (HowTo center + text prompt). Phones open
    // on Home — the owner's schedule — but ONLY for someone who's actually an
    // owner. Braeden 8/13 check: a brand-new visitor on a phone landed on a
    // job calendar stamped "Schedule — owner-default", i.e. somebody else's
    // business, before ever seeing the product. Same leak class as the
    // HOWTO_DEFAULT card (8/12). Strangers get Ask Aria on every screen size.
    const _known = (() => {
      try { return !!(localStorage.getItem('howto_owner_id') || localStorage.getItem('aria_session_token') || localStorage.getItem('aria_projects')); }
      catch (_) { return false; }
    })();
    if (typeof window !== 'undefined' && window.innerWidth < 768 && !_known && !window.electronAPI) return 'ask';
    return (typeof window !== 'undefined' && window.innerWidth >= 768) ? 'ask' : 'home';
  });
  const [screenStream, setScreenStream] = useState(null);
  const [screenShareError, setScreenShareError] = useState('');
  const [hasConfirmedScreenAccess, setHasConfirmedScreenAccess] = useState(() => {
    try { return localStorage.getItem(SCREEN_ACCESS_CONFIRMED_KEY) === '1'; } catch (_) { return false; }
  });
  /** True while getDisplayMedia is in progress (Assist auto-start or Allow retry). */
  const [screenShareRequesting, setScreenShareRequesting] = useState(false);
  const [screenPermissionPromptNeeded, setScreenPermissionPromptNeeded] = useState(false);
  const screenShareInFlightRef = useRef(false);
  // Assist-mode confirm-first: when Aria asks "Want me to start Assist?" we arm
  // this one-shot flag and wait for the user's next "yes"/"no" (handled locally
  // at the top of sendMessageWithText, so a simple "yes" — voice or text —
  // launches Assist instantly instead of falling through to the brain).
  const pendingAssistConfirmRef = useRef(false);
  // Permission-first: when Aria (desktop assist) asks "want me to go ahead?"
  // before taking the screen, we flag it so the user's next "yes"/"wait" — voice
  // OR text — routes straight to electronAPI.assistConsent instead of the brain.
  const pendingConsentRef = useRef(false);
  // Two-path help offer: when Aria asks "show you, or do it?", we remember the
  // task so the user's next "show me" / "do it" routes to a walkthrough vs.
  // taking control — without restating the task. { task } or null.
  const pendingHelpRef = useRef(null);
  // Teach session: once Aria starts coaching ("show me"), keep her in teach mode
  // across follow-up turns (so "no" / "I don't know where" continue the lesson)
  // until the user clearly exits ("thanks", "got it", "just do it").
  const teachActiveRef = useRef(false);
  // Watch desktop assist status: arm the consent prompt when Aria asks to go
  // ahead, disarm once she's acting/done so a later "yes" isn't misread.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI?.onAgentStatus) return;
    window.electronAPI.onAgentStatus((status) => {
      const ph = status && status.phase;
      if (ph === 'needsPermission') pendingConsentRef.current = true;
      else if (ph === 'executing' || ph === 'done' || ph === 'paused' || ph === 'error') pendingConsentRef.current = false;
    });
  }, []);
  /** Guard: only call requestMicrophoneAccess once per app session — macOS 26 may return
   * 'not-determined' from getMediaAccessStatus even after the user has granted access,
   * which would otherwise cause repeated permission dialogs every 3 s. */
  const micAccessRequestedRef = useRef(false);
  // Anonymous (not signed in) → always start with an empty screen. No leftover
  // chat from a prior session, no welcome line — feels like a brand-new user
  // every visit. Signed-in users keep their continuity.
  const [messages, setMessages] = useState(() => {
    if (!isLoggedIn()) {
      try {
        localStorage.removeItem(ARIA_SESSION_MESSAGES_KEY);
        localStorage.removeItem(ARIA_SESSION_HISTORY_KEY);
      } catch (_) {}
      return [];
    }
    return readPersistedSession(ARIA_SESSION_MESSAGES_KEY) || [];
  });
  const [feedbackGiven, setFeedbackGiven] = useState({});  // { messageIndex: 'up' | 'down' }
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // ── Free credits gate ────────────────────────────────────────────────
  // Anonymous users get FREE_CREDITS_INITIAL message sends before this modal
  // pops up. Signed-in users bypass entirely. Persisted in localStorage.
  const [outOfCreditsOpen, setOutOfCreditsOpen] = useState(false);
  const [creditsRemaining, setCreditsRemaining] = useState(() => isLoggedIn() ? -1 : getCreditsRemaining());
  // First-time visitor "First Time?" button: shows for not-signed-in users on the
  // Ask page until they tap it once (then it's dismissed for good). Persisted so it
  // doesn't nag returning anonymous visitors.
  // First-Time card (Braeden 8/12): shows for EVERY visitor who isn't in an
  // account, EVERY page load — dismissal is session-only (a click hides it
  // until refresh). No localStorage: signing in is what retires it for good
  // (the render gate checks !isLoggedIn()).
  const [firstTimeDismissed, setFirstTimeDismissed] = useState(false);
  // Computed ONCE at mount: isFirstVisit() writes the marker as it reads it,
  // so calling it from the render body would return true on the first paint
  // and false on every re-render - the button would appear and then vanish
  // while the person was reaching for it.
  const [isNewVisitor] = useState(() => {
    // Logged-out, on the website — that is the gate (Braeden 8/31: "only for
    // people who aren't logged in"). The first-visit marker was dropped 9/2:
    // it hid the button from anyone who had ever loaded the page once, which
    // is how he could stand on his own website and not see it ("i dont see
    // it on the website"). It still goes away for the session once tapped.
    try { return !isLoggedIn() && !isDesktopShell(); } catch (_) { return false; }
  });
  useEffect(() => {
    const onChange = (e) => setCreditsRemaining(isLoggedIn() ? -1 : (e?.detail?.remaining ?? getCreditsRemaining()));
    window.addEventListener('aria-credits-changed', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('aria-credits-changed', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);
  // ── Text-only mode ────────────────────────────────────────────────────
  // When the user has set preferences.communication_mode = 'text' on the
  // backend (e.g. by saying "text only"), the Ask page swaps the orb out
  // for a full chat transcript. Persisted to localStorage so the UI
  // remembers the choice across reloads — the source of truth is still
  // the server preference, which sets/clears _communicationMode on every
  // response and we sync from that (see useEffect below).
  const [textOnlyMode, setTextOnlyMode] = useState(() =>
    typeof localStorage !== 'undefined' && localStorage.getItem('aria-text-only-mode') === '1'
  );
  // Tracks the input method of the user's most-recent message: 'text' (typed
  // or auto-advance) → swaps the orb out for the full-screen chat AND tells
  // the backend not to attach audio. 'voice' (mic) → orb + audio reply.
  // Default 'text' so the glass chat surface (the recovered calibrated-v2
  // layout) is what users land on. Tap the mic to switch to voice/orb.
  const [lastInputMode, setLastInputMode] = useState('text');

  // Sync text-mode state from server: every Aria reply may carry a
  // `_communicationMode` flag. If the backend just switched the user TO
  // text mode we flip on; if a reply comes back WITH audio attached (no
  // mode flag, but audioStreamUrl/audioUrl is set) the user is back on
  // voice/both and we flip off.
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last.role !== 'assistant' || !last.data) return;
    const serverMode = last.data._communicationMode;
    const hasAudio = !!(last.data.audioStreamUrl || last.data.audioUrl || last.data.audioData);
    if (serverMode === 'text' && !textOnlyMode) {
      setTextOnlyMode(true);
      try { localStorage.setItem('aria-text-only-mode', '1'); } catch (_) {}
    } else if (hasAudio && textOnlyMode) {
      setTextOnlyMode(false);
      try { localStorage.setItem('aria-text-only-mode', '0'); } catch (_) {}
    }
  }, [messages, textOnlyMode]);
  // Onboarding stage — when set, the user's NEXT message routes through the
  // onboarding handler instead of the regular AI conversation.
  //   'gate'    → "Have you used Aria before?" — only "no" runs onboarding
  //   'unmute'  → asked them to enable mic; advance on any reply
  //   'name'    → captured their name → save → advance to branch
  //   'branch'  → "Do you run a business?" → routes to CEO or consumer
  //   'consumer'→ free-time question → Phase 3
  //   null      → onboarding done / skipped, back to normal Aria
  const [onboardingStage, setOnboardingStage] = useState(null);
  const onboardingStageRef = useRef(null);
  useEffect(() => { onboardingStageRef.current = onboardingStage; }, [onboardingStage]);

  // CEO mode state — when active, the sidebar shows the business name + an
  // Aria orb-dot in place of the generic "Dashboard" label. Fetched on
  // sign-in and refreshed when the conversation route flips CEO state.
  const [ceoState, setCeoState] = useState(null); // { active, businessName, businessType }
  // Sidebar Projects — user-created projects (persisted on this device), shown
  // above the example projects. "+ Projects" opens NewProjectModal.
  // ── NOTHING IS KEPT WITHOUT AN ACCOUNT (Braeden 2026-09-04) ───────────
  //
  // "if someone goes to it for the first time, if they do NOTHIng is saved
  //  like a blank page, no projects or memory saves once they refresh, unless
  //  they are logged into there account"
  //
  // conversationHistory and messages already had this rule ("no leftover
  // history when there's no account behind it"). Projects did not: they were
  // read from localStorage and written back on every change with no gate, so a
  // signed-out visitor's projects came back after a refresh. Same rule now.
  const [userProjects, setUserProjects] = useState(() => {
    try {
      if (!_keepsState()) { try { localStorage.removeItem('howto_user_projects'); } catch (_) {} return []; }
      const r = localStorage.getItem('howto_user_projects');
      if (r) { const a = JSON.parse(r); if (Array.isArray(a)) return a; }
    } catch (_) {}
    return [];
  });
  const [newProjOpen, setNewProjOpen] = useState(false);
  // Dashboard mode — Business vs Personal. Business shows companies + "Business +";
  // Personal shows personal projects + "Project +". Flipping animates the list.
  const [dashMode, setDashMode] = useState(() => { try { return localStorage.getItem('howto_dash_mode') === 'personal' ? 'personal' : 'business'; } catch (_) { return 'business'; } });
  useEffect(() => { try { localStorage.setItem('howto_dash_mode', dashMode); } catch (_) {} }, [dashMode]);
  const [personalProjOpen, setPersonalProjOpen] = useState(false);
  const [personalProjName, setPersonalProjName] = useState('');
  // "Business +" flow — Aria first asks "what's the business?" in the support box,
  // then a "Generate business plan" button builds the right agents + dashboard for
  // that niche (POST /api/aria/business-plan). See SetupHelpBox + bizLocalResponder.
  const [bizPlusOpen, setBizPlusOpen] = useState(false);
  const [bizText, setBizText] = useState('');
  // Fallback copy of what the owner typed (used only if the interview state is
  // somehow missing at build time).
  const [bizDraft, setBizDraft] = useState('');
  const [bizPlanBusy, setBizPlanBusy] = useState(false);
  // LIVE BUILD (Braeden 8/13): "as shes asking quesitons she needs, she also
  // needs to be making what is needed for the user, and showing the user in
  // real time." She starts generating the moment she knows the business and
  // streams each step into a panel above the chat, so he WATCHES it get built
  // while she keeps talking — instead of a button, a spinner, and a jump.
  const [bizBuild, setBizBuild] = useState({ active: false, steps: [], done: false, plan: null });
  const [bizDock, setBizDock] = useState(false);   // chat slides aside; workspace takes the screen
  const bizStep = useCallback((id, label, status) => {
    setBizBuild((b) => {
      const steps = b.steps.slice();
      const i = steps.findIndex((s) => s.id === id);
      if (i >= 0) steps[i] = { ...steps[i], label: label || steps[i].label, status };
      else steps.push({ id, label, status });
      return { ...b, active: true, steps };
    });
  }, []);
  // ── Aria-led Business+ interview (Braeden 2026-07-10): NO Generate button
  // until she's ready. She asks the basics herself — what you do → what it's
  // called → (researches it online with the real name, learns the niche if she
  // doesn't know it) → how long → how many employees. Once she has that, the
  // ✦ Generate a plan button APPEARS. State lives here; the box just chats.
  const bizBoxRef = useRef(null);
  const bizPipelineRef = useRef(null); // { stage, answers, discovery, found, website }
  const [bizReady, setBizReady] = useState(false);
  // Single default project shown when the user hasn't created any yet — renameable.
  const HOWTO_DEFAULT = { name: 'HowTo', tag: 'SaaS company', sub: 'howtoaria.com · 1 user · 21 trade playbooks live', status: 'Active', activity: 'Aria · Online', img: '/cards/business.jpg', fallback: 'https://images.unsplash.com/photo-1559028012-481c04fa702d?w=520&h=320&fit=crop&q=80' };
  // A FIRST-TIME WEB VISITOR MUST NOT SEE OUR COMPANY (Braeden 8/12: "your
  // telling me a first fucking user is going to have acceess the a how to sas
  // company?"). HOWTO_DEFAULT was the empty-state filler for EVERYONE — a
  // stranger's dashboard opened with our business card and our logo on it.
  // The desktop app is ours, so it keeps the card; the public web starts
  // empty, with "Business +" as the first move.
  // PARITY, 8/22 (Braeden): "when i say the app needs to flow like the website
  // i meant everything the buttons too ... it needs to be the exact same."
  // The desktop build used to seed its dashboard with HOWTO_DEFAULT, so the app
  // listed a project the website did not — one extra button, on the one surface
  // he asked to be identical. Worse, that card is tagged 'SaaS company', which
  // sent its workstation down CompanyView's non-trades branch and printed a KPI
  // row of invented numbers. Both surfaces now start from his real project list;
  // "+ New project" creates one that persists.
  const DEFAULT_PROJECTS = [];
  // The agents shown on a business's real page (mirror of CompanyView's AGENTS).
  // The hover preview renders THIS board so you see the actual business page.
  // Mirrors AGENT_CAPS in CompanyView.jsx — same order, same cadence grouping,
  // so the preview card shows the real first rows of the business page rather
  // than a list that drifted out of sync with it (8/13).
  const BIZ_PAGE_AGENTS = [
    { t: 'Missed calls', c: 'Every day', i: '📞', g: 'day' },
    { t: 'Calendar invites', c: 'Every day', i: '📅', g: 'day' },
    { t: 'Text reminders', c: 'Every day', i: '💬', g: 'day' },
    { t: 'New leads', c: 'Every day', i: '🧲', g: 'day' },
    { t: "Today's route", c: 'Every day', i: '🗺️', g: 'day' },
    { t: 'Job photos', c: 'Every day', i: '📸', g: 'day' },
    { t: 'Quotes & estimates', c: 'Every job', i: '🧾', g: 'job' },
    { t: 'Invoices', c: 'Every job', i: '💵', g: 'job' },
    { t: 'Payments', c: 'Every job', i: '💳', g: 'job' },
    { t: 'Crew hours', c: 'Every job', i: '⏱️', g: 'job' },
    { t: 'Quotes with no reply', c: 'Every week', i: '📨', g: 'week' },
    { t: 'Overdue invoices', c: 'Every week', i: '⏰', g: 'week' },
    { t: 'Receipts & spend', c: 'Every week', i: '📊', g: 'week' },
    { t: 'Supplies & materials', c: 'Every week', i: '📦', g: 'week' },
    { t: 'Where jobs came from', c: 'Every week', i: '📈', g: 'week' },
    { t: 'Reviews', c: 'Every month', i: '⭐', g: 'month' },
    { t: 'Email promotions', c: 'Every month', i: '📣', g: 'month' },
    { t: 'Payroll & contractors', c: 'Every month', i: '🧑‍🔧', g: 'month' },
    { t: 'Repeat customers', c: 'Every month', i: '🔁', g: 'month' },
    { t: 'Truck & equipment', c: 'Every month', i: '🚚', g: 'month' },
    { t: 'Google Business page', c: 'Every month', i: '📍', g: 'month' },
    { t: 'Quarterly taxes', c: 'Every quarter', i: '🏦', g: 'quarter' },
    { t: 'Price & margin check', c: 'Every quarter', i: '⚖️', g: 'quarter' },
    { t: 'Sub & vendor paperwork', c: 'Every quarter', i: '📋', g: 'quarter' },
    { t: 'Renewals & filings', c: 'Every year', i: '🪪', g: 'year' },
    { t: 'Year-end books', c: 'Every year', i: '📕', g: 'year' },
    { t: 'Win back old customers', c: 'Every year', i: '💌', g: 'year' },
  ];
  const ARCH_LABEL = { trades: 'Trades', services: 'Appointments', saas: 'SaaS', retail: 'Retail', creative: 'Creative', campaign: 'Marketing', web: 'Web', default: 'Business' };
  // Owners type their business as one run-on word ("opencorpjunkremoval"), and
  // plain title-case turned that into "Opencorpjunkremoval" — which then ran
  // off the card (8/13). Split known trade words back out so the card reads
  // like a real name: "Open Corp Junk Removal".
  const WORDY = ['junk removal', 'junk', 'removal', 'hauling', 'plumbing', 'roofing', 'landscaping',
    'lawn care', 'lawn', 'care', 'cleaning', 'painting', 'electric', 'electrical', 'heating',
    'cooling', 'hvac', 'construction', 'remodeling', 'moving', 'towing', 'detailing', 'services',
    'service', 'company', 'corp', 'group', 'brothers', 'and', 'sons', 'pros', 'experts'];
  const deRunOn = (s) => {
    const raw = String(s || '').trim();
    if (/\s/.test(raw) || raw.length < 9) return raw;      // already spaced
    let out = raw.toLowerCase(), spaced = '';
    while (out.length) {
      const hit = WORDY.filter((w) => !w.includes(' ')).sort((a, b) => b.length - a.length)
        .find((w) => out.startsWith(w));
      if (hit) { spaced += (spaced ? ' ' : '') + hit; out = out.slice(hit.length); }
      else { spaced += (spaced && !spaced.endsWith(' ') ? '' : '') + out[0]; out = out.slice(1); }
    }
    return spaced.replace(/\s+/g, ' ').trim() || raw;
  };
  const titleCase = (s) => deRunOn(String(s || '')).replace(/\s+/g, ' ').trim().replace(/\b\w/g, (c) => c.toUpperCase());
  // BUILD — fires from the ✦ button that only exists once Aria said she's
  // ready. Answers + discovery + owner id all feed the plan.
  const buildBizPlan = useCallback(async () => {
    const p = bizPipelineRef.current;
    const a = (p && p.answers) || {};
    // The button is live from the first second (8/13), so it may be pressed
    // before she's asked anything. Use whatever exists — the trade, the name,
    // or what he's typing right now — and only stop if there's truly nothing.
    const text = String(a.type || a.name || bizText || bizDraft || '').trim();
    if (bizPlanBusy) return;
    if (!text) {
      try { bizBoxRef.current?.addAssistant("Give me the business name and I'll build it from there."); } catch (_) {}
      return;
    }
    setBizPlanBusy(true);
    try {
      const base = String(getApiBase() || '').replace(/\/$/, '');
      const uid = (() => { try { return localStorage.getItem('aria_user_id') || ''; } catch (_) { return ''; } })();
      // Discovery comes from the prepare stage; only when the pipeline never ran
      // (prepare failed / direct build) do we scan inline as a fallback.
      let discovery = p ? p.discovery : null;
      if (!p) {
        try {
          const dctl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
          const dtimer = dctl ? setTimeout(() => { try { dctl.abort(); } catch (_) {} }, 12000) : null;
          let dres;
          try {
            dres = await fetch(`${base}/api/aria/discovery/scan`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ businessText: text, name: text, query: text }), signal: dctl ? dctl.signal : undefined });
          } finally { if (dtimer) clearTimeout(dtimer); }
          if (dres && dres.ok) { const dj = await dres.json().catch(() => null); discovery = (dj && dj.ok !== false) ? (dj.profile || dj.result || dj) : null; }
        } catch (_) { /* discovery is best-effort — never block plan generation */ }
      }
      // SIDE-DOCK + SHOW THE WORKSPACE (Braeden 8/13: "the text box needs to
      // go to the side, and the main screen needs to be working, like what
      // ever is being made needs to be seen by the user"). The chat slides to
      // the corner and the real dashboard takes the main screen, so he watches
      // the thing itself get built — not a progress list in a modal.
      setBizDock(true);
      // Show the work, step by step, while she keeps talking (8/13).
      bizStep('found', `Found ${String(a.name || text).trim()}`, 'done');
      if ((p && p.card && (p.card.services || []).length)) {
        bizStep('read', `Read their services — ${(p.card.services || []).slice(0, 3).join(', ')}`, 'done');
      }
      bizStep('layout', 'Working out the right layout for this trade', 'run');
      const res = await fetch(`${base}/api/aria/business-plan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessText: text, name: String(a.name || text).trim(), discovery, answers: (p && p.answers) || null, ...(uid ? { userId: uid } : {}) }),
      });
      const plan = await res.json().catch(() => ({}));
      if (!res.ok || !plan || !plan.ok) { bizStep('layout', 'Layout failed', 'fail'); throw new Error((plan && plan.error) || 'plan failed'); }
      bizStep('layout', `Layout: ${ARCH_LABEL[plan.archetype] || plan.archetype || 'business'}`, 'done');

      // EVERY PIECE, AS IT LANDS (Braeden 8/13: "it needs to be showing the
      // user whats being done, like litterealy everything thats being added").
      // The plan arrives all at once; she reveals it item by item so he WATCHES
      // his system get assembled instead of reading a summary after the fact.
      const reveal = [];
      const unitName = (plan.terminology && plan.terminology.unit) || 'Jobs';
      (plan.drawers && Array.isArray(plan.drawers.drawers) ? plan.drawers.drawers : []).forEach((d) => {
        const label = typeof d === 'string' ? d : (d.label || d.name || d.id);
        if (label) reveal.push({ id: 'dr-' + label, text: `Added the ${String(label).replace(/_/g, ' ')} drawer` });
      });
      (plan.kpis || []).slice(0, 4).forEach((k) => {
        const label = typeof k === 'string' ? k : (k.label || k.name);
        if (label) reveal.push({ id: 'kpi-' + label, text: `Tracking ${String(label).toLowerCase()}` });
      });
      (plan.agents || []).forEach((ag) => {
        const nm2 = typeof ag === 'string' ? ag : (ag.label || ag.name || ag.id);
        const does = (typeof ag === 'object' && (ag.does || ag.purpose || ag.description)) || '';
        if (nm2) reveal.push({ id: 'ag-' + nm2, text: `Hired ${String(nm2).replace(/[-_]/g, ' ')}${does ? ' — ' + String(does).toLowerCase().slice(0, 48) : ''}` });
      });
      reveal.push({ id: 'unit', text: `Your work is counted in ${String(unitName).toLowerCase()}` });
      if (plan.niche && plan.niche.label) reveal.push({ id: 'niche', text: `Pricing set for your niche` });
      // stagger so it reads as work happening, not a dump
      reveal.forEach((r, i) => setTimeout(() => bizStep(r.id, r.text, 'done'), 260 * (i + 1)));
      // Name the card after the business NAME the owner gave in the interview,
      // falling back to the trade they described.
      const nm = titleCase(String(a.name || text)).slice(0, 42) || 'My Business';
      const unit = (plan.terminology && plan.terminology.unit) || 'Clients';
      const proj = {
        name: nm,
        tag: ARCH_LABEL[plan.archetype] || 'Business',
        sub: `${(plan.agents || []).length} agents · tracks ${String(unit).toLowerCase()}`,
        status: 'Active', activity: 'Aria · Setting up',
        img: '/cards/business.jpg',
        fallback: 'https://images.unsplash.com/photo-1559028012-481c04fa702d?w=520&h=320&fit=crop&q=80',
        plan, // archetype, niche, terminology.unit, kpis[], agents[] — drives CompanyView
      };
      setUserProjects((ps) => [proj, ...ps]);
      setSelectedProject(proj);
      // Put the REAL dashboard on the main screen behind the docked chat —
      // he watches his own business page exist, not a checklist (8/13).
      window.location.hash = 'company'; setDashboardSection('company'); setLeftSidebarOpen(false);
      setBizBuild((b) => ({ ...b, active: true, done: true, plan }));
      // …and she explains it in plain words, one short line at a time, as he
      // looks at it. Simple communicative language — no jargon dump.
      const say = (t, ms) => setTimeout(() => { try { bizBoxRef.current?.addAssistant(t); } catch (_) {} }, ms);
      const unitWord = String(unit || 'jobs').toLowerCase();
      say(`That's your page — everything on screen is yours now.`, 900);
      say(`The cards up top are your money: what you've made, what's still owed, what went out. They fill in as jobs finish.`, 3200);
      say(`Down the middle is your schedule. Every ${unitWord.replace(/s$/, '')} you book lands there, and I text the customer the day before so nobody forgets.`, 6200);
      if ((plan.agents || []).length) {
        say(`I put ${plan.agents.length} helpers to work behind it — answering your phone, chasing quotes, asking for reviews after a job. You don't have to touch them.`, 9400);
      }
      setBizReady(false);
      setBizPlanBusy(false);
      // Do NOT slam the box shut and jump — he asked to SEE it get built, and
      // she may still be asking. The panel stays with a live "Open it" so he
      // moves when he's ready.
      //
      // No chat message here: the panel already says "Your setup — built" with
      // the button, and this fired BEFORE her "I'm building it now" line
      // (async race), so the thread read backwards. Less talking, no race.
    } catch (_) {
      setBizPlanBusy(false);
      setBizBuild((b) => ({ ...b, active: true, done: false }));
      try { bizBoxRef.current?.addAssistant('I hit a snag building that — give the button another try.'); } catch (_) {}
    }
  }, [bizText, bizDraft, bizPlanBusy]);

  // Aria's side of the interview — SHE drives it, stage by stage. Every user
  // message is the answer to whatever she just asked. Between "what's it
  // called?" and the next question she researches the business online (the
  // typing dots cover the wait). RULES (Braeden): ONE text per turn, never a
  // burst of bubbles; the ✦ button only appears at stage 'ready'.
  // The interview in order, with the answer each stage fills. Having this as
  // DATA (rather than only as jumps inside the switch) is what lets her skip a
  // question she already has the answer to — the 'name' stage could route back
  // to 'llc' even when llc was answered, which is exactly how she asked "have
  // you set up an LLC?" twice in a row.
  // `fits` = does this answer even make sense for this question? Without it she
  // stored "yes" as the business NAME and "TTA" as the LLC status — the reply
  // was captured with no thought about whether it answered what was asked.
  // NAME FIRST (Braeden 8/13): "first question she should ask is whats the
  // business" — the name is what lets her go LOOK them up; asking the type
  // first made her interview them for facts the web already has.
  const BIZ_QUESTIONS = React.useMemo(() => ([
    { stage: 'name', key: 'name', q: 'What’s the business called?',
      // A business name is not a yes/no and not a duration.
      fits: (s) => !/^(yes|yeah|yep|yup|sure|no|nope|nah|idk)$/i.test(s) && !/^\d+\s*(?:years?|months?|weeks?|days?)$/i.test(s),
      onBad: 'What’s the name itself — what do you put on the invoice?' },
    { stage: 'type', key: 'type', q: 'What kind of business is it?',
      fits: (s) => s.split(/\s+/).length >= 1 && !/^(yes|yeah|yep|no|nope|nah)$/i.test(s) },
    { stage: 'llc',  key: 'llc',  q: 'Have you set up an LLC or registered the business yet?',
      // This one IS a yes/no. A proper noun here is almost always the name.
      fits: (s) => /\b(yes|yeah|yep|yup|no|nope|nah|not yet|llc|s-?corp|sole prop|registered|filed|working on)\b/i.test(s),
      onBad: 'Just so I’ve got it — is it registered yet, or not yet?' },
    { stage: 'age',  key: 'age',  q: 'How long have you been at it?',
      fits: (s) => /\d|\b(?:just started|new|brand new|years?|months?|weeks?|decade|while|since)\b/i.test(s),
      onBad: 'Roughly how long — months, a year, longer?' },
    { stage: 'size', key: 'size', q: 'How many employees — or is it just you?',
      fits: (s) => /\d|\b(?:just me|me|myself|solo|alone|nobody|none|couple|few|crew|guys?|people)\b/i.test(s),
      onBad: 'Ballpark — is it just you, or do you have a crew?' },
  ]), []);

  /**
   * The first question she genuinely does not have an answer to. Advances
   * p.stage to match, or returns null when there is nothing left to ask (which
   * means she has enough and should stop interviewing).
   */
  const nextUnanswered = useCallback((p, a, skipCurrent = false) => {
    for (const item of BIZ_QUESTIONS) {
      if (skipCurrent && item.stage === p.stage) { a[item.key] = a[item.key] || ''; continue; }
      const have = String(a[item.key] || '').trim();
      if (!have) { p.stage = item.stage; return item.q; }
    }
    p.stage = 'ready';
    return null;
  }, [BIZ_QUESTIONS]);

  // Held in a ref so the interview can kick the build itself the moment it has
  // enough (8/13) — without making the responder depend on buildBizPlan and
  // re-create on every render.
  const buildBizPlanRef = useRef(null);
  useEffect(() => { buildBizPlanRef.current = buildBizPlan; }, [buildBizPlan]);

  // onThinking(label) — lets her narrate the step she's on while it runs
  // ("Looking up Open Corp Junk Removal…"), instead of silent dots.
  const bizLocalResponder = useCallback(async (text, _history, onThinking) => {
    let p = bizPipelineRef.current;
    // Opens on 'name' — the greeting asks what the business is CALLED, and the
    // name is what lets her go look them up (8/13). Left at 'type' after the
    // reorder, the first answer was filed as the trade and she skipped the
    // lookup entirely: "junk removal" → "How long have you been at it?"
    if (!p) p = bizPipelineRef.current = { stage: 'name', answers: {} };
    const a = p.answers;

    // ── READ WHAT HE ACTUALLY SAID (Braeden 8/12) ──────────────────────────
    // "she needs to analyse whats said, not just have autimatic responses to
    // everything, like acaully think about what the person said"
    //
    // The comment above says "every user message is the answer to whatever she
    // just asked". That assumption is what produced this, verbatim:
    //
    //   Aria: have you set up an LLC?      him: yes
    //   Aria: what's the business called?  him: TTA
    //   Aria: have you set up an LLC?          ← already answered
    //   him: i just says yes dumbfuck      Aria: how long have you been at it?
    //   him: who cares                     Aria: how many employees?
    //   him: stop                          Aria: Perfect — that's everything I need.
    //
    // Three different things got treated as answers: a repeat, a refusal, and
    // "stop". Before the state machine runs, classify the message. A person who
    // is annoyed, refusing, or telling her to stop is not supplying data.
    const said = String(text || '').trim();
    const low = said.toLowerCase();

    const isStop = /^(stop|quit|cancel|nevermind|never mind|forget it|shut up|enough|leave me alone|no more)\b/i.test(low);
    const isRefusal = /^(who cares|dont care|don'?t care|idc|whatever|skip|pass|n\/?a|none of your business|why|does it matter)\b/i.test(low);
    const isFrustrated = /\b(dumb ?fuck|dumbass|stupid|idiot|fuck|shut up|i (?:just )?(?:said|says|told you)|already (?:said|told)|asked me that|you asked)\b/i.test(low);

    // ── "JUST BUILD IT" BEATS EVERY QUESTION (Braeden 8/13) ────────────────
    // Live transcript: he typed "make me the fucking generation" and she
    // answered "Roughly how long — months, a year, longer?", then "fuck off"
    // → "Have you set up an LLC?", then "dumbass" → the SAME LLC question
    // again. A direct order to build, or plain anger, is not interview data.
    // If she knows who the business is, she stops asking and generates with
    // what she has. Everything else can be asked after he sees something real.
    const wantsBuild = /\b(just )?(make|build|generate|create|do) (it|me|my|the|this)?\b|\bgeneration\b|\bjust do it\b|\bhurry\b|\bgo ahead\b|\bstop asking\b|\bno more questions\b/i.test(low);
    const hasEnough = !!(a.name || p.website || p.found);
    if ((wantsBuild || isFrustrated || isRefusal) && hasEnough && p.stage !== 'ready' && p.stage !== 'stopped') {
      if (!a.type) a.type = ((p.card && (p.card.services || []).join(', ')) || a.name || 'their business');
      p.stage = 'ready';
      setBizReady(true);
      setTimeout(() => { try { buildBizPlanRef.current && buildBizPlanRef.current(); } catch (_) {} }, 300);
      return wantsBuild ? 'On it — building your setup now.'
                        : 'My bad — enough questions. Building it now with what I’ve got.';
    }

    // He told her to stop. That is not an answer to a question — it ends the
    // interview. Saying "Perfect, that's everything I need" to "stop" is the
    // single worst line in that transcript.
    if (isStop) {
      p.stage = 'stopped';
      return "Stopped. Tell me when you want to pick it back up.";
    }

    // He already answered this and she asked again. Own it, do not re-ask, and
    // move to the first thing she genuinely does not have.
    if (isFrustrated && /\b(?:said|says|told|asked)\b/i.test(low)) {
      // Only apologise for repeating if she ACTUALLY has the answer already.
      // Saying "you did — my mistake" about a question he never answered is
      // just a different way of not listening.
      const cur = BIZ_QUESTIONS.find((x) => x.stage === p.stage);
      const hadIt = !!(cur && String(a[cur.key] || '').trim());
      if (!hadIt && cur) a[cur.key] = a[cur.key] || '';   // he is not answering this one
      const next = nextUnanswered(p, a, !hadIt);
      if (!next) return "Alright — I've got enough. Hit ✦ Generate a plan and I'll build it.";
      return hadIt ? `You did — my mistake. ${next}` : `Alright, moving on. ${next}`;
    }
    if (isFrustrated) {
      const next = nextUnanswered(p, a);
      return next ? `Fair. ${next}` : "Fair — I've got enough. Hit ✦ Generate a plan.";
    }

    // "who cares" is not the number of employees. Do not store it as one.
    if (isRefusal) {
      const next = nextUnanswered(p, a, /* skipCurrent */ true);
      return next || "That's fine — I've got enough to work with. Hit ✦ Generate a plan.";
    }

    // DOES THIS ANSWER THE QUESTION SHE ASKED? "yes" is not a business name.
    // She used to store it anyway and carry the wrong value into the plan.
    // Asking once more is cheap; building a company record on "yes" is not.
    const asked = BIZ_QUESTIONS.find((x) => x.stage === p.stage);
    if (asked && asked.fits && said && !asked.fits(said) && !p._reasked) {
      p._reasked = true;                       // only once, never a loop
      return asked.onBad || asked.q;
    }
    if (asked) p._reasked = false;
    // The niche-knowledge mention rides along on the NEXT question (one text),
    // consumed once so it never repeats. The niche line ends "— " so the
    // question stays lowercase after it; otherwise the question is capitalized.
    const withNiche = (q, lead = '') => {
      const n = p.nicheLine || ''; p.nicheLine = '';
      const body = n ? n + q : q.charAt(0).toUpperCase() + q.slice(1);
      return lead + body;
    };
    switch (p.stage) {
      case 'type': {
        // Reached only when she still doesn't know the trade after looking
        // them up (name-first flow — the site usually tells her).
        a.type = text;
        p.stage = 'age';
        return withNiche('how long have you been at it?');
      }
      case 'name': {
        // THINK ABOUT THE ANSWER (Braeden 8/13: "she needs to think when shes
        // talking not just spit stuff out"). "junk removal" is a TRADE, not a
        // business name — filing it as the name and moving on is how she ended
        // up asking "how long have you been at it?" with nothing looked up.
        // Recognise a bare trade, keep it as the trade, and ask for the name.
        const _bare = String(text || '').trim().toLowerCase().replace(/[^a-z\s&]/g, '').trim();
        const TRADE_ONLY = /^(junk removal|hauling|plumbing|plumber|hvac|heating( and cooling)?|air conditioning|roofing|lawn ?care|mowing|landscaping|tree service|electrical|electrician|handyman|cleaning|house cleaning|painting|concrete|fencing|remodeling|remodel|construction|moving|movers|pressure washing|window cleaning|pest control|snow removal|detailing|mobile detailing|towing|welding|flooring|drywall|siding|gutters)$/;
        if (TRADE_ONLY.test(_bare) && !a.nameAskedTwice) {
          a.type = text.trim();
          a.nameAskedTwice = true;
          return `Got it — ${text.trim()}. And what's the business itself called? The name you'd put on an invoice.`;
        }
        a.name = text;
        // Research pass: web scan with the REAL name + niche check/learn. The
        // box's typing indicator runs while this awaits. Never dead-ends.
        // Narrate it — the owner sees WHO she's looking for, not just dots.
        try { onThinking && onThinking(`Looking up ${String(text).trim()}`); } catch (_) {}
        try {
          const base = String(getApiBase() || '').replace(/\/$/, '');
          const uid = (() => { try { return localStorage.getItem('aria_user_id') || ''; } catch (_) { return ''; } })();
          const res = await fetch(`${base}/api/aria/business-plan/prepare`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ businessText: a.type || a.name, name: a.name, ...(uid ? { userId: uid } : {}) }),
          });
          const prep = await res.json().catch(() => ({}));
          if (res.ok && prep && prep.ok) {
            p.card = prep.card || null;         // screenshot + meta + services
            p.discovery = prep.discovery || null;
            p.found = !!prep.found;
            p.website = prep.website || null;
            p.bestGuess = prep.bestGuess || null;   // closest match when the strict gates missed
            const n = prep.niche || {};
            p.nicheLine = n.known && n.label ? `I know ${n.label} well — ` : (n.learned ? 'I just read up on how that line of work runs — ' : '');
          }
        } catch (_) { /* research is best-effort */ }
        const cand = (p.discovery && p.discovery.candidate) || {};
        const socials = cand.socials || {};
        const socialKeys = Object.keys(socials);
        if (p.website) {
          p.stage = 'website';
          const host = String(p.website).replace(/^https?:\/\//, '').replace(/\/$/, '');
          // Show the business, don't just name a domain (8/13).
          return { text: `Found you — this you?`, card: p.card || { host, url: p.website, title: host } };
        }
        if (p.found && socialKeys.length) {
          // No website, but a social under that name still counts — ask.
          p.stage = 'social';
          const k = socialKeys[0];
          const h = (socials[k] && socials[k].handle) || '';
          return `I looked you up — found a ${k}${h ? ` (@${h})` : ''} under that name. Is that you?`;
        }
        if (!p.found) {
          // CLOSEST MATCH (Braeden 8/13: "it has to be somewhere aria just
          // needs to find it"). The strict gates missed, but the web scan
          // still surfaced the nearest real business — show it and let them
          // confirm instead of dead-ending on "couldn't find anything".
          if (p.bestGuess && p.bestGuess.host) {
            p.stage = 'guess';
            const alts = (p.bestGuess.alternates || []).slice(0, 2)
              .map((x) => String(x.host || '').replace(/^www\./, '')).filter(Boolean);
            return {
              text: `Closest thing I can find online — is this you?`
                + (alts.length ? ` (If not, I also saw ${alts.join(' and ')}.)` : ''),
              card: p.card || { host: p.bestGuess.host, url: p.bestGuess.website, title: p.bestGuess.name },
            };
          }
          // Only ask about the LLC if she does not already have that answer.
          // She asked this, he said "yes", she asked the business name, and
          // then landed back here and asked the SAME question again.
          if (!String(a.llc || '').trim()) {
            p.stage = 'llc';
            return 'I checked around online and couldn’t find anything for it yet — no worries. Have you set up an LLC or registered the business yet?';
          }
          const nxt = nextUnanswered(p, a);
          return nxt
            ? `Couldn’t find it online yet — no worries. ${nxt}`
            : 'Couldn’t find it online yet — no worries. Hit ✦ Generate a plan and I’ll build your setup.';
        }
        p.stage = 'age';
        return withNiche('how long have you been doing it?');
      }
      case 'scope': {
        // Their answer about what else they do IS the trade detail she needs —
        // no separate "what kind of business" interrogation afterward.
        a.scope = text;
        if (!a.type) a.type = text;
        p.stage = 'age';
        return withNiche('how long have you been at it?', 'Got it. ');
      }
      case 'guess': {
        // "yes that's me" → adopt the guessed site as theirs; "no" → set it
        // aside and carry on with setup (never a dead end either way).
        const yes = /\b(y(es|eah|ep|up)?|that'?s (me|us|it|mine|ours)|correct|right|mine|ours|thats us)\b/i.test(String(text));
        if (yes && p.bestGuess) {
          p.website = p.bestGuess.website;
          p.found = true;
          a.website = p.bestGuess.website;
          p.bestGuess = null;
          // Same rule as a confirmed site (8/13): she has enough — build, don't quiz.
          const svc = (p.card && p.card.services) || [];
          if (!a.type && svc.length) a.type = svc.join(', ');
          if (!a.type) a.type = a.name || 'their business';
          p.stage = 'ready';
          setBizReady(true);
          setTimeout(() => { try { buildBizPlanRef.current && buildBizPlanRef.current(); } catch (_) {} }, 400);
          return 'Perfect — got your site. Building your setup now.';
        }
        p.bestGuess = null;
        p.stage = 'age';
        return 'No problem, setting that aside. ' + withNiche('how long have you been doing it?');
      }
      case 'website':
      case 'social': {
        a[p.stage] = text;
        // "no, that isn't mine" → drop the discovery so nothing builds on it.
        const denied = /\b(no|nope|nah|not (mine|ours|me|us)|isn'?t)\b/i.test(String(text));
        if (denied) { p.discovery = null; p.card = null; }
        // SITE CONFIRMED → BUILD IT (Braeden 8/13: "she talks to much, she
        // needs to do the work and generate the fucking system, to many
        // questions are bing asked before the generation").
        //
        // Their own site already told her the name, the trade, the services
        // and the phone. Age / crew size / current system / LLC are NOT needed
        // to lay out a system — they're follow-up detail she can ask for AFTER
        // he sees something real. So she stops interviewing and generates.
        if (!denied && p.card) {
          const svc = p.card.services || [];
          if (!a.type && svc.length) a.type = svc.join(', ');
          if (!a.type) a.type = a.name || 'their business';
          a.services = svc.join(', ');
          a.phone = p.card.phone || '';
          a.website = p.website || (p.card && p.card.url) || '';
          // Build STARTS now and streams into the panel; she keeps talking.
          setBizReady(true);
          setTimeout(() => { try { buildBizPlanRef.current && buildBizPlanRef.current(); } catch (_) {} }, 300);
          // ...and she carries on with the one question worth asking while it
          // builds, instead of going silent or interrogating first.
          p.stage = 'size';
          return (svc.length ? `Got it — ${svc.slice(0, 3).join(', ')}. ` : 'Got it. ')
            + `I'm building your setup now — you'll see it fill in above. While it runs: is it just you, or do you have a crew?`;
        }
        p.stage = 'age';
        const lead = denied ? 'Good to know — I’ll set that aside. ' : 'Perfect, that helps me picture it. ';
        return withNiche('how long have you been doing it?', lead);
      }
      case 'llc': {
        a.llc = text;
        p.stage = 'age';
        return withNiche('how long have you been at it?');
      }
      case 'age': {
        a.age = text;
        p.stage = 'size';
        return 'How many employees — or is it just you?';
      }
      case 'size': {
        a.size = text;
        p.stage = 'ready';
        setBizReady(true);
        // If the build is already running/finished (site-confirmed path), do
        // NOT send him back to a button he doesn't need (8/13).
        if (bizBuild.active) {
          const crew = /just me|myself|solo|only me|one|1\b/i.test(String(text)) ? 'Solo — I\'ll keep the scheduling simple.' : 'Got it — I\'ll set it up for a crew.';
          return `${crew} Your setup's coming together above.`;
        }
        setTimeout(() => { try { buildBizPlanRef.current && buildBizPlanRef.current(); } catch (_) {} }, 300);
        return 'Perfect — that’s everything I need. Building it now.';
      }
      case 'stopped': {
        // She was told to stop. She does not resume interviewing on her own —
        // he restarts it, in his words, when he wants to.
        const wantsBack = /\b(?:ok|okay|go|continue|keep going|resume|carry on|yes|yeah|alright)\b/i.test(low);
        if (!wantsBack) return 'Whenever you’re ready.';
        const nxt = nextUnanswered(p, a);
        return nxt || 'Picking back up — hit ✦ Generate a plan and I’ll build your setup.';
      }
      default: {
        // Ready (or building) — extra messages become notes for the plan.
        a.notes = [a.notes, text].filter(Boolean).join(' · ');
        return 'Noted — I’ll factor that in. Generate whenever you’re ready.';
      }
    }
  }, [nextUnanswered]);
  const [renamingIdx, setRenamingIdx] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const commitRename = (idx) => {
    const name = (renameVal || '').trim();
    setRenamingIdx(null);
    if (!name) return;
    setUserProjects((ps) => (ps.length ? ps : DEFAULT_PROJECTS).map((p, i) => (i === idx ? { ...p, name } : p)));
  };
  // Project card ⋮ menu → Edit (rename in a popup) / Delete (with an "are you
  // sure" confirm). Each project (business) on the dashboard gets its own.
  const [projMenuIdx, setProjMenuIdx] = useState(null);   // which card's ⋮ menu is open
  const [editProj, setEditProj] = useState(null);          // { idx, name } → rename popup
  const [deleteProj, setDeleteProj] = useState(null);      // { idx, name } → delete confirm
  const saveEditProject = () => {
    if (!editProj) return;
    const name = (editProj.name || '').trim();
    const idx = editProj.idx;
    if (name) setUserProjects((ps) => (ps.length ? ps : DEFAULT_PROJECTS).map((p, i) => (i === idx ? { ...p, name } : p)));
    setEditProj(null);
  };
  const confirmDeleteProject = () => {
    if (!deleteProj) return;
    const idx = deleteProj.idx;
    setUserProjects((ps) => (ps.length ? ps : DEFAULT_PROJECTS).filter((_, i) => i !== idx));
    setDeleteProj(null);
  };
  // Global back-button nav history — the top-left back arrow returns through
  // everything the user clicked into (e.g. company → leads → back → company).
  const navStackRef = useRef([]);
  const navPrevRef = useRef(null);
  const navBackRef = useRef(false);
  const [navDepth, setNavDepth] = useState(0);
  // Which project the Company view is showing (drives the business name + which
  // metric set Aria renders for that business type).
  const [selectedProject, setSelectedProject] = useState(null);
  // Global day/night app theme — flips CSS variables on <body> so the WHOLE app switches.
  const [appTheme, setAppTheme] = useState(() => { try { return localStorage.getItem('howto_theme') === 'day' ? 'day' : 'night'; } catch (_) { return 'night'; } });
  // Signed out, nothing is written down — see the note on userProjects above.
  // A signed-out session still WORKS (they can make a project and use it); it
  // just does not survive the refresh, which is the whole point.
  useEffect(() => {
    try {
      if (!_keepsState()) { localStorage.removeItem('howto_user_projects'); return; }
      localStorage.setItem('howto_user_projects', JSON.stringify(userProjects));
    } catch (_) {}
  }, [userProjects]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = (typeof localStorage !== 'undefined' && localStorage.getItem('aria_user_id')) || null;
        if (!id) return;
        // /api/ceo/status DOES NOT EXIST on the backend — no route, no handler,
        // no state store behind it (checked 8/22). This fired on every load,
        // 404'd, and the 404 was swallowed into exactly the same result as
        // success-with-no-data: setCeoState(null). So it could never do
        // anything except add red lines to the console, which is worse than
        // useless — it buries the errors that DO matter.
        //
        // Left in place, disabled, so whoever builds the CEO status API knows
        // this consumer is waiting for it. Flip the flag when the route lands.
        const CEO_STATUS_API_EXISTS = false;
        if (!CEO_STATUS_API_EXISTS) return;
        const res = await fetchWithTimeout(`${API_BASE}/api/ceo/status/${encodeURIComponent(id)}`, { timeout: API_TIMEOUT_MS });
        const d = await res.json();
        if (cancelled) return;
        if (!d?.ok) return setCeoState(null);
        setCeoState({
          active: !!d.active,
          onboarded: !!d.onboarded,
          businessName: d.businessName || null,
          businessType: d.businessType || null,
        });
      } catch (_) { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Onboarding gate seeding: DISABLED per owner request — "she only needs
  // to respond if the user talks to her, no start up screen that says hey
  // user." Aria opens to an empty canvas. The onboarding stage machine
  // still exists; it just doesn't pre-populate a question.
  useEffect(() => {
    // no-op
  }, []);
  // ── TWO TRACKS, NOT ONE (Braeden 8/31) ──────────────────────────────────
  //
  //   "i need you to understand the search bar, and aria tts are two
  //    differenct things, when shes talking over the mic that is an active
  //    conversation, if the search bar is going through that is a different
  //    task, almost like a conversation but its just a search engine, aria
  //    tts is the central communication point that control everyting"
  //
  // Until now BOTH surfaces wrote into `conversationHistory` and both sent it
  // as the context for the next turn. So typing "show me used cars for sale"
  // became a thing he had SAID, and shaped the next spoken exchange as if he
  // had said it out loud. A search is a task, not a turn in the conversation.
  //
  // So the typed track keeps its own chain. It is still a chain — "narrow it
  // to under twenty grand" has to know what the last search was — but it is
  // the SEARCH's chain, not the conversation's. The voice track stays the
  // controlling one: it is handed a compact list of what has been searched so
  // she is aware of it, and it can drive the bar itself.
  const [searchTrack, setSearchTrack] = useState([]);
  // ONE PLACE DECIDES WHICH CHAIN A TURN LANDS IN. Seven fast-path lanes
  // append their own history; if each picks the track for itself they drift
  // the first time somebody adds an eighth (the fourteen-wrapper lesson).
  const rememberTurn = React.useCallback((mode, entries) => {
    if (!Array.isArray(entries) || !entries.length) return;
    if (mode === 'voice') setConversationHistory((prev) => [...prev, ...entries]);
    else setSearchTrack((prev) => [...prev, ...entries].slice(-20));
  }, []);
  const [conversationHistory, setConversationHistory] = useState(() => {
    // Same anonymous-fresh rule as messages above — no leftover history when
    // there's no account behind it.
    if (!isLoggedIn()) return [];
    return readPersistedSession(ARIA_SESSION_HISTORY_KEY) || [];
  });
  const [initialQuery, setInitialQuery] = useState('');
  // backendReachable is now provided by useBackendHealth hook (see below)
  const bottomRef = useRef(null);
  // The text-chat scroller + a flag tracking whether the user is pinned to the
  // bottom (so new messages auto-scroll down, but we DON'T yank them back while
  // they're reading history higher up).
  const glassInnerRef = useRef(null);
  const atBottomRef = useRef(true);

  const isDesktopApp = typeof window !== 'undefined' && window.electronAPI?.onFullScreenChange;
  const [isFullScreen, setIsFullScreen] = useState(() => {
    if (typeof window === 'undefined') return true;
    if (window.electronAPI?.onFullScreenChange) return false;
    return true;
  });

  // Persistent sidebar default closed — user requested the dashboard be
  // collapsed behind a settings-icon dropdown at top-left instead.
  // (Old behavior preserved via getStoredLayout if explicitly toggled on.)
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(false);
  // Account panel — the dashboard's settings pop-up (8/22).
  const [accountPanelOpen, setAccountPanelOpen] = useState(false);
  // Assist permission — she asks before she takes the screen (8/22).
  const [assistAsk, updateAssistAsk] = useState(null);
  const [assistPolicyVersion, refreshAssistPolicy] = useState(0);
  function assistPermissionKey() {
    const uid = localStorage.getItem('aria_user_id');
    return uid && localStorage.getItem('aria_session_token') ? 'aria.assist.permission.' + uid : null;
  }
  function hasRememberedAssist() {
    try { const key = assistPermissionKey(); return !!key && localStorage.getItem(key) === 'allow'; } catch (_) { return false; }
  }
  function setAssistAsk(request) {
    if (request && isDesktopApp && hasRememberedAssist()) {
      void startApprovedAssist(request);
      return;
    }
    updateAssistAsk(request);
    // Permission lives in this renderer so it cannot disappear behind the companion.
    if (request) window.electronAPI?.ringOverlay?.({ show: false });
  }

  async function startApprovedAssist(request) {
    // Keep the companion visible while capture starts, avoiding a chat-window flash.
    let target = null;
    try { if (request.task) target = await window.electronAPI?.selectTaskScreen?.(request.task); } catch (_) {}
    const stream = await requestScreenShare(true);
    updateAssistAsk(null);
    if (!stream) return;
    await window.electronAPI?.assistConsent?.(true);
    try { if (target?.matched && target.app) await window.electronAPI?.executeCommand?.({ type: 'focus_window', title: target.title || target.app }); } catch (_) {}
    if (request.task) setInjectQuestion({ text: request.task, requireScreen: true, inputMode: request.inputMode === 'voice' ? 'voice' : 'text', diagnosticTest:request.diagnosticTest===true, capability:request.capability });
  }
  // UPDATE READY (Braeden 8/22): "make sure the app is always on the most up
  // to date server, either add an update button or something". The desktop app
  // serves its own bundle from disk, so a reload IS the update - the only thing
  // missing was knowing when there is one. Every 20s it reads build.json (the
  // sync script writes it) and compares the bundle hash to the one running.
  const [updateReady, setUpdateReady] = useState(null);
  // AM I ON THE CURRENT BUILD, RIGHT NOW? (Braeden 8/22: "to check the app is
  // fully on the up to date version it needs to tell me in real time if im on
  // the right server or not, and if not, it has a update button that restarts
  // the app"). Two things can be behind, and both are measured every 10s:
  //   UI     - the bundle this window runs vs. dist/build.json (written by the
  //            sync script on every build)
  //   server - /api/health says when the backend booted and when its newest
  //            code file changed; newer file than boot = stale server
  // The chip at the bottom-left shows the verdict at all times; the button
  // does a REAL relaunch (new main.js + new dist) when the desktop exposes
  // it, else a reload (dist is served from disk, so a reload is an update).
  const [buildStatus, setBuildStatus] = useState({ running: null, latest: null, builtAt: null, uiBehind: false, handsBehind: false, server: null, checkedAt: 0 });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI) return undefined;
    const running = (() => {
      const sc = [...document.querySelectorAll('script[src]')].map((x) => x.src).find((x) => /\/assets\/index-/.test(x)) || '';
      const m = sc.match(/(index-[A-Za-z0-9_-]+)\.js/);
      return m ? m[1] : null;
    })();
    let alive = true;
    const check = async () => {
      let latest = null, builtAt = null;
      try {
        const r = await fetch('/build.json?t=' + Date.now(), { cache: 'no-store' });
        if (r.ok) { const j = await r.json(); latest = j && j.bundle ? j.bundle : null; builtAt = j && j.builtAt ? j.builtAt : null; }
      } catch (_) { /* no stamp yet */ }
      let server = null;
      try {
        const r = await fetch(getApiBase() + '/api/health?t=' + Date.now(), { cache: 'no-store', signal: AbortSignal.timeout(4000) });
        server = r.ok ? { ok: true, ...(await r.json()) } : { ok: false, status: r.status };
      } catch (_) { server = { ok: false }; }
      // ── ARE HER HANDS OLDER THAN THE CODE ON DISK? (8/27) ──────────
      // agent.js loads once when Electron starts. Braeden asked whether a
      // backend change restarts the app: it does not, and until now nothing
      // even noticed. /api/health reports when those files last changed and
      // the main process reports when this instance started; newer files than
      // the instance means she is driving with old hands.
      let handsBehind = false;
      try {
        const hands = server && server.desktopHands;
        if (hands && hands.newest && window.electronAPI
            && typeof window.electronAPI.getStartedAt === 'function') {
          const startedAt = await window.electronAPI.getStartedAt();
          if (startedAt) handsBehind = new Date(hands.newest).getTime() > Number(startedAt);
        }
      } catch (_) { /* an older app has no getStartedAt - stay quiet */ }
      if (!alive) return;
      const uiBehind = !!(running && latest && latest !== running);
      setBuildStatus({ running, latest, builtAt, uiBehind, handsBehind, server, checkedAt: Date.now() });
      if (uiBehind || handsBehind) setUpdateReady({ bundle: latest, builtAt, hands: handsBehind }); else setUpdateReady(null);
    };
    check();
    const id = setInterval(check, 10000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  // ONE CLICK, EVERYTHING BEHIND IT (8/25). "when you click it it updates and
  // restarts the app." Three different things can be stale and they need
  // different treatment, so the button does all three in the right order:
  //   backend behind its code, or a trained model not being served
  //        -> ask the backend to restart itself first (via the main process,
  //           which holds the admin token; the renderer never sees it)
  //   new UI bundle / new main.js / new agent
  //        -> relaunch Electron, which loads all three from disk
  // Relaunching alone would have left a stale backend exactly as stale, and
  // that is how R10, R11 and R12 stayed unserved for days.
  const restartInFlight = useRef(false);
  const restartApp = async () => {
    if (restartInFlight.current) return;
    restartInFlight.current = true;
    try {
      const sv = buildStatus.server || {};
      await restartWithUpdate(window.electronAPI, !!(sv.stale || sv.models?.behind));
    } catch (error) {
      window.alert(error.message || 'Update failed. Please try again.');
    } finally {
      restartInFlight.current = false;
    }
  };
  // Read at OPEN time, not at mount: sign-in happens inside the app, so a
  // value captured on first render shows a stale 'Not signed in' to someone
  // who signed in two minutes ago.
  const accountIdentity = useMemo(() => {
    if (!accountPanelOpen) return { name: '', email: '', signedIn: false };
    let name = '', email = '', token = '';
    try {
      name = localStorage.getItem('aria_user_first') || '';
      // aria_session_email is the account's own email; aria_user_email is the
      // billing mirror written at checkout. Session first, billing as fallback.
      email = localStorage.getItem('aria_session_email') || localStorage.getItem('aria_user_email') || '';
      token = localStorage.getItem('aria_session_token') || '';
    } catch (_) {}
    // ── "I STILL CANT SIGN OUT" (Braeden 9/4) ────────────────────────────
    // `signedIn: !!token` was the whole test, so the panel offered SIGN IN to
    // someone it was simultaneously greeting by name — the name, email, plan
    // and user id come from the billing/profile keys, which survive without a
    // session token, and the desktop shell has never held one at all.
    //
    // Signing out means "take me off this device", and that is a real thing to
    // do whenever ANY identity is stored here, token or not. So the button
    // follows the identity, not the token.
    // NOT the user id: `ensureAriaUserId()` mints a fresh `aria_*` one on the
    // very next load, so counting it made Sign out permanent and meaningless —
    // measured 9/4, the button was still there after signing out because a new
    // anonymous id had already been written. A device id is not a person.
    const hasIdentity = !!(token || email || name);
    return { name, email, signedIn: !!token, hasIdentity };
  }, [accountPanelOpen]);
  // Escape closes the account panel. The comment on the panel promised this
  // before the handler existed — a doc that lies is worse than no doc.
  useEffect(() => {
    if (!accountPanelOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setAccountPanelOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [accountPanelOpen]);
  // Drawer-section toggles: collapsible "Explore" with workbench categories.
  const [exploreOpen, setExploreOpen] = useState(false);
  // Settings accordion — which category (if any) is expanded.
  const [expandedSetting, setExpandedSetting] = useState(null);
  // Settings navigation: which category the right-hand detail pane is showing.
  // The left rail is always visible (desktop two-pane), so this defaults to the
  // first category rather than a null "top-level list".
  const [settingsCat, setSettingsCat] = useState('account');
  // Transient toast shown at the bottom of the settings detail pane
  // ("Reset link sent", "Your data downloaded", etc.).
  const [settingsToast, setSettingsToast] = useState('');
  const settingsToastTimer = useRef(null);
  const flashSettingsToast = useCallback((msg) => {
    setSettingsToast(String(msg || ''));
    try { clearTimeout(settingsToastTimer.current); } catch (_) {}
    settingsToastTimer.current = setTimeout(() => setSettingsToast(''), 4200);
  }, []);
  // "Forget my data" (GDPR) flow: idle → sent (waiting for the emailed token) → done.
  const [forgetPhase, setForgetPhase] = useState('idle');
  const [forgetToken, setForgetToken] = useState('');
  const [forgetBusy, setForgetBusy] = useState(false);
  // Live plan + card pulled from Stripe when Settings opens (so Plan / Payment
  // method show the REAL state instead of hardcoded "Free" / "Add a card").
  const [billingSnapshot, setBillingSnapshot] = useState(null);
  // Reactive Appearance/Voice prefs so the segmented controls highlight instantly.
  const [themePrefState, setThemePrefState] = useState(() => { try { return localStorage.getItem('aria_theme_pref') || ''; } catch (_) { return ''; } });
  const [textSizePref, setTextSizePref] = useState(() => { try { return localStorage.getItem('aria_text_size') || 'm'; } catch (_) { return 'm'; } });
  const [voiceSpeedPref, setVoiceSpeedPref] = useState(() => { try { return localStorage.getItem('aria_voice_speed') || '1.0'; } catch (_) { return '1.0'; } });
  // Whether the "See more" explanation under "Allow extra usage" is open.
  const [overageInfoOpen, setOverageInfoOpen] = useState(false);
  // Are-you-sure confirmation modal before enabling extra usage.
  const [overageConfirmOpen, setOverageConfirmOpen] = useState(false);
  // Dashboard dropdown menu — opens when the user clicks the top-left
  // gear icon, shows the same 7 nav items as the sidebar.
  const [dashboardMenuOpen, setDashboardMenuOpen] = useState(false);
  const dashboardMenuRef = useRef(null);
  // Close dropdown on outside click
  useEffect(() => {
    if (!dashboardMenuOpen) return;
    const onDocClick = (e) => {
      if (dashboardMenuRef.current && !dashboardMenuRef.current.contains(e.target)) {
        setDashboardMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [dashboardMenuOpen]);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(() => getStoredLayout().right);
  const preAssistLayoutRef = useRef(null);
  // Desktop updates — extracted to useDesktopUpdates hook
  const {
    desktopUpdateInfo, setDesktopUpdateInfo,
    updateDownloadReady, setUpdateDownloadReady,
    updateInstallMessage, setUpdateInstallMessage,
    updateInstalling,
    updateProgressStage,
    updateDismissed, setUpdateDismissed,
    appVersion,
    updateChecking,
    runUpdateCheck,
    installUpdate: installUpdateFromHook,
  } = useDesktopUpdates(isDesktopApp);
  /** Web only: Mac/Windows download URLs from backend /api/desktop/latest */
  const [desktopDownloadInfo, setDesktopDownloadInfo] = useState(null);
  /** Membership — gates the desktop-app download (members-only perk). */
  const [desktopMember, setDesktopMember] = useState(false);
  useEffect(() => {
    let dead = false;
    const check = () => {
      let uid = '';
      try { uid = localStorage.getItem('aria_user_id') || ''; } catch (_) {}
      if (!uid) { setDesktopMember(false); return; }
      fetchWithTimeout(`${API_BASE}/api/subscription/credits?userId=${encodeURIComponent(uid)}`, { timeout: API_TIMEOUT_MS })
        .then((r) => r.json())
        .then((d) => { if (!dead) setDesktopMember(['basic_20', 'pay_as_you_go', 'ultra_200', 'owner'].includes(d?.planId)); })
        .catch(() => {});
    };
    check();
    window.addEventListener('aria-billing-identity', check);
    window.addEventListener('aria-account-id', check);
    return () => { dead = true; window.removeEventListener('aria-billing-identity', check); window.removeEventListener('aria-account-id', check); };
  }, []);

  const [onboardingStep, setOnboardingStep] = useState(() => {
    if (typeof window === 'undefined') return -1;
    if (!window.electronAPI) return -1;
    try {
      return localStorage.getItem(ONBOARDING_DONE_KEY) === '1' ? -1 : 0;
    } catch { return -1; }
  });

  // Business onboarding (Aria-for-trades): 5-question profile capture.
  // Different from desktop permission onboarding above — this is the
  // owner_profile capture (trade · stage · mindset · energy · comm style).
  //
  // DISABLED: the auto-firing modal was a bad onboarding experience. Users now
  // land directly in the Aria chat and can describe their business naturally —
  // small_business_discovery arc picks up trade/stage from conversation as
  // needed. The component is kept so we can re-enable it as an optional
  // settings panel later, but it never shows on its own anymore.
  const ARIA_BIZ_ONBOARDING_KEY = 'aria_business_onboarding_completed_v1';
  const [showBusinessOnboarding, setShowBusinessOnboarding] = useState(false);
  const handleBusinessOnboardingComplete = useCallback(() => {
    setShowBusinessOnboarding(false);
    try { localStorage.setItem(ARIA_BIZ_ONBOARDING_KEY, '1'); } catch (_) {}
  }, []);
  const handleBusinessOnboardingSkip = useCallback(() => {
    setShowBusinessOnboarding(false);
    // No localStorage flag — we'll ask again next session if they skipped.
  }, []);
  const [overlayMuted, setOverlayMuted] = useState(false);
  /** Desktop: main process shrinks window to a phone-width rail while Assist / screen share is active. */
  // ?assist=1 forces the compact assist layout for design review — the same
  // escape hatch ?ball= gives the ring states. Without it the compact box can
  // only be seen by entering real assist mode in Electron, which is how its
  // clipped talking-ball went unnoticed (8/21).
  const [assistCompactLayout, setAssistCompactLayout] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('assist') === '1'; } catch (_) { return false; }
  });
  const [injectQuestion, setInjectQuestion] = useState(null);
  const screenStreamRef = useRef(null);
  // >0 while she has asked "which screen?" on Assist entry and is waiting for
  // the answer (the value is how many screens there are). See the entry effect
  // and the pick intercept at the top of sendMessage (9/6).
  const awaitingScreenPickRef = useRef(0);
  screenStreamRef.current = screenStream;
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  // Start mic silent — auto-enabled after 1.5 s so the window is visible before getUserMedia fires.
  // This prevents macOS TCC from crashing the process before the permission dialog can appear.
  const [micOn, _setMicOnRaw] = useState(false);
  const micOnRef = useRef(false);
  /** Timestamp when mic was last turned ON — used to debounce accidental toggles (Bluetooth headphone controls). */
  const micOnAtRef = useRef(0);
  // Wrapper: tracks micOnRef synchronously (before effects) so debounce guards work immediately.
  const setMicOn = useCallback((valOrFn) => {
    const prev = micOnRef.current;
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      // Update refs synchronously so debounce guards work before effects run
      micOnRef.current = next;
      // Sync module-scope flag that prepareAriaPlayback() reads to enforce
      // the "mic muted = Aria silent" rule across every TTS playback site.
      _setAriaMicOn(next);
      if (!prev && next) micOnAtRef.current = Date.now();
      if (!next) voiceTurnRef.current?.cancel();
      _setMicOnRaw(next);
  }, []);
  // micSpeaking → provided by useVoiceCapture hook

  // ── Push-to-talk: `\` toggles mic ──────────────────────────────────
  // Owner spec 2026-05-03: mic stays OFF until user explicitly opens it.
  // Press `\` → mic on, transcription starts. Aria auto-closes when she
  // signals turnState=DONE (conversation feels complete) or after silence
  // in the WAITING state. Whenever the user wants to talk again, press `\`.
  const lastTurnStateRef = useRef(null);
  const micAutoCloseTimerRef = useRef(null);
  useEffect(() => {
    const handleKey = async (e) => {
      if (e.key !== '\\') return;
      // Backslash is essentially never used in normal chat — fire toggle
      // ALWAYS, even when the chat input has focus. Prevent the `\`
      // character from being typed into the input.
      console.log('[PTT] key=\\ pressed, tag=', (e.target?.tagName || '').toLowerCase());
      e.preventDefault();
      e.stopPropagation();
      // Cancel any pending auto-close so user-press is authoritative.
      if (micAutoCloseTimerRef.current) {
        clearTimeout(micAutoCloseTimerRef.current);
        micAutoCloseTimerRef.current = null;
      }
      micUserMutedRef.current = false; // user is opening it explicitly

      // ── macOS TCC prompt (PTT user gesture) ───────────────────────
      // The `\` press is a real user gesture, which is exactly when
      // macOS allows us to call askForMediaAccess and pop the system
      // permission dialog. Without this, getUserMedia in the renderer
      // just returns silent streams forever even with entitlements.
      // No-op on Windows / web. Awaits before flipping mic so the
      // permission grant lands first.
      try {
        if (typeof window !== 'undefined' && window.electronAPI?.requestMicrophoneAccess) {
          const r = await window.electronAPI.requestMicrophoneAccess();
          console.log('[PTT] mic permission status:', r);
        }
      } catch (err) {
        console.warn('[PTT] requestMicrophoneAccess failed:', err);
      }

      setMicOn((on) => {
        console.log('[PTT] toggling mic from', on, 'to', !on);
        return !on;
      });
    };
    // capture phase so we beat input handlers
    window.addEventListener('keydown', handleKey, true);
    console.log('[PTT] keybinding active — press \\ to toggle mic (works even in chat input)');
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [setMicOn]);

  const [cameraOpen, setCameraOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [referenceImages, setReferenceImages] = useState([]); // max 10 for scanning/references
  const [referenceVideo, setReferenceVideo] = useState(null); // { file, dataUrl, mimeType, dataBase64 }
  const [dragOver, setDragOver] = useState(false);
  // Backend health — extracted to useBackendHealth hook
  const { backendConnected, backendReachable } = useBackendHealth();
  const addMenuRef = useRef(null);
  const chatInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  // recognitionRef → provided by useVoiceCapture hook
  const sendMessageWithTextRef = useRef(null);
  // Last turn actually accepted — guards against the same utterance being sent
  // twice by two different call sites. See sendMessageWithText.
  const _lastTurnRef = useRef(null);
  const micBtnRef = useRef(null);
  const lastVoiceReadRef = useRef(null); // latest voice-read from Whisper final, consumed by sendMessageWithText
  const commitTaskRef = useRef(null);   // active commit-mode task { task, steps?, currentStep?, totalSteps? }; cleared on COMMIT_DONE
  // Session: when the app started so Aria can suggest breaks over time
  const sessionStartRef = useRef(Date.now());
  // Ref-mirror for effectiveSection — lets proactive timer read current value without closure ordering issues
  const effectiveSectionRef = useRef('ask');
  // Break interval: loaded from localStorage (set when Aria learns user's preference via memory)
  const storedBreakMins = (() => {
    try { const v = parseInt(localStorage.getItem('aria_break_interval_mins') || '', 10); return isNaN(v) ? 60 : v; } catch (_) { return 60; }
  })();
  const breakIntervalMsRef = useRef(storedBreakMins * 60 * 1000);
  const [voiceError, setVoiceError] = useState('');
  // voiceListening, voiceRecording → provided by useVoiceCapture hook
  const [introLocked, setIntroLocked] = useState(false);
  const [hasSpokenDesktopIntro, setHasSpokenDesktopIntro] = useState(() => {
    try { return localStorage.getItem(DESKTOP_INTRO_SPOKEN_KEY) === '1'; } catch (_) { return false; }
  });
  /** Desktop: false until Electron UI flags are merged with localStorage — avoids racing intro playback vs getUiFlag (mic on mid-intro). */
  const [desktopUiFlagsReady, setDesktopUiFlagsReady] = useState(() => {
    if (typeof window === 'undefined') return true;
    return !window.electronAPI;
  });
  const [ariaSpeaking, setAriaSpeaking] = useState(false);

  // ── SOUND (Braeden 8/31) ────────────────────────────────────────────────
  // "does it all have noise sounds that are different so people can distinct
  //  the difference" — there were none at all. See src/uiSound.js for why each
  //  cue sounds the way it does.
  //
  // HER VOICE WINS. Ducking is wired to the ariaSpeaking STATE, not to the
  // nine separate audio.play() sites, so a new speech path cannot forget to
  // silence the cues and land a click tick in the middle of her sentence.
  useEffect(() => { try { uiSound.setSpeaking(ariaSpeaking); } catch (_) {} }, [ariaSpeaking]);

  // The mic opening and closing are the two states you most need to know
  // without looking at the circle. Rising = open, falling = closed.
  const _micWasOn = useRef(false);
  useEffect(() => {
    if (micOn === _micWasOn.current) return;
    // Skip the very first paint so a restored mic state does not chirp on load.
    if (_micWasOn.current !== micOn && typeof micOn === 'boolean') {
      try { uiSound.play(micOn ? 'listen' : 'stop'); } catch (_) {}
    }
    _micWasOn.current = micOn;
  }, [micOn]);
  const ariaSpeakingRef = useRef(false);
  // AUTO-APPLY THE UPDATE WHEN HE IS IDLE (Braeden 8/22: "when i talk to aria
  // in the app, i feel like its a session behind"). The pill above needs a
  // click, and a window that has been open since the morning never gets one.
  // So: once a newer bundle is stamped, wait until nothing is in flight - no
  // assist screen, no mic, not speaking, no request loading, no popup open -
  // and he has not touched the keyboard or mouse for two minutes, then reload.
  // The backend already updates itself under nodemon; this makes the UI match.
  const _lastTouchRef = useRef(Date.now());
  useEffect(() => {
    const bump = () => { _lastTouchRef.current = Date.now(); };
    window.addEventListener('pointerdown', bump);
    window.addEventListener('keydown', bump);
    window.addEventListener('pointermove', bump, { passive: true });
    return () => {
      window.removeEventListener('pointerdown', bump);
      window.removeEventListener('keydown', bump);
      window.removeEventListener('pointermove', bump);
    };
  }, []);
  useEffect(() => {
    if (!updateReady) return undefined;
    const id = setInterval(() => {
      const busy = !!(screenStream || micOn || ariaSpeakingRef.current || loading || assistAsk || accountPanelOpen);
      const idleMs = Date.now() - _lastTouchRef.current;
      if (!busy && idleMs > 120000) {
        try { window.location.reload(); } catch (_) { /* next tick */ }
      }
    }, 5000);
    return () => clearInterval(id);
  }, [updateReady, screenStream, micOn, loading, assistAsk, accountPanelOpen]);
  // When she STARTED speaking. Only used to notice the flag has leaked — see
  // the echo gate in queueOrSendDesktopTranscript.
  const ariaSpeakingSinceRef = useRef(0);

  // True only while a whisper STREAMING session is genuinely alive. The legacy
  // MediaRecorder transcriber checks this and stands down, so the two can never
  // both be decoding the same microphone and shipping competing text.
  const streamingSessionActiveRef = useRef(false);
  // WHEN the streaming session came up. The fallback gate needs this: a session
  // that started two seconds ago and has not been spoken to yet still owns the
  // mic. See the gate in transcribeDesktopBlob.
  const streamingSessionStartedAtRef = useRef(0);
  const streamingReleaseUntilRef = useRef(0);
  // The live streaming session itself, so the ariaSpeaking effect can mute it
  // without tearing it down. Held in a ref because the session outlives every
  // render and must never be recreated by one.
  const streamingSessionRef = useRef(null);
  // WHEN the streaming transcriber last actually delivered a final. A socket
  // being open proves nothing — this is the only evidence it is doing its job,
  // and the legacy fallback defers to it on this timestamp, not on the socket.
  const lastStreamingFinalAtRef = useRef(0);
  const lastVoiceFinalPerfRef = useRef(0);

  // The live send function, owned HERE. The streaming loop lives for the whole
  // time the mic is open, so it must not close over a callback that React
  // recreates — it reads this ref each final instead.
  const queueSendRef = useRef(null);

  // Their own website, on screen, in a browser frame. null = nothing showing.
  const [sitePreview, setSitePreview] = useState(null);

  // A site build in flight, with the stages it has actually reported.
  const [buildRun, setBuildRun] = useState(null);
  const buildPollRef = useRef(null);

  /** Kick off a build and watch it. Quotes first — never spends silently. */
  const startSiteBuild = React.useCallback(async (spec, authorized) => {
    setBuildRun({ stages: [], done: false, elapsedMs: 0 });
    let started = null;
    try {
      const r = await fetch(`${API_BASE}/api/aria/build/site`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ spec, kind: 'website', authorized: !!authorized }),
      });
      started = await r.json();
    } catch (_) { setBuildRun(null); return null; }

    // Not authorized yet — this is the quote, not a build. Nothing was spent.
    if (started && started.needsAuthorization) { setBuildRun(null); return started; }

    // The buildId is derived from the spec, so polling can start immediately.
    const id = started && started.buildId;
    if (!id) { setBuildRun(null); return started; }

    if (buildPollRef.current) clearInterval(buildPollRef.current);
    buildPollRef.current = setInterval(async () => {
      try {
        const p = await fetch(`${API_BASE}/api/aria/build/progress/${encodeURIComponent(id)}`);
        if (!p.ok) return;
        const d = await p.json();
        setBuildRun(d);
        if (d.done) { clearInterval(buildPollRef.current); buildPollRef.current = null; }
      } catch (_) {}
    }, 600);
    return started;
  }, []);
  useEffect(() => () => { if (buildPollRef.current) clearInterval(buildPollRef.current); }, []);

  // ── THE CUE SHE IS CURRENTLY DRAWING ────────────────────────────────────
  //   "she needs to use assist mode features to show me what she does"
  // One at a time, same rule as the desktop overlay: cues that stack up stop
  // meaning anything. null = nothing drawn.
  const [ariaCue, setAriaCue] = useState(null);
  // Fire a cue, then clear it after `ms`. Returns a promise so a demo can be
  // written as a sequence instead of a pile of nested timeouts.
  const showCue = React.useCallback((cue, ms = 2600) => new Promise((res) => {
    setAriaCue(cue);
    setTimeout(() => { setAriaCue((c) => (c === cue ? null : c)); res(); }, ms);
  }), []);

  // ── WHEN SHE ASKS FOR SOMETHING TYPED, SHE POINTS AT WHERE ──────────────
  // A label on the input bar plus the Assist-Mode outline. Null = not asking.
  // Derived from the step she is on rather than set by hand at each call site,
  // so a new question that needs typing cannot forget to point.
  const [inputHint, setInputHint] = useState(null);
  const HINTS = { ask_site: 'YOUR WEBSITE', work: 'YOUR TRADE', trade: 'YOUR TRADE' };
  useEffect(() => {
    const id = setInterval(() => {
      const node = firstRunRef.current ? firstRunRef.current.node : null;
      const want = node ? (HINTS[node] || null) : null;
      setInputHint((cur) => {
        if (cur === want) return cur;
        // ── THE ARROW GOES UP WITH THE ASK (Braeden 8/16) ─────────────────
        //   "so she can say look at the arrow on the boom of the screen, type
        //    in your website here and ill check it out"
        // "Here" has to mean somewhere. The outline alone says "this box
        // matters"; the arrow says "put it THERE", which is what someone who
        // has never used the app actually needs.
        if (want) setAriaCue({ kind: 'point', at: 'input.input', label: 'TYPE IT HERE' });
        else setAriaCue((c) => (c && c.kind === 'point' ? null : c));
        return want;
      });
    }, 400);
    return () => clearInterval(id);
  }, []);

  // ── A WAY INTO THE ONBOARDING ROAD THAT DOES NOT NEED A LOGGED-OUT USER ──
  //
  //   "she still isnt following the onboarding process i layed out"
  //
  // She never was, and it was not the flow's fault. The road is armed in ONE
  // place — the "First Time?" button — and that button renders only when
  // `!isLoggedIn()`. Braeden is signed in as the owner, so it has never been on
  // his screen: every test he has run went straight to the brain, which is why
  // she answered with a generic assistant list instead of his script.
  //
  // ?firstrun=1 arms it directly. It also gives the road a repeatable entry
  // point for testing, instead of one that depends on being signed out.
  // ── SHE WAITS FOR THE CLICK (Braeden 8/16) ──────────────────────────────
  //
  //   "when i restart the howto, she needs to repsond when you click first
  //    time not before"
  //
  // My first version of this SPOKE on page load. That is wrong twice over: it
  // talks at someone who has not asked for anything yet, and on a restart it
  // fires before he can even see the screen. The button is the consent — she
  // should be silent until it is pressed.
  //
  // So ?firstrun=1 no longer starts anything. All it does is make the "First
  // Time?" button VISIBLE while signed in, which is the actual reason the road
  // was unreachable for him: that button only renders when logged out.
  const [forceFirstRunButton, setForceFirstRunButton] = useState(false);
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get('firstrun') === '1') {
        setForceFirstRunButton(true);
      }
    } catch (_) {}
  }, []);

  // ── WHERE THE TALKING BALL SITS ─────────────────────────────────────────
  // Directly under the top-right live chat, measured rather than assumed. The
  // chat is fixed at top:56 and shows the last two messages, so it grows and
  // shrinks on every turn — a fixed offset would eventually collide with it.
  // 176 is only the first-paint guess, replaced as soon as the chat is measured.
  // THE BAR IS IN USE — the typed surface owns the screen (Braeden 8/31).
  // True from the moment a typed turn starts until the mic is opened, so the
  // layout does not flicker back mid-answer. Opening the mic hands the screen
  // back to the conversation.
  const textMode = lastInputMode !== 'voice' && !micOn
    && (messages.some((m) => m.mode !== 'voice') || loading);
  // ── THE CIRCLE IS BIG ONLY WHEN SOMETHING IS HAPPENING (Braeden 8/31) ──
  //
  // "the aria muute button is to far in the top right and looks bad"
  //
  // Measured on a fresh page: a 210px ring sat at top:96 / right:14 with the
  // wordmark running 506-1054 and the ring 941-1151 — they overlapped by
  // 113px, so the brand had a circle punched through it. `textMode` was the
  // only thing that shrank it, and textMode requires messages to EXIST, so
  // the very first thing anybody sees was the worst version of it.
  //
  // Size follows state instead. Idle and muted is a small corner control;
  // the full ring is earned by her actually speaking, thinking, or listening,
  // which is the only time its size is telling you anything.
  // (computed below, once preparingSpeech exists — see orbCompact)
  const [talkingBallTop, setTalkingBallTop] = useState(176);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let ro = null;
    const measure = () => {
      const el = document.querySelector('.aria-live-chat');
      // No chat on screen yet — sit just under where it starts (top:56).
      if (!el) { setTalkingBallTop(96); return; }
      const r = el.getBoundingClientRect();
      // Clamp to KEEPING THE BALL ON SCREEN, not to an arbitrary fraction of
      // the viewport. The first version capped at 55% of height, which on a
      // short window pinned the ball above a tall chat and produced the exact
      // overlap the measuring was there to prevent (caught 8/16 by forcing the
      // chat to 600px). BALL_PX must track the size prop below.
      const BALL_PX = 210;
      const lowest = window.innerHeight - BALL_PX - 12;
      const next = Math.min(Math.round(r.bottom) + 14, Math.max(96, lowest));
      setTalkingBallTop(next);
    };
    measure();
    try {
      const el = document.querySelector('.aria-live-chat');
      if (el && window.ResizeObserver) { ro = new ResizeObserver(measure); ro.observe(el); }
    } catch (_) {}
    window.addEventListener('resize', measure);
    // the chat mounts/remounts with messages, so re-measure on the same beat
    const poll = setInterval(measure, 900);
    return () => {
      window.removeEventListener('resize', measure);
      clearInterval(poll);
      try { if (ro) ro.disconnect(); } catch (_) {}
    };
  }, [messages.length, loading]);

  // ── DECODE HER WHOLE VOICE ONCE, AT BOOT ────────────────────────────────
  // 2.7 MB for every line she owns. After this, speaking a banked line is a
  // BufferSource.start() instead of a fetch plus an mp3 decode — under a
  // millisecond against 20-100ms, and it stays that way on the hundredth play.
  // Fire and forget: if it fails, say() returns false and every line takes the
  // normal /tts path exactly as before.
  useEffect(() => { try { instantVoice.preload(); } catch (_) {} }, []);
  // Her playback AudioContext is born suspended (preload runs before any
  // gesture) and Chrome only honours resume() inside a real input event. The
  // FIRST pointer or key anywhere unlocks it for the whole session — without
  // this, every banked line "plays" into a frozen clock and the app shows
  // "speaking" over dead silence (8/19).
  useEffect(() => {
    const unlock = () => { try { instantVoice.unlock(); } catch (_) {} };
    window.addEventListener('pointerdown', unlock, { capture: true });
    window.addEventListener('keydown', unlock, { capture: true });
    return () => {
      window.removeEventListener('pointerdown', unlock, { capture: true });
      window.removeEventListener('keydown', unlock, { capture: true });
    };
  }, []);

  // The exact words of her last spoken line, so "let me try that again" can
  // REPLAY it instead of announcing a retry and playing nothing.
  const lastSpokenLineRef = useRef('');
  // How many times she has already tried to fix their audio this conversation.
  // Each failed attempt moves her to the next cause, so she never repeats a
  // suggestion that has already not worked.
  const hearFixTriesRef = useRef(0);

  // ── Conversation bubble state ──
  // Conversation bubbles & log — extracted to useConversationLog hook
  const {
    bubbleText, setBubbleText,
    bubbleRole, setBubbleRole,
    conversationLog, setConversationLog,
    showConversationOverlay, setShowConversationOverlay,
    showConversationHistory, setShowConversationHistory,
    selectedArchiveDay, setSelectedArchiveDay,
    conversationArchive, setConversationArchive,
    ariaBubbleText, setAriaBubbleText,
    userBubbleText, setUserBubbleText,
    addConversationEntry,
    showLiveInBubble,
    refreshArchive,
  } = useConversationLog();

  // ── Attention system state ──
  // 'sleeping' = only listening for wake word locally (free), 'attentive' = full Whisper transcription
  // Start attentive so Aria works immediately -- sleep kicks in after idle timeout
  const [ariaAttention, setAriaAttention] = useState('attentive');
  const ariaAttentionRef = useRef('attentive');
  const attentionTimeoutRef = useRef(null);
  const wakeRecognitionRef = useRef(null); // Web Speech API instance for wake word detection
  const [deviceCaps, setDeviceCaps] = useState(null); // desktop capability preflight
  // liveTranscript, activeMicLabel, lastMicPeak → provided by useVoiceCapture hook
  const [audioInputCount, setAudioInputCount] = useState(0);
  const [audioInputs, setAudioInputs] = useState([]);
  const [preferredMicId, setPreferredMicId] = useState(() => {
    try { return localStorage.getItem(PREFERRED_MIC_ID_KEY) || ''; } catch (_) { return ''; }
  });
  // voiceProfileEnrolled, voiceAdaptationCount, voiceMatchStrict → provided by useVoiceCapture hook
  const [voiceEnrolling, setVoiceEnrolling] = useState(false);
  // voiceEnrollProgress → provided by useVoiceCapture hook
  const preferredNameRef = useRef('');
  const sendFeedback = useCallback((msgIndex, rating) => {
    setFeedbackGiven(prev => ({ ...prev, [msgIndex]: rating }));
    const msg = messages[msgIndex];
    if (!msg) return;
    const uid = typeof localStorage !== 'undefined' ? localStorage.getItem('aria_user_id') || '' : '';
    // Send to backend (fire-and-forget)
    fetchWithTimeout(`${API_BASE}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: uid,
        rating, // 'up' or 'down'
        query: messages[msgIndex - 1]?.content || '',
        response: msg.content || '',
        timestamp: Date.now(),
      }),
      timeout: API_TIMEOUT_MS,
    }).catch(() => {});
  }, [messages]);
  const setPersistentUiFlag = useCallback((key, value) => {
    const normalized = value ? '1' : '0';
    try { localStorage.setItem(key, normalized); } catch (_) {}
    try { window.electronAPI?.setUiFlag?.(key, normalized); } catch (_) {}
  }, []);
  const markScreenAccessConfirmed = useCallback(() => {
    setHasConfirmedScreenAccess(true);
    setPersistentUiFlag(SCREEN_ACCESS_CONFIRMED_KEY, true);
    setPersistentUiFlag(DESKTOP_UIFLAG_SCREEN_CONFIRMED, true);
  }, [setPersistentUiFlag]);

  const loadingRef = useRef(false);
  const pendingDesktopVoiceRef = useRef([]);
  const heldTranscriptRef = useRef('');
  const heldTranscriptTimerRef = useRef(null);
  const activeReplyAudioRef = useRef(null);
  const desktopIntroPlayedRef = useRef(false);
  const desktopIntroInProgressRef = useRef(false);
  const introLockedRef = useRef(false);
  const awaitingIntroNameRef = useRef(readAwaitingIntroName());
  const firstRunResumeHandledRef = useRef(false);
  const [voicePolicy, setVoicePolicy] = useState(() => readDesktopVoicePolicy());
  const micUserMutedRef = useRef(readDesktopMicUserMuted());
  const [firstRunUxComplete, setFirstRunUxComplete] = useState(() => {
    try { return localStorage.getItem(DESKTOP_FIRST_RUN_UX_COMPLETE_KEY) === '1'; } catch (_) { return false; }
  });
  const [showFirstRunExamplesModal, setShowFirstRunExamplesModal] = useState(false);
  const preferredMicIdRef = useRef(preferredMicId);
  preferredMicIdRef.current = preferredMicId;
  /** Echo cancellation + noise suppression help keep the mic “live” while Aria plays through speakers (active conversation). */
  const getAudioConstraints = useCallback(() => {
    const id = preferredMicIdRef.current;
    const processing = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    };
    if (id && id !== 'default') return { audio: { deviceId: { exact: id }, ...processing } };
    return { audio: processing };
  }, []);

  const stopAriaPlayback = useCallback(() => {
    // Clear the module-level one-at-a-time tracker too, so a manual stop/mute
    // doesn't leave a stale "active clip" that would mute the NEXT reply.
    try { _stopActiveAriaAudio(); } catch (_) {}
    const a = activeReplyAudioRef.current;
    if (!a) { ariaSpeakingRef.current = false; setAriaSpeaking(false); return; }
    try {
      a.pause();
      a.currentTime = 0;
    } catch (_) {}
    activeReplyAudioRef.current = null;
    ariaSpeakingRef.current = false;
    setAriaSpeaking(false);
  }, []);
  useEffect(() => {
    ariaSpeakingRef.current = ariaSpeaking;
  }, [ariaSpeaking]);

  const ttsPostPlaybackUntilRef = useRef(0);
  const prevAriaSpeakingForCooldownRef = useRef(false);
  useEffect(() => {
    // stamp when she starts talking, so a leaked flag can be spotted later
    if (!prevAriaSpeakingForCooldownRef.current && ariaSpeaking) {
      ariaSpeakingSinceRef.current = Date.now();
    }
    if (prevAriaSpeakingForCooldownRef.current && !ariaSpeaking) {
      ariaSpeakingSinceRef.current = 0;
      ttsPostPlaybackUntilRef.current = Date.now() + TTS_TAIL_GATE_MS;

      // ── THE MIC STAYS OPEN UNTIL HE CLOSES IT (Braeden 8/16) ──────────
      //
      //   "can you also keep the microphone on until the user turns it off"
      //
      // REPLACES the 2026-05-03 spec ("once aria thinks the conversation is
      // over she automatically turns off the microphone"). Two things used to
      // close it and both are gone:
      //   DONE      - she decided the conversation had ended. She was guessing,
      //               and being wrong means the next thing you say lands
      //               nowhere with no sign anything is off.
      //   45s idle  - a safety timer to stop the mic "burning credits". That
      //               reasoning is stale: transcription is LOCAL now, on our
      //               own whisper, so an open mic costs no money.
      //
      // A mic that closes itself is indistinguishable from a mic that broke.
      // He opens it, he closes it - `\` toggles.
      if (micAutoCloseTimerRef.current) {
        clearTimeout(micAutoCloseTimerRef.current);
        micAutoCloseTimerRef.current = null;
      }
    }
    prevAriaSpeakingForCooldownRef.current = ariaSpeaking;
  }, [ariaSpeaking, setMicOn]);

  // ── FIRST-TIME BUTTON SPEAKS (Braeden 8/12) ─────────────────────────────
  // "the first time, should be a demo, not a message, so when they click it,
  //  aria needs to speak, and ask the user, that opens the mic, can you hear
  //  me" — the click is on HIS existing First Time button (below, left rail).
  // When her greeting finishes, the mic opens — not before, or the
  // speaking-guard would immediately close it again.
  const demoHandoffRef = useRef(false);
  // where we are in the first-run conversation (null = not in it)
  const firstRunRef = useRef(null);
  const [firstRunOn, setFirstRunOn] = useState(false);   // drives the talking ball
  // the picture for the CURRENT turn. Held in state and rendered under her
  // latest line, rather than mutated into a message object — the visual often
  // resolves before her reply exists, so there was nothing to attach it to.
  const [turnVisual, setTurnVisual] = useState(null);
  // WORKSTATION (8/22) — "its just where all of the info being worked on
  // lives". Holds the last conversation payload so the adaptive surface can
  // render the deal sheet / plan / source panel / results page from it.
  //
  // NOTE: `turnVisual` above has a render slot and a setter that is never
  // called anywhere in this file — a visual slot that has sat empty since it
  // was added. Rather than add a second competing surface, the workstation
  // renders in that same position, under the last assistant line.
  const [wsPayload, setWsPayload] = useState(null);
  // The workstation coming up is the loudest thing on screen and made no
  // sound at all. Rising two-note = something arrived. Only on the OPEN edge:
  // re-firing on every payload swap during a job would turn it into a rattle.
  const _wsWasOpen = useRef(false);
  useEffect(() => {
    const open = !!wsPayload;
    if (open !== _wsWasOpen.current) {
      if (open) { try { uiSound.play('open'); } catch (_) {} }
      _wsWasOpen.current = open;
    }
  }, [wsPayload]);
  // ── visual doctrine state (8/12, ARIA_VISUAL_DOCTRINE.md) ────────────────
  // The notepad rides along for the whole session (doctrine §3.3) — it stays
  // pinned until he closes it or a new case file replaces it.
  const [notepadFile, setNotepadFile] = useState(null);
  // ── THE FIRST-TIME TOUR (Braeden 8/31) ──────────────────────────────────
  // `tourBeat` is the RECOMMENDATION she is holding, not a script position:
  // the server hands back the next one, and hands back nothing the moment the
  // person asks for something real. `tourPoint` is the ring she is drawing.
  // ── ONE TURN AT A TIME (Braeden 8/31) ─────────────────────────────────
  //
  // "sometimes when aria is thinking after the user gets done talking, she
  //  wont finish her speech becasue we have to wait for edge to give us the
  //  audio, but before its done the whisper will pick up more whisper ques so
  //  it messes it up, she cant listen to another whisper until she has fully
  //  completed the first one, she will run slow"
  //
  // There WAS an echo gate, but it only covered the window where she is
  // audibly SPEAKING. The gap he is describing is the one before that: the
  // user stops, the turn is in flight, Edge has not returned audio yet - and
  // in that gap every whisper final was QUEUED and fired afterwards, so a
  // half-heard cough became the next turn and the replies stacked up.
  //
  // The floor is one flag held from the moment her turn starts until her
  // reply has finished playing. While she holds it the transcriber is MUTED
  // at the source (streamingTranscribe.setMuted) - she is not listening at
  // all, so nothing accumulates and whisper does no work. Anything that
  // still arrives is dropped, never queued.
  // ── THINKING MEANS THINKING UNTIL SHE SPEAKS (Braeden 8/31) ───────────
  //
  //   "when its thinking, it needs to look like it thinking until its ALL
  //    the way ready to speak"
  //
  // The ring's thinking state was tied to `loading`, which clears the moment
  // the TEXT arrives - but the voice is fetched after that, so the spinner
  // stopped and the ring fell back to the listening look for the whole Edge
  // wait. She looked ready, said nothing, then suddenly spoke. This stays
  // true from the turn going out until audio actually starts playing.
  const [preparingSpeech, setPreparingSpeech] = useState(false);
  // When it went true. Read by the leak reaper below: setPreparingSpeech(true)
  // happens in ONE place and is cleared in EIGHT, all inside the TTS/audio
  // path, so a turn that answers without reaching one of them leaves the orb
  // stuck on THINKING for the rest of the session (seen live 9/7).
  const preparingSinceRef = useRef(0);
  useEffect(() => { preparingSinceRef.current = preparingSpeech ? Date.now() : 0; }, [preparingSpeech]);

  // Declared HERE, not up beside textMode, because `preparingSpeech` is a
  // const declared on this line — reading it earlier in the component body is
  // a temporal dead zone throw during render, and this file has already lost a
  // feature to exactly that (the first-time button, 8/31).
  const orbLive = ariaSpeaking || loading || preparingSpeech || micOn;
  const orbCompact = textMode || !orbLive;
  const floorHeldRef = useRef(false);
  const micSessionRef = useRef(null);
  const voiceTurnRef = useRef(null);
  const lastVoiceOwnerRef = useRef(null);
  if (!voiceTurnRef.current) voiceTurnRef.current = createVoiceTurn({ onChange: (state) => {
    floorHeldRef.current = state.held;
    // Assist owns its playback outside the main audio bus. Publish that same
    // owner's state to the host ring, echo gate and transcriber synchronously.
    if (state.owner === 'assist' || lastVoiceOwnerRef.current === 'assist') {
      ariaSpeakingRef.current = state.speaking;
      setAriaSpeaking(state.speaking);
      setPreparingSpeech(state.held && !state.speaking);
    }
    lastVoiceOwnerRef.current = state.owner;
    voiceTrace.record('host.gates', { id: state.id, owner: state.owner, floor: state.held,
      speaking: !!ariaSpeakingRef.current, hasMic: !!micSessionRef.current });
    try { micSessionRef.current?.setMuted?.(state.held || ariaSpeakingRef.current); } catch (_) {}
    voiceTrace.record('host.mute-applied', { id: state.id, floor: state.held,
      muted: micSessionRef.current?.isMuted?.() ?? null, hasMic: !!micSessionRef.current });
  } });
  const takeFloor = useCallback(() => {
    return voiceTurnRef.current.begin('conversation');
  }, []);
  const releaseFloor = useCallback(() => {
    // A delayed conversation callback cannot release an Assist turn.
    voiceTurnRef.current.current('conversation')?.finish();
  }, []);
  useEffect(() => () => voiceTurnRef.current.cancel(), []);
  const releaseFloorRef = useRef(null);
  const takeFloorRef = useRef(null);
  useEffect(() => { releaseFloorRef.current = releaseFloor; takeFloorRef.current = takeFloor; }, [releaseFloor, takeFloor]);
  const [tourBeat, setTourBeat] = useState(null);
  const [tourPoint, setTourPoint] = useState(null);
  // ── THE WALKED TOUR, NOW A CHOICE INSTEAD OF THE ONLY DOOR (8/31) ──────
  //
  // This is the old "First Time?" button's whole body, lifted out so the
  // front door can offer it as the SECOND option ("show me around instead")
  // behind the three things she will just go and do. Same recommendation,
  // same yield-on-any-real-request; it simply no longer stands between a
  // stranger and the demonstration.
  //
  // ORDER MATTERS ON THESE THREE LINES. `speakAriaLine` used to fire before
  // the message was appended, and AriaSubtitle captures her words on the
  // RISING EDGE of ariaSpeaking - so it looked for the newest assistant
  // message, found the array still empty, captured '', and returned null for
  // the whole line. Her opening sentence, the single most important thing a
  // stranger ever hears, rendered as nothing at all: mic denied or volume
  // down and the pitch was a black screen. The append goes first now.
  const startAriaTour = () => {
    // A demo, not a message (8/12): she SPEAKS, then the mic opens when her
    // line ends, and the conversation is live.
    demoHandoffRef.current = true;
    // ── SHE OPENS A CONVERSATION, NOT A SCRIPT (Braeden 8/31) ──
    // "ITS NOT A SCRIPT, she just needs to ask opening questions like shes
    //  getting to know the user, aria - you ever use ai?"
    //
    // The node graph is gone from this path. She says hello, asks ONE
    // getting-to-know question, and every answer after that goes to the
    // server, which decides per turn whether to keep showing her features or
    // step aside for what they actually want. `tourBeat` is the
    // recommendation she is holding; the server takes it away the moment they
    // ask for something real.
    // ── SHE CHECKS IN FIRST (Braeden 9/2) ─────────────────────────────
    // "Her: my name's aria, can you hear me ok? They say whatever. Is this
    //  your first time using ai (change the question, it can be different
    //  every time ... that's what makes her fun)"
    //
    // So the opener is the check-in, and the warm-up question comes on the
    // NEXT turn from the server, drawn from a pool by `tourSeedRef` so two
    // visits never get the same one. Beat 'hello' tells the server that
    // whatever they say next is the answer to "can you hear me".
    setTourBeat('hello');
    setTourPoint(null);
    setThreeThings(null);
    const _open = "Hi - my name's Aria. Can you hear me ok?";
    try {
      setConversationHistory((prev) => [...prev, { role: 'assistant', content: _open }]);
      setMessages((m) => [...m, { role: 'assistant', content: _open, mode: 'voice' }]);
    } catch (_) {}
    // ── AND IF SHE IS NOT HEARD, SHOW THE WORDS (8/31) ──────────────────
    //
    // Her opening question existed ONLY as sound. Every silent failure - TTS
    // down, autoplay held until a gesture, no output device, a muted laptop,
    // which between them cover a large share of first visits to a site nobody
    // has heard of - left the whole introduction as a black screen with
    // "speaking…" in the corner. Measured that way on a fresh visit.
    //
    // `_showWhatSheSaid` already enforces exactly this rule on every other
    // failure branch in the turn path ("Silence is the one outcome that must
    // never happen"). The first-run opener was simply never wired to it, and
    // it is the one line where going unheard costs the most.
    //
    // The test is voiceState().played, NOT ariaSpeakingRef: the ref is set
    // optimistically before play() is even called, so it reads true during a
    // failure and the fallback would never fire. `played` only moves on a real
    // onplay (or a real BufferSource start on the instant path) - it is the
    // only honest answer to "was she actually heard".
    let _heardBefore = 0;
    try { _heardBefore = voiceState().played || 0; } catch (_) {}
    try { speakAriaLine(_open, { track: true }); } catch (_) {}
    try {
      setTimeout(() => {
        let now = _heardBefore;
        try { now = voiceState().played || 0; } catch (_) {}
        if (now <= _heardBefore) _showWhatSheSaid(_open);
      }, 1000);
    } catch (_) {}
  };
  // ── THE FIRST TIME BUTTON STARTS THE VOICE ONBOARDING (Braeden 9/26) ─────
  //
  // Click -> mic permission (it rides on this click gesture) -> SHE speaks first
  // -> the mic listens -> they talk back -> the server's onboarding answers, one
  // question a turn, until it says the flow is over. Web only. Typed input is the
  // fallback, not the path. Nothing new is built here: the mic is the app's own
  // mic, the words come from middleware/ariaOnboarding (a kickoff turn with none
  // of theirs), the line is spoken the way the tour's opener was, and every turn
  // after this rides the normal send path with `onboarding: true`, which
  // onboardingRoute.js routes to /api/ai/conversation - no GPT, no guest turn.
  const startOnboarding = async () => {
    if (isDesktopShell()) return;                          // the desktop keeps its own sign-in + permission flows
    try { setOnboardingActive(true); } catch (_) {}        // from here every turn routes to the JSON lanes
    setTourBeat(null); setTourPoint(null); setThreeThings(null);   // the old walked tour stands down
    try { setMicOn(true); } catch (_) {}                   // permission prompt now, on the gesture; she speaks into an open mic
    let data = null;
    try {
      const base = (getApiBase() || '').replace(/\/$/, '');
      const r = await fetch(`${base}/api/ai/conversation`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        // `prompt` must be non-empty (the route rejects an empty one with 400, measured
        // live 9/26); the lane reads a kickoff as "hello" whatever this says, and it is
        // mounted above every lane that could answer that word. Nothing of theirs is shown.
        body: JSON.stringify({ userId: ensureAriaUserId(), onboardingStart: true, prompt: 'hello', inputMode: 'voice', conversationHistory: [], language: 'en', skipAudio: true }),
      });
      data = await r.json();
    } catch (_) { data = null; }
    // If the server could not be reached she still says who she is (the tour's own rule:
    // silence is the one outcome that must never happen).
    const line = String((data && data.reply) || '').trim() || 'Hi - this is Aria. If prompted, click Allow for the microphone so we can hear each other. Can you hear me okay?';
    try { if (data && typeof data._lastAsked === 'string') lastAskedRef.current = data._lastAsked; } catch (_) {}
    try { applyOnboardingSignal(data || {}); } catch (_) {}
    try {
      setConversationHistory((prev) => [...prev, { role: 'assistant', content: line }]);
      setMessages((m) => [...m, { role: 'assistant', content: line, mode: 'voice' }]);
    } catch (_) {}
    let _heardBefore = 0;
    try { _heardBefore = voiceState().played || 0; } catch (_) {}
    try { speakAriaLine(line, { track: true }); } catch (_) {}
    try {
      setTimeout(() => {
        let now = _heardBefore;
        try { now = voiceState().played || 0; } catch (_) {}
        if (now <= _heardBefore) _showWhatSheSaid(line);   // not heard (autoplay held, muted laptop): show the words
      }, 1000);
    } catch (_) {}
  };

  // Which job this conversation is filed under. The backend decides (see
  // middleware/ariaWorkspace.js) and sends it back on every answer; this is
  // only the display of it.
  const [workspace, setWorkspace] = useState(null);
  // The visible search (doctrine §3.1): on a lookup turn her query types into
  // the REAL Ask bar — but only when it's empty. His draft is never eaten;
  // any real keystroke cancels the show (see the input's onChange).
  const searchShowRef = useRef({ timers: [], active: false });
  const runSearchShow = (q) => {
    try {
      const s = searchShowRef.current;
      if (!q || s.active) return;
      if (chatInputRef.current && chatInputRef.current.value) return; // he's typing
      s.active = true;
      s.timers = [];
      const str = String(q).slice(0, 80);
      // she is seen typing it: hand on the bar, keyboard up, keys lighting (9/5)
      try { ariaHand.startTyping(['input.input', '.input-row input']); } catch (_) {}
      for (let k = 1; k <= str.length; k++) {
        s.timers.push(setTimeout(() => { setInput(str.slice(0, k)); try { ariaHand.key(str.charAt(k - 1)); } catch (_) {} }, 280 + k * 22));
      }
      s.timers.push(setTimeout(() => { try { ariaHand.stopTyping(); } catch (_) {} }, 280 + str.length * 22 + 200));
      s.timers.push(setTimeout(() => { setInput(''); s.active = false; }, 280 + str.length * 22 + 1700));
    } catch (_) { /* the show may never break the turn */ }
  };
  useEffect(() => {
    if (!ariaSpeaking && demoHandoffRef.current) {
      demoHandoffRef.current = false;
      setMicOn(true);
    }
  }, [ariaSpeaking, setMicOn]);

  // ── THE WALKED INTRO: TWO BEATS ADVANCE ON A CLICK, NOT A WORD (9/2) ───
  //
  // Braeden: "Click on this burger looking thing (assist mode features points
  // to dashboard lines so they can open it) then if you make a new project
  // (again she points to it) as they go through it she talks at the right
  // times too."
  //
  // The server owns every line that answers a PERSON (it can yield to a real
  // request). These two lines answer a CLICK — the menu opening, the New
  // project box opening — and a click has no words for the server to weigh,
  // so they live here, fire the moment the thing actually opens, and move the
  // beat forward so the server knows what their next sentence is answering.
  const tourSeedRef = useRef(Math.floor(Math.random() * 1e6));
  const tourGoalRef = useRef('');          // their goal, in their words, from the naming beat (9/3)
  const lastAskedRef = useRef('');         // the intake question she just asked, so the answer is filed under it (9/3)
  const [threeThings, setThreeThings] = useState(null);
  const [madeTitle, setMadeTitle] = useState(null);
  // Where the tour is and whether the New-project box is open, readable from
  // callbacks that were created before these states changed (9/2).
  const tourStateRef = useRef({});
  tourStateRef.current = { beat: tourBeat, boxOpen: personalProjOpen };
  const sendDesktopTranscriptRef = useRef(null);

  // ── SHE SEES WHAT YOU DO ON HER OWN SCREEN (Braeden 9/2) ─────────────
  // "she needs to actively see what's being done on the computer, and
  //  remember as she talks, and also needs to confirm when something gets
  //  done, and if it's done right, almost like she's checking like a helper."
  // The app knows every one of these the instant it happens - the drawer,
  // the New-project box, a project made or picked, the view - so it keeps a
  // short trail and sends what happened since her last turn with the next
  // one. The server lane (ariaSeesTheScreen) turns that into a note she
  // remembers and a one-line confirmation or correction at the front of
  // her reply. Nothing here reads the screen; it reads the app's own state.
  // Her own pace during the intro - declared here because `trail` reads it.
  // The machinery is below, next to the tour beats (9/5).
  const tourPaceRef = useRef({
    mode: 'normal', queue: [], running: false, moves: [], cuts: 0, flagged: false,
    personAt: 0, speechEndedAt: 0, lastMoveAt: 0, movingUntil: 0, pendingUntil: 0,
  });
  const uiTrailRef = useRef([]);
  const uiSentAtRef = useRef(0);
  const uiTrailMountedRef = useRef(false);
  // ── THE DESKTOP HALF OF WHAT SHE CAN SEE (9/5) ─────────────────────────
  // The Electron app has sampled the foreground window and the switch
  // history since 8/28 (machineContext.js) and never sent it on a
  // conversation turn — so she saw her own UI and nothing of his desktop.
  // Polled every 5s (one cached IPC), read synchronously when a turn is
  // built, and absent on the web, where there is no desktop to see.
  const machineCtxRef = useRef(null);
  useEffect(() => {
    const api = (typeof window !== 'undefined' && window.aria && typeof window.aria.machineContext === 'function') ? window.aria : null;
    if (!api) return undefined;
    let on = true;
    const tick = async () => { try { const s = await api.machineContext(); if (on && s && typeof s === 'object') machineCtxRef.current = s; } catch (_) { /* no desktop */ } };
    tick();
    const id = setInterval(tick, 5000);
    return () => { on = false; clearInterval(id); };
  }, []);
  const trail = (kind, detail) => {
    try {
      // WHO DID IT (9/5). Since 9/4 she opens the drawer and the box HERSELF
      // during the intro, and the trail recorded those exactly like a click
      // of theirs - so ariaSeesTheScreen confirmed "Menu's open - good." to
      // a person who had touched nothing. A move she is making stamps the
      // next few hundred ms; the state effects that follow it are hers.
      const mine = tourPaceRef.current && Date.now() < (tourPaceRef.current.movingUntil || 0);
      uiTrailRef.current.push({ t: Date.now(), kind, detail: detail == null ? '' : String(detail).slice(0, 80), ...(mine ? { by: 'aria' } : {}) });
      if (uiTrailRef.current.length > 30) uiTrailRef.current.shift();
    } catch (_) { /* a trail must never cost a click */ }
  };
  useEffect(() => { if (uiTrailMountedRef.current) trail('menu', leftSidebarOpen ? 'opened' : 'closed'); }, [leftSidebarOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (uiTrailMountedRef.current) trail('new_project_box', personalProjOpen ? 'opened' : 'closed'); }, [personalProjOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (uiTrailMountedRef.current && selectedProject && selectedProject.name) trail('project_selected', selectedProject.name); }, [selectedProject && selectedProject.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const uiProjCountRef = useRef(null);
  useEffect(() => {
    const n = Array.isArray(userProjects) ? userProjects.length : 0;
    if (uiProjCountRef.current !== null && n > uiProjCountRef.current && userProjects[0] && userProjects[0].name) trail('project_created', userProjects[0].name);
    uiProjCountRef.current = n;
  }, [userProjects]); // eslint-disable-line react-hooks/exhaustive-deps
  // (the `view` event is recorded further down, after effectiveSection exists -
  //  referencing it here threw "before initialization" on the live site, 9/2)
  useEffect(() => { const id = setTimeout(() => { uiTrailMountedRef.current = true; }, 1500); return () => clearTimeout(id); }, []);
  const tourSaidRef = useRef({});
  // ── SHE SAYS WHO SHE IS, THEN WAITS, THEN TAKES THE INITIATIVE ────────
  //
  // Braeden 9/4: "(wait a couple of seconds, see if they say anything, if
  // they dont than aria can take annititive, want me to show you an example?
  // (thats when she does the whole new project thing)"
  //
  // This lives in the client because the server cannot wait — it answers a
  // request and the turn is over. Nothing sends a "they said nothing" turn,
  // so the pause has to be held here.
  //
  // The wait does not start when her reply ARRIVES, it starts when she stops
  // TALKING: the overview takes six or seven seconds to speak, and a timer
  // begun at reply time would have her interrupt herself.
  const tourWaitRef = useRef(null);
  const _tourWaitCancel = () => {
    if (tourWaitRef.current) { clearTimeout(tourWaitRef.current); tourWaitRef.current = null; }
  };
  // Two beats hold a pause: after who-she-is (`ai`, then the example offer)
  // and after her first move (`keep`, then "want to keep going?" - stage
  // three of the intro, Braeden 9/5). Same wait, different line.
  const TOUR_WAIT_LINES = {
    ai: ['offer_example', 'Want me to show you an example?', 2600],
    keep: ['keep_ask', "That's the kind of thing I do all day. Want to keep going with me?", 3200],
  };
  const _tourWaitThenOffer = (beat = 'ai') => {
    _tourWaitCancel();
    const startedAt = Date.now();
    const [key, line, pause] = TOUR_WAIT_LINES[beat] || TOUR_WAIT_LINES.ai;
    const tick = () => {
      let speaking = false;
      try { speaking = !!ariaSpeakingRef.current; } catch (_) { speaking = false; }
      // still mid-sentence — check again shortly. The ceiling stops a stuck
      // audio element holding the offer forever.
      if (speaking && Date.now() - startedAt < 30000) { tourWaitRef.current = setTimeout(tick, 400); return; }
      tourWaitRef.current = setTimeout(() => {
        tourWaitRef.current = null;
        // they answered, or the tour moved on — her turn is no longer wanted
        try { if ((tourStateRef.current || {}).beat !== beat) return; } catch (_) { return; }
        try { if (ariaSpeakingRef.current) return; } catch (_) {}
        _tourSay(key, line, null);
      }, pause);
    };
    tourWaitRef.current = setTimeout(tick, 300);
  };
  // ── MEMBER -> DOWNLOAD -> ASSIST MODE (Braeden 9/5) ──────────────────
  // Payment success fires aria-billing-identity. If the walk is at the
  // member beat, that is the moment: she says where the app is and rings
  // the download pill (members-only, top right on the web).
  useEffect(() => {
    if (tourBeat !== 'member') return undefined;
    const onPaid = () => {
      try { setDesktopMember(true); } catch (_) {}
      _tourSay('download', "You're in. Top right - Download for Windows. That's the desktop app, and that's where I work on your computer with you.", { target: 'download', caption: 'The desktop app', when: 'Download' });
      setTourBeat(null);
    };
    window.addEventListener('aria-billing-identity', onPaid);
    return () => window.removeEventListener('aria-billing-identity', onPaid);
  }, [tourBeat]); // eslint-disable-line react-hooks/exhaustive-deps
  // ── HER HANDS WAIT FOR HER VOICE, AND SHE NOTICES WHEN THEY DON'T (9/5) ──
  //
  // Braeden 9/5: "she moves too fast ... she clicks on too many things and
  // moves too fast, and aria should be able to pick that up."
  //
  // Measured before this: at the "let me show you" beat the reply landed,
  // the drawer opened in the same tick, the drawer line started - and
  // ariaInstantVoice.say() stops whatever clip is playing when a new one
  // starts, so her "I'm opening your dashboard..." sentence was cut a word
  // in. 1.5s later the box opened and its line cut the drawer line. Three
  // sentences, two panels, about two seconds, none of it finished. That is
  // the whole complaint, and no timer constant fixes it, because the panels
  // were never tied to her speech at all.
  //
  // So every move she makes on her own UI during the intro goes through ONE
  // queue, and a move may only run when:
  //   - she is not talking, and no line she just queued is about to start;
  //   - a breath has passed since her voice ended (900ms; 2s once slowed);
  //   - a gap has passed since her last move (1.2s; 3.5s once slowed);
  //   - the line that explains the move has actually ENDED after the move was
  //     queued - or, with no voice at all (text mode, a muted laptop), a
  //     reading-time ceiling sized to that line has elapsed.
  // Once slowed, she also waits for a sign of the person (a click, a key, a
  // turn) before each move, up to 7s, then takes the initiative - his 9/4
  // rule for silence, applied to hands.
  //
  // AND SHE PICKS IT UP HERSELF. After every move she looks back at her own
  // last ten seconds: three moves with no sign of the person between them, or
  // a line of hers that started over another one, means she is going too
  // fast. She says so once, slows herself, tells the server (the trail
  // carries `aria_pace`, ariaSeesTheScreen keeps it as a note), and remembers
  // it for this browser so the next visit starts slow. The person can say it
  // too - "slow down", "too fast", "wait" - and the tour lane answers with
  // `_tourPace: 'slow'`, which lands here the same way.
  const PACE = {
    normal: { breath: 900, gap: 1200, waitPerson: 0 },
    slow: { breath: 2000, gap: 3500, waitPerson: 7000 },
  };
  try {
    if (!tourPaceRef.current._loaded) {
      tourPaceRef.current._loaded = true;
      if (localStorage.getItem('aria_tour_pace') === 'slow') tourPaceRef.current.mode = 'slow';
    }
  } catch (_) {}
  // her voice ending is the clock everything below waits on
  useEffect(() => { if (!ariaSpeaking) tourPaceRef.current.speechEndedAt = Date.now(); }, [ariaSpeaking]);
  // a sign of the person: a click, a key, or (below, in sendMessageWithText) a turn
  useEffect(() => {
    const seen = () => { tourPaceRef.current.personAt = Date.now(); };
    window.addEventListener('pointerdown', seen, true);
    window.addEventListener('keydown', seen, true);
    return () => { window.removeEventListener('pointerdown', seen, true); window.removeEventListener('keydown', seen, true); };
  }, []);
  const _setPace = (mode, why, opts = {}) => {
    const p = tourPaceRef.current;
    if (p.mode === mode) return;
    p.mode = mode;
    try { localStorage.setItem('aria_tour_pace', mode); } catch (_) {}
    try { trail('aria_pace', `${mode}: ${why}`); } catch (_) {}
    try { console.info('[aria-pace]', mode, why); } catch (_) {}
    // She says it, once, unless the person just said it to her (then the
    // server's own line already did).
    if (mode === 'slow' && !opts.quiet && !p.flagged) {
      p.flagged = true;
      _tourSay('pace_slow', "Hang on - I'm going too fast. One thing at a time.", null);
    }
  };
  const _paceCheck = () => {
    const p = tourPaceRef.current;
    if (p.mode === 'slow') return;
    const now = Date.now();
    const recent = p.moves.filter((m) => now - m.t <= 10000);
    const untouched = recent.length >= 3 && !recent.slice(1).some((m) => m.personBefore);
    if (untouched) _setPace('slow', `${recent.length} moves in ${Math.round((now - recent[0].t) / 100) / 10}s with no sign of them`);
    else if (p.cuts >= 1) _setPace('slow', 'a line of mine started over another');
  };
  const _tourDrain = () => {
    const p = tourPaceRef.current;
    if (p.running) return;
    const item = p.queue[0];
    if (!item) return;
    p.running = true;
    const tick = () => {
      const now = Date.now();
      const cfg = PACE[p.mode] || PACE.normal;
      let speaking = false;
      try { speaking = !!ariaSpeakingRef.current; } catch (_) {}
      // a stuck flag must not hold her hands forever (same ceiling the echo gate uses)
      if (speaking && now - item.at > 20000) speaking = false;
      // A clip that never played "ends" the instant it fails (play() rejected,
      // context suspended) - measured 9/5: the box came 3.7s after the drawer
      // instead of 7 because the drawer line was never heard. A line cannot
      // have been SAID in less than ~35ms a character, so an ending that
      // early is not a reading, and the reading-time ceiling governs instead.
      const spoken = p.speechEndedAt >= item.at && (p.speechEndedAt - item.at) >= item.minSpeak;
      const lineDone = spoken || now - item.at >= item.ceiling;
      const ready = !speaking && now >= p.pendingUntil
        && now - item.at >= item.floor
        && lineDone
        && now - p.speechEndedAt >= cfg.breath
        && now - p.lastMoveAt >= cfg.gap
        && (!cfg.waitPerson || p.personAt >= p.lastMoveAt || now - Math.max(item.at, p.lastMoveAt) >= cfg.waitPerson);
      if (!ready) { setTimeout(tick, 150); return; }
      p.queue.shift();
      p.movingUntil = now + 900;
      p.lastMoveAt = now;
      // "personBefore": a sign of them since her previous move (for the first
      // move, in the 15s before it was queued - the turn that asked for it)
      p.moves.push({ t: now, kind: item.kind, personBefore: p.personAt >= (p.moves.length ? p.moves[p.moves.length - 1].t : item.at - 15000), wait: now - item.at });
      if (p.moves.length > 20) p.moves.shift();
      try { trail('aria_move', item.kind); } catch (_) {}
      // ── SHE IS SEEN DOING IT (Braeden 9/5) ─────────────────────────────
      // "if aria ever does anything for the user, like takes control, she
      //  needs to show it always, like with a cursor clicking"
      // Her cursor glides to the control and presses it; THEN the change
      // happens. If the control is not on screen the change still happens,
      // just without a mime at a place where nothing is.
      const go = () => {
        p.movingUntil = Date.now() + 900;
        try { item.run(); } catch (_) { /* a move may never break the turn */ }
        p.running = false;
        _paceCheck();
        if (p.queue.length) setTimeout(_tourDrain, 50);
      };
      if (item.target) ariaHand.click(item.target, { hold: !!item.hold }).then(go, go);
      else go();
    };
    tick();
  };
  // Queue one move of hers. `after` is the line that explains it (its length
  // sets the reading-time ceiling when nothing is heard); `floor` is the
  // least time after queueing it may run.
  const tourMove = (kind, run, opts = {}) => {
    const p = tourPaceRef.current;
    const after = String(opts.after || '');
    const ceiling = Number.isFinite(opts.ceiling) ? opts.ceiling : (after ? Math.min(14000, Math.max(2500, after.length * 62 + 800)) : 2500);
    const minSpeak = after ? Math.min(ceiling, after.length * 35) : 0;
    p.queue.push({ kind, run, at: Date.now(), floor: Number.isFinite(opts.floor) ? opts.floor : 1500, ceiling, minSpeak, target: opts.target || null, hold: !!opts.hold });
    _tourDrain();
  };
  // read-out for the harness (and for anyone watching her pace from the console)
  try { window.__ariaTourPace = () => ({ ...tourPaceRef.current, queue: tourPaceRef.current.queue.map((q) => q.kind), move: tourMove, say: _tourSay }); } catch (_) {}

  const _tourSay = (key, line, point) => {
    if (tourSaidRef.current[key]) return;
    tourSaidRef.current[key] = true;
    // A line of hers starting while another is still playing is her cutting
    // herself off - the thing that made the 9/4 intro sound rushed. Counted,
    // and the next _paceCheck slows her (the pace line itself is exempt, it
    // is allowed to interrupt).
    try { if (key !== 'pace_slow' && ariaSpeakingRef.current) { tourPaceRef.current.cuts += 1; console.info('[aria-pace] cut myself off before', key); } } catch (_) {}
    // Between asking for the clip and hearing it there is a gap where nothing
    // reads as "speaking"; the queue treats the line as started for that long.
    try { tourPaceRef.current.pendingUntil = Date.now() + Math.min(2500, Math.max(600, String(line || '').length * 62)); } catch (_) {}
    try {
      setConversationHistory((prev) => [...prev, { role: 'assistant', content: line }]);
      setMessages((m) => [...m, { role: 'assistant', content: line, mode: 'voice' }]);
    } catch (_) {}
    let _before = 0;
    try { _before = voiceState().played || 0; } catch (_) {}
    try { speakAriaLine(line, { track: true }); } catch (_) {}
    try { setTimeout(() => { let n = _before; try { n = voiceState().played || 0; } catch (_) {} if (n <= _before) _showWhatSheSaid(line); }, 1000); } catch (_) {}
    // the ring lands on the phrase she is saying, not at the start (9/2)
    if (point && point.when) whenSaid(line, point.when, () => setTourPoint(point));
    else if (point !== undefined) setTourPoint(point);
  };
  // the real New-project field, for her hand and her keyboard (9/5)
  const PROJECT_BOX_INPUT = ['.personal-proj-modal input', "input[placeholder=\"What's the project?\"]"];
  // THEY CLICK, SHE POINTS (Braeden 9/5, reversing the 9/4 "she does it"):
  // "let the user click at first, dont do it for them ... click on those 3
  //  lines, this is the dashboard where all of your projects go, lets try and
  //  make a new one, can you click this for me (she points), now is there
  //  anything you've been trying to work on lately so I can type it in for you?"
  // how long she lets a panel they opened sit before she speaks about it (9/5)
  const TOUR_AFTER_CLICK_MS = 1100;
  const TOUR_LINE_MENU = "This is the dashboard, where all of your projects go. Let's try and make a new one - can you click this for me?";
  const TOUR_LINE_BOX = "Now - is there anything you've been trying to work on lately, so I can type it in for you?";
  useEffect(() => {
    if (tourBeat === 'menu' && leftSidebarOpen) {
      // She says what she SAW happen before the next step (9/2: "confirm
      // when something gets done") - the drawer opening is the proof.
      // Descriptive, and the ring lands on "New project" mid-sentence (9/3).
      // In his words (9/3): no "that's the one" - she explains what's going on.
      // The hamburger ring is done the moment the drawer opens - its button
      // just unmounted, and the ring was hanging over the drawer header for
      // six seconds until the next one landed (seen 9/3 with frames forced).
      try { setTourPoint(null); } catch (_) {}
      // A BEAT AFTER THEIR CLICK, NOT THE SAME INSTANT (Braeden 9/5): "maybe
      // starts talking right after you click a certain thing, she can wait a
      // second and ease into it." The drawer slides in, they look at it, and
      // THEN she says what it is. The beat moves at once (their next words
      // are still answered right); only her voice waits.
      setTourBeat('project');
      setTimeout(() => { _tourSay('menu', TOUR_LINE_MENU, { target: 'project', caption: 'Click this', when: 'click this' }); }, TOUR_AFTER_CLICK_MS);
    }
  }, [tourBeat, leftSidebarOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tourBeat === 'project' && personalProjOpen) {
      // Braeden 9/2: "can you have her ask, is there any projects you've been
      // trying to get done lately?" - a beat after the box opens (9/5)
      setTourBeat('title');
      setTimeout(() => { _tourSay('project', TOUR_LINE_BOX, null); }, TOUR_AFTER_CLICK_MS);
    }
  }, [tourBeat, personalProjOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // She TYPES the title into the real New-project box, letter by letter (the
  // same show the search bar does), then creates it exactly the way the
  // Create button would. If they closed the box meanwhile, nothing is forced.
  const runTitleShow = (title) => {
    try {
      const t = String(title || '').trim().slice(0, 60);
      if (!t) return;
      setPersonalProjOpen(true);
      setPersonalProjName('');
      // ── SLOWER, BECAUSE HE COULD NOT FOLLOW IT (Braeden 9/5) ──────
      // "shes moving a little fast on the onboarding like when creating a
      //  new project, she moves very fast"
      // This is the one moment she DEMONSTRATES something, and it was
      // running at 38ms a character with a 900ms tail — the title was typed
      // and the box was gone before a first-time person had read it. The
      // point of the show is being watched, so the show waits: a slower
      // keystroke, and a beat on the finished title before it becomes a
      // project. Roughly two seconds longer end to end, once, on the first
      // run only.
      // HER KEYBOARD COMES UP WHILE SHE TYPES (Braeden 9/5): the hand goes to
      // the box, the keyboard appears, and each letter lights its key as it
      // lands in the field - so it reads as her typing, not text appearing.
      setTimeout(() => { try { ariaHand.startTyping(PROJECT_BOX_INPUT); } catch (_) {} }, 200);
      for (let k = 1; k <= t.length; k++) setTimeout(() => { setPersonalProjName(t.slice(0, k)); try { ariaHand.key(t.charAt(k - 1)); } catch (_) {} }, 520 + k * 58);
      setTimeout(() => {
        // Two moves, not one: the project is made (the box closes with it),
        // and only after a breath does the drawer go. Making, closing and
        // clearing the screen in one tick was three things at once (9/5).
        // The keyboard stays up over the finished title until her cursor has
        // pressed Create - a 13-letter title types in under a second, and a
        // keyboard that vanished with the last letter was on screen too
        // briefly to register as hers (measured 9/5: ~1.3s).
        tourMove('create_project', () => {
          try { ariaHand.stopTyping({ after: 0, hold: true }); } catch (_) {}
          const _p = { id: 'p' + Date.now().toString(36), name: t, status: 'Active', createdAt: new Date().toISOString() };
          setUserProjects((ps) => [_p, ...ps]);
          setPersonalProjOpen(false);
          setSelectedProject(_p);
          setMadeTitle(t);                 // the card says what she just made (9/2)
        }, { floor: 1700, ceiling: 1700, target: ['.personal-proj-add'] });
        // STAY IN THE CONVERSATION. The Create button jumps to the project's
        // workstation, and the first live run showed that hides everything
        // that comes next — the three-things card and her scoped question
        // were never seen. His spec keeps both in the conversation; the
        // workstation is one tap away in the drawer once they have answered.
        tourMove('close_menu', () => setLeftSidebarOpen(false), { floor: 800, ceiling: 800, target: ['.sidebar-toggle', '.app-sidebar-left-title--btn', 'text:close menu'] });
      }, 520 + t.length * 58);
    } catch (_) { /* the show may never break the turn */ }
  };

  const runDesktopWakeAndExamplesFlow = useCallback(async () => {
    if (typeof window === 'undefined' || !window.electronAPI) return;
    try {
      if (localStorage.getItem(DESKTOP_FIRST_RUN_UX_COMPLETE_KEY) === '1') return;
    } catch (_) {}
    setVoicePolicy('hold');
    setMicOn(false);
    let wakeAlready = false;
    try { wakeAlready = localStorage.getItem(DESKTOP_WAKE_LINE_DONE_KEY) === '1'; } catch (_) {}
    const wakeText =
      'Anytime you need my help, all you have to do is say Aria — and you have my attention.';
    if (!wakeAlready) {
      unlockAudioForReply();
      try {
        const base = getApiBase() || (typeof window !== 'undefined' ? window.location.origin : '') || API_BASE;
        const ttsUrl = base ? `${String(base).replace(/\/$/, '')}/tts` : '/tts';
        const r = await fetchWithTimeout(ttsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: wakeText, ...ariaUserIdJsonBody() }),
          timeout: 30000,
        });
        if (r.ok) {
          const buf = await r.arrayBuffer();
          if (buf && buf.byteLength > 0) {
            const bytes = new Uint8Array(buf);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            const dataUrl = `data:audio/wav;base64,${typeof btoa !== 'undefined' ? btoa(binary) : ''}`;
            if (dataUrl.length > 100) {
              await new Promise((resolve) => {
                const audio = new Audio(dataUrl);
                prepareAriaPlayback(audio);
                audio.playbackRate = 1.05;
                activeReplyAudioRef.current = audio;
                ariaSpeakingRef.current = true; // raise VAD threshold before play()
                audio.onplay = () => setAriaSpeaking(true);
                audio.onended = () => {
                  if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null;
                  ariaSpeakingRef.current = false;
                  setAriaSpeaking(false);
                  resolve();
                };
                audio.onerror = () => {
                  if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null;
                  ariaSpeakingRef.current = false;
                  setAriaSpeaking(false);
                  resolve();
                };
                audio.play().catch(() => { ariaSpeakingRef.current = false; resolve(); });
              });
            }
          }
        } else {
          try {
            const j = await r.json();
            setVoiceError(j?.message || `Wake voice failed (${r.status})`);
          } catch (_) {
            setVoiceError(`Wake voice failed (${r.status})`);
          }
        }
      } catch (_) {}
      try { localStorage.setItem(DESKTOP_WAKE_LINE_DONE_KEY, '1'); } catch (_) {}
      try { window.electronAPI?.setUiFlag?.('desktop.wake_line_done', '1'); } catch (_) {}
    }
    setShowFirstRunExamplesModal(true);
  }, [setAriaSpeaking, setMicOn, setVoiceError]);

  const completeDesktopFirstRunUx = useCallback(() => {
    try {
      localStorage.setItem(DESKTOP_FIRST_RUN_UX_COMPLETE_KEY, '1');
      localStorage.setItem(DESKTOP_WAKE_LINE_DONE_KEY, '1');
    } catch (_) {}
    setPersistentUiFlag(DESKTOP_FIRST_RUN_UX_COMPLETE_KEY, true);
    setPersistentUiFlag(DESKTOP_UIFLAG_FIRST_RUN_UX, true);
    setVoicePolicy('free');
    setFirstRunUxComplete(true);
    setShowFirstRunExamplesModal(false);
    // MIC AUTO-ENABLE DISABLED — owner spec 2026-05-03: mic stays OFF
    // until user presses `\`. Aria auto-closes it when conversation ends.
    // if (!micUserMutedRef.current) setMicOn(true);
  }, [setPersistentUiFlag, setMicOn]);

  const loadPreferredName = useCallback(() => {
    try {
      const primary = (localStorage.getItem(ARIA_PREFERRED_NAME_KEY) || '').trim();
      const backup = (localStorage.getItem(ARIA_PREFERRED_NAME_BACKUP_KEY) || '').trim();
      const name = primary || backup;
      if (name && !primary) localStorage.setItem(ARIA_PREFERRED_NAME_KEY, name);
      return name.slice(0, 120);
    } catch (_) {
      return '';
    }
  }, []);

  useEffect(() => {
    preferredNameRef.current = loadPreferredName();
  }, [loadPreferredName]);

  /** Speaker fingerprint gate: only transcribe if utterance matches enrolled profile when strict. */
  const voiceProfileGateRef = useRef({
    embedding: null,
    strict: false,
    threshold: DEFAULT_VOICE_MATCH_THRESHOLD,
  });
  const syncVoiceProfileGate = useCallback(() => {
    const p = loadVoiceProfile();
    // Enable voice gate when a profile exists -- only the enrolled user's voice triggers responses
    voiceProfileGateRef.current = {
      embedding: p?.embedding || null,
      strict: !!p?.embedding, // strict when enrolled, permissive when not
      threshold: DEFAULT_VOICE_MATCH_THRESHOLD,
    };
  }, []);
  useEffect(() => {
    syncVoiceProfileGate();
  }, [syncVoiceProfileGate]);

  useEffect(() => {
    if (isDesktopApp) return;
    const base = (getApiBase() || API_BASE || '').replace(/\/$/, '');
    // Vite dev: base is '' — same-origin /api is proxied to the backend
    const url = base ? `${base}/api/desktop/latest` : '/api/desktop/latest';
    const applyDownloads = (d) => {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const fallbackMac = `${origin}/aria-downloads.html#mac`;
      const fallbackWin = `${origin}/download/aria-windows`;
      if (d && d.ok) {
        const wb = d.windowsInstallerBytes;
        setDesktopDownloadInfo({
          macUrl: rewriteDownloadUrlsToPageOrigin(d.macUrl || fallbackMac),
          winUrl: rewriteDownloadUrlsToPageOrigin(d.winUrl || fallbackWin),
          version: d.version || null,
          windowsInstallerReady: d.windowsInstallerReady === true,
          windowsInstallerFileName:
            typeof d.windowsInstallerFileName === 'string' ? d.windowsInstallerFileName : null,
          windowsInstallerBytes:
            typeof wb === 'number' && Number.isFinite(wb) && wb > 0 ? wb : null,
        });
      } else {
        setDesktopDownloadInfo({
          macUrl: rewriteDownloadUrlsToPageOrigin(fallbackMac),
          winUrl: rewriteDownloadUrlsToPageOrigin(fallbackWin),
          version: null,
          windowsInstallerReady: false,
          windowsInstallerFileName: null,
          windowsInstallerBytes: null,
        });
      }
    };
    fetchWithTimeout(url, { timeout: 12000 })
      .then((r) => r.json())
      .then(applyDownloads)
      .catch(() => {
        applyDownloads(null);
      });
  }, [isDesktopApp]);

  const runVoiceEnrollment = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    setVoiceEnrolling(true);
    setVoiceEnrollProgress(0);
    setVoiceError('');
    let stream = null;
    let ctx = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia(getAudioConstraints());
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.5;
      src.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const sum = new Float32Array(analyser.frequencyBinCount);
      const start = Date.now();
      let frameCount = 0;
      await new Promise((resolve) => {
        const step = () => {
          analyser.getByteFrequencyData(buf);
          addBinsToSum(sum, buf);
          frameCount++;
          const elapsed = Date.now() - start;
          setVoiceEnrollProgress(Math.min(100, Math.round((elapsed / VOICE_ENROLL_MS) * 100)));
          if (elapsed < VOICE_ENROLL_MS) {
            requestAnimationFrame(step);
          } else {
            resolve();
          }
        };
        requestAnimationFrame(step);
      });
      const vec = sumToMeanVector(sum, frameCount);
      if (vec) {
        const now = new Date().toISOString();
        saveVoiceProfile(vec, true, { enrolledAt: now, adaptationCount: 1 });
        syncVoiceProfileGate();
        setVoiceProfileEnrolled(true);
        setVoiceMatchStrict(true);
        setVoiceAdaptationCount(1);
        try { localStorage.setItem(VOICE_ONBOARDING_DONE_KEY, '1'); } catch (_) {}
      }
    } catch (e) {
      setVoiceError('Could not access the microphone for voice setup.');
    } finally {
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (ctx) try { ctx.close(); } catch (_) {}
      setVoiceEnrolling(false);
      setVoiceEnrollProgress(0);
    }
  }, [getAudioConstraints, syncVoiceProfileGate]);

  /** After scripted intro + name confirmation, save preferred name and run spectral enrollment. */
  const handleVoiceOnboardingComplete = useCallback(async ({ name }) => {
    if (name) {
      try {
        const n = String(name).trim().slice(0, 120);
        localStorage.setItem(ARIA_PREFERRED_NAME_KEY, n);
        localStorage.setItem(ARIA_PREFERRED_NAME_BACKUP_KEY, n);
        preferredNameRef.current = n;
      } catch (_) {}
    }
    await runVoiceEnrollment();
  }, [runVoiceEnrollment]);

  const clearUserVoiceProfile = useCallback(() => {
    clearStoredVoiceProfile();
    syncVoiceProfileGate();
    setVoiceProfileEnrolled(false);
    setVoiceAdaptationCount(0);
    setVoiceMatchStrict(true);
    setVoiceError('');
  }, [syncVoiceProfileGate]);

  useEffect(() => {
    if (!isDesktopApp) return;
    if (loadVoiceProfile()) {
      try { localStorage.setItem(VOICE_ONBOARDING_DONE_KEY, '1'); } catch (_) {}
    }
  }, [isDesktopApp]);

  // No auto full-screen onboarding. Users land directly on home and can run setup manually.
  useEffect(() => {
    if (!isDesktopApp) return;
  }, [isDesktopApp]);

  // Desktop app opens on Ask Aria (HowTo centered + text prompt). A blank or #home
  // first-launch is redirected to Ask; any other explicit deep link is respected.
  useEffect(() => {
    if (!isDesktopApp || hasSpokenDesktopIntro) return;
    const h = (typeof window !== 'undefined' ? (window.location.hash || '') : '').replace(/^#/, '').toLowerCase();
    if (h === '' || h === 'home') {
      try { window.location.hash = 'ask'; } catch (_) {}
      if (dashboardSection !== 'ask') setDashboardSection('ask');
    }
  }, [isDesktopApp, hasSpokenDesktopIntro, dashboardSection]);

  // First-launch spoken intro: DISABLED per owner request — "no start up
  // screen that says hey user." Auto-marks the intro as spoken so any
  // downstream gates (mic-enable, section routing) don't wait forever.
  // Aria stays silent on launch — first sound the user hears is their
  // own message's reply.
  useEffect(() => {
    if (isDesktopApp && desktopUiFlagsReady && !hasSpokenDesktopIntro) {
      setHasSpokenDesktopIntro(true);
      try { setPersistentUiFlag('hasSpokenDesktopIntro', true); } catch (_) {}
    }
  }, [isDesktopApp, desktopUiFlagsReady, hasSpokenDesktopIntro, setPersistentUiFlag]);

  // Legacy intro effect — kept disabled below so future devs see what
  // was here. Do not re-enable without explicit owner go-ahead.
  useEffect(() => {
    return; // ← first-launch spoken intro disabled
    /* eslint-disable no-unreachable */
    if (!isDesktopApp) return;
    if (!desktopUiFlagsReady) return;
    if (hasSpokenDesktopIntro) return;
    if (desktopIntroInProgressRef.current) return;
    const h = (typeof window !== 'undefined' ? (window.location.hash || '') : '').replace(/^#/, '').toLowerCase();
    const currentSection = h === 'ask'
      ? 'ask'
      : h === 'imagegen'
        ? 'imagegen'
        : h === 'explore'
          ? 'explore'
          : h === 'subscription'
            ? 'subscription'
            : h === 'screen'
              ? 'screen'
              : ['ask', 'explore', 'imagegen', 'subscription', 'screen'].includes(dashboardSection)
                ? dashboardSection
                : 'imagegen';
    if (currentSection !== 'ask') return;
    if (desktopIntroPlayedRef.current) return;
    let cancelled = false;
    (async () => {
      desktopIntroPlayedRef.current = true;
      desktopIntroInProgressRef.current = true;
      introLockedRef.current = true;
      setIntroLocked(true);
      setMicOn(false);
      unlockAudioForReply();
      try {
        const introLines = [
          { text: 'Hi.', rate: 0.88 },
          { text: "My name's", rate: 1.12 },
          { text: 'Aria.', rate: 0.9 },
          { text: "Before I help you with anything, I'd love to learn a bit about you.", rate: 1.05 },
          { text: 'Who do I have the pleasure of speaking with?', rate: 1.1 },
        ];
        const base = getApiBase() || (typeof window !== 'undefined' ? window.location.origin : '') || API_BASE;
        const ttsUrl = base ? `${String(base).replace(/\/$/, '')}/tts` : '/tts';
        // Preload all intro clips first to avoid fetch gaps between sentences.
        const clips = await Promise.all(
          introLines.map(async (part) => {
            const r = await fetchWithTimeout(ttsUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ text: part.text, ...ariaUserIdJsonBody() }),
              timeout: 30000,
            });
            if (!r.ok) return null;
            const buf = await r.arrayBuffer();
            if (!buf || buf.byteLength === 0) return null;
            const bytes = new Uint8Array(buf);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            const dataUrl = `data:audio/wav;base64,${typeof btoa !== 'undefined' ? btoa(binary) : ''}`;
            if (dataUrl.length <= 100) return null;
            return { dataUrl, rate: part.rate || chooseSpeechRate(part.text) };
          }),
        );
        let introPhrasePlayedCount = 0;
        for (const clip of clips) {
          if (cancelled) return;
          if (!clip) continue;
          await new Promise((resolve) => {
            const audio = new Audio(clip.dataUrl);
            prepareAriaPlayback(audio);
            audio.playbackRate = clip.rate;
            activeReplyAudioRef.current = audio;
            audio.onplay = () => { introPhrasePlayedCount++; setAriaSpeaking(true); };
            audio.onended = () => {
              if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null;
              setAriaSpeaking(false);
              // Extra tail so accidental early mic-on does not trigger VAD on speaker bleed.
              try {
                ttsPostPlaybackUntilRef.current = Date.now() + TTS_TAIL_GATE_MS + 450;
              } catch (_) {}
              resolve();
            };
            audio.onerror = () => {
              if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null;
              setAriaSpeaking(false);
              resolve();
            };
            audio.play().catch(() => {
              setAriaSpeaking(false);
              resolve();
            });
          });
        }
        // Only mark intro done if audio actually played. If TTS failed silently, reset so user hears it next launch.
        if (introPhrasePlayedCount === 0) {
          desktopIntroPlayedRef.current = false;
          desktopIntroInProgressRef.current = false;
          introLockedRef.current = false;
          setIntroLocked(false);
          return;
        }
        // Only after the full scripted intro (ending with the name question) do we listen — so we hear tone on the name.
      } catch (_) {}
      desktopIntroInProgressRef.current = false;
      if (!cancelled) {
        setHasSpokenDesktopIntro(true);
        setPersistentUiFlag(DESKTOP_INTRO_SPOKEN_KEY, true);
        setPersistentUiFlag(DESKTOP_UIFLAG_INTRO_SPOKEN, true);
        setVoicePolicy('name_only');
        micUserMutedRef.current = false;
        persistDesktopMicUserMuted(false);
        awaitingIntroNameRef.current = true;
        try {
          ttsPostPlaybackUntilRef.current = Date.now() + TTS_TAIL_GATE_MS + 500;
        } catch (_) {}
        introLockedRef.current = false;
        setIntroLocked(false);
        // MIC AUTO-ENABLE DISABLED — owner spec 2026-05-03: user opens mic with `\`
        // setMicOn(true);
      } else {
        introLockedRef.current = false;
        setIntroLocked(false);
      }
    })();
    return () => {
      cancelled = true;
      desktopIntroInProgressRef.current = false;
      // Release UI lock if this run is torn down (unmount / rare dep change). Never setMicOn here —
      // that caused the mic to open mid-intro while desktopIntroPlayedRef was true at intro start.
      introLockedRef.current = false;
      setIntroLocked(false);
      // Do NOT clear awaitingIntroNameRef here — hasSpokenDesktopIntro flip would fight name capture.
    };
  }, [isDesktopApp, hasSpokenDesktopIntro, desktopUiFlagsReady, setPersistentUiFlag]);

  // Resume mid first-run: name + intro already saved but UX not completed (e.g. quit before Continue).
  useEffect(() => {
    if (!isDesktopApp) return;
    if (firstRunResumeHandledRef.current) return;
    if (firstRunUxComplete) return;
    let name = '';
    try {
      name = (localStorage.getItem(ARIA_PREFERRED_NAME_KEY) || '').trim();
    } catch (_) {}
    if (!name) return;
    try {
      if (localStorage.getItem(DESKTOP_INTRO_SPOKEN_KEY) !== '1') return;
      if (localStorage.getItem(DESKTOP_FIRST_RUN_UX_COMPLETE_KEY) === '1') return;
    } catch (_) {}
    firstRunResumeHandledRef.current = true;
    setVoicePolicy('hold');
    setMicOn(false);
    let wakeDone = false;
    try { wakeDone = localStorage.getItem(DESKTOP_WAKE_LINE_DONE_KEY) === '1'; } catch (_) {}
    if (wakeDone) {
      setShowFirstRunExamplesModal(true);
    } else {
      void runDesktopWakeAndExamplesFlow();
    }
    // Intentionally omit runDesktopWakeAndExamplesFlow from deps — identity changes would re-run and duplicate the flow.
  }, [isDesktopApp, firstRunUxComplete, desktopUiFlagsReady]);

  const MAX_REFERENCE_IMAGES = 10;

  const addReferenceImages = (filesOrItems) => {
    const files = Array.isArray(filesOrItems) ? filesOrItems : [filesOrItems];

    // Handle video files separately (only one video at a time)
    const videoFiles = files.filter((f) => f.type && f.type.startsWith('video/'));
    if (videoFiles.length > 0) {
      const videoFile = videoFiles[0];
      const videoEntry = { file: videoFile, dataUrl: null, mimeType: videoFile.type || 'video/mp4', dataBase64: null };
      setReferenceVideo(videoEntry);
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        const base64 = typeof dataUrl === 'string' && dataUrl.includes(',') ? dataUrl.split(',')[1] : '';
        setReferenceVideo((prev) => prev ? { ...prev, dataUrl, dataBase64: base64 } : null);
      };
      reader.readAsDataURL(videoFile);
      return; // Video takes priority — don't also add images from the same drop
    }

    const imageFiles = files.filter((f) => f.type && f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    let added = [];
    setReferenceImages((prev) => {
      const capped = imageFiles.slice(0, Math.max(0, MAX_REFERENCE_IMAGES - prev.length));
      added = capped.map((file, i) => ({
        id: `ref-${Date.now()}-${i}-${Math.random().toString(36).slice(2)}`,
        file,
        dataUrl: null,
        mimeType: file.type || 'image/jpeg',
        dataBase64: null,
      }));
      return [...prev, ...added];
    });
    setTimeout(() => {
      added.forEach(({ id, file }) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result;
          const base64 = typeof dataUrl === 'string' && dataUrl.includes(',') ? dataUrl.split(',')[1] : '';
          setReferenceImages((p) => p.map((item) => (item.id === id ? { ...item, dataUrl, dataBase64: base64 } : item)));
        };
        reader.readAsDataURL(file);
      });
    }, 0);
  };

  const removeReferenceImage = (id) => {
    setReferenceImages((prev) => prev.filter((p) => p.id !== id));
  };

  useEffect(() => {
    setStoredLayout(leftSidebarOpen, rightSidebarOpen);
  }, [leftSidebarOpen, rightSidebarOpen]);

  useEffect(() => {
    if (!window.electronAPI?.onDoStopShare) return;
    window.electronAPI.onDoStopShare(() => {
      const stream = screenStreamRef.current;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        setScreenStream(null);
      }
      setScreenShareError('');
    });
  }, []);

  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.onAssistCompact) return undefined;
    window.electronAPI.onAssistCompact((v) => setAssistCompactLayout(!!v));
    return undefined;
  }, [isDesktopApp]);

  // Play TTS audio then trigger an action sent from main process (e.g. assist mode switch)
  useEffect(() => {
    if (!isDesktopApp) return;
    let second;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => window.electronAPI?.signalAssistLayoutPainted?.(assistCompactLayout));
    });
    return () => { cancelAnimationFrame(first); if (second) cancelAnimationFrame(second); };
  }, [isDesktopApp, assistCompactLayout]);

  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.onPlayAndThen) return undefined;
    window.electronAPI.onPlayAndThen(({ audioB64, action }) => {
      if (!audioB64) return;
      try {
        const src = `data:audio/wav;base64,${audioB64}`;
        unlockAudioForReply();
        const audio = new Audio(src);
        prepareAriaPlayback(audio);
        ariaSpeakingRef.current = true;
        audio.onplay = () => setAriaSpeaking(true);
        // When audio finishes (or errors), signal main process to switch layout immediately
        // instead of waiting for the fixed fallback timeout.
        const signalDone = () => {
          ariaSpeakingRef.current = false;
          setAriaSpeaking(false);
          if (action === 'assistCompact') {
            try { window.electronAPI.signalAssistCompactReady?.(); } catch (_) {}
          }
        };
        audio.onended = signalDone;
        audio.onerror = signalDone;
        audio.play().catch(() => {
          // play() failed (e.g. autoplay blocked) — signal immediately so we don't hang
          signalDone();
        });
      } catch (_) {}
    });
    return undefined;
  }, [isDesktopApp]);

  // Desktop should feel hands-free by default: when user is on Ask, keep listening enabled.
  useEffect(() => {
    if (!isDesktopApp) return;
    if (voicePolicy === 'hold') return;
    if (introLocked || introLockedRef.current) return;
    // Auto-enable mic on startup, but respect user's mute button.
    //
    // Owner spec 2026-05-01: voice has to work, full stop. The previous
    // version gated mic-on behind `hasSpokenDesktopIntro` AND
    // `desktopUiFlagsReady`. If TTS failed for the intro greeting (or
    // it never got loaded from cache), `hasSpokenDesktopIntro` stayed
    // false forever and the mic never came on. The user would talk and
    // Aria would never hear them — no clear failure mode, no log.
    //
    // MIC AUTO-ENABLE DISABLED — owner spec 2026-05-03: mic stays OFF
    // on every app launch. User explicitly opens it with `\`. Aria
    // auto-closes when conversation ends (turnState = DONE / WAITING).
    // Previous behavior re-enabled the mic 1.5s after launch unless the
    // user had previously muted it — which meant launching the app =
    // immediate listening. That's the opposite of what we want.
    // if (!micOn && !micUserMutedRef.current) {
    //   const t = setTimeout(() => setMicOn(true), 1500);
    //   return () => clearTimeout(t);
    // }
  }, [isDesktopApp, dashboardSection, micOn, introLocked, voicePolicy]);

  // Startup greeting: DISABLED per owner request — "she only needs to
  // respond if the user talks to her, no start up screen that says hey user."
  // Code kept (commented gate) so we can flip it back on if we ever want
  // it back. Aria stays silent until the user initiates.
  const startupGreetingPlayedRef = useRef(false);
  useEffect(() => {
    return; // ← startup greeting disabled
    /* eslint-disable no-unreachable */
    if (!isDesktopApp || !desktopUiFlagsReady || !hasSpokenDesktopIntro) return;
    if (startupGreetingPlayedRef.current) return;
    if (introLocked || introLockedRef.current) return;
    const h = (typeof window !== 'undefined' ? (window.location.hash || '') : '').replace(/^#/, '').toLowerCase();
    const section = h || dashboardSection;
    if (section && section !== 'ask' && ['imagegen','explore','subscription','screen'].includes(section)) return;
    startupGreetingPlayedRef.current = true;
    (async () => {
      try {
        const name = (typeof localStorage !== 'undefined' ? localStorage.getItem(ARIA_PREFERRED_NAME_KEY) || '' : '').trim();
        const greeting = name
          ? `Hey ${name}.`
          : `Hey.`;
        const base = getApiBase() || (typeof window !== 'undefined' ? window.location.origin : '') || API_BASE;
        const ttsUrl = `${String(base).replace(/\/$/, '')}/tts`;
        const r = await fetchWithTimeout(ttsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: greeting, ...ariaUserIdJsonBody() }),
          timeout: 30000,
        });
        if (!r.ok) return;
        const buf = await r.arrayBuffer();
        if (!buf || buf.byteLength === 0) return;
        const bytes = new Uint8Array(buf);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const dataUrl = `data:audio/wav;base64,${btoa(binary)}`;
        if (dataUrl.length <= 100) return;
        unlockAudioForReply();
        await new Promise((resolve) => {
          const audio = new Audio(dataUrl);
          prepareAriaPlayback(audio);
          audio.playbackRate = 1.0;
          activeReplyAudioRef.current = audio;
          audio.onplay = () => setAriaSpeaking(true);
          audio.onended = () => {
            if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null;
            setAriaSpeaking(false);
            try { ttsPostPlaybackUntilRef.current = Date.now() + TTS_TAIL_GATE_MS; } catch (_) {}
            resolve();
          };
          audio.onerror = () => {
            if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null;
            setAriaSpeaking(false);
            resolve();
          };
          audio.play().catch(() => { setAriaSpeaking(false); resolve(); });
        });
      } catch (_) {}
    })();
  }, [isDesktopApp, desktopUiFlagsReady, hasSpokenDesktopIntro, introLocked]);

  // ── Proactive silence check-in ─────────────────────────────────────────────
  // After 45s of silence (no user input, Aria not speaking, mic on, Ask view),
  // Aria plays a brief check-in to keep the conversation alive.
  // Clears whenever: user sends a message, Aria speaks, mic turns off, or section changes.
  // Paused by "stop"/"quiet"/"that's all" — resumed on next user message.
  const proactivePausedRef = useRef(false);
  const proactiveTimerRef = useRef(null);
  const lastUserMessageAtRef = useRef(Date.now());

  const clearProactiveTimer = useCallback(() => {
    if (proactiveTimerRef.current) { clearTimeout(proactiveTimerRef.current); proactiveTimerRef.current = null; }
  }, []);

  const scheduleProactiveCheckin = useCallback(() => {
    // DISABLED per owner request — "no long silence and her saying still there."
    // Aria only speaks when the user speaks to her first. Clearing any
    // existing timer so previously scheduled pings don't fire.
    clearProactiveTimer();
    return;
    /* eslint-disable no-unreachable */
    if (proactivePausedRef.current) return;
    proactiveTimerRef.current = setTimeout(async () => {
      // Guard: only fire if still in Ask, mic on, not speaking, not loading.
      // Read from refs (live values), NOT closure (stale — captured 45s
      // ago when scheduleProactiveCheckin was last called). Without this,
      // a user who toggles mic OFF still gets a check-in 45s later.
      if (effectiveSectionRef.current !== 'ask') return;
      if (!micOnRef.current) return;
      if (ariaSpeakingRef.current) return;
      if (loadingRef.current) return;
      if (proactivePausedRef.current) return;
      try {
        const base = (getApiBase() || API_BASE || '').replace(/\/$/, '');
        const name = (typeof localStorage !== 'undefined' ? localStorage.getItem(ARIA_PREFERRED_NAME_KEY) || '' : '').trim();
        const sessionMins = Math.floor((Date.now() - sessionStartRef.current) / 60000);
        const breakEveryMins = Math.floor(breakIntervalMsRef.current / 60000);

        // Choose check-in text based on how long the user has been working
        let checkinText;
        if (sessionMins >= breakEveryMins && sessionMins >= 25) {
          // Suggest a break — friendly, not nagging
          const breakPhrases = name ? [
            `Hey ${name}, you've been at this for ${sessionMins} minutes — want to take a quick break?`,
            `${name}, you've been working hard. Maybe step away for a few minutes?`,
            `You've been going for a while${name ? `, ${name}` : ''}. Might be a good time to grab some water.`,
          ] : [
            `You've been at it for ${sessionMins} minutes — want to take a quick break?`,
            `You've been working for a while. Might be good to step away for a few.`,
            `Just checking in — you've been going for ${sessionMins} minutes. Want a breather?`,
          ];
          checkinText = breakPhrases[Math.floor(Math.random() * breakPhrases.length)];
        } else if (sessionMins >= 10) {
          // Mid-session: offer to help with the next thing
          const workPhrases = name ? [
            `Still there, ${name}? What are we working on next?`,
            `Hey ${name}, anything else I can jump on for you?`,
            `${name}, what's the next thing on your list?`,
          ] : [
            `Still there? What are we working on next?`,
            `Anything else I can help you knock out?`,
            `What's the next thing on your list?`,
          ];
          checkinText = workPhrases[Math.floor(Math.random() * workPhrases.length)];
        } else {
          // Short session: simple check-in
          checkinText = name ? `Still there, ${name}? What can I help with?` : `Still there? What can I help you with?`;
        }

        const url = `${base}/tts`;
        const res = await fetchWithTimeout(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: checkinText }), timeout: 30000 });
        if (!res.ok) return;
        const buf = await res.arrayBuffer();
        if (!buf || buf.byteLength === 0) return;
        const bytes = new Uint8Array(buf);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const src = `data:audio/wav;base64,${btoa(binary)}`;
        if (src.length <= 100) return;
        unlockAudioForReply();
        const audio = new Audio(src);
        prepareAriaPlayback(audio);
        audio.playbackRate = ARIA_AUDIO_PLAYBACK_RATE;
        activeReplyAudioRef.current = audio;
        ariaSpeakingRef.current = true; // raise VAD threshold before play() fires
        audio.onplay = () => setAriaSpeaking(true);
        audio.onended = () => { ariaSpeakingRef.current = false; setAriaSpeaking(false); if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null; };
        audio.onerror = () => { ariaSpeakingRef.current = false; setAriaSpeaking(false); if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null; };
        audio.play().catch(() => {});
      } catch (_) {}
    }, 45000);
  }, [micOn, ariaSpeaking, clearProactiveTimer]);

  // Reset proactive timer whenever Aria finishes speaking or user sends a message
  useEffect(() => {
    if (!isDesktopApp || !hasSpokenDesktopIntro) return;
    if (!ariaSpeaking && micOn && effectiveSectionRef.current === 'ask' && !introLocked) {
      scheduleProactiveCheckin();
    } else {
      clearProactiveTimer();
    }
    return clearProactiveTimer;
  }, [ariaSpeaking, micOn, introLocked, isDesktopApp, hasSpokenDesktopIntro, scheduleProactiveCheckin, clearProactiveTimer]);
  // ─────────────────────────────────────────────────────────────────────────

  // Assist selects its work monitor without a numbered-screen interview.
  useEffect(() => {
    if (!isDesktopApp || !assistCompactLayout) return;
    awaitingScreenPickRef.current = null;
    try { window.electronAPI?.screenNumbers?.(false); } catch (_) {}
  }, [isDesktopApp, assistCompactLayout]);

  // ── THE RING, AND NOTHING ELSE — desktop (Braeden 9/5) ──────────────────
  // In Assist on the desktop the ring is its own transparent always-on-top
  // window (shimmer-desktop/ring-overlay.html) and this window - the one that
  // owns the mic - is hidden behind it. This effect is the only thing that
  // drives it: show while Assist is on and the mic is open, hide (chat box
  // back) when muted or when Assist ends. A click on the ring arrives as
  // onRingToggleMute and mutes here, exactly like tapping the ring in-page.
  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.ringOverlay) return;
    const show = assistCompactLayout && micOn;
    const state = !show ? 'muted'
      : ariaSpeaking ? 'speaking'
      : (loading || preparingSpeech) ? 'thinking'
      : 'listening';
    // The live sender below (search: THE RING HAS TO TELL THE TRUTH) owns this
    // now and carries the mic level too. This one fired on state changes only
    // and, having no `mic` in its payload, zeroed his voice on every send.
    void show; void state;
  }, [isDesktopApp, assistCompactLayout, micOn, ariaSpeaking, loading, preparingSpeech]);
  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.onRingToggleMute) return;
    return window.electronAPI.onRingToggleMute((requestedMicOn) => {
      const next = typeof requestedMicOn === 'boolean' ? requestedMicOn : !micOnRef.current;
      if (!next) {
        try { stopAriaPlayback(); } catch (_) {}
        try { instantVoice.stop(); } catch (_) {}
      }
      setVoiceError('');
      micUserMutedRef.current = !next;
      try { persistDesktopMicUserMuted(!next); } catch (_) {}
      setMicOn(next);
    });
  }, [isDesktopApp]);
  // THE × ON THE RING ENDS ASSIST (Braeden 9/5): identical to the × on the
  // compact chat box - stop the screen stream, tell main (which restores the
  // window, drops the agent gate and hides the ring), clear the error line.
  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.onRingExitAssist) return;
    window.electronAPI.onRingExitAssist(() => {
      try { window.electronAPI?.screenShareEnded?.(); } catch (_) {}
      const s = screenStreamRef.current;
      if (s) { try { s.getTracks().forEach((t) => t.stop()); } catch (_) {} }
      screenStreamRef.current = null;
      setScreenStream(null);
      setScreenShareError('');
    });
  }, [isDesktopApp]);

  useEffect(() => {
    if (assistCompactLayout) {
      if (!preAssistLayoutRef.current) {
        preAssistLayoutRef.current = { left: leftSidebarOpen, right: rightSidebarOpen };
      }
      setLeftSidebarOpen(false);
      setRightSidebarOpen(false);
      // THE RING IS THE FIRST THING (Braeden 9/5): "i need to see this same
      // thing when im IN assist mode ... thats the only thing i see, not the
      // chat box ... if i mute that than the chat box pops up". Entering
      // Assist opens the mic, so the ring is what he lands on; the chat box is
      // what muting brings up, not the other way round.
      setVoiceError('');
      setLastInputMode('voice');
      if (!micUserMutedRef.current) setMicOn(true);
      return;
    }
    // Restore the user's prior layout after exiting Assist compact mode.
    if (preAssistLayoutRef.current) {
      setLeftSidebarOpen(preAssistLayoutRef.current.left);
      setRightSidebarOpen(preAssistLayoutRef.current.right);
      preAssistLayoutRef.current = null;
    }
  }, [assistCompactLayout]);

  useEffect(() => {
    if (!window.electronAPI?.onSetMicActive) return;
    window.electronAPI.onSetMicActive((active) => setOverlayMuted(!active));
  }, []);

  useEffect(() => {
    if (!window.electronAPI?.onDoAsk) return;
    window.electronAPI.onDoAsk((text) => setInjectQuestion(text));
  }, []);

  // [EXTRACTED] Backend health polling → useBackendHealth hook
  // [EXTRACTED] Desktop update listeners → useDesktopUpdates hook

  // Send user activity to main so "Run when idle" can pause the agent while the user uses the computer.
  // Don't count scrolling the how-to or texting Aria as activity – so the user can let Aria work while they read or chat.
  const ARIA_SAFE_SELECTOR = '.input-row, .messages, .app-sidebar-left, .app-sidebar-right, .screen-share-view, .app-center';
  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.sendUserActivity) return;
    let throttle = 0;
    const onActivity = (e) => {
      if (e.target && typeof e.target.closest === 'function' && e.target.closest(ARIA_SAFE_SELECTOR)) return;
      const now = Date.now();
      if (now - throttle < 400) return;
      throttle = now;
      window.electronAPI.sendUserActivity();
    };
    window.addEventListener('mousemove', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity);
    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
    };
  }, [isDesktopApp]);

  // [EXTRACTED] App version fetch → useDesktopUpdates hook

  // Desktop UI is served on a dynamic localhost port, so localStorage can reset across launches.
  // Mirror critical permission/onboarding flags from Electron's persistent config.
  // Intro: merge main-process + renderer storage *before* intro playback — otherwise getUiFlag can flip
  // hasSpokenDesktopIntro mid-intro, cancel clips, and auto-mic opens while Aria is still speaking.
  useEffect(() => {
    if (!isDesktopApp) return;
    let cancelled = false;
    (async () => {
      try {
        try {
          const onboardingDone = await window.electronAPI?.getUiFlag?.(DESKTOP_UIFLAG_ONBOARDING_DONE);
          if (!cancelled && String(onboardingDone || '') === '1') setOnboardingStep(-1);
        } catch (_) {}
        try {
          const screenConfirmed = await window.electronAPI?.getUiFlag?.(DESKTOP_UIFLAG_SCREEN_CONFIRMED);
          if (!cancelled && String(screenConfirmed || '') === '1') setHasConfirmedScreenAccess(true);
        } catch (_) {}
        try {
          let electronIntro = false;
          try {
            const introSpoken = await window.electronAPI?.getUiFlag?.(DESKTOP_UIFLAG_INTRO_SPOKEN);
            electronIntro = String(introSpoken || '') === '1';
          } catch (_) {}
          let localIntro = false;
          try {
            localIntro = localStorage.getItem(DESKTOP_INTRO_SPOKEN_KEY) === '1';
          } catch (_) {}
          if (!cancelled) {
            if (electronIntro && !localIntro) {
              setPersistentUiFlag(DESKTOP_INTRO_SPOKEN_KEY, true);
              localIntro = true;
            } else if (localIntro && !electronIntro) {
              setPersistentUiFlag(DESKTOP_UIFLAG_INTRO_SPOKEN, true);
            }
            if (localIntro) {
              setHasSpokenDesktopIntro(true);
              desktopIntroPlayedRef.current = true;
              try {
                const name = (localStorage.getItem(ARIA_PREFERRED_NAME_KEY) || '').trim();
                if (!name) awaitingIntroNameRef.current = true;
              } catch (_) {}
              setVoicePolicy(readDesktopVoicePolicy());
            }
          }
        } catch (_) {}
        try {
          const micMutedFlag = await window.electronAPI?.getUiFlag?.(DESKTOP_UIFLAG_MIC_USER_MUTED);
          if (!cancelled) {
            if (String(micMutedFlag || '') === '1') {
              micUserMutedRef.current = true;
              setMicOn(false);
            } else {
              // IPC says not muted — override stale localStorage value
              micUserMutedRef.current = false;
            }
          }
        } catch (_) {}
      } finally {
        if (!cancelled) setDesktopUiFlagsReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [isDesktopApp, setPersistentUiFlag]);

  // Desktop: prompt for mic only once per session when status is not already granted/denied.
  // macOS 26 may return 'not-determined' from getMediaAccessStatus even after the user
  // Mic permission: on desktop, DON'T auto-request on startup — it triggers macOS TCC dialogs
  // every time. Instead, let getUserMedia handle it naturally when micOn becomes true.
  // Only proactively request if the user explicitly clicks the mic button (handled below).
  // If already granted, this is a no-op. If denied, show settings link.
  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.requestMicrophoneAccess) return;
    if (!deviceCaps) return;
    const mic = String(deviceCaps.microphone || '').toLowerCase();
    if (mic === 'granted' || mic === 'authorized') {
      // Already have permission — make sure micAccessRequested is set so we don't ask later
      micAccessRequestedRef.current = true;
      return;
    }
    // Don't auto-request — wait for user gesture (mic button click or getUserMedia in VAD)
  }, [isDesktopApp, deviceCaps]);

  // Desktop capability preflight: keep permission/capability status fresh.
  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.getDeviceCapabilities) return;
    let cancelled = false;
    const pull = () => {
      window.electronAPI.getDeviceCapabilities()
        .then((caps) => { if (!cancelled) setDeviceCaps(caps || null); })
        .catch(() => { if (!cancelled) setDeviceCaps(null); });
    };
    pull();
    const t = setInterval(pull, 3000);
    return () => { cancelled = true; clearInterval(t); };
  }, [isDesktopApp]);

  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    let cancelled = false;
    const refreshInputs = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const inputs = devices.filter((d) => d.kind === 'audioinput');
        if (!cancelled) {
          setAudioInputCount(inputs.length);
          setAudioInputs(inputs);
          if (!preferredMicIdRef.current && inputs.length > 0) {
            const lbl = (d) => (d.label || '').toLowerCase();
            const builtIn = inputs.find((d) => lbl(d).includes('built-in') || lbl(d).includes('macbook') || lbl(d).includes('internal'));
            const notPhone = inputs.find((d) => !lbl(d).includes('iphone') && !lbl(d).includes('ipad') && !lbl(d).includes('android') && d.deviceId !== 'default');
            const best = builtIn || notPhone || inputs.find((d) => d.deviceId === 'default') || inputs[0];
            setPreferredMicId(best?.deviceId || '');
            if (best && best.label) console.log('[Aria mic] auto-selected:', best.label);
          } else if (preferredMicIdRef.current && !inputs.some((d) => d.deviceId === preferredMicIdRef.current)) {
            setPreferredMicId('');
          }
        }
      } catch (_) {}
    };
    refreshInputs();
    const onDeviceChange = () => refreshInputs();
    if (navigator.mediaDevices?.addEventListener) navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
    return () => {
      cancelled = true;
      if (navigator.mediaDevices?.removeEventListener) navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
    };
  }, []);

  useEffect(() => {
    try {
      if (preferredMicId) localStorage.setItem(PREFERRED_MIC_ID_KEY, preferredMicId);
      else localStorage.removeItem(PREFERRED_MIC_ID_KEY);
    } catch (_) {}
  }, [preferredMicId]);

  // [EXTRACTED] runUpdateCheck → useDesktopUpdates hook
  // installUpdateFromSidebar — wraps hook's installUpdate with extra fallback logic
  const installUpdateFromSidebar = useCallback(async () => {
    if (!desktopUpdateInfo || updateInstalling) return;
    setUpdateInstallMessage(null);
    try {
      const result = await window.electronAPI?.installUpdate?.(desktopUpdateInfo.downloadUrl);
      if (result?.reason === 'dev') {
        setUpdateInstallMessage('dev');
        setTimeout(() => setUpdateInstallMessage(null), 6000);
      } else if (result?.ok !== true) {
        setUpdateInstallMessage('failed');
        setTimeout(() => setUpdateInstallMessage(null), 6000);
        if (desktopUpdateInfo.downloadUrl?.startsWith('http')) {
          window.electronAPI?.openExternal?.(desktopUpdateInfo.downloadUrl);
        }
      }
    } catch (_) {}
  }, [desktopUpdateInfo, updateInstalling, setUpdateInstallMessage]);

  // Derive visible section from hash so URL and view stay in sync. #ask shows Ask Aria; unknown falls back to 'ask'.
  // Strip any sub-path so e.g. `canvas/pj_xxx` still matches `canvas`.
  // Workstation routes (imagegen/website/write/video/workbench) were retired
  // 2026-06-05 — Aria is a helper, not a builder. 'shop' is the only physical
  // workstation that survived because finding-and-curating IS a helper's job.
  const _fullHash = typeof window !== 'undefined' ? (window.location.hash || '').replace(/^#/, '').toLowerCase() : '';
  const hash = _fullHash.split('/')[0];
  // 'canvas' (the "My Projects" gallery) removed per owner request (2026-06-13) —
  // not in the list, so any #canvas route now falls back to 'ask' (home). The
  // CanvasSection render branch + component remain in place but are unreachable.
  // 'screen' is deliberately NOT routable (owner rule 2026-07-15: "i never want
  // the user to see it"). Assist still runs — the engine mounts hidden below —
  // but there is no page to land on, so #screen falls back to 'ask'.
  // 'google' added 8/10 — the Google page (profile + live rating + review-text).
  // Routes missing from this whitelist silently fall back to 'ask', which is why
  // clicking the Google row appeared to do nothing at all.
  const _KNOWN_SECTIONS = ['home', 'ask', 'company', 'leads', 'google', 'jobs', 'books', 'system', 'explore', 'shop', 'trends', 'subscription', 'history', 'settings', 'integrations', 'dev', 'live', 'whisper-test', 'review'];
  const _sectionRaw = _KNOWN_SECTIONS.includes(hash) ? hash : (_KNOWN_SECTIONS.includes(dashboardSection) ? dashboardSection : 'ask');
  // Assist mode ('screen') is dev-channel only — the release channel hides the
  // entry entirely (config/release_manifest.json: surfaces.assist_mode false).
  // Assist/screen-share is DESKTOP-ONLY (owner rule 2026-07-11): the website
  // never offers it, on any channel - the browser can't drive the computer.
  const _assistAllowed = typeof window !== 'undefined' && !!window.electronAPI && getReleaseChannel() !== 'release';
  const effectiveSection = (_sectionRaw === 'screen' && !_assistAllowed) ? 'ask' : _sectionRaw;
  // She sees the view change too (9/2) - declared here, after effectiveSection.
  useEffect(() => { if (uiTrailMountedRef.current) trail('view', effectiveSection); }, [effectiveSection]); // eslint-disable-line react-hooks/exhaustive-deps
  // Keep ref in sync so callbacks defined before effectiveSection can read current value
  effectiveSectionRef.current = effectiveSection;
  // Record where we came from so the top-left back arrow can pop back.
  useEffect(() => {
    const prev = navPrevRef.current;
    if (prev === null) { navPrevRef.current = effectiveSection; return; }
    if (effectiveSection === prev) return;
    if (navBackRef.current) { navBackRef.current = false; }
    else { navStackRef.current.push(prev); }
    navPrevRef.current = effectiveSection;
    setNavDepth(navStackRef.current.length);
  }, [effectiveSection]);
  const goBack = () => {
    const dest = navStackRef.current.pop() || 'ask';
    setNavDepth(navStackRef.current.length);
    navBackRef.current = true;
    try { window.location.hash = dest; } catch (_) {}
    setDashboardSection(dest);
    setLeftSidebarOpen(false);
  };
  const permissionAppName = (isDesktopApp && deviceCaps?.appNameForPermissions) ? deviceCaps.appNameForPermissions : 'Aria';
  const desktopPlatform = isDesktopApp
    ? (deviceCaps?.platform || (typeof window !== 'undefined' && window.electronAPI?.platform) || null)
    : null;
  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || navigator.userAgent || '');
  const isChromeBrowser = typeof navigator !== 'undefined' && /Chrome/i.test(navigator.userAgent || '') && !/Edg/i.test(navigator.userAgent || '');
  const browserName = isChromeBrowser ? 'Google Chrome' : /Edg/i.test(navigator.userAgent || '') ? 'Microsoft Edge' : /Safari/i.test(navigator.userAgent || '') ? 'Safari' : 'your browser';
  const microphoneDeniedMessage = isDesktopApp
    ? (desktopPlatform === 'win32'
      ? `Microphone denied. Allow mic for ${permissionAppName} in Settings → Privacy & Security → Microphone.`
      : `Microphone denied. Allow mic for ${permissionAppName} in System Settings → Privacy & Security → Microphone.`)
    : isMac
      ? `Microphone blocked. Two things to check: (1) macOS: System Settings → Privacy & Security → Microphone → toggle ON for ${browserName}. (2) Browser: click the lock/tune icon in the address bar → Site settings → Microphone → Allow. Then reload.`
      : `Microphone blocked. Click the lock/tune icon in the address bar → Site settings → Microphone → Allow, then reload.`;
  const micInsecureContextMessage =
    'Microphone in the browser needs a secure context (HTTPS or localhost). Serve the site over HTTPS, open it at http://127.0.0.1:3001 on the machine running the backend, or install the Aria desktop app. Plain http:// with a LAN IP (for example 192.168.x.x) blocks microphone access in most browsers.';
  const micAccessGranted = ['granted', 'authorized'].includes(String(deviceCaps?.microphone || '').toLowerCase());
  const screenStatusNorm = String(deviceCaps?.screenCapture || '').toLowerCase();
  // macOS/Electron often returns "unknown" or "not-determined" even when Screen Recording is already on — don't block the app.
  const screenGranted = ['granted', 'authorized'].includes(screenStatusNorm);
  const screenExplicitlyDenied = screenStatusNorm === 'denied';
  const accessibilityGranted = deviceCaps?.accessibilityTrusted === true;
  // Full-screen onboarding when the user is in Assist and Screen Recording is denied OR
  // when capture already failed with a permission error (screenPermissionPromptNeeded) and
  // the status is not confirmed-granted (catches 'not-determined' + failed capture).
  const needsDesktopPermissionOnboarding =
    isDesktopApp &&
    deviceCaps != null &&
    effectiveSection === 'screen' &&
    screenPermissionPromptNeeded &&
    (screenExplicitlyDenied || !screenGranted) &&
    !screenStream &&
    !hasConfirmedScreenAccess;
  const missingForSection = useMemo(() => {
    if (!isDesktopApp || !deviceCaps) return [];
    const missing = [];
    if (effectiveSection === 'ask') {
      const micStatus = String(deviceCaps.microphone || '').toLowerCase();
      if (micStatus === 'denied') {
        missing.push('microphone');
      }
    } else if (effectiveSection === 'screen') {
      // Only nag when macOS reports denied — not "unknown" (common false negative when permission is already on).
      if (screenExplicitlyDenied) {
        missing.push('screen');
      }
      // Screen capture + mic work without the click/scroll agent; don't block Assist on agent toggles.
    }
    return missing;
  }, [isDesktopApp, deviceCaps, effectiveSection, screenExplicitlyDenied]);

  // If we can positively detect screen permission is granted, persist that and stop showing onboarding nags.
  // Also auto-retry screen capture if user just granted permission while on Assist.
  useEffect(() => {
    if (!isDesktopApp || deviceCaps == null) return;
    if (screenGranted) {
      markScreenAccessConfirmed();
      const wasWaiting = screenPermissionPromptNeeded;
      setScreenPermissionPromptNeeded(false);
      // Auto-start Assist if user just granted Screen Recording while on the Assist tab
      if (wasWaiting && effectiveSection === 'screen' && !screenStream) {
        requestScreenShare();
      }
    }
  }, [isDesktopApp, deviceCaps, screenGranted, markScreenAccessConfirmed, screenPermissionPromptNeeded, effectiveSection, screenStream]);

  // If required desktop permissions are already granted, never show onboarding.
  useEffect(() => {
    if (!isDesktopApp || deviceCaps == null) return;
    if (!needsDesktopPermissionOnboarding && onboardingStep >= 0) {
      setPersistentUiFlag(ONBOARDING_DONE_KEY, true);
      setPersistentUiFlag(DESKTOP_UIFLAG_ONBOARDING_DONE, true);
      setOnboardingStep(-1);
    }
  }, [isDesktopApp, deviceCaps, needsDesktopPermissionOnboarding, onboardingStep, setPersistentUiFlag]);

  const runMicDiagnostic = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setVoiceError('Microphone not supported.');
      return;
    }
    try {
      setVoiceError('');
      const stream = await navigator.mediaDevices.getUserMedia(getAudioConstraints());
      const activeTrack = stream.getAudioTracks ? stream.getAudioTracks()[0] : null;
      if (activeTrack?.label) setActiveMicLabel(activeTrack.label);
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) {
        stream.getTracks().forEach((t) => t.stop());
        setVoiceError('');
        return;
      }
      const ctx = new AudioCtx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      let peakRms = 0;
      const start = Date.now();
      while (Date.now() - start < 1200) {
        const rms = computeMicRms(analyser);
        if (rms > peakRms) peakRms = rms;
        await new Promise((r) => setTimeout(r, 60));
      }
      stream.getTracks().forEach((t) => t.stop());
      try { await ctx.close(); } catch (_) {}
      const peakPercent = Math.round(peakRms * 1000) / 10;
      setLastMicPeak(peakPercent);
      if (peakRms >= MIC_DIAGNOSTIC_PASS_RMS) {
        setVoiceError('');
      } else if (peakRms >= MIC_DIAGNOSTIC_SOFT_RMS) {
        setVoiceError('Microphone input is very quiet. I can barely hear you — speak a little louder or raise input level.');
      } else {
        setVoiceError('Microphone access is granted, but no input was detected. Check selected input device and speak closer to the mic.');
      }
    } catch (_) {
      setVoiceError(microphoneDeniedMessage);
    }
  }, [microphoneDeniedMessage]);

  useEffect(() => {
    const syncFromHash = () => {
      const h = (window.location.hash || '').replace(/^#/, '').toLowerCase();
      // The standalone subscription PAGE is gone — the payment popup is the only
      // subscription surface. If anything still routes to #subscription, open the
      // popup over the current screen and send the section somewhere real.
      if (h === 'subscription') {
        try { window.dispatchEvent(new CustomEvent('aria-open-checkout', { detail: { plan: 'subscription' } })); } catch (_) {}
        const dest = (typeof window !== 'undefined' && window.innerWidth >= 768) ? 'ask' : 'home';
        try { window.location.hash = dest; } catch (_) {}
        setDashboardSection(dest);
        return;
      }
      // Whitelist of known sections. Anything not in the list falls back to
      // wide/desktop falls back to 'ask' (HowTo center + text prompt); phone falls back to 'home'.
      const known = ['home','ask','company','leads','shop','trends','imagegen','explore','screen','settings','history','integrations','dev','live','whisper-test'];
      setDashboardSection(known.includes(h) ? h : ((typeof window !== 'undefined' && window.innerWidth >= 768) ? 'ask' : 'home'));
    };
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, []);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // When on Ask Aria with no hash, set URL to #ask so voice/chat and bookmarks work
  useEffect(() => {
    if (effectiveSection === 'ask' && typeof window !== 'undefined' && !window.location.hash) {
      window.location.hash = 'ask';
    }
  }, [effectiveSection]);

  useEffect(() => {
    if (!addMenuOpen) return;
    const close = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setAddMenuOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [addMenuOpen]);



  // ── Attention system: wake/sleep transitions ───────────────────────────────
  const wakeAria = useCallback(() => {
    if (ariaAttentionRef.current === 'attentive') return;
    console.log('[Attention] 🟢 Aria waking up — now attentive');
    ariaAttentionRef.current = 'attentive';
    setAriaAttention('attentive');
    // Clear any sleep timer
    if (attentionTimeoutRef.current) { clearTimeout(attentionTimeoutRef.current); attentionTimeoutRef.current = null; }
  }, []);

  const sleepAria = useCallback(() => {
    if (ariaAttentionRef.current === 'sleeping') return;
    console.log('[Attention] 😴 Aria going to sleep — listening for wake word only');
    ariaAttentionRef.current = 'sleeping';
    setAriaAttention('sleeping');
    if (attentionTimeoutRef.current) { clearTimeout(attentionTimeoutRef.current); attentionTimeoutRef.current = null; }
  }, []);

  /** Reset the idle timer — call after every user interaction or Aria response */
  const resetAttentionTimer = useCallback(() => {
    if (attentionTimeoutRef.current) clearTimeout(attentionTimeoutRef.current);
    attentionTimeoutRef.current = setTimeout(() => {
      // Don't sleep if Aria is mid-response or user is mid-speech
      if (loadingRef.current || ariaSpeakingRef.current) {
        resetAttentionTimer(); // reschedule
        return;
      }
      sleepAria();
    }, ATTENTION_IDLE_TIMEOUT_MS);
  }, [sleepAria]);

  // When Aria finishes speaking, start the idle countdown
  useEffect(() => {
    if (!ariaSpeaking && ariaAttentionRef.current === 'attentive') {
      resetAttentionTimer();
    }
  }, [ariaSpeaking, resetAttentionTimer]);

  // When we send a message, stay attentive and reset timer
  useEffect(() => {
    if (loading && ariaAttentionRef.current === 'attentive') {
      if (attentionTimeoutRef.current) { clearTimeout(attentionTimeoutRef.current); attentionTimeoutRef.current = null; }
    }
  }, [loading]);

  // ── Wake word detector: runs Web Speech API locally (FREE) while sleeping ──
  useEffect(() => {
    if (!micOn || effectiveSection !== 'ask' || !isDesktopApp) return;
    if (ariaAttention !== 'sleeping') {
      // Stop wake word listener when attentive
      if (wakeRecognitionRef.current) {
        try { wakeRecognitionRef.current.stop(); } catch (_) {}
        wakeRecognitionRef.current = null;
      }
      return;
    }
    if (!SpeechRecognitionAPI) {
      console.log('[Attention] No Web Speech API — falling back to always-attentive mode');
      wakeAria();
      return;
    }

    let cancelled = false;
    let restarts = 0;
    let networkErrors = 0;
    const MAX_RESTARTS = 100; // Web Speech API stops after ~5-10s, we keep restarting
    const MAX_NETWORK_ERRORS = 5; // After 5 network errors, fall back to always-attentive (bad WiFi)

    const startWakeListener = () => {
      if (cancelled || ariaAttentionRef.current !== 'sleeping') return;
      try {
        const rec = new SpeechRecognitionAPI();
        wakeRecognitionRef.current = rec;
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = 'en-US';

        rec.onresult = (e) => {
          if (cancelled || ariaAttentionRef.current !== 'sleeping') return;
          let transcript = '';
          for (let i = 0; i < e.results.length; i++) {
            if (e.results[i]?.[0]?.transcript) transcript += ' ' + e.results[i][0].transcript;
          }
          const lower = transcript.toLowerCase().trim();
          const hasWake = WAKE_WORD_VARIANTS.some(w => lower.includes(w));
          if (hasWake) {
            console.log('[Attention] 🎤 Wake word detected in:', JSON.stringify(lower));
            try { rec.stop(); } catch (_) {}
            wakeRecognitionRef.current = null;
            wakeAria();
            // Strip wake word and check if there's a command attached
            let command = lower;
            for (const w of WAKE_WORD_VARIANTS) {
              command = command.replace(new RegExp(`\\b${w}\\b[,.]?\\s*`, 'gi'), '').trim();
            }
            if (command && command.length > 2) {
              // User said "Aria, open Google" — send the command part directly
              console.log('[Attention] Wake word + command:', command);
              // Small delay to let attentive mode fully activate, then send through Whisper for accuracy
              // (Web Speech API transcript can be rough — let the next VAD capture get the clean version)
            }
          }
        };

        rec.onend = () => {
          wakeRecognitionRef.current = null;
          if (!cancelled && ariaAttentionRef.current === 'sleeping' && restarts < MAX_RESTARTS) {
            restarts++;
            setTimeout(startWakeListener, 200);
          }
        };

        rec.onerror = (e) => {
          if (e.error === 'not-allowed' || e.error === 'service-not-available') {
            console.log('[Attention] Web Speech API unavailable:', e.error, '— falling back to always-attentive');
            cancelled = true;
            wakeAria();
            return;
          }
          // network errors on bad WiFi — fall back after too many failures
          if (e.error === 'network') {
            networkErrors++;
            if (networkErrors >= MAX_NETWORK_ERRORS) {
              console.log('[Attention] Too many network errors (' + networkErrors + ') — Web Speech API unreliable on this network, falling back to always-attentive');
              cancelled = true;
              wakeAria();
              return;
            }
          }
          // no-speech, network, aborted — just restart
          wakeRecognitionRef.current = null;
          if (!cancelled && ariaAttentionRef.current === 'sleeping' && restarts < MAX_RESTARTS) {
            restarts++;
            setTimeout(startWakeListener, 500);
          }
        };

        rec.start();
        console.log('[Attention] 👂 Wake word listener started (Web Speech API — free)');
      } catch (err) {
        console.log('[Attention] Web Speech API failed:', err.message, '— falling back to always-attentive');
        wakeAria();
      }
    };

    startWakeListener();

    return () => {
      cancelled = true;
      if (wakeRecognitionRef.current) {
        try { wakeRecognitionRef.current.stop(); } catch (_) {}
        wakeRecognitionRef.current = null;
      }
    };
  }, [micOn, effectiveSection, isDesktopApp, ariaAttention, wakeAria]);

  const sendDesktopTranscript = useCallback((text, _opts = {}) => {
    voiceTrace.record('transcript.send-enter', { chars: String(text || '').trim().length, floor: floorHeldRef.current, speaking: !!ariaSpeakingRef.current });
    console.log('[PIPELINE] sendDesktopTranscript called:', text);
    if (introLockedRef.current || introLocked) { console.log('[PIPELINE] BLOCKED: introLocked'); _drop('intro_locked'); return; }
    if (isDesktopApp && !hasSpokenDesktopIntro) { console.log('[PIPELINE] BLOCKED: introNotSpoken'); _drop('intro_not_spoken'); return; }
    const clean = (text || '').trim();
    if (!clean) return;
    // ── IF THEY'RE TALKING, SHE TYPES (Braeden 9/2) ──────────────────────
    // "when we get to the project box, if the user talks to Aria she needs
    //  to type into the box for them; they can type too, but if they are
    //  talking have Aria type." At the naming beat with the box open, a
    // spoken goal is typed into the real box letter by letter first, then
    // sent - so they see their words land where a typist would have put
    // them, and the named title replaces them a moment later.
    try {
      const ts = tourStateRef.current || {};
      if (!_opts._typed && ts.beat === 'title' && ts.boxOpen) {
        const g = clean.slice(0, 120);
        setPersonalProjName('');
        // her keyboard up while she types their words in for them (9/5)
        try { ariaHand.startTyping(['.personal-proj-modal input', "input[placeholder=\"What's the project?\"]"]); } catch (_) {}
        for (let k = 1; k <= g.length; k++) setTimeout(() => { setPersonalProjName(g.slice(0, k)); try { ariaHand.key(g.charAt(k - 1)); } catch (_) {} }, 120 + k * 38);
        setTimeout(() => { try { ariaHand.stopTyping({ hold: true }); } catch (_) {} try { sendDesktopTranscriptRef.current?.(clean, { _typed: true }); } catch (_) {} }, 120 + g.length * 38 + 350);
        return;
      }
    } catch (_) { /* typing is a nicety; the turn still goes */ }
    // Show what the user said in the bubble immediately
    showLiveInBubble(clean);
    // Allow all voice through -- no policy/name gates
    if (isDesktopApp && awaitingIntroNameRef.current) awaitingIntroNameRef.current = false;
    unlockAudioForReply();
    // She owns the floor from here until her reply has finished playing.
    try { takeFloorRef.current?.(); } catch (_) {}
    try { setPreparingSpeech(true); } catch (_) {}
    const send = sendMessageWithTextRef.current;
    console.log('[PIPELINE] sendMessageWithTextRef exists:', !!send);
    // Mic transcription path → 'voice' mode (audio reply, orb stays visible).
    if (send) {
      try {
        fetch('/api/pipeline/event', { method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ stage: 'voice.sent', level: 'ok', text: clean.slice(0, 60), meta: {} }) }).catch(() => {});
      } catch (_) {}
      voiceTrace.record('transcript.dispatch', { chars: clean.length });
      send(clean, { inputMode: 'voice' });
    }
    else { voiceTrace.record('transcript.disposition', { reason: 'no_send_fn' }); console.log('[PIPELINE] FALLBACK: setting input field'); setInput(clean); }
  }, [introLocked, isDesktopApp, hasSpokenDesktopIntro, voicePolicy, showLiveInBubble]);

  const queueOrSendDesktopTranscript = useCallback((text) => {
    voiceTrace.record('transcript.gate', { chars: String(text || '').trim().length,
      floor: floorHeldRef.current, speaking: !!ariaSpeakingRef.current, loading: !!loadingRef.current });
    console.log('[PIPELINE] queueOrSend called:', text, 'loading:', loadingRef.current, 'ariaSpeaking:', ariaSpeakingRef.current);
    // ── EVERY DROP IS NOW VISIBLE ON THE SERVER (Braeden 8/16) ───────────
    // "again i answer her and she doesnt respond why?" — the transcript
    // reached the browser (ws.final proved it) and then vanished behind one of
    // five silent guards below, each of which only console.logged. From the
    // outside that is indistinguishable from her ignoring him, and I spent a
    // dozen turns guessing WHICH guard. A drop that only exists in a browser
    // console is a drop nobody can diagnose.
    const _drop = (why, extra) => {
      voiceTrace.record('transcript.disposition', { reason: why });
      try {
        fetch('/api/pipeline/event', {
          method: 'POST', headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ stage: 'voice.dropped', level: 'warn',
            text: `dropped: "${String(text || '').slice(0, 40)}"`,
            meta: { why, ...(extra || {}) } }),
        }).catch(() => {});
      } catch (_) {}
    };
    // ── ECHO GATE — if Aria is currently speaking, the mic is almost
    // certainly picking up her own voice. Drop the transcript entirely.
    // Without this gate, Aria responds to herself in a loop and her
    // sentence gets killed mid-word. The user can still interrupt by
    // typing or hitting the stop button — this only filters voice echo.
    if (ariaSpeakingRef.current) {
      // THE GATE MUST BE ABLE TO REOPEN ON ITS OWN. It is right to drop audio
      // while she is talking — otherwise she answers her own voice in a loop.
      // But if the flag ever sticks true (a clip whose 'ended' never fires, a
      // playback that silently failed), this gate makes her permanently deaf
      // and there is no symptom except "she doesn't respond". That is exactly
      // what happened on 8/16. No clip she owns is longer than ~12s, so a flag
      // still set after 15 is not speech, it is a leak — and a rare doubled
      // reply is a far cheaper failure than an assistant that stops listening.
      const since = ariaSpeakingSinceRef.current || 0;
      const stuckMs = since ? Date.now() - since : 0;
      if (since && stuckMs > 15000 && !voiceTurnRef.current.current('assist')) {
        console.warn('[PIPELINE] ariaSpeaking stuck for', stuckMs, 'ms — treating as leaked, letting audio through');
        _drop('echo_gate_was_stuck_reopened', { stuckMs });
        ariaSpeakingRef.current = false;
        try { setAriaSpeaking(false); } catch (_) {}
        // fall through and process this transcript normally
      } else {
        console.log('[PIPELINE] BLOCKED: ariaSpeaking - assuming mic echo, dropping:', text);
        _drop('aria_speaking_echo_gate', { stuckMs });
        return;
      }
    }
    if (introLockedRef.current || introLocked) { console.log('[PIPELINE] BLOCKED: introLocked'); voiceTrace.record('transcript.disposition', { reason: 'intro_locked' }); return; }
    if (isDesktopApp && !hasSpokenDesktopIntro) { console.log('[PIPELINE] BLOCKED: introNotSpoken'); voiceTrace.record('transcript.disposition', { reason: 'intro_not_spoken' }); return; }
    const clean = (text || '').trim();
    if (!clean) { voiceTrace.record('transcript.disposition', { reason: 'empty' }); return; }
    if (isDesktopApp && (voicePolicy === 'intro_pending' || voicePolicy === 'hold') && !/^my name is\s+/i.test(clean)) { console.log('[PIPELINE] BLOCKED: voicePolicy', voicePolicy); _drop('voice_policy', { voicePolicy }); return; }
    // Name gate disabled — let all voice through
    if (isDesktopApp && awaitingIntroNameRef.current) {
      awaitingIntroNameRef.current = false;
    }
    // Keep mic live during loading; queue utterances. Let Aria FINISH her
    // sentence — don't kill her audio just because the mic caught a sound.
    // ── SHE FINISHES BEFORE SHE LISTENS AGAIN (8/31) ────────────────────
    // Queuing whatever the mic caught mid-turn is what made her "run slow":
    // a cough or the tail of the user's own sentence became the NEXT turn the
    // moment she stopped talking. While she holds the floor the transcript is
    // dropped, not banked.
    if (floorHeldRef.current) {
      _drop('floor_held', { phase: loadingRef.current ? 'thinking' : 'speaking' });
      return;
    }
    if (loadingRef.current) {
      // A typed turn can still be in flight (the floor is voice-only). That
      // case keeps the old hold-and-flush behaviour.
      _drop('queued_while_loading', { queued: pendingDesktopVoiceRef.current.length + 1 });
      pendingDesktopVoiceRef.current.push(clean);
      if (!voiceError) setVoiceError('Heard you. I will send that as soon as Aria finishes replying...');
      return;
    }
    // User may be pausing mid-thought; hold briefly before sending.
    const merged = heldTranscriptRef.current
      ? `${heldTranscriptRef.current} ${clean}`.replace(/\s+/g, ' ').trim()
      : clean;
    heldTranscriptRef.current = merged;
    if (heldTranscriptTimerRef.current) {
      clearTimeout(heldTranscriptTimerRef.current);
      heldTranscriptTimerRef.current = null;
    }
    // THE HOLD IS THE EAR'S JOB NOW (9/26, measured). The ear does not emit a final
    // until its own silence timers say the turn is over (600 ms floor for an
    // acknowledgement or a sentence, 1200 ms otherwise, 2400 ms on a trailing "and").
    // This client hold sat ON TOP of that, 150-400 ms on every voice turn, waiting for a
    // second final that cannot arrive inside it (a new utterance needs a new socket
    // plus the ear's floor). When the final carries the ear's timing - i.e. the ear
    // decided - the hold is 0; the old hold stays for finals without it (a close-flush).
    const _earDecided = !!(lastVoiceReadRef.current && lastVoiceReadRef.current.earTiming && lastVoiceReadRef.current.earTiming.eou_reason);
    const delayMs = _earDecided ? 0 : responseDelayForTranscript(merged);
    voiceTrace.record('transcript.deferred', { delayMs, reason: _earDecided ? 'ear-decided' : 'client-hold' });
    setVoiceError('');
    heldTranscriptTimerRef.current = setTimeout(() => {
      const finalText = heldTranscriptRef.current.trim();
      heldTranscriptRef.current = '';
      heldTranscriptTimerRef.current = null;
      if (!finalText) return;
      sendDesktopTranscript(finalText);
    }, delayMs);
  }, [sendDesktopTranscript, voiceError, stopAriaPlayback, introLocked, isDesktopApp, hasSpokenDesktopIntro, voicePolicy]);

  // Keep the streaming transcriber pointed at the CURRENT send function. Without
  // this the long-lived loop would either crash on an undeclared name (the bug
  // above) or hold a stale closure from whenever the mic happened to open.
  useEffect(() => { queueSendRef.current = queueOrSendDesktopTranscript; }, [queueOrSendDesktopTranscript]);
  useEffect(() => { sendDesktopTranscriptRef.current = sendDesktopTranscript; }, [sendDesktopTranscript]);

  // Flush queued speech once Aria finishes the current reply.
  useEffect(() => {
    if (loading) return;
    const queued = pendingDesktopVoiceRef.current;
    if (!queued.length) return;
    const next = queued.shift();
    if (next) {
      setVoiceError('');
      queueOrSendDesktopTranscript(next);
    }
  }, [loading, queueOrSendDesktopTranscript]);

  useEffect(() => () => {
    if (heldTranscriptTimerRef.current) {
      clearTimeout(heldTranscriptTimerRef.current);
      heldTranscriptTimerRef.current = null;
    }
  }, []);

  const transcribeDesktopBlob = useCallback(async (blob, mime) => {
    // ── THE FALLBACK MUST NOT RUN ALONGSIDE THE REAL ONE (Braeden 8/16) ────
    //
    //   "shes not saying the onboarding road i layed out ... sometimes she
    //    speaks a different laguage"
    //
    // BOTH transcribers were live at once and they disagreed. Measured on the
    // same turns:
    //   streaming WS : "I can."  "What is this? Yes, I can."  "Yes."
    //   this fallback: "guys."  "subscribe to top of set."  "I think you go?"
    // "subscribe to" is Whisper's training artefact when handed non-speech —
    // this path was decoding room noise every couple of seconds and shipping
    // the hallucination to the conversation, so she answered words he never
    // said and walked off the script he wrote.
    //
    // It is meant to be a FALLBACK (the streaming loop drops to it after
    // repeated failures), never a second opinion. While a streaming session is
    // live, this returns nothing.
    // ── THE FALLBACK STANDS DOWN ONLY WHILE STREAMING IS ACTUALLY WORKING ──
    //
    //   "still not working right, didnt pick up when i was tryign to talk"
    //
    // My earlier version muted this whenever a streaming session was OPEN. But
    // "open" is not "working": measured 8/16, the websocket connected, ran
    // twelve seconds, produced NOT ONE partial or final, and closed. With the
    // fallback muted on the strength of that connection, nothing sent at all —
    // I had removed the only path that was still delivering words.
    //
    // So the test is DELIVERY, not connection. If the streaming transcriber has
    // handed us a final recently it owns the mic and this stays quiet; if it has
    // gone silent, the fallback takes over regardless of what its socket claims.
    // Being talked over is a bad day. Being unheard is a broken product.
    // ── TWO TRANSCRIBERS ON ONE MICROPHONE (8/25) ─────────────────────────
    // Braeden: "when i tell it to do something through tts, this keeps
    // happening" - his screenshot shows every sentence posted twice:
    // "settings" / "settings" / "Can you open up settings?" / "Can you open up
    // settings?".
    //
    // The streaming transcriber and this legacy MediaRecorder fallback both
    // listen to the same mic. Only one may deliver, and this gate is what is
    // supposed to keep the fallback quiet - but it measured freshness from
    // lastStreamingFinalAt, which starts at ZERO.
    //
    // So on the FIRST utterance of every session, `now - 0` is thirty years,
    // the gate reads "streaming is dead", and the fallback transcribes and
    // sends the same sentence the streaming path is already sending. Two sends,
    // seconds apart - far enough apart that the duplicate-turn guard in
    // sendMessageWithText (1.5s) never saw them as the same turn. The same hole
    // reopens after any pause longer than 20 seconds, which is most of the way
    // he actually talks to her.
    //
    // A session that just started and has not been spoken to yet still owns the
    // mic. Freshness now counts from the LATER of its start and its last final,
    // so the fallback only takes over when streaming genuinely goes silent
    // mid-session - which is the case it was written for.
    const _streamAlive = Math.max(
      lastStreamingFinalAtRef.current || 0,
      streamingSessionStartedAtRef.current || 0,
    );
    const streamingFresh = streamingSessionActiveRef.current
      && _streamAlive > 0
      && (Date.now() - _streamAlive) < 20000;
    // Closing the microphone flushes the streaming final asynchronously.
    // Keep its ownership briefly so the recorder cannot submit the same clip
    // in the gap between stop() and that final callback.
    if (streamingFresh || Date.now() < streamingReleaseUntilRef.current) return '';
    if (introLockedRef.current || introLocked) { console.log('[TRANSCRIBE] BLOCKED: introLocked'); return ''; }
    if (isDesktopApp && !hasSpokenDesktopIntro) { console.log('[TRANSCRIBE] BLOCKED: introNotSpoken'); return ''; }
    if (isDesktopApp && voicePolicy !== 'name_only' && voicePolicy !== 'free') { console.log('[TRANSCRIBE] BLOCKED: voicePolicy=' + voicePolicy); return ''; }
    const reader = new FileReader();
    const base64 = await new Promise((resolve, reject) => {
      reader.onloadend = () => resolve((reader.result || '').split(',')[1] || '');
      reader.onerror = () => reject(new Error('read failed'));
      reader.readAsDataURL(blob);
    });
    if (!base64) throw new Error('empty');
    if (import.meta.env?.DEV) console.log('[Aria voice] utterance', blob.size, 'bytes');
    const billing = ariaUserIdJsonBody();

    // Electron: POST from renderer to another loopback port is often blocked (Private Network Access / CORS).
    // Main process uses Node fetch — same machine, no browser restrictions.
    if (typeof window !== 'undefined' && window.electronAPI?.transcribeAudio) {
      const ipc = await window.electronAPI.transcribeAudio({
        audio: base64,
        mime,
        ...billing,
      });
      if (!ipc) throw new Error('failed to fetch');
      if (ipc.error === 'network') {
        throw new Error(`failed to fetch: ${ipc.message || ''}`);
      }
      const data = ipc.data || {};
      if (!ipc.ok) {
        if (data?.error === 'USER_ID_REQUIRED') {
          throw new Error(`USER_ID_REQUIRED: ${data.message || 'Pass userId or set ALLOW_ANONYMOUS_AI=1 on the backend for local dev.'}`);
        }
        if (data?.error === 'OUT_OF_CREDITS') {
          throw new Error(`OUT_OF_CREDITS: ${data.message || 'No AI usage left this period.'}`);
        }
        if (data?.error === 'TRANSCRIBE_NOT_AVAILABLE') {
          throw new Error(`TRANSCRIBE_NOT_AVAILABLE: ${data.message || ''}`);
        }
        throw new Error(
          data?.error ? `${data.error}: ${data.message || ''}`.trim() : (data?.message || data?.error || 'Request failed'),
        );
      }
      // Return the WHOLE verdict, not just text. The server runs the ECAPA
      // voiceprint gate ("VOICEPRINT IS THE GATE") and whisper's own speech
      // confidence — returning only .text threw both away, which is how
      // Aria ended up answering Spotify. (2026-07-15)
      //
      // 2026-08-03 — the same bug, one layer deeper. The server also returns
      // emotion / prosody / pace / voice / emotionRead / ttsSpeed, and this
      // object kept three fields and dropped all six. So on every turn that is
      // NOT the WebSocket streaming path — the MediaRecorder fallback (used
      // after 3 streaming failures), Electron IPC, and iOS — Aria went
      // acoustically deaf, silently, with no log line saying so. A live POST of
      // a real clip came back emotionRead:{dominant:{angry:0.994},
      // register:'firm_calm', responseLean:'acknowledge_first'} and none of it
      // survived this return. Braeden's requirement is that she hear pitch,
      // volume and pace; the data was arriving and being binned right here.
      return {
        text: (data && data.ok && data.text) ? String(data.text).trim() : '',
        gate: (data && data.gate) || null,
        speech: (data && data.speech) || null,
        read: buildVoiceRead(data),
      };
    }

    const base = (getApiBase() || API_BASE || '').replace(/\/$/, '');
    const url = base ? `${base}/api/speech/transcribe` : '/api/speech/transcribe';
    const r = await fetchWithTimeout(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audio: base64, mime, ...billing }),
      timeout: 30000,
    });
    const data = await r.json();
    if (!r.ok) {
      if (data?.error === 'USER_ID_REQUIRED') {
        throw new Error(`USER_ID_REQUIRED: ${data.message || 'Pass userId or set ALLOW_ANONYMOUS_AI=1 on the backend for local dev.'}`);
      }
      if (data?.error === 'OUT_OF_CREDITS') {
        throw new Error(`OUT_OF_CREDITS: ${data.message || 'No AI usage left this period.'}`);
      }
      if (data?.error === 'TRANSCRIBE_NOT_AVAILABLE') {
        throw new Error(`TRANSCRIBE_NOT_AVAILABLE: ${data.message || ''}`);
      }
      throw new Error(
        data?.error ? `${data.error}: ${data.message || ''}`.trim() : (data?.message || data?.error || 'Request failed'),
      );
    }
    return {
      text: (data && data.ok && data.text) ? String(data.text).trim() : '',
      gate: (data && data.gate) || null,
      speech: (data && data.speech) || null,
      read: buildVoiceRead(data),   // see the note on the other return site
    };
  }, [introLocked, isDesktopApp, hasSpokenDesktopIntro, voicePolicy]);


  // ── Voice capture (VAD, mic level, Web Speech fallback, auto-retry) ──────────
  const {
    voiceRecording,
    voiceListening,
    liveTranscript, setLiveTranscript,
    activeMicLabel,
    lastMicPeak, setLastMicPeak,
    voiceProfileEnrolled, setVoiceProfileEnrolled,
    voiceAdaptationCount, setVoiceAdaptationCount,
    voiceMatchStrict, setVoiceMatchStrict,
    voiceEnrollProgress, setVoiceEnrollProgress,
    micSpeaking,
    vadCanRun,
    recognitionRef,
  } = useVoiceCapture({
    isDesktopApp, effectiveSection, micOn, setMicOn, voiceEnrolling, introLocked, introLockedRef,
    hasSpokenDesktopIntro, voicePolicy, setVoicePolicy, getAudioConstraints,
    ariaSpeakingRef, ariaAttentionRef, voiceProfileGateRef, ttsPostPlaybackUntilRef,
    awaitingIntroNameRef, proactivePausedRef, preferredNameRef, breakIntervalMsRef,
    micBtnRef, sendMessageWithTextRef,
    transcribeDesktopBlob, queueOrSendDesktopTranscript, stopAriaPlayback,
    // SHE ASKED SOMETHING AND IS WAITING. The cost gate must not bin the
    // answer to her own question — see the bypass in useVoiceCapture.
    expectingAnswerRef: firstRunRef,
    // Non-streaming turns (MediaRecorder fallback, Electron, iOS) now hand their
    // acoustic read up here, the same way streamingTranscribe's onFinal does.
    // Without this the read was computed server-side and binned client-side.
    onVoiceRead: (read) => { if (read) lastVoiceReadRef.current = read; },
    micInsecureContextMessage, microphoneDeniedMessage,
    isDev, setInput, showLiveInBubble, unlockAudioForReply,
    syncVoiceProfileGate, resetAttentionTimer,
    voiceError, setVoiceError,
  });

  // ── THE RING HAS TO TELL THE TRUTH (Braeden 9/6) ────────────────────────
  //
  // "its really buggy when i talk to it ... it just needs to be accurate with
  //  communicating what its actually doing, if it can hear me, think, or
  //  whatever its doing it just seems off"
  //
  // He is right, and the reason is exact: ring-overlay.html is built to draw
  // two LIVE signals - `mic` (how loud HE is) and `level` (how loud SHE is) -
  // and the only ringOverlay() call in this file sends `{ show, state }`. So
  // both arrive as 0 on every frame, and the ring falls back to what it does
  // with no data: a canned wave while "listening" that never reacts to his
  // voice, and a synthetic sine while "speaking" unrelated to her actual
  // speech. It was animation, not telemetry - it looked identical whether it
  // could hear him or not.
  //
  // Everything needed was already measured a few lines above and never left
  // the component: micSpeaking (his voice detected), lastMicPeak (how loud),
  // voiceListening (the microphone really opened).
  //
  // Sent on a 100ms tick rather than a React dependency, because the ring is a
  // 60fps canvas and re-rendering App on every audio frame would cost far more
  // than the IPC does.
  const _ringSigRef = useRef('');
  // Last state reported by the STREAMING transcriber ('listening' | 'muted' |
  // 'stopped' | 'requesting-mic' | 'connecting-whisper' | 'closed'). Written in
  // its onStateChange. Null until the streaming path starts.
  //
  // NOTHING READS THIS ANY MORE (9/7). It existed for the ring tick's
  // `micReallyOpen` test, which was removed when the ring's state machine was
  // made identical to the website's — see the block in that tick. Kept because
  // the write is free and this is a genuinely useful signal to have on hand,
  // but do not assume it is driving anything: grep before you trust it.
  const streamMicStateRef = useRef(null);
  const assistVisualRef = useRef({ speaking: false, thinking: false });
  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.ringOverlay) return;
    const tick = () => {
      const show = !assistAsk && (assistCompactLayout || !!screenStream || screenShareRequesting);
      // ── THE STATE IS DECIDED THE WAY THE WEBSITE DECIDES IT (2026-09-07) ─
      // Braeden: "animation of the circle still isnt right, it needs to match
      // when im talking, tell me when its thinking, when its talking JUST LIKE
      // THE WEBSITE ONE."
      //
      // The drawing and the motion constants were already provably identical
      // (check_ring_parity.js). What was never compared is THIS: how each side
      // decides which state it is in. The website's orb uses four expressions
      // and nothing else (App.jsx's own <TalkingBall> mounts):
      //
      //     speaking  = ariaSpeaking
      //     thinking  = !ariaSpeaking && (loading || preparingSpeech)
      //     listening = !ariaSpeaking && !loading && !preparingSpeech && micOn
      //     muted     = !micOn
      //
      // This effect had grown two extra rungs the website has never had - a
      // `micReallyOpen` test built from streamMicStateRef / voiceListening, and
      // a `!show` rung - and `!micReallyOpen` painted MUTED while the ring was
      // on screen. That is not a theory: the ring trace in main.js recorded
      // `show=true state=muted` SIXTEEN times across his real sessions, a state
      // the website's assist ring cannot reach at all (muted is `!micOn`, and
      // micOn must be true for the ring to be shown in the first place).
      //
      // And painting muted does not just look wrong, it SILENCES HIS VOICE: the
      // overlay draws `rawMic = listening ? cur.mic : 0`, so the moment the
      // state went muted his amplitude was dropped to zero and the lights
      // stopped following him — exactly "it doesn't match when I'm talking",
      // while the mic itself was working the whole time (the same trace shows
      // live values, 0.72 / 0.34 / 0.20, arriving right next to those rows).
      //
      // Losing the mic-is-dead warning is the right trade, not just the
      // requested one. `mic` is a LIVE amplitude now, so a genuinely dead
      // microphone shows as a ring that does not move — which says the same
      // thing honestly. The old rung had it backwards: it made a WORKING mic
      // look dead, which is the one failure a status light must never have.
      //
      // `show` stays exactly as it was and still carries the real mute: micOn
      // false hides the overlay entirely and brings the chat box back.
      const state = !micOn ? 'muted'
        : (screenStream ? assistVisualRef.current.speaking : ariaSpeaking) ? 'speaking'
          : (screenStream ? assistVisualRef.current.thinking : (loading || preparingSpeech)) ? 'thinking'
            : 'listening';
      // His voice, scaled off the same threshold the speech detector uses, so
      // the lights move when and only when it can genuinely hear him.
      // ── THE RING WAS READING A DEAD MIC (2026-09-07) ────────────────────
      // Braeden: "when im in assist mode, it still doesnt do any kind of
      // animation like it should."
      //
      // `lastMicPeak` comes from useVoiceCapture — the LEGACY mic path, which
      // explicitly stands down the moment the streaming transcriber takes the
      // mic (streamingSessionActiveRef). Assist mode is exactly when that
      // happens, so lastMicPeak sits at 0, the ring receives mic:0, and the
      // outer ring renders as a steady lit circle with NOTHING MOVING.
      // Same shape as the muted-circle bug above: the ring was reading a
      // signal belonging to the path that is not running.
      //
      // ariaAudioBus.micLevel() is the live one — streamingTranscribe.js
      // feeds it via pushMicPcm on every 100ms PCM chunk, already scaled 0..1
      // with a compressive curve and its own attack/decay, so it needs no
      // threshold division. lastMicPeak stays as the fallback for the
      // non-streaming path, where it is the signal that does work.
      // ── AND HIS LEVEL IS READ THE WAY THE WEBSITE READS IT (9/7) ────────
      // TalkingBall.jsx:95 is one line: `rawMic = listening ? micLevel() : 0`.
      // No threshold, no substitution — whatever the bus says is what the
      // lights do.
      //
      // This had a CLIFF AT 0.01: any bus value at or below it was thrown away
      // and replaced with lastMicPeak, which belongs to the legacy capture hook
      // and reads 0 for the whole of Assist (it stands down when the streaming
      // transcriber owns the mic). So ordinary quiet speech — the start of a
      // word, the end of a sentence, anyone talking normally rather than
      // leaning into the mic — was swapped for a dead signal, and the ring sat
      // still through it. Loud syllables still registered, which is what made
      // it read as "sometimes it moves, mostly it doesn't" instead of as an
      // obviously dead ring.
      //
      // The bus wins whenever it is producing anything at all. The legacy peak
      // stays as the fallback for the non-streaming path, where it is the
      // signal that genuinely works and the bus is the dead one.
      const busMic = Number(busMicLevel()) || 0;
      const legacyPeak = Number(lastMicPeak) || 0;
      const mic = state === 'listening'
        ? (busMic > 0
            ? Math.max(0, Math.min(1, busMic))
            : Math.max(0, Math.min(1, legacyPeak / (MIC_SPEAKING_RMS_THRESHOLD * 6))))
        : 0;
      // ── HER SIDE OF THE RING IS REAL NOW (2026-09-07) ──────────────────
      // This used to send a literal 0, so while she SPOKE the ring had no
      // amplitude to draw and fell back to a synthetic sine in
      // ring-overlay.html — it pulsed to a rhythm that had nothing to do with
      // her voice. That is the "animation is wrong" half of the complaint.
      //
      // The honest signal is already here: the edge voice returns real
      // per-word times (X-Aria-Words) and speechClockRef holds them as
      // [[word, ms], ...] with the moment playback started. Distance to the
      // nearest word onset gives a pulse ON EACH WORD SHE SAYS.
      //
      // Deliberately NOT a WebAudio AnalyserNode: attaching one routes her
      // audio through a graph, an element can only ever have one
      // MediaElementSource, and a mistake there makes her SILENT. A wrong
      // pulse is cosmetic; a silent Aria is not.
      // ── FED FROM THE SAME PLACE AS THE WEBSITE'S CIRCLE (2026-09-07) ──
      // Braeden: "the aria circle in the WEBSITE is perfect example, they all
      // need to look like the one in the website, IDENTICAL."
      //
      // They already ARE identical to draw: ring-overlay.html and
      // TalkingBall.jsx have the same LED counts (132/108), the same radii
      // (0.845 / 0.655 of R), the same colours and the same dot sizes. What
      // differed was the DATA. The website's circle reads the audio bus
      // directly, in-process, every frame:
      //     const real = isLive() ? busLevel() : 0;      (TalkingBall.jsx:88)
      // while the overlay was fed a value computed here — first a literal 0,
      // then my word-clock approximation. Same picture, different truth.
      //
      // So it now reads the exact same source. One place decides how loud she
      // is, and both circles draw it.
      const level = busIsLive() ? Math.max(0, Math.min(1, Number(busLevel()) || 0)) : 0;

      // `level` is in the signature and 'speaking' is exempt from the
      // dedupe for the same reason 'listening' is: both carry a LIVE value
      // that changes every tick, and without this the first frame of her
      // speech would be the only one ever sent (9/7).
      const sig = `${show}|${state}|${Math.round(mic * 20)}|${Math.round(level * 20)}`;
      if (sig === _ringSigRef.current && state !== 'listening' && state !== 'speaking') return;
      _ringSigRef.current = sig;
      try { window.electronAPI.ringOverlay({ show, state, mic, level }); } catch (_) {}
    };
    // ── IT MUST ANIMATE LIKE THE WEBSITE, NOT JUST LOOK LIKE IT ──────────
    // Braeden 9/7: "i said identical, not only how it looks, but how it
    // animates to ... litterally identical."
    //
    // The drawing is provably identical now (check_ring_parity.js: 34 canvas
    // operations each) and every animation constant matches - same clamped dt,
    // same spin rates 3.30/0.24, same smoothing k 25.8/6.3/35.9/7.7/21.4.
    // What was still different was the INPUT RATE. TalkingBall reads the audio
    // bus in-process on EVERY FRAME (240/s on his monitor); this shipped the
    // same numbers over IPC every 100ms - ten samples a second. Identical maths
    // fed a 10Hz staircase does not move like the same maths fed a smooth
    // curve: the smoothing attack constant is ~28ms, far shorter than a 100ms
    // gap, so every new value landed as a visible step.
    //
    // 16ms puts the input well under that smoothing constant, so the rendered
    // curve is the one the website draws. Deliberately NOT rAF: the overlay is
    // a SEPARATE PROCESS and cannot share the in-process bus, and 240 IPC
    // messages a second to move a 300px ring is waste, not fidelity. The
    // dedupe above still suppresses sends when nothing has actually changed.
    const id = setInterval(tick, 16);
    tick();
    return () => clearInterval(id);
  }, [isDesktopApp, assistCompactLayout, screenStream, assistAsk, screenShareRequesting, micOn, ariaSpeaking, loading, preparingSpeech, voiceListening, lastMicPeak]);

  // ── Audio ducking: smooth suppression during entire conversation ──
  useAudioDucking({ ariaSpeaking, micSpeaking, loading });

  // ── EXPERIMENTAL: Streaming transcribe (Phase B) ─────────────────────
  // Opt-in via localStorage flag. When on, MediaRecorder/VAD path is
  // bypassed and audio streams continuously to Whisper which acts as
  // both speech detector + transcriber. Off by default — flip with:
  //   localStorage.setItem('aria.streamingTranscribe', '1');
  useEffect(() => {
    // Whisper-first streaming: works on BOTH web (localhost:5173) and desktop.
    // Disable explicitly with: localStorage.setItem('aria.streamingTranscribe', '0')
    let on; try {
      const v = localStorage.getItem('aria.streamingTranscribe');
      on = v == null ? true : v === '1';
    } catch (_) { on = true; }
    if (!on) return;
    if (!micOn) return;
    if (introLocked || introLockedRef.current) return;
    if (effectiveSection !== 'ask') return;

    console.log('[streaming-transcribe] starting…');
    let session = null;
    let cancelled = false;

    const launch = async () => {
      if (!voiceTrace.enabled()) voiceTrace.start();
      // Cap consecutive failures. After 3 in a row we GIVE UP on streaming
      // entirely so the legacy MediaRecorder/HTTP path can have the mic.
      // The 1-second retry loop was hogging the mic and starving the fallback,
      // making Aria appear deaf when streaming fails (AudioWorklet errors etc.).
      let consecutiveFailures = 0;
      const MAX_FAILURES = 3;
      // THE LATENCY WATERFALL (9/26): a mic-less way to drive the SAME final
      // handler from the console, so the client -> backend -> TTS -> first-audio
      // hops can be measured in a browser with no microphone. Off unless
      // localStorage aria_voice_sim === '1'; produces exactly what the ear's
      // final would (a turn id, a voice read) and nothing else.
      const _simFinal = { fn: null };
      try {
        if (typeof window !== 'undefined' && localStorage.getItem('aria_voice_sim') === '1') {
          window.__ariaVoiceSim = (text, earTiming) => {
            const fn = _simFinal.fn;
            if (typeof fn !== 'function') return false;
            fn({ text: String(text || ''), read: { emotion: {}, prosody: {}, quality: 0, pitch_hz: null, energy: null, intonation: '', pace: null,
              turnId: 'vt' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7), earTiming: earTiming || null, finalReceivedAt: Date.now(), sim: true } });
            return true;
          };
        }
      } catch (_) {}
      while (!cancelled && micOn) {
        try {
          session = await startStreamingTranscribe({
            shouldStayMuted: () => floorHeldRef.current || ariaSpeakingRef.current,
            isFloorHeld: () => floorHeldRef.current,
            onPartial: (text) => { if (cancelled || !micOnRef.current) return; try { showLiveInBubble(text); setLiveTranscript(text); } catch (_) {} },
            onFinal: (_simFinal.fn = ({ text, read } = {}) => {
              // Closing/muting can flush a late transcript from the old socket.
              // It must not become a new conversation turn after cancellation.
              if (cancelled || !micOnRef.current) return;
              voiceTrace.record('transcript.received', { chars: String(text || '').trim().length, floor: floorHeldRef.current, speaking: !!ariaSpeakingRef.current });
              console.log('[streaming-transcribe] final:', text);
              // proof of life for the fallback gate above
              lastStreamingFinalAtRef.current = Date.now();
              lastVoiceFinalPerfRef.current = typeof performance !== 'undefined' ? performance.now() : Date.now();
              try { setLiveTranscript(''); } catch (_) {}
              if (read) lastVoiceReadRef.current = read;
              if (!text || text.trim().length < 2) voiceTrace.record('transcript.disposition', { reason: 'short_final' });
              if (text && text.trim().length >= 2) {
                // ── THIS LINE IS WHY SHE NEVER ANSWERED (Braeden 8/16) ─────
                //
                //   "im talking to her and no response after the first time
                //    she asks can you hear me"
                //
                // It used to read `queueOrSendDesktopTranscriptRef.current?.()`
                // — and that ref is declared in hooks/useVoiceCapture.js, NOT
                // here. In this scope the identifier does not exist, so every
                // final threw a ReferenceError inside a callback with nothing
                // to catch or report it. The optional-chaining `?.` made it
                // look defensive while the crash was on the line BEFORE the
                // call: you cannot `?.` your way out of an undeclared name.
                //
                // So the streaming transcriber has never delivered a single
                // word. The only reason voice ever worked at all was the legacy
                // fallback running in parallel — which is why silencing that
                // (correctly, it was hallucinating "subscribe to top of set")
                // made her go completely mute.
                //
                // App.jsx owns this ref, kept fresh by the effect below, so the
                // long-lived streaming loop can never call a stale closure.
                const send = queueSendRef.current;
                if (typeof send === 'function') send(text.trim());
                else {
                  voiceTrace.record('transcript.disposition', { reason: 'no_queue_fn' });
                  try {
                    fetch('/api/pipeline/event', { method: 'POST', headers: { 'content-type': 'application/json' },
                      body: JSON.stringify({ stage: 'voice.dropped', level: 'warn',
                        text: `dropped: "${text.trim().slice(0, 40)}"`, meta: { why: 'no_send_fn' } }) }).catch(() => {});
                  } catch (_) {}
                }
              }
            }),
            onError: (err) => { console.warn('[streaming-transcribe] error:', err.message); },
            // ── THE RING WAS READING THE WRONG MIC (2026-09-07) ────────────
            // Braeden: "the circle is muted while i try to talk to it."
            //
            // These states went to console.log and nowhere else. The ring
            // decides muted-vs-listening from `voiceListening`, which belongs
            // to useVoiceCapture — the LEGACY path, which explicitly "stands
            // down while this is true" (see streamingSessionActiveRef right
            // below). So the moment the streaming transcriber took the mic,
            // voiceListening went false and the ring painted MUTED on a mic
            // that was wide open and listening.
            //
            // A ref, not state: the ring already ticks at 100ms and the
            // comment above it is explicit that re-rendering App on audio
            // frames costs more than the IPC. Transitions are rare, but a ref
            // keeps this free either way.
            onStateChange: (s) => {
              voiceTrace.record('mic.ui-state', { reason: s });
              streamMicStateRef.current = s;
              console.log('[streaming-transcribe] state:', s);
            },
          });
          // The floor mutes the transcriber at the source while she talks.
          micSessionRef.current = session;
          // Success path — reset failure counter
          consecutiveFailures = 0;
          // the streaming transcriber owns the mic from here; the legacy
          // MediaRecorder path stands down while this is true
          streamingSessionActiveRef.current = true;
          streamingSessionStartedAtRef.current = Date.now();
          // Published so the effect below can mute it the moment Aria starts
          // talking. Without this the open mic hears her through the speakers,
          // finalizes HER question as HIS turn, and kills the socket she is
          // going to need one second later when he actually answers.
          streamingSessionRef.current = session;
          // Match the gate to reality on arrival: if she is mid-sentence when
          // this session comes up (she is — the mic opens as her line ends),
          // it must start muted rather than wait for the next transition.
          try { session.setMuted(floorHeldRef.current || !!ariaSpeakingRef.current); } catch (_) {}
          // Wait for the session to END — the whole session, not one sentence.
          // This used to fire after every utterance, because a final tore the
          // capture stack down; the loop below then rebuilt getUserMedia + the
          // AudioContext + the worklet before he could say anything. The
          // session now survives its sockets, so this resolves only on a real
          // stop: mic closed, section changed, or whisper unreachable.
          await new Promise((r) => {
            const tick = () => { if (cancelled || !session.isActive()) r(); else setTimeout(tick, 200); };
            tick();
          });
          if (streamingSessionRef.current === session) streamingSessionRef.current = null;
          session = null;
          // deliberately NOT clearing streamingSessionActiveRef here: the loop
          // restarts on the next line, and the legacy recorder never stops
          // capturing, so releasing between turns hands it a window to post
          // room noise. The flag is held for as long as this loop owns the mic
          // and released only on give-up or teardown.
          // Loop: immediately start next turn
        } catch (e) {
          consecutiveFailures++;
          // hand the mic back to the fallback — this is the case it exists for
          streamingSessionActiveRef.current = false;
          console.warn('[streaming-transcribe] launch failed (' + consecutiveFailures + '/' + MAX_FAILURES + '):', e.message);
          if (consecutiveFailures >= MAX_FAILURES) {
            streamingSessionActiveRef.current = false;   // the fallback takes over now
            console.warn('[streaming-transcribe] giving up — legacy MediaRecorder path will handle voice');
            try {
              fetch('http://127.0.0.1:3001/api/pipeline/event', {
                method: 'POST', headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ stage: 'streaming.gaveup', level: 'warn',
                  text: 'streaming WS failed ' + MAX_FAILURES + ' times — falling back to legacy MediaRecorder path',
                  meta: { lastError: e.message } }),
              }).catch(() => {});
            } catch (_) {}
            break;  // exit the while loop entirely
          }
          // Back off progressively: 5s, 15s, 30s. Gives the mic to legacy path
          // in between so something can capture audio.
          const backoffMs = [5000, 15000, 30000][consecutiveFailures - 1] || 30000;
          await new Promise(r => setTimeout(r, backoffMs));
        }
      }
    };
    launch();

    return () => {
      cancelled = true;
      // Release the mic claim on teardown (mic closed, section changed). If this
      // is missed the flag stays true after the streaming loop is gone and the
      // legacy fallback stays muted forever — the opposite failure, and just as
      // silent.
      if (streamingSessionActiveRef.current) streamingReleaseUntilRef.current = Date.now() + 2000;
      streamingSessionActiveRef.current = false;
      streamingSessionRef.current = null;
      if (session && session.stop) session.stop();
    };
  }, [micOn, effectiveSection, introLocked]);

  // ── SHE STOPS LISTENING WHILE SHE TALKS, AND STARTS AGAIN INSTANTLY ─────
  //
  //   "after pressing the button adn listening to arias question the mic
  //    doesnt work and it needs to work instantly"
  //
  // Muting drops her own voice at the worklet so it never becomes a turn.
  // Unmuting opens a fresh socket the moment her last word lands — which is
  // the moment he starts talking, so it is already listening when he does.
  // Nothing here rebuilds the microphone; that is the entire point.
  useEffect(() => {
    const s = streamingSessionRef.current;
    if (!s || typeof s.setMuted !== 'function') return;
    try { s.setMuted(floorHeldRef.current || !!ariaSpeaking); } catch (_) {}
  }, [ariaSpeaking]);

  // ── A LEAKED ariaSpeaking MUST BE REAPED ON A TIMER (2026-09-07) ────────
  // There IS already a stuck-flag check — inside queueOrSendDesktopTranscript,
  // where it fires when a transcript arrives. It can never run when it is
  // needed most, because it is gated behind the very event the bug prevents:
  //
  //   ariaSpeaking sticks true -> the effect above mutes the session ->
  //   setMuted(true) RETIRES the socket -> no audio is sent -> no transcript
  //   ever arrives -> the reaper that would clear the flag never runs.
  //
  // A deadlock, and the only thing that broke it was streamingTranscribe's
  // 12s MUTE_CEILING watchdog forcing the mic back on — which unmutes the
  // session but does NOT clear this flag, so the next render re-mutes it and
  // the ring keeps showing the mute logo while he is talking.
  //
  // So: a timer that owes nothing to the audio path. Same 15s reasoning as
  // the original check ("no clip she owns is longer than ~12s, so a flag
  // still set after 15 is not speech, it is a leak"), and it unmutes the
  // session itself rather than waiting for the next state change to do it.
  useEffect(() => {
    const id = setInterval(() => {
      try {
        // Assist's lease tracks real playback; the main bus cannot judge it.
        if (voiceTurnRef.current.current('assist')) return;
        // ── STUCK "THINKING" ORB (9/7) ────────────────────────────────
        // preparingSpeech gates the thinking ring:
        //   thinking = !ariaSpeaking && (loading || preparingSpeech)
        // Verified live through the real UI: asked "what is the capital of
        // france", got the right answer rendered, and the orb STAYED on
        // THINKING afterwards. loading had cleared, so preparingSpeech was the
        // leak. It is set in one place and cleared in eight, every one of them
        // inside the TTS/audio path.
        // Nothing she says takes 12s to synthesise (edge is ~1s warm, ~5s
        // cold), so past that it is not preparation, it is a leaked flag.
        try {
          const pSince = preparingSinceRef.current || 0;
          if (pSince && !ariaSpeakingRef.current && Date.now() - pSince > 12000) {
            console.warn('[orb] preparingSpeech leaked for', Date.now() - pSince, 'ms — clearing the stuck THINKING ring');
            setPreparingSpeech(false);
          }
        } catch (_) { /* a watchdog must never throw */ }

        if (!ariaSpeakingRef.current) return;
        const since = ariaSpeakingSinceRef.current || 0;
        if (!since) return;
        const heldMs = Date.now() - since;
        // TWO WAYS TO KNOW THE FLAG HAS LEAKED, and the first is much faster:
        //  * the audio bus analyser is not live — nothing is playing, so she
        //    is provably not speaking. Measured 9/7: the ring trace showed
        //    `state=speaking` held for seconds with level pinned at the floor
        //    while nothing was playing at all.
        //  * or the 15s ceiling from the original check ("no clip she owns is
        //    longer than ~12s"), as a backstop for when the bus never attached.
        let live = false;
        try { live = !!busIsLive(); } catch (_) { live = false; }
        const leaked = (!live && heldMs > 2500) || heldMs > 15000;
        if (!leaked) return;
        console.warn('[ring] ariaSpeaking leaked for', heldMs, 'ms (analyserLive=' + live + ') — clearing and reopening the mic');
        ariaSpeakingRef.current = false;
        setAriaSpeaking(false);
        const s = streamingSessionRef.current;
        if (s && typeof s.setMuted === 'function') s.setMuted(floorHeldRef.current);
      } catch (_) { /* a watchdog must never throw */ }
    }, 1000);
    return () => clearInterval(id);
  }, []);





  useEffect(() => {
    if (!isDesktopApp) return;
    window.electronAPI.onFullScreenChange((full) => {
      setIsFullScreen(!!full);
    });
  }, [isDesktopApp]);

  const requestScreenShare = async (approved = false) => {
    if (isDesktopApp && approved !== true && !hasRememberedAssist()) {
      setAssistAsk({ task: '', steps: [], inspectFirst: true });
      return null;
    }
    console.log('[Assist] requestScreenShare called, inFlight:', screenShareInFlightRef.current);
    if (screenShareInFlightRef.current) return null;
    screenShareInFlightRef.current = true;
    try { fetch(`${getApiBase() || API_BASE}/api/screen/prewarm`, { method: 'POST' }).catch(() => {}); } catch (_) {}
    setScreenShareRequesting(true);
    setScreenShareError('');
    try {
      // Log screen capture status for debugging, but always attempt getDisplayMedia.
      // macOS 26 often reports 'denied' even after a TCC reset or when status is actually
      // 'not-determined'. The desktopCapturer retry logic in main.js handles the TCC race
      // condition. If capture truly fails, the catch block below will guide the user.
      if (window.electronAPI?.getScreenCaptureStatus) {
        const status = await window.electronAPI.getScreenCaptureStatus();
        const s = String(status || '').toLowerCase();
        console.log('[Assist] screen capture pre-check:', s, '(proceeding regardless)');
      }
      // In desktop app we use minimal constraints; main process provides primary screen
      const opts = window.electronAPI
        ? { video: true, audio: false }
        : {
            video: {
              displaySurface: 'monitor',
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
            audio: false,
          };
      console.log('[Assist] calling getDisplayMedia with opts:', JSON.stringify(opts));
      // Single getDisplayMedia call — in Electron the main process picks the source via
      // setDisplayMediaRequestHandler so no constraint fallback is needed, and a double
      // call would trigger two full retry cycles (each up to 2 s) on macOS 26.
      const stream = await navigator.mediaDevices.getDisplayMedia(opts);
      console.log('[Assist] getDisplayMedia succeeded, tracks:', stream?.getTracks()?.length);
      setScreenStream(stream);
      // THE RING IS THE FIRST THING (Braeden 9/5). Assist has begun, so the
      // mic opens and the ring is what he lands on - the chat box is what
      // muting brings up. Same rule as the desktop's compact layout.
      try {
        setVoiceError('');
        setLastInputMode('voice');
        if (!micUserMutedRef.current) setMicOn(true);
      } catch (_) {}
      // Successful capture is a stronger signal than getMediaAccessStatus('screen').
      markScreenAccessConfirmed();
      setScreenPermissionPromptNeeded(false);
      if (window.electronAPI?.screenShareStarted) await window.electronAPI.screenShareStarted();
      if (isDesktopApp && (approved === true || hasRememberedAssist())) await window.electronAPI?.assistConsent?.(true);
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onended = () => {
          stream.getTracks().forEach((t) => t.stop());
          setScreenStream(null);
          setScreenShareError('');
          try {
            window.electronAPI?.screenShareEnded?.();
          } catch (_) {}
        };
      }
      return stream;
    } catch (e) {
      console.error('[Assist] getDisplayMedia FAILED:', e?.name, e?.message, e);
      const message = (e && e.message) ? String(e.message) : 'Screen share failed';
      const norm = `${String(e?.name || '')} ${message}`.toLowerCase();
      const likelyPermissionIssue =
        norm.includes('notallowederror') ||
        norm.includes('permission denied') ||
        norm.includes('permission') ||
        norm.includes('denied');
      if (isDesktopApp && likelyPermissionIssue) {
        setScreenPermissionPromptNeeded(true);
        // Open System Settings so the user can grant the permission
        window.electronAPI?.openScreenRecordingSettings?.();
      }
      const helpMessage = window.electronAPI
        ? (desktopPlatform === 'win32'
          ? `${message}. If nothing happened, check Windows Settings → Privacy & security for screen capture, then try Allow again.`
          : `${message}. If nothing happened, allow Screen Recording for Aria in System Settings → Privacy & Security → Screen Recording, then click Allow again.`)
        : message;
      setScreenShareError(helpMessage);
      // Error is already shown in the Assist panel — don't also spam it into the chat.
      return null;
    } finally {
      screenShareInFlightRef.current = false;
      setScreenShareRequesting(false);
    }
  };

  // Speak a short line in Aria's voice via the backend /tts endpoint. Best-
  // effort (never throws) — used for local, no-LLM replies (e.g. the Assist
  // confirm/decline) so voice users still HEAR her, not just see text.
  // What she says while she is still working. Short, varied so it does not
  // sound like a recording, and each one PROMISES the answer rather than just
  // filling air — "one sec" alone is a stall, "one sec, getting you the real
  // number" is a person working.
  const HOLD_LINES = [
    'Give me a few seconds — I want to get you the real answer, not a guess.',
    'One second, let me check that properly.',
    'Hang on, I am looking that up now.',
    'Give me five seconds and I will have it.',
  ];

  // ── WHEN A LINE STARTED AND HOW LONG IT RUNS (Braeden 9/2) ─────────────
  // "when she's talking, the timing has to be right." A ring, a card, or a
  // typed title must land on the PHRASE she is saying, not at the start of
  // the sentence. The instant pack knows its clip length up front; the
  // network path learns it from the audio's metadata. `whenSaid` schedules
  // a callback for the fraction of the line where the phrase sits, a beat
  // early so the eye leads the ear; with no clock it estimates from length.
  const speechClockRef = useRef({ line: '', startedAt: 0, ms: 0 });
  // how long after the word a ring or card lands (was 150ms BEFORE it, 9/5)
  const TOUR_EASE_MS = 450;
  const whenSaid = (line, phrase, cb, fallbackMs = 0) => {
    try {
      const L = String(line || ''); const P = String(phrase || '');
      const i = P ? L.toLowerCase().indexOf(P.toLowerCase()) : -1;
      if (i < 0 || !L.length) { setTimeout(cb, fallbackMs); return; }
      const clock = speechClockRef.current;
      const known = clock.line === L && clock.ms > 0;
      const total = known ? clock.ms : Math.min(12000, Math.max(600, L.length * 62));
      const elapsed = known ? Math.max(0, Date.now() - clock.startedAt) : 0;
      // EASE IN, DON'T SNAP (Braeden 9/5): "she can wait a second and ease
      // into it so it feels more natural instead of being so rushed and
      // tense." This used to land the ring 150ms BEFORE the word - the eye
      // leading the ear reads as her jumping the gun. Now it lands a beat
      // after the word has been said, once the ear has had it.
      const at = Math.max(0, (i / L.length) * total + TOUR_EASE_MS - elapsed);
      setTimeout(cb, Math.min(15000, at));
    } catch (_) { try { cb(); } catch (__) {} }
  };
  const speakAriaLine = async (line, opts = {}) => {
    // ── ALREADY DECODED? THEN SPEAK NOW. ──────────────────────────────────
    // Every line she owns is decoded once at boot, so this path costs a
    // BufferSource.start() - under a millisecond, against 20-100ms for the
    // fetch-and-decode below. It returns false for anything not in the pack,
    // so an unbanked line falls straight through to /tts unchanged.
    try {
      let rate;
      try { rate = parseFloat(localStorage.getItem('aria_voice_speed') || ''); } catch (_) {}
      const _ms = instantVoice.say(line, {
        rate,
        onended: () => { ariaSpeakingRef.current = false; setAriaSpeaking(false); },
      });
      if (_ms) {
        // the clip's real length: rings, cards and typing time themselves to it (9/2)
        speechClockRef.current = { line: String(line || ''), startedAt: Date.now(), ms: Number(_ms) || 0 };
        ariaSpeakingRef.current = true;
        setAriaSpeaking(true);
        noteSpeechAttempt(); noteSpeechPlayed();
        // The repeat path ("what?") reads lastSpokenLineRef, so remembering it
        // HERE is what makes a repeat work on the instant path. There is no
        // server round-trip: /tts/spoken does not exist — I nearly shipped a
        // call to it, which would have fired a 404 on every spoken line while
        // looking like it worked, because the .catch swallows everything.
        lastSpokenLineRef.current = String(line || '');
        return;
      }
    } catch (_) { /* fall through to the network path */ }

    try {
      // userId → the pace mirror actually applies; track → ariaSpokenState
      // remembers she said this (so "what did you say?" can repeat it);
      // role → per-role edge prosody (e.g. 'repeat' = slower re-say).
      const r = await fetch(`${API_BASE}/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: String(line || ''),
          ...ariaUserIdJsonBody(),
          ...(opts.role ? { role: opts.role } : {}),
          ...(opts.track ? { track: true } : {}),
        }),
      });
      // EVERY EXIT BELOW USED TO BE SILENT. Five of them, and each one left her
      // believing she had spoken — so she asked "can you hear me?", they heard
      // nothing, and she had no idea why. Now the cause is recorded, and the
      // "no" branch answers the ACTUAL failure instead of guessing (8/15).
      noteSpeechAttempt();
      if (!r.ok) { noteSpeechFailure(SPEECH_CAUSE.HTTP, 'tts ' + r.status); return; }
      // WORD TIMES (9/2): the edge voice reports where each word starts;
      // the server passes them in a header so scenes, rings and cards can
      // land on the word itself instead of a guess from the line's length.
      let _words = null;
      try {
        const h = r.headers.get('X-Aria-Words');
        if (h) _words = JSON.parse(decodeURIComponent(h)).map((p) => ({ w: String(p[0]), t: Number(p[1]) || 0 }));
      } catch (_) { _words = null; }
      const buf = await r.arrayBuffer();
      if (!buf || !buf.byteLength) { noteSpeechFailure(SPEECH_CAUSE.EMPTY); return; }
      const url = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
      unlockAudioForReply();
      const audio = new Audio(url);
      prepareAriaPlayback(audio);
      // feeds TalkingBall real amplitude. Never throws out to here, and always
      // connects onward to the destination, so her audio cannot go silent.
      try { attachAriaAnalyser(audio); } catch (_) {}
      ariaSpeakingRef.current = true;
      speechClockRef.current = { line: String(line || ''), startedAt: Date.now(), ms: 0, words: _words };
      audio.onloadedmetadata = () => { try { if (audio.duration && isFinite(audio.duration)) speechClockRef.current = { line: String(line || ''), startedAt: Date.now(), ms: Math.round(audio.duration * 1000), words: _words }; } catch (_) {} };
      // onplay is the ONLY proof she was actually heard. Everything before it
      // is intent; this is the event.
      audio.onplay = () => {
        // the word offsets count from the first sample, so the clock starts HERE
        try { speechClockRef.current = { ...speechClockRef.current, startedAt: Date.now() }; } catch (_) {}
        setAriaSpeaking(true); noteSpeechPlayed();
      };
      const done = () => { ariaSpeakingRef.current = false; setAriaSpeaking(false); try { URL.revokeObjectURL(url); } catch (_) {} };
      audio.onended = done;
      audio.onerror = () => { noteSpeechFailure(SPEECH_CAUSE.DECODE); done(); };
      // A blocked play() is the single most likely reason the FIRST thing she
      // ever says is never heard: browsers hold audio until the page has been
      // touched, and on first run nothing has been touched yet.
      audio.play().catch((e) => { noteSpeechFailure(causeFromPlayError(e), e); done(); });
      // Remember the line so she can genuinely replay it, rather than saying
      // "let me try again" and playing nothing.
      lastSpokenLineRef.current = String(line || '');
    } catch (e) { noteSpeechFailure(SPEECH_CAUSE.UNKNOWN, e); }
  };

  // ── "Give me a sec…" fillers ────────────────────────────────────────────
  // Aria is a front-line communicator: when an answer is taking a moment (deep
  // reasoning, a lookup), she shouldn't leave the user in silence — she says a
  // short, varied filler so they know she's actively working on it. Only fires
  // for slow VOICE turns; the moment the real reply lands it's cancelled.
  const FILLERS = [
    'Give me a second.',
    'Let me check on that.',
    'One second, pulling that up.',
    'Hang on, let me look.',
    'Let me work that out.',
    'One moment, checking.',
    'Let me dig that up.',
  ];
  const fillerAudioRef = useRef(null);
  const fillerCancelRef = useRef(false);
  // Turn token: each sendMessage bumps this; a filler/bridge may only PLAY if
  // it still belongs to the CURRENT turn. Without it, a slow /tts fetch from a
  // finished turn played after the next turn reset fillerCancelRef — Aria's
  // ── THE TOUR IS AN OFFER, NOT A FRONT DOOR (Braeden 2026-09-06) ─────────
  //
  //   "the onboarding system is optional almost ... if they know what they
  //    want to use the ai for, than let them, start letting aria do her thing
  //    and do what they ask, but if they seem like they are looking for
  //    direction (which means they arnt talking or doing anything, inactive)
  //    than aria goes by the system and asks them almost like offering
  //    strongly in a way, thats what its for"
  //
  // Half of this already worked: middleware/ariaTour has the request-yield, so
  // anything she could actually go and do takes the turn away from the tour
  // outright. What was missing is the other half — the tour only ever STARTED
  // from a click, so a person who just sits there staring at the screen got
  // nothing at all, which is exactly the person it was built for.
  //
  // So: watch for someone doing nothing, and only then offer. INACTIVE here
  // means no typing, no clicking, no speaking — deliberately NOT pointermove,
  // because drifting the mouse around the page with no idea where to start is
  // the very thing this is looking for, not a reason to stay quiet.
  //
  // She OFFERS, she does not start. She says the line and the door gets her
  // pointing at it; they still click it themselves. That is his standing rule
  // from earlier today - "let the user click at first dont do it for them ...
  // can you click this for me (she points)".
  //
  // One shot, and it stands down for good the moment they do anything real:
  // an offer repeated is nagging, and this is meant to feel like being
  // noticed, not chased.
  const TOUR_OFFER_IDLE_MS = 22000;
  const [tourOffer, setTourOffer] = useState(null);   // the line she offered with
  const tourOfferDoneRef = useRef(false);             // offered once, or stood down
  const tourOfferTouchRef = useRef(Date.now());       // last real input, shared across re-runs
  const TOUR_OFFER_LINES = [
    "Not sure where to start? I can show you what I do - takes a minute.",
    "Want me to walk you through it? Quicker than guessing.",
    "I can show you around if you want. Up to you.",
  ];
  useEffect(() => {
    if (tourOfferDoneRef.current) return undefined;
    // Only the empty landing, only the person the door is for, only while the
    // tour has not already been started some other way.
    const armed = (isNewVisitor || forceFirstRunButton)
      && !firstTimeDismissed
      && effectiveSection === 'ask'
      && messages.length === 0
      && !tourBeat
      && !isOnboardingActive()    // she is already talking to them
      && !FIRST_TIME_IS_VOICE_ONBOARDING;   // 9/26: the idle tour offer stands down; the First Time button is the door
    if (!armed) {
      // They typed something, they opened the tour, they left the screen —
      // whichever it was, they are not the person who needed the offer.
      if (messages.length > 0 || tourBeat || firstTimeDismissed) tourOfferDoneRef.current = true;
      return undefined;
    }
    // A TIMESTAMP, NOT A TIMER (9/6). The first cut re-armed a setTimeout on
    // every keypress, and it fired early: this effect re-runs whenever its
    // deps settle, so more than one timeout could be in flight and a `bump`
    // only ever reset the one in its own closure. Measured - a keypress at 12s
    // still let the offer land at 30s instead of 34s. One interval reading one
    // shared timestamp cannot race with itself.
    // Deliberately NOT reset here: a dep settling is not the person doing
    // something, and restarting the clock on every re-run would let a busy
    // render keep her quiet forever.
    const bump = () => { tourOfferTouchRef.current = Date.now(); };
    window.addEventListener('keydown', bump);
    window.addEventListener('pointerdown', bump);
    const tick = setInterval(() => {
      if (tourOfferDoneRef.current) return;
      if (Date.now() - tourOfferTouchRef.current < TOUR_OFFER_IDLE_MS) return;
      tourOfferDoneRef.current = true;
      const line = TOUR_OFFER_LINES[Math.floor(Math.random() * TOUR_OFFER_LINES.length)];
      setTourOffer(line);
      try { speakAriaLine(line); } catch (_) {}
    }, 1000);
    return () => {
      clearInterval(tick);
      window.removeEventListener('keydown', bump);
      window.removeEventListener('pointerdown', bump);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewVisitor, forceFirstRunButton, firstTimeDismissed, effectiveSection, messages.length, tourBeat]);
  // Speaking counts as doing something. Kept separate so the listener set
  // above stays to real input events only.
  useEffect(() => {
    if (micSpeaking && !tourOffer) tourOfferDoneRef.current = true;
  }, [micSpeaking, tourOffer]);

  // "give me a second" going off out of nowhere (owner bug 2026-07-15).
  const fillerTurnRef = useRef(0);
  // ── Bridge line ("I'm already moving" cover for REAL lookups) ───────────
  // Tracked SEPARATELY from the stutter filler: a bridge is a full committed
  // sentence ("Let me pull the HVAC pricing data — one sec"), so it follows
  // the never-cut rule. cancelFiller() does NOT touch these — the real reply
  // WAITS for the bridge to finish (waitForBridge) instead of cutting it.
  const bridgeTextRef = useRef(null);   // the warranted bridge line for this turn
  const bridgeAudioRef = useRef(null);  // the playing bridge <audio>
  const bridgeDoneRef = useRef(null);   // Promise that resolves when bridge ends
  const speakFiller = useCallback(async (turnId = null) => {
    try {
      if (ariaSpeakingRef.current || fillerCancelRef.current) return;
      if (turnId != null && turnId !== fillerTurnRef.current) return;
      const line = FILLERS[Math.floor(Math.random() * FILLERS.length)];
      const base = getApiBase() || API_BASE;
      const r = await fetch(`${String(base).replace(/\/$/, '')}/tts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: line, ...ariaUserIdJsonBody() }),
      });
      if (!r.ok) return;
      const buf = await r.arrayBuffer();
      if (!buf || !buf.byteLength) return;
      // Real reply landed, Aria already speaking, or the TURN MOVED ON while
      // TTS was fetching → this filler is orphaned, never play it.
      if (ariaSpeakingRef.current || fillerCancelRef.current) return;
      if (turnId != null && turnId !== fillerTurnRef.current) return;
      if (!loadingRef.current) return; // nothing pending — a filler makes no sense
      const url = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
      unlockAudioForReply();
      const audio = new Audio(url);
      prepareAriaPlayback(audio);
      fillerAudioRef.current = audio;
      // Same rule as the bridge below: a filler must NOT set ariaSpeaking, or
      // the echo gate goes deaf for its duration (see speakBridge for why both
      // the ref AND setAriaSpeaking had to go - an effect syncs one from the
      // other). cancelFiller() still cuts a filler mid-word; only the BRIDGE
      // is protected by the never-cut rule.
      const done = () => {
        if (fillerAudioRef.current === audio) fillerAudioRef.current = null;
        try { URL.revokeObjectURL(url); } catch (_) {}
      };
      audio.onended = done; audio.onerror = done;
      audio.play().catch(done);
    } catch (_) { /* best-effort */ }
  }, []);
  // Cancel/stop any in-flight filler the instant the real answer is ready.
  // NOTE: this only kills the tiny stutter filler — it deliberately leaves the
  // BRIDGE line alone (bridgeAudioRef), because a bridge is a full sentence Aria
  // committed to and must never be cut mid-word. The reply waits for it instead.
  const cancelFiller = useCallback(() => {
    fillerCancelRef.current = true;
    fillerTurnRef.current += 1; // invalidate any in-flight filler/bridge fetch for good
    try {
      if (fillerAudioRef.current) {
        fillerAudioRef.current.pause();
        fillerAudioRef.current = null;
        ariaSpeakingRef.current = false;
        setAriaSpeaking(false);
      }
    } catch (_) {}
  }, []);
  // ── BARGE-IN apology (constitution r16, ratified 7/22) ───────────────────
  // The voice hook fires 'aria:barge-in' after yielding to 1.5s of user
  // speech. Two outcomes: (a) their words arrive → the normal send/reply flow
  // answers them, nothing to do here; (b) SILENCE follows — they stopped when
  // she stopped — so after a 2.5s grace window she owns the awkward moment:
  // "sorry, didn't mean to cut you off — what were you thinking?"
  const bargeInPendingRef = useRef(0);
  useEffect(() => {
    const onBargeIn = (e) => {
      const at = (e && e.detail && e.detail.at) || Date.now();
      bargeInPendingRef.current = at;
      setTimeout(async () => {
        try {
          if (bargeInPendingRef.current !== at) return;          // superseded by a newer barge
          // Their transcript made it through → she's answering it. Stay out.
          if (loadingRef.current || ariaSpeakingRef.current) return;
          const base = getApiBase() || API_BASE;
          const line = "Sorry — didn't mean to cut you off. What were you thinking?";
          const r = await fetch(`${String(base).replace(/\/$/, '')}/tts`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: line, ...ariaUserIdJsonBody() }),
          });
          if (!r.ok) return;
          const buf = await r.arrayBuffer();
          if (!buf || !buf.byteLength) return;
          // World may have moved on while TTS was fetching — re-check.
          if (bargeInPendingRef.current !== at || loadingRef.current || ariaSpeakingRef.current) return;
          const url = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
          unlockAudioForReply();
          const audio = new Audio(url);
          prepareAriaPlayback(audio);
          ariaSpeakingRef.current = true;
          audio.onplay = () => setAriaSpeaking(true);
          const done = () => {
            ariaSpeakingRef.current = false; setAriaSpeaking(false);
            try { URL.revokeObjectURL(url); } catch (_) {}
          };
          audio.onended = done; audio.onerror = done;
          audio.play().catch(done);
        } catch (_) { /* apology is best-effort — never break the loop */ }
      }, 2500);
    };
    window.addEventListener('aria:barge-in', onBargeIn);
    return () => window.removeEventListener('aria:barge-in', onBargeIn);
  }, []);

  // Speak a SPECIFIC bridge line ("Let me pull the HVAC pricing data — one sec")
  // while the real answer generates. Plays via the normal TTS path (already
  // pre-warmed in the edge voice cache by /api/aria/bridge, so it's instant).
  // Sets bridgeDoneRef to a promise the reply awaits so it never cuts her off.
  const speakBridge = useCallback(async (line, turnId = null) => {
    if (!line || ariaSpeakingRef.current) return;
    if (turnId != null && turnId !== fillerTurnRef.current) return;
    let resolveDone;
    bridgeDoneRef.current = new Promise((res) => { resolveDone = res; });
    try {
      const base = getApiBase() || API_BASE;
      const r = await fetch(`${String(base).replace(/\/$/, '')}/tts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: line, ...ariaUserIdJsonBody() }),
      });
      if (!r.ok) { resolveDone && resolveDone(); bridgeDoneRef.current = null; return; }
      const buf = await r.arrayBuffer();
      // Real reply already speaking, or the turn moved on while fetching?
      // Don't start the bridge late on top of it.
      if (!buf || !buf.byteLength || ariaSpeakingRef.current
          || (turnId != null && turnId !== fillerTurnRef.current)) { resolveDone && resolveDone(); bridgeDoneRef.current = null; return; }
      const url = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
      unlockAudioForReply();
      const audio = new Audio(url);
      prepareAriaPlayback(audio);
      bridgeAudioRef.current = audio;
      // ── A BRIDGE MUST NOT DEAFEN HER (2026-09-07) ─────────────────────
      // The note where this was switched off is explicit: "If that comes back
      // it must not set ariaSpeaking, or it will deafen her again." The echo
      // gate in queueOrSendDesktopTranscript drops every transcript while
      // ariaSpeakingRef is true, so for the length of a filler she could not
      // hear him at all - "doesnt listen", caused by her own filler.
      //
      // BOTH lines had to go, not just the ref. There is an effect
      //   useEffect(() => { ariaSpeakingRef.current = ariaSpeaking }, [ariaSpeaking])
      // so setAriaSpeaking(true) writes the ref on the next render - removing
      // the direct assignment alone would have put the deafness straight back
      // through the state, and looked fixed.
      //
      // Overlap is still impossible without the flag: the real reply calls
      // waitForBridge() and awaits bridgeDoneRef before it speaks, and
      // cancelFiller() bumps fillerTurnRef so a bridge cannot start late.
      const done = () => {
        if (bridgeAudioRef.current === audio) bridgeAudioRef.current = null;
        try { URL.revokeObjectURL(url); } catch (_) {}
        if (resolveDone) { resolveDone(); resolveDone = null; }
        bridgeDoneRef.current = null;
      };
      audio.onended = done; audio.onerror = done;
      audio.play().catch(done);
    } catch (_) {
      if (resolveDone) resolveDone();
      bridgeDoneRef.current = null;
    }
  }, []);

  // The real reply calls this before it speaks. If a bridge is mid-sentence it
  // waits for it to finish (never cut); if none is playing it resolves instantly.
  const waitForBridge = useCallback(async () => {
    try { if (bridgeDoneRef.current) await bridgeDoneRef.current; } catch (_) {}
  }, []);

  const onScreenShareClick = () => {
    // Skip the Assist UI entirely — go straight into compact overlay mode.
    // The Electron main process handles entering the floating overlay and starting the agent.
    //
    // THE TWO HALVES MUST AGREE (2026-09-10). screenShareStarted COMMITS the
    // whole assist transition in main (shrink to 400x540, pin topmost, renderer
    // assistCompact=true) and is irreversible from here; requestScreenShare is
    // what actually produces the capture. Sending the first unconditionally
    // while gating the second on a ref meant any stale stream — one whose
    // tracks had already ended — left the app committed to an assist mode that
    // could never start, and the idempotence guard in main then made every
    // retry click a no-op. Ask whether the stream is LIVE, not whether the ref
    // is merely non-null, and decide once for both halves.
    const existing = screenStreamRef.current;
    let live = false;
    try {
      live = !!existing && existing.getVideoTracks().some((t) => t.readyState === 'live');
    } catch (_) { live = false; }
    if (existing && !live) {
      // Dead stream: drop it so nothing downstream treats it as a session.
      try { existing.getTracks().forEach((t) => t.stop()); } catch (_) {}
      screenStreamRef.current = null;
      setScreenStream(null);
    }
    if (window.electronAPI?.screenShareStarted) {
      window.electronAPI.screenShareStarted();
    }
    // Kick off screen capture in the background (no intermediate UI).
    if (!live) {
      requestScreenShare();
    }
  };

  const openAddMenu = (e) => {
    e.stopPropagation();
    setAddMenuOpen((o) => !o);
  };

  const onAddAssist = () => {
    setAddMenuOpen(false);
    onScreenShareClick();
  };

  const onAddFiles = () => {
    setAddMenuOpen(false);
    fileInputRef.current?.click();
  };

  const onFileInputChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    addReferenceImages(files);
    e.target.value = '';
  };

  const onAddTakePicture = () => {
    setAddMenuOpen(false);
    setCameraOpen(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
    addReferenceImages(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      cameraStreamRef.current = stream;
      if (cameraVideoRef.current) cameraVideoRef.current.srcObject = stream;
    } catch (e) {
      console.error('Camera error:', e);
      setCameraOpen(false);
    }
  };

  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((t) => t.stop());
      cameraStreamRef.current = null;
    }
    if (cameraVideoRef.current) cameraVideoRef.current.srcObject = null;
    setCameraOpen(false);
  };

  const capturePicture = () => {
    const video = cameraVideoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    setCapturedImage(canvas.toDataURL('image/jpeg'));
    stopCamera();
  };

  useEffect(() => {
    if (cameraOpen) startCamera();
    return () => {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((t) => t.stop());
        cameraStreamRef.current = null;
      }
    };
  }, [cameraOpen]);

  // Auto-scroll the TEXT chat to the bottom when new content arrives. The old
  // code did bottomRef.scrollIntoView(), but bottomRef lives in a DIFFERENT
  // container than the glass chat — so it yanked the wrong element and made the
  // view jump ("scrolls up"). Scroll the actual glass scroller instead, and
  // only when the user is already pinned to the bottom.
  // A NEW QUESTION ALWAYS LANDS (sweep 9/2). "Only when pinned to the bottom"
  // is right for a reply arriving while someone is reading history - but the
  // first artifact makes the column taller than the view, atBottom goes false
  // and stays false, and from then on no new turn was ever scrolled to:
  // measured newest question at 967px, 2722px, 3660px below the top of a
  // 560px scroller. The person just typed - they are not reading history.
  const typedTurnCountRef = useRef(0);
  useEffect(() => {
    const el = glassInnerRef.current;
    if (!el) return;
    const typedTurns = messages.filter((m) => m.role === 'user' && m.mode === 'text').length;
    const newTurn = typedTurns > typedTurnCountRef.current;
    typedTurnCountRef.current = typedTurns;
    if (!newTurn && !atBottomRef.current) return;
    // A macrotask, not requestAnimationFrame: by the time useEffect runs the
    // nodes are committed, and rAF never fires at all in a hidden tab (the
    // desktop app's window is often behind another one while she works) - a
    // scroll that waits for a frame that never comes is a scroll that never
    // happens. Measured 9/2: zero scroll events across three turns while the
    // tab was hidden; setTimeout(0) lands every time.
    // AN ARTIFACT READS TOP-DOWN, A CHAT READS BOTTOM-UP (8/31). The
    // workstation shares this scroller, so "scroll to the newest line" opened
    // every results panel BELOW ITS OWN HEADER — measured live: scrollTop
    // pinned to max, with the kicker, the query and the first result above
    // the fold and no way to know they were there. Asking the DOM instead of
    // threading state keeps this out of the hook's dependency array.
    setTimeout(() => {
      try {
        // THE TIMELINE (9/2): the newest turn is at the BOTTOM now, and a turn
        // still reads top-down - so land on the newest QUESTION, with her
        // answer and that turn's cards unfolding beneath it. Older turns stay
        // above for scrolling back; nothing new is ever below the fold.
        const asked = el.querySelectorAll('.ws-thread--timeline .ws-asked');
        const newest = asked.length ? asked[asked.length - 1] : null;
        if (newest) {
          // Rects, not offsetTop: the thread is not a positioned ancestor, so
          // offsetTop would measure from the fixed sheet, not the scroller.
          const y = newest.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop;
          el.scrollTop = Math.max(0, y - 12);
          return;
        }
        el.scrollTop = el.querySelector('.workstation') ? 0 : el.scrollHeight;
      } catch (_) {}
    });
    // (queuedMessages is declared later in this component, so it can't be a dep
    // here — the queue push scrolls to the bottom itself.)
  }, [messages, loading]);

  // Tag <body> when the left drawer is open so CSS can push fixed-positioned
  // center elements (hero, input bar, chat column) to the right and keep them
  // visually balanced in the remaining viewport.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('aria-drawer-open', !!leftSidebarOpen);
    return () => {
      try { document.body.classList.remove('aria-drawer-open'); } catch (_) {}
    };
  }, [leftSidebarOpen]);

  // (Removed the global document zoom — it caused more visual glitches than it
  // solved and was inert at normal screen sizes. The app renders at native 1.0.)

  // Global day/night theme — toggles `theme-light` on <body>, flipping the CSS
  // variables (bg, surface, text, border, input) so the whole app changes color.
  // Apply the theme to <body> whenever it changes (also covers first load). The
  // animated reveal-wipe itself is driven from switchTheme() at the toggle so that
  // the React state change and this class flip land INSIDE the transition snapshot.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('theme-light', appTheme === 'day');
    try { localStorage.setItem('howto_theme', appTheme); } catch (_) {}
  }, [appTheme]);

  // Cross-WINDOW theme sync (owner 2026-07-12): the Assist popup is its own
  // Electron window — when the main window flips day/night, every other window
  // hears the localStorage change and follows instantly, so Aria's boxes always
  // match the app theme.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e) => {
      if (e && e.key === 'howto_theme' && (e.newValue === 'day' || e.newValue === 'night')) {
        setAppTheme(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Text size (Settings → Appearance): re-apply the saved size class to <body> on
  // boot so the whole app reflects the owner's choice. App.css scales the chat
  // reading text off the body class. Default 'm' (medium).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    let v = 'm';
    try { v = localStorage.getItem('aria_text_size') || 'm'; } catch (_) {}
    if (!['s', 'm', 'l'].includes(v)) v = 'm';
    document.body.classList.remove('aria-text--s', 'aria-text--m', 'aria-text--l');
    document.body.classList.add('aria-text--' + v);
  }, []);

  // When Settings opens, pull the REAL plan + default card from Stripe so the
  // Subscription rows show truth (not the old hardcoded "Free" / "Add a card").
  // Best-effort: any failure just leaves the graceful fallbacks in place.
  useEffect(() => {
    if (effectiveSection !== 'settings') return;
    let cancelled = false;
    (async () => {
      try {
        const uid = localStorage.getItem(ARIA_USER_ID_KEY) || '';
        const email = localStorage.getItem('aria_user_email') || localStorage.getItem('aria_session_email') || '';
        if (!uid && !email) return;
        const qs = new URLSearchParams({ userId: uid, email }).toString();
        const r = await fetch(`${API_BASE}/api/stripe/subscription?${qs}`);
        if (r.ok) { const d = await r.json(); if (!cancelled) setBillingSnapshot(d || null); }
        // Mirror the real server-side overage choice into the local flag the
        // Subscription toggle reads, so it reflects truth on open.
        if (uid) {
          try {
            const or = await fetch(`${API_BASE}/api/user/billing/overage?userId=${encodeURIComponent(uid)}`);
            if (or.ok) { const od = await or.json(); localStorage.setItem('aria_allow_overage', od && od.allow ? '1' : '0'); }
          } catch (_) {}
        }
      } catch (_) { /* keep fallbacks */ }
    })();
    return () => { cancelled = true; };
  }, [effectiveSection]);

  // Night/Day toggle = a true reveal-wipe across the WHOLE app (every page) via the
  // View Transitions API. flushSync forces React to re-render + commit + run the
  // body-class effect synchronously INSIDE the VT callback, so prop-themed pages
  // (CompanyView's theme prop, the day-gradient layers) AND the body.theme-light
  // cascade all change together — otherwise prop-driven pages flip before the
  // snapshot and never wipe. Reduced-motion / unsupported browsers snap instantly.
  const switchTheme = useCallback((next) => {
    if (next === appTheme) return;
    // INSTANT, non-blocking flip. The old View-Transitions "wipe" snapshotted
    // the whole screen and — despite pointer-events:none — kept feeling like it
    // blocked clicks mid-animation and lagged. Flipping the theme immediately
    // means the live page is the new theme the instant you press Day/Night, so
    // you can spam the buttons and click ANYTHING the whole time. A short CSS
    // color cross-fade (App.css) keeps it smooth instead of a hard snap.
    setAppTheme(next);
  }, [appTheme]);

  // "System" theme: track the OS preference live so Dark/Light follows it while
  // the owner has System selected. Re-subscribes whenever the pref changes.
  // (Placed AFTER switchTheme so the dep array doesn't hit its TDZ.)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    if (themePrefState !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => switchTheme(mq.matches ? 'night' : 'day');
    on();
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, [themePrefState, switchTheme]);

  // THE DRAWER STAYS PUT (Braeden 8/13): "everytime i click off of the dash
  // board, it gets rid of it, dont do that any more the only way to remove the
  // dash is to click the top box only." The click-outside listener that used
  // to live here made the dashboard vanish any time he touched the page —
  // while reading a business card, while clicking into chat. It's gone: the
  // header box (and the hamburger) are the only things that close it.

  // ── Minimum-think window ────────────────────────────────────────────────
  //   Owner rule: Aria ALWAYS shows the "..." typing indicator for at least
  //   ARIA_MIN_THINK_MS after the user sends a text — even if the backend has
  //   the answer ready instantly. It should read like a real person texting
  //   back: the typing bubble pops up every time and takes a deliberate beat.
  const ARIA_MIN_THINK_MS = 1800;
  const lastTextSendRef = useRef(0);
  const [holdTick, setHoldTick] = useState(0); // re-render trigger at hold end

  // Whenever a new message arrives during a hold window, schedule a re-render
  // at the moment the hold ends so the message can transition in.
  useEffect(() => {
    if (lastTextSendRef.current === 0) return;
    const remaining = lastTextSendRef.current + ARIA_MIN_THINK_MS - Date.now();
    if (remaining > 0) {
      const t = setTimeout(() => setHoldTick((x) => x + 1), remaining + 30);
      return () => clearTimeout(t);
    }
  }, [messages]);

  // ── Sound: receive-chime when a new text reply from Aria lands ──────────
  //   Tracks the last assistant TEXT-mode message and fires the chime + stops
  //   the thinking loop the moment a fresh one appears. Voice replies don't
  //   trigger this — TTS audio is its own channel. Also defers the chime
  //   until the minimum-think window has elapsed.
  const lastAriaTextRef = useRef(0);
  useEffect(() => {
    const ariaTexts = messages.filter(
      (m) => m && m.role === 'assistant' && m.mode !== 'voice' && typeof m.content === 'string' && m.content.trim()
    );
    if (ariaTexts.length > lastAriaTextRef.current) {
      lastAriaTextRef.current = ariaTexts.length;
      const remaining = Math.max(0, lastTextSendRef.current + ARIA_MIN_THINK_MS - Date.now());
      const replyText = (ariaTexts[ariaTexts.length - 1]?.content || '').toString();
      const fire = () => {
        try { stopThinking(); } catch (_) {}
        try { playReceive(); } catch (_) {}
        // Push notifications (Settings → Notifications): when enabled and the tab
        // is in the background, surface a native notification for the new reply.
        try {
          if (localStorage.getItem('aria_notif_push') === '1'
              && typeof Notification !== 'undefined' && Notification.permission === 'granted'
              && document.hidden) {
            const n = new Notification('Aria', { body: replyText.slice(0, 140) || 'You have a new reply.', tag: 'aria-reply' });
            n.onclick = () => { try { window.focus(); n.close(); } catch (_) {} };
          }
        } catch (_) {}
      };
      if (remaining === 0) fire();
      else setTimeout(fire, remaining);
    }
  }, [messages]);

  // Belt-and-suspenders: whenever loading flips OFF, kill the thinking ticks.
  // Covers error paths and edge cases where no new assistant message lands.
  // Respects the hold window though — only stops AFTER the minimum think time.
  useEffect(() => {
    if (!loading) {
      const remaining = Math.max(0, lastTextSendRef.current + ARIA_MIN_THINK_MS - Date.now());
      if (remaining === 0) {
        try { stopThinking(); } catch (_) {}
      } else {
        const t = setTimeout(() => { try { stopThinking(); } catch (_) {} }, remaining);
        return () => clearTimeout(t);
      }
    }
  }, [loading]);

  // ── Persist conversation so the user returns to the same state ──────────────
  //   Only signed-in users get persistence. Anonymous visitors leave no trail —
  //   refresh the page and the screen is blank again, as if it's their first
  //   time. Their conversation lives only in this tab's React state.
  useEffect(() => {
    if (!isLoggedIn()) return;
    if (messages.length > 0) {
      writePersistedSession(ARIA_SESSION_MESSAGES_KEY, messages);
    }
  }, [messages]);
  useEffect(() => {
    if (!isLoggedIn()) return;
    if (conversationHistory.length > 0) {
      writePersistedSession(ARIA_SESSION_HISTORY_KEY, conversationHistory);
    }
  }, [conversationHistory]);

  // ── Action detection for home screen (same patterns as ScreenShareView) ──

  function detectHomeAction(text) {
    if (!text || typeof text !== 'string') return null;
    const t = text.trim();
    // Map launches need the task lifecycle: speak the missing-location
    // handoff, observe the launched app, and retain it for the next query.
    // The fire-and-forget home URL shortcut cannot verify any of that.
    if (isDesktopApp && /\bGoogle (?:Earth|Maps)\b/i.test(t) && isAssistWorkRequest(t)) return null;
    // THE APP ENTRIES ARE MAC NAMES (8/23): "open settings" matched
    // { app: 'System Settings' } and openApp() on Windows found nothing, so
    // the command died here and never reached the backend - that is why
    // Settings "didn't work". On anything but a Mac, app launches go to the
    // backend, whose launcher searches the real Start menu. Searches
    // ("look up X") are never a Google tab: the search lane puts results on
    // the workstation.
    const _isMac = (typeof window !== 'undefined' && window.electronAPI && window.electronAPI.platform === 'darwin');
    for (const entry of HOME_ACTION_MAP) {
      if (entry.searchUrl) continue;
      if (entry.app && !_isMac) continue;
      const m = t.match(entry.pattern);
      if (m) {
        if (entry.url) return { type: 'url', url: entry.url, label: entry.label };
        if (entry.app) return { type: 'app', app: entry.app, label: entry.label };
        if (entry.searchUrl && m[1]) {
          const query = m[1].trim().replace(/[?.!]+$/, '');
          if (query.length > 0) return { type: 'url', url: entry.searchUrl + encodeURIComponent(query), label: `${entry.label}: ${query}` };
        }
      }
    }
    const urlMatch = t.match(/\b(?:open|go to|navigate to|visit)\s+(https?:\/\/\S+)/i);
    if (urlMatch) return { type: 'url', url: urlMatch[1], label: urlMatch[1] };
    return null;
  }

  function executeHomeAction(action) {
    if (!action) return false;
    if (action.type === 'url' && window.electronAPI?.openExternal) {
      window.electronAPI.openExternal(action.url);
      return true;
    }
    if (action.type === 'app' && window.electronAPI?.openApp) {
      window.electronAPI.openApp(action.app);
      return true;
    }
    if (action.type === 'url' && !window.electronAPI) {
      window.open(action.url, '_blank', 'noopener');
      return true;
    }
    return false;
  }

  // ── User preference learning: track what the user prefers from their actions ──
  const PREF_KEY = 'aria_user_preferences';
  function loadPreferences() {
    try { return JSON.parse(localStorage.getItem(PREF_KEY) || '{}'); } catch (_) { return {}; }
  }
  function savePreferences(prefs) {
    try { localStorage.setItem(PREF_KEY, JSON.stringify(prefs)); } catch (_) {}
  }
  function learnPreference(text, action) {
    const prefs = loadPreferences();
    const t = text.toLowerCase();
    // Browser preference: if user says "open chrome" instead of "open safari", they prefer Chrome
    if (/chrome|google chrome/i.test(t)) {
      prefs.preferredBrowser = 'Google Chrome';
      prefs.browserHistory = (prefs.browserHistory || []).concat({ app: 'Google Chrome', ts: Date.now() }).slice(-20);
    } else if (/safari/i.test(t)) {
      prefs.preferredBrowser = 'Safari';
      prefs.browserHistory = (prefs.browserHistory || []).concat({ app: 'Safari', ts: Date.now() }).slice(-20);
    } else if (/firefox/i.test(t)) {
      prefs.preferredBrowser = 'Firefox';
      prefs.browserHistory = (prefs.browserHistory || []).concat({ app: 'Firefox', ts: Date.now() }).slice(-20);
    }
    // Track frequently opened apps
    if (action.type === 'app' && action.app) {
      if (!prefs.appUsage) prefs.appUsage = {};
      prefs.appUsage[action.app] = (prefs.appUsage[action.app] || 0) + 1;
    }
    // Track frequently visited sites
    if (action.type === 'url' && action.label) {
      if (!prefs.siteUsage) prefs.siteUsage = {};
      prefs.siteUsage[action.label] = (prefs.siteUsage[action.label] || 0) + 1;
    }
    // Food preferences
    if (/pizza/i.test(t)) { prefs.foodMentions = { ...prefs.foodMentions, pizza: (prefs.foodMentions?.pizza || 0) + 1 }; }
    if (/burger/i.test(t)) { prefs.foodMentions = { ...prefs.foodMentions, burger: (prefs.foodMentions?.burger || 0) + 1 }; }
    if (/coffee|starbucks/i.test(t)) { prefs.foodMentions = { ...prefs.foodMentions, coffee: (prefs.foodMentions?.coffee || 0) + 1 }; }
    if (/sushi/i.test(t)) { prefs.foodMentions = { ...prefs.foodMentions, sushi: (prefs.foodMentions?.sushi || 0) + 1 }; }

    prefs.lastUpdated = Date.now();
    savePreferences(prefs);

    // Also send to backend for server-side profiling
    try {
      const userId = localStorage.getItem('aria_user_id');
      if (userId) {
        const base = getApiBase() || API_BASE || '';
        fetchWithTimeout(`${String(base).replace(/\/$/, '')}/api/user/${userId}/preferences`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ preferences: prefs }),
          timeout: API_TIMEOUT_MS,
        }).catch(() => {});
      }
    } catch (_) {}
  }

  // Message QUEUE (Claude-style) — send more messages while Aria is still
  // replying and they line up. Each shows as a pending bubble with an × to
  // cancel it; Aria finishes the reply in flight, then fires the next queued
  // one automatically. State (renders in the UI) + a ref mirror so the flush
  // effect can read the current queue synchronously.
  const [queuedMessages, setQueuedMessages] = useState([]);
  const queuedMessagesRef = useRef([]);
  const queueIdRef = useRef(0);
  useEffect(() => { queuedMessagesRef.current = queuedMessages; }, [queuedMessages]);
  const removeQueued = (id) => setQueuedMessages((q) => q.filter((m) => m.id !== id));

  const sendMessage = async () => {
    if (isDesktopApp && !hasSpokenDesktopIntro) {
      setVoiceError("Let me introduce myself first.");
      return;
    }
    if (introLockedRef.current || introLocked) {
      setVoiceError("Let me finish introducing myself, then tell me your name.");
      return;
    }
    if (isDesktopApp && voicePolicy === 'hold') {
      setVoiceError('Finish the quick start below, then we can chat.');
      return;
    }
    const text = input.trim();
    if (!text && !referenceVideo) return;
    // ── HIS ANSWER TO "WHICH SCREEN?" (9/6) ───────────────────────────────
    // Only /api/screen/analyze understands "screen 2" / "the middle one", and
    // the app's Assist turns never reach it with a deviceId. While the entry
    // question is open, a pick-shaped answer goes straight there; the route
    // records the screen, hides the numbers and the hands move to that screen.
    if (awaitingScreenPickRef.current > 0
        && /^(?:the\s+)?(?:screen|monitor|display)?\s*(?:number\s*)?(\d|one|two|three|four|first|second|third|fourth|left|right|middle|center|centre)(?:\s*(?:one|screen|monitor|display))?\.?$/i.test(text)) {
      const count = awaitingScreenPickRef.current;
      setInput('');
      try { setMessages((m) => [...m, { role: 'user', content: text, mode: 'text' }]); } catch (_) {}
      try {
        let deviceId = window.__ariaDeviceId || null;
        if (!deviceId && window.electronAPI?.getAgentConfig) {
          try { deviceId = (await window.electronAPI.getAgentConfig())?.deviceId || null; } catch (_) {}
        }
        let image = '';
        try { const cap = await window.electronAPI?.captureScreen?.(); image = (cap && (cap.image || cap.dataUrl)) || ''; } catch (_) {}
        const base = getApiBase() || API_BASE;
        const r = await fetchWithTimeout(`${String(base).replace(/\/$/, '')}/api/screen/analyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...ariaUserIdJsonBody(), question: text, image, deviceId, screenCount: count, desktopAssist: true, screenShareMode: true, inputMode: 'text', skipAudio: true, os: (window.electronAPI && window.electronAPI.platform) || '' }),
          timeout: 20000,
        });
        const data = await r.json().catch(() => ({}));
        const line = String(data.reply || data.text || '').trim() || `Screen ${text.replace(/\D+/g, '') || ''} it is.`;
        awaitingScreenPickRef.current = 0;
        try { setMessages((m) => [...m, { role: 'assistant', content: line, data, mode: 'text' }]); } catch (_) {}
        try { await window.electronAPI?.screenNumbers?.(false); } catch (_) {}
        try { await speakAriaLine(line); } catch (_) {}
      } catch (e) {
        console.warn('[assist] screen pick failed:', e?.message || e);
        awaitingScreenPickRef.current = 0;
      }
      return;
    }
    // If a reply is still in flight, QUEUE this message so it fires when the
    // current one finishes. It shows as a pending bubble (with an × to cancel).
    if (loading) {
      const id = ++queueIdRef.current;
      setQueuedMessages((q) => [...q, { id, text, refVideo: referenceVideo }]);
      setInput('');
      // Keep the new queued bubble in view — unless an artifact owns the
      // sheet, in which case its top is what matters (see the scroll effect).
      requestAnimationFrame(() => {
        try {
          const el = glassInnerRef.current;
          if (el && !el.querySelector('.workstation')) el.scrollTop = el.scrollHeight;
        } catch (_) {}
      });
      return;
    }
    setInput('');
    try { uiSound.play('send'); } catch (_) {}
    await sendMessageWithText(text || 'What is this and how can I fix it?');
  };

  // ── IF SHE CANNOT SPEAK, SHOW THE WORDS (Braeden 8/31) ────────────────
  // "it says its hearing me but its not responding." A voice turn renders NO
  // text by his own rule, so ANY failure in the speech path - TTS down, a
  // phone blocking autoplay, a slow network timing out - leaves the person
  // staring at a silent screen with an answer they can neither see nor hear.
  // Every failure branch below now falls back to putting her reply on screen.
  // Silence is the one outcome that must never happen.
  const _showWhatSheSaid = (txt) => {
    try {
      const t = String(txt || '').trim();
      if (!t) return;
      setMessages((m) => (m.some((x) => x.role === 'assistant' && x.content === t && x.mode === 'text')
        ? m
        : [...m, { role: 'assistant', content: t, mode: 'text' }]));
      setLastInputMode('text');
    } catch (_) {}
  };
  const sendMessageWithText = async (text, opts = {}) => {
    // They filled the pause themselves — she must not talk over the answer
    // she was waiting for. See _tourWaitThenOffer.
    try { _tourWaitCancel(); } catch (_) {}
    // a turn from them is the clearest sign of the person her pace waits on (9/5)
    try { tourPaceRef.current.personAt = Date.now(); } catch (_) {}
    // Did this turn hand off to speech? If not, the floor is released in the
    // finally - otherwise a text-only answer to a spoken question would keep
    // her deaf until the 30s ceiling (8/31).
    let _willSpeak = false;
    // ── ONE TURN PER THING SAID (8/25) ──────────────────────────────────────
    // Braeden: "the double texting thing is annoying" — his screenshot shows
    // "Settings. Open up settings." twice, two identical bubbles from one
    // sentence. More than one call site fires this function for a single
    // utterance (a mic-final and a mic-close both resolve to the same text),
    // and each one appends its own message AND runs the whole turn — so the
    // backend does the work twice too, which is how one "open settings" became
    // two attempts.
    //
    // Nobody says or types the same sentence twice inside 1.5 seconds. Identical
    // text that fast is a double-fire, so the second one is dropped before it
    // can append a bubble or reach the server. Deliberate repeats still work —
    // wait a moment, or the text differs by a word, and it goes through.
    try {
      const _t = String(text || '').trim();
      if (_t) {
        const now = Date.now();
        const last = _lastTurnRef.current;
        // 1500ms WAS TOO TIGHT (8/25). His screen showed every single sentence
        // twice — "Open up settings." / "Use 3-1." / "Screen 1." / "Dumbass."
        // each rendered as a pair — while the 1.5s window saw them as separate
        // turns because the two sends arrive seconds apart, not milliseconds.
        //
        // Nobody says the exact same sentence twice inside ten seconds. When he
        // DOES repeat himself it is because she failed him, and he rewords it —
        // "I said use screen", "Screen 1." — which are different strings and
        // still go through. Only a byte-identical echo inside 10s is dropped.
        // Whisper's streaming and fallback paths can differ only by casing,
        // punctuation, or a trailing filler word. Treat those as the same
        // utterance too, otherwise Aria generates two replies and both get
        // spoken over one another.
        const _key = _t.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
        const _lastKey = last && (last.key || String(last.text || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim());
        if (last && _lastKey === _key && (now - last.at) < 15000) {
          console.log('[sendMessageWithText] dropped duplicate turn:', _t.slice(0, 50));
          return;
        }
        _lastTurnRef.current = { text: _t, key: _key, at: now };
      }
    } catch (_) { /* the guard must never block a real turn */ }

    // ── Free credits gate (TEMP DISABLED for layout preview) ──────────
    // Original block returned early when anonymous users ran out of free
    // messages and opened the OutOfCreditsModal. Disabled so messages
    // always go through. Re-enable by un-commenting the block below.
    /*
    if (!isLoggedIn() && !onboardingStageRef.current && !opts.bypassCreditsGate) {
      if (!canSendMessage()) {
        setOutOfCreditsOpen(true);
        return;
      }
      decrementCredits();
    }
    */
    // inputMode: 'text' (typed / auto-advance) → no audio reply, full-screen chat view
    //            'voice' (mic transcription)   → audio reply, orb view
    // Default 'text' since most call sites are typed-equivalent.
    const _inputMode = opts.inputMode === 'voice' ? 'voice' : 'text';
    setLastInputMode(_inputMode);

    // ── Permission-first consent answer (local — desktop assist) ────────────
    // Aria asked "want me to go ahead?" before taking the screen and is waiting.
    // Route yes/no straight to the agent so the paused action resumes (or stops)
    // — voice OR text — instead of the bare "yes" falling through to the brain.
    if (pendingConsentRef.current && text) {
      pendingConsentRef.current = false; // consume — one shot
      const a = String(text).toLowerCase().replace(/[^a-z0-9\s']/g, ' ').replace(/\s+/g, ' ').trim();
      const SAYS_GO = /^(y|ya|ye|yea|yes+|yup|yep|yeah|yeh|sure|ok|okay|kk?|do it|go|go ahead|go for it|lets go|let's go|sounds good|please|please do|yes please|absolutely|definitely|for sure|of course|take over|take control|continue|proceed|keep going)\b/;
      const SAYS_WAIT = /^(n|no|nope|nah|not now|not yet|never mind|nevermind|cancel|stop|don'?t|do not|hold on|hold up|wait|pause|maybe later|later)\b/;
      const mode = _inputMode === 'voice' ? 'voice' : 'text';
      if (SAYS_GO.test(a)) {
        try { window.electronAPI?.assistConsent?.(true); } catch (_) {}
        setMessages((m) => [...m, { role: 'user', content: text, mode }]);
        return;
      }
      if (SAYS_WAIT.test(a)) {
        try { window.electronAPI?.assistConsent?.(false); } catch (_) {}
        const line = "Okay — I'll hold off. Just say go ahead when you're ready.";
        setMessages((m) => [...m, { role: 'user', content: text, mode }, { role: 'assistant', content: line, mode }]);
        if (mode === 'voice') speakAriaLine(line);
        return;
      }
      pendingConsentRef.current = true; // neither yes nor no → stay armed, fall through
    }

    // ── SHOW IT (8/14) ──────────────────────────────────────────────────────
    // Runs alongside the reply, not before it: the visual must never delay her
    // answer. When it lands it attaches to her latest line.
    try {
      setTurnVisual(null);              // last turn's picture goes away
      const _vd = decideVisual(text);
      if (_vd) resolveVisual(_vd).then((spec) => { if (spec) setTurnVisual(spec); }).catch(() => {});
    } catch (_) {}

    // ── FIRST-RUN CONVERSATION (8/14) ───────────────────────────────────────
    // "aria needs to have responses ready to go so its almost like an instant
    // conversation, than she needs to direct the user what to do based on what
    // they want." Her next line is already on the client, so it starts the
    // moment they stop talking — no round trip, no thinking gap.
    // Off-script answers fall through to the brain (the node is kept, so the
    // flow picks back up on their next reply) — she is guided, not scripted.
    if (firstRunRef.current && text) {
      const st = firstRunRef.current;
      const mode = _inputMode === 'voice' ? 'voice' : 'text';
      // one place to emit a line so history, the transcript and her voice can
      // never drift apart (the server repeats from ariaSpokenState on "what?")
      const emit = (line) => {
        setMessages((m) => [...m, { role: 'user', content: text, mode }, { role: 'assistant', content: line, mode }]);
        try { rememberTurn(mode, [{ role: 'user', content: text }, { role: 'assistant', content: line }]); } catch (_) {}
        if (mode === 'voice') { try { speakAriaLine(line, { track: true }); } catch (_) {} }
      };

      // ── did they ask something instead of answering? ──────────────────────
      // Answer it instantly and put the question back, so a curious owner never
      // waits on the brain and never loses the thread.
      // ── "I CAN'T HEAR YOU" — DIAGNOSE AND REPAIR, DO NOT SURRENDER ───────
      //
      //   "if they say no, think of every reson they might not be able to hear
      //    the ai, than you have to fiind the solustion and fix it"
      //
      // The old answer was "No problem, we can do this by text" on the FIRST
      // no — throwing her voice away without once asking why, when the cause is
      // usually ours and usually already fixed by the time they answer.
      const atGreeting = st.node === 'greet' || st.node === 'cant_hear';
      const hearing = readHearingAnswer(text, { atGreeting });
      if (hearing && atGreeting) {
        // "what?" is not deafness. It is one word from it and means the
        // opposite: they heard her and want it again.
        if (hearing === 'missed') {
          const again = lastSpokenLineRef.current || lineForFirstRun(st.node, st.data);
          setMessages((m) => [...m, { role: 'user', content: text, mode }, { role: 'assistant', content: again, mode }]);
          if (mode === 'voice') { try { speakAriaLine(again, { role: 'repeat', track: true }); } catch (_) {} }
          return;
        }
        // "you're too quiet / too fast / you cut out" — they CAN hear her.
        // Each of these is ours to fix, and none of them is a browser problem.
        if (hearing === 'heard_but') {
          const hb = matchHeardBut(text);
          try {
            const cur = parseFloat(localStorage.getItem('aria_voice_speed') || '1') || 1;
            if (hb.action === 'slower') localStorage.setItem('aria_voice_speed', String(Math.max(0.7, cur - 0.15)));
            if (hb.action === 'faster') localStorage.setItem('aria_voice_speed', String(Math.min(1.4, cur + 0.15)));
          } catch (_) {}
          firstRunRef.current = { ...st, lastSaid: hb.say };
          emit(hb.say);
          // Prove the change rather than promising it — say the fixed line again.
          if (hb.action === 'replayGreeting' || hb.action === 'fallbackVoice' || hb.action === 'slower' || hb.action === 'louder') {
            const again = lineForFirstRun(st.node, st.data);
            if (again && mode === 'voice') setTimeout(() => { try { speakAriaLine(again, { track: true }); } catch (_) {} }, 900);
          }
          return;
        }
        // Genuinely no sound. Read WHY from what actually happened to the last
        // clip, and answer that cause specifically.
        const fix = fixForCannotHear(hearFixTriesRef.current);
        hearFixTriesRef.current += 1;
        firstRunRef.current = { ...st, node: 'cant_hear', lastSaid: fix.say };
        emit(fix.say);
        if (fix.action === 'textOnly') {
          // She stops fighting the audio and simply carries on. No new question
          // — she was never owed one; the person still has whatever they came
          // here to say.
          firstRunRef.current = { ...st, node: 'open', lastSaid: fix.say };
        } else if (fix.action === 'replayGreeting' || fix.action === 'retryVoice' || fix.action === 'fallbackVoice') {
          // THE REPAIR. Their tap to answer "no" is itself the user gesture the
          // browser was waiting for, so this replay genuinely works where the
          // first attempt was blocked.
          const again = lastSpokenLineRef.current || "Hey, my name's Aria. Can you hear me okay?";
          setTimeout(() => { try { speakAriaLine(again, { track: true }); } catch (_) {} }, 800);
        }
        return;
      }

      // the node is passed so she can refuse to use a canned line where one is
      // always wrong — see NO_ASIDES_AT in firstRunFlow.js
      const aside = matchFirstRunAside(text, st.node);
      if (aside) {
        if (aside.end) {
          firstRunRef.current = null; setFirstRunOn(false);
          emit(aside.say);
          return;
        }
        if (aside.repeat) {
          // "what?" — say the same thing again, slower. Not a new sentence:
          // a new sentence is what makes people say "what?" a second time.
          const again = st.lastSaid || lineForFirstRun(st.node, st.data) || "I asked if you run a business.";
          setMessages((m) => [...m, { role: 'user', content: text, mode }, { role: 'assistant', content: again, mode }]);
          if (mode === 'voice') { try { speakAriaLine(again, { role: 'repeat', track: true }); } catch (_) {} }
          return;
        }
        const seen = st.asked || {};
        const back = reAskFirstRun(st.node, st.data, seen[st.node] || 0);
        const line = back ? `${aside.say} ${back}` : aside.say;
        firstRunRef.current = { ...st, lastSaid: line, asked: { ...seen, [st.node]: (seen[st.node] || 0) + 1 } };
        emit(line);
        return;
      }

      const step = advanceFirstRun(st.node, text, st.data);

      if (step.toBrain) {
        // step.data carries what she just learned (how much AI they know). It
        // was being dropped here, so the demo line came back generic even when
        // she had already read them as a developer (8/14).
        firstRunRef.current = { node: step.node, data: step.data || st.data, asked: st.asked || {} };
        // fall through: the brain takes this turn
      } else if (step.action) {
        firstRunRef.current = null; setFirstRunOn(false);
        const trade = (step.data && step.data.trade) || '';
        const because = (step.data && step.data.because) || '';
        const go = (line, fn) => {
          setMessages((m) => [...m, { role: 'user', content: text, mode }, { role: 'assistant', content: line, mode }]);
          try { rememberTurn(mode, [{ role: 'user', content: text }, { role: 'assistant', content: line }]); } catch (_) {}
          if (mode === 'voice') { try { speakAriaLine(line, { track: true }); } catch (_) {} }
          // "let the screen sit there why she talks to the user" (8/14): the
          // screen must not move mid-sentence. Wait for her audio to finish,
          // then a beat. Hard-capped so a failed clip can never strand it.
          const startedAt = Date.now();
          const waitForHerToFinish = () => {
            const stillTalking = ariaSpeakingRef.current;
            const tooLong = Date.now() - startedAt > 12000;
            if (stillTalking && !tooLong) { setTimeout(waitForHerToFinish, 120); return; }
            setTimeout(fn, 450);
          };
          // give the clip a moment to actually start before we watch for its end
          setTimeout(waitForHerToFinish, mode === 'voice' ? 700 : 350);
        };
        const nav = (sec) => { try { window.location.hash = sec; } catch (_) {} setDashboardSection(sec); };
        if (step.action === 'showSite') {
          // ── PULL THEIR SITE UP ON SCREEN ────────────────────────────────
          // A real screenshot of their real homepage, in a browser frame. It
          // takes ~5s (actual Chrome loading an actual page), so she says what
          // she's doing rather than going quiet — same rule as the library line.
          const url = (step.data && step.data.url) || '';
          setMessages((m) => [...m, { role: 'user', content: text, mode }]);
          try { rememberTurn(mode, [{ role: 'user', content: text }]); } catch (_) {}
          setLoading(true);
          setSitePreview({ url, loading: true });
          const looking = 'Pulling it up now.';
          setMessages((m) => [...m, { role: 'assistant', content: looking, mode }]);
          if (mode === 'voice') { try { speakAriaLine(looking, { track: true }); } catch (_) {} }

          (async () => {
            let d = null;
            try {
              const r = await fetch(`${API_BASE}/api/aria/site-preview`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url }),
              });
              if (r.ok) d = await r.json();
            } catch (_) {}
            setLoading(false);
            if (!d || !d.ok || !d.shot) {
              // Their site not loading is a fact about their site. Say it
              // plainly instead of pretending the feature failed.
              setSitePreview(null);
              const miss = "That one didn't load for me. I can still work with it — want me to take it over?";
              setMessages((m) => [...m, { role: 'assistant', content: miss, mode }]);
              if (mode === 'voice') { try { speakAriaLine(miss, { track: true }); } catch (_) {} }
              firstRunRef.current = { node: 'no_site', data: step.data || {}, asked: {} };
              return;
            }
            setSitePreview({ url, loading: false, ...d });

            // ── SHE SHOWS IT, THEN SAYS ONE LINE (Braeden 8/16) ───────────
            //   "she needs to use assist mode features to show me what she does"
            //
            // This is the first time she DEMONSTRATES instead of describing.
            // The site is on screen, so she boxes it — the same region cue she
            // uses on the desktop, which is also how the person learns what her
            // pointing means before she ever takes the screen.
            //
            // Region, not spotlight: she is talking ABOUT their site, not
            // rescuing someone who is lost. Dimming the whole screen here would
            // be the exact misuse the cue registry warns about.
            const say = (line) => {
              setMessages((m) => [...m, { role: 'assistant', content: line, mode }]);
              try { rememberTurn(mode, [{ role: 'assistant', content: line }]); } catch (_) {}
              if (mode === 'voice') { try { speakAriaLine(line, { track: true }); } catch (_) {} }
            };

            (async () => {
              await showCue({ kind: 'region', at: '[data-aria-cue="site"]', label: 'YOUR SITE' }, 2200);
              say(`That's you — ${d.host}.`);
              // What she found ON it, pointed at rather than recited.
              if (d.phone) {
                await showCue({ kind: 'popup', at: '[data-aria-cue="site"]',
                  title: 'Found on your site', body: `${d.phone} — every call off this goes in one list.` }, 3000);
              }
              await showCue({ kind: 'status', text: 'This is what I take over' }, 1800);
              say('I keep it current and chase what comes off it. Want me to take it?');
              firstRunRef.current = { node: 'no_site', data: { ...(step.data || {}), url }, asked: {} };
            })();
          })();
          return;
        }
        if (step.action === 'matchFeatures') {
          // ── SHE GOES AND LOOKS, AND SAYS SO IF IT TAKES A MOMENT ─────────
          //
          //   "(if it takes a while for her to think she needs to say that,
          //    Im going through the library to see the best way to help your
          //    situation (while it shows aria loading with the circle thing"
          //
          // The searching line is on a TIMER, not unconditional. If the match
          // comes back in 80ms — which it usually will, it is a lookup — then
          // announcing that she is searching is worse than saying nothing: it
          // invents a delay to fill. It only speaks if she is genuinely slow.
          const trade = (step.data && step.data.trade) || '';
          setMessages((m) => [...m, { role: 'user', content: text, mode }]);
          try { rememberTurn(mode, [{ role: 'user', content: text }]); } catch (_) {}
          setLoading(true);                     // ← this is what puts the ball into its thinking state
          let saidSearching = false;
          const searchTimer = setTimeout(() => {
            saidSearching = true;
            const line = "I'm going through the library to see the best way to help your situation.";
            setMessages((m) => [...m, { role: 'assistant', content: line, mode }]);
            if (mode === 'voice') { try { speakAriaLine(line, { track: true }); } catch (_) {} }
          }, 700);

          (async () => {
            let out = null;
            try {
              const r = await fetch(`${API_BASE}/api/aria/feature-match`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ trade }),
              });
              if (r.ok) out = await r.json();
            } catch (_) {}
            clearTimeout(searchTimer);
            setLoading(false);
            // If she announced the search, let that line finish before the
            // answer lands on top of it — she must never talk over herself.
            const deliver = () => {
              // ── ASK ABOUT HIM BEFORE NAMING HIS PROBLEMS (Braeden 8/16) ──
              //
              //   "she says with buinsess its mostly, than she goes off ... she
              //    needs to get an idea of this users situation before she talks
              //    about problems"
              //
              // She used to answer the trade with a paragraph: "With junk
              // removal, it's usually pricing a load sight unseen..." — a pain
              // assumed from the CATEGORY, told to a man she knows nothing
              // about, before he has said a word about how he works. Half the
              // time it is wrong, and being confidently wrong about someone's
              // business in the first minute is worse than saying nothing.
              //
              // So she acknowledges the trade and asks. Whatever he answers
              // lands on ariaTaskIntent, which starts the actual job — so the
              // question is not small talk, it is the thing that decides what
              // she switches on first.
              // Plain, answerable in three words, and the answer switches
              // something on. "What's eating most of your time?" asked a
              // stranger to diagnose his own business (8/16).
              const trade = (out && out.trade) || (step.data && step.data.trade) || '';
              const line = trade
                ? `Ok, ${trade}. What do customers call you on?`
                : 'What do customers call you on?';
              setMessages((m) => [...m, { role: 'assistant', content: line, mode }]);
              try { rememberTurn(mode, [{ role: 'assistant', content: line }]); } catch (_) {}
              if (mode === 'voice') { try { speakAriaLine(line, { track: true }); } catch (_) {} }
              // ── SHE ASKED. SHE WAITS. ─────────────────────────────────────
              // She used to fire the website question straight after this one,
              // which is two questions in a row and breaks the locked rule.
              // Worse, it asked for a thing before she knew whether the website
              // was even relevant to his problem.
              //
              // Now she stops. Whatever he answers goes to the server, where
              // ariaTaskIntent turns a stated problem into the automation that
              // fixes it — so "the phone rings while I'm under a sink" starts
              // missed-call texting, and THAT decides what she asks for next.
              // The website question comes later, if it is the right one.
              firstRunRef.current = { node: 'pain', data: { ...(step.data || {}) }, asked: {} };
            };
            if (saidSearching && mode === 'voice') {
              const started = Date.now();
              const wait = () => {
                if (ariaSpeakingRef.current && Date.now() - started < 8000) { setTimeout(wait, 120); return; }
                deliver();
              };
              setTimeout(wait, 500);
            } else deliver();
          })();
          return;
        }
        if (step.action === 'openBusinessSetup') {
          // NOT "what's it called?" — the setup box opens with exactly that
          // question, and asking it twice in two seconds reads as not listening.
          go("Alright, let's build it.", () => { setBizText(''); setBizPlanBusy(false); setBizPlusOpen(true); });
        } else if (step.action === 'goLeads') {
          go(`Then ${because || 'that'} is where we start. This is where every lead lands.`, () => nav('leads'));
        } else if (step.action === 'goBooks') {
          go(`Then ${because || 'that'} is where we start. Here's every dollar in and out.`, () => nav('books'));
        } else if (step.action === 'goJobs') {
          go(`Then ${because || 'that'} is where we start. Here's the schedule.`, () => nav('jobs'));
        } else {
          go("All good. I'm right here — ask me anything.", () => {});
        }
        return;
      } else {
        // her next question — instant, straight from the client
        firstRunRef.current = { node: step.node, data: step.data, lastSaid: step.say, asked: st.asked || {} };
        setMessages((m) => [...m, { role: 'user', content: text, mode }, { role: 'assistant', content: step.say, mode }]);
        try { rememberTurn(mode, [{ role: 'user', content: text }, { role: 'assistant', content: step.say }]); } catch (_) {}
        if (mode === 'voice') { try { speakAriaLine(step.say, { track: true }); } catch (_) {} }
        return;
      }
    }

    // ── Assist-mode confirmation (local — no server round-trip) ──────────────
    // Aria already asked "Want me to start Assist?" (confirmFirst) and is waiting
    // on a yes/no. Handle it RIGHT HERE so a simple "yes" — voice OR text —
    // launches Assist instantly, instead of the bare word "yes" falling through
    // to the brain as small talk. One-shot: we consume the flag on entry.
    if (pendingAssistConfirmRef.current && text) {
      pendingAssistConfirmRef.current = false; // consume — single use
      const a = String(text).toLowerCase().replace(/[^a-z0-9\s']/g, ' ').replace(/\s+/g, ' ').trim();
      const SAYS_YES = /^(y|ya|ye|yea|yes+|yup|yep|yeah|yeh|sure|ok|okay|kk?|do it|go|go ahead|go for it|lets go|let's go|sounds good|sound good|please|please do|yes please|absolutely|definitely|for sure|of course|start|begin|jump in|take over|take control|go on|alright|aight)\b/;
      const SAYS_NO = /^(n|no|nope|nah|not now|not yet|never mind|nevermind|cancel|stop|don'?t|do not|hold on|hold up|wait|maybe later|later)\b/;
      const mode = _inputMode === 'voice' ? 'voice' : 'text';
      if (SAYS_YES.test(a)) {
        setMessages((m) => [...m,
          { role: 'user', content: text, mode },
          { role: 'assistant', content: 'On it — starting Assist now.', mode },
        ]);
        try {
          if (window.electronAPI?.sendOverlayAssistMode) window.electronAPI.sendOverlayAssistMode('assist');
          // No nav: the Assist page is never shown. requestScreenShare sets
          // screenStream, which mounts the hidden engine that owns the wire-up.
          // The OS screen-permission prompt still comes from this call, and
          // main.js still plays "Assist is on." as her spoken confirm.
          setTimeout(() => { try { if (typeof requestScreenShare === 'function') requestScreenShare(); } catch (_) {} }, 60);
        } catch (e) { console.warn('[assist-confirm] launch failed:', e?.message); }
        return;
      }
      if (SAYS_NO.test(a)) {
        const line = "No worries — just say the word whenever you want me to jump in.";
        setMessages((m) => [...m,
          { role: 'user', content: text, mode },
          { role: 'assistant', content: line, mode },
        ]);
        if (mode === 'voice') speakAriaLine(line);
        return;
      }
      // Neither yes nor no → the user moved on (e.g. "actually open chrome").
      // Fall through to the normal pipeline so that still works.
    }

    // ── Two-path help choice (show vs do) — local, no extra server hop ───────
    // Aria offered "show you how, or do it for you?" and is waiting. Route the
    // answer: re-send the REMEMBERED task with helpMode so the server gives a
    // walkthrough ('show') or takes control ('do'). Works for voice + text.
    if (pendingHelpRef.current && text) {
      const task = pendingHelpRef.current.task;
      pendingHelpRef.current = null; // consume — one shot
      const a = String(text).toLowerCase().replace(/[^a-z0-9\s']/g, ' ').replace(/\s+/g, ' ').trim();
      const SAYS_DO = /^(do it|you do it|just do it|do this|do that|take (?:over|control)|handle it|go ahead(?: and do)?|please do|you can do it|second|option (?:two|2)|the second one|yes do it)\b/;
      const SAYS_SHOW = /^(show(?: me)?(?: how)?|walk me through|teach me|guide me|i'?ll do it|let me|first|option (?:one|1)|the first one|just show)\b/;
      const mode = _inputMode === 'voice' ? 'voice' : 'text';
      if (SAYS_DO.test(a)) {
        setMessages((m) => [...m, { role: 'user', content: text, mode }]);
        sendMessageWithText(task, { inputMode: _inputMode, helpMode: 'do' });
        return;
      }
      if (SAYS_SHOW.test(a)) {
        setMessages((m) => [...m, { role: 'user', content: text, mode }]);
        sendMessageWithText(task, { inputMode: _inputMode, helpMode: 'show' });
        return;
      }
      // Neither → user changed their mind / asked something else; fall through.
    }

    // ── Onboarding state machine ─────────────────────────────────────────
    // Each stage owns one user reply. Aria responds locally (no LLM), then
    // advances. After the final stage we set onboardingStage=null and the
    // message routes through the regular Aria conversation flow.
    if (onboardingStageRef.current && text) {
      const stage = onboardingStageRef.current;
      const userId = (typeof localStorage !== 'undefined' && localStorage.getItem('aria_user_id')) || null;

      // Helper: append user msg + Aria reply to chat in one go.
      // Tagged with the mode the turn actually arrived in (9/2). This used to
      // hard-code 'text' so onboarding lines would show in the text surface -
      // which also put SPOKEN onboarding answers into the search chat.
      // Braeden 9/2: "the tts is a different conversation than the text chat,
      // which means nothing should be displayed in the text chat." A spoken
      // turn renders no text anywhere; a typed one renders in the thread.
      const exchange = (ariaText) => {
        setMessages((m) => [
          ...m,
          { role: 'user', content: text, mode: _inputMode },
          { role: 'assistant', content: ariaText, mode: _inputMode },
        ]);
      };

      try {
        // GATE — only "no" runs onboarding; anything else silently skips
        if (stage === 'gate') {
          const r = await fetchWithTimeout(`${API_BASE}/api/onboarding/gate/${userId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: text }),
            timeout: API_TIMEOUT_MS,
          });
          const data = await r.json();
          if (data?.ok && data.verdict === 'answeredNo') {
            setOnboardingStage('unmute');
            onboardingStageRef.current = 'unmute';
            exchange("Perfect — let me walk you through. First, tap the unmute mic button down below so I can talk to you. (If macOS asks, allow microphone access.) When you're ready, just say hi.");
            return;
          }
          // "yes" or random message → silently disarm and fall through to normal Aria
          setOnboardingStage(null);
          onboardingStageRef.current = null;
        }

        // UNMUTE — any reply advances to NAME
        else if (stage === 'unmute') {
          setOnboardingStage('name');
          onboardingStageRef.current = 'name';
          exchange("Got it. So — who do I have the pleasure of speaking with?");
          return;
        }

        // NAME — strip filler ("I'm", "My name is", etc.) and save
        else if (stage === 'name') {
          const name = text
            .replace(/^\s*(my\s+name'?s?\s+is\s+|i'?m\s+|it'?s\s+|hi[,\s]+)/i, '')
            .replace(/[.!?]+\s*$/, '')
            .trim()
            .slice(0, 60);
          await fetchWithTimeout(`${API_BASE}/api/onboarding/name/${userId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name }),
            timeout: API_TIMEOUT_MS,
          }).catch(() => {});
          setOnboardingStage('branch');
          onboardingStageRef.current = 'branch';
          const friendly = name && name.length >= 2 ? name : 'friend';
          exchange(`Nice to meet you, ${friendly}. Quick one — do you run a business, or are you here for personal stuff?`);
          return;
        }

        // BRANCH — yes → CEO; no → consumer
        else if (stage === 'branch') {
          const t = text.toLowerCase();
          const isBusinessOwner = (
            /\b(yes|yeah|yep|yup|i\s+(own|run|operate|manage)|i'?m\s+the\s+(owner|ceo|founder)|business\s+owner)\b/.test(t)
          );
          await fetchWithTimeout(`${API_BASE}/api/onboarding/branch/${userId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ isBusinessOwner }),
            timeout: API_TIMEOUT_MS,
          }).catch(() => {});

          if (isBusinessOwner) {
            // Hand off to existing CEO mode entry — their next message
            // will fire the standard CEO triage flow on the backend.
            setOnboardingStage(null);
            onboardingStageRef.current = null;
            exchange("Perfect. I'm great at organization and time-efficiency — let me show you. Tell me about your business: what do you run? (Like \"I run a plumbing company\" or \"I have a small clinic.\")");
            return;
          } else {
            setOnboardingStage('consumer');
            onboardingStageRef.current = 'consumer';
            exchange("Cool. So I can show you the parts you'll actually use — what do you like to do in your free time?");
            return;
          }
        }

        // CONSUMER — Phase 3 will analyze + pick demos; v1 just acknowledges
        else if (stage === 'consumer') {
          await fetchWithTimeout(`${API_BASE}/api/onboarding/stage/${userId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              stage: 'subscription',
              consumerInterests: [text.slice(0, 200)],
            }),
            timeout: API_TIMEOUT_MS,
          }).catch(() => {});
          await fetchWithTimeout(`${API_BASE}/api/onboarding/complete/${userId}`, {
            method: 'POST', timeout: API_TIMEOUT_MS,
          }).catch(() => {});
          setOnboardingStage(null);
          onboardingStageRef.current = null;
          exchange(`Nice — I'll keep that in mind. Try me with anything: ask a question, generate an image, look something up. I'll learn what works for you as we go.`);
          return;
        }
      } catch (e) {
        console.warn('[onboarding] stage handler failed:', e?.message);
        setOnboardingStage(null);
        onboardingStageRef.current = null;
      }
    }

    if (isDesktopApp && !hasSpokenDesktopIntro) {
      setVoiceError("Let me introduce myself first.");
      return;
    }
    if (introLockedRef.current || introLocked) {
      setVoiceError("Let me finish introducing myself, then tell me your name.");
      return;
    }
    if (!text) return;
    // Voice policy gate removed — allow all typed messages through
    if (isDesktopApp && awaitingIntroNameRef.current) {
      // Clear name gate — let all messages through
      awaitingIntroNameRef.current = false;
    }
    if (loading) {
      setVoiceError('Aria is replying — wait a moment, then try again.');
      return;
    }

    // ── Action detection: "open google chrome", "open youtube", etc. ──
    const actionMatch = detectHomeAction(text);
    if (actionMatch) {
      const executed = executeHomeAction(actionMatch);
      if (executed) {
        const confirmText = actionMatch.label ? `Opening ${actionMatch.label}.` : 'On it.';
        setMessages((m) => [...m, { role: 'user', content: text, mode: _inputMode }, { role: 'assistant', content: confirmText, mode: _inputMode }]);
        addConversationEntry('user', text);
        addConversationEntry('aria', confirmText);
        // Learn user preferences from actions
        learnPreference(text, actionMatch);
        // TTS the confirmation — ONLY for spoken commands. Typed command =
        // typed reply; the action still runs either way (modality never
        // gates assist), only the voice does.
        if (_inputMode !== 'voice') return;
        try {
          const base = getApiBase() || API_BASE || '';
          const ttsUrl = `${String(base).replace(/\/$/, '')}/tts`;
          const r = await fetchWithTimeout(ttsUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: confirmText, ...ariaUserIdJsonBody() }),
            timeout: 30000,
          });
          if (r.ok) {
            const buf = await r.arrayBuffer();
            const bytes = new Uint8Array(buf);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            const dataUrl = `data:audio/wav;base64,${btoa(binary)}`;
            if (dataUrl.length > 100) {
              unlockAudioForReply();
              const audio = new Audio(dataUrl);
              prepareAriaPlayback(audio);
              activeReplyAudioRef.current = audio;
              audio.onplay = () => setAriaSpeaking(true);
              audio.onended = () => { if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null; setAriaSpeaking(false); try { ttsPostPlaybackUntilRef.current = Date.now() + TTS_TAIL_GATE_MS; } catch (_) {} };
              audio.onerror = () => { if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null; setAriaSpeaking(false); };
              audio.play().catch(() => setAriaSpeaking(false));
            }
          }
        } catch (_) {}
        return;
      }
    }

    // Any real message resumes proactive check-ins
    proactivePausedRef.current = false;
    // DO NOT call stopAriaPlayback() here — that was killing Aria's sentence
    // mid-word whenever the mic picked up her own voice as a "new user turn".
    // She should ALWAYS finish what she's saying. When the next reply audio
    // is ready, setting <audio>.src naturally swaps it. If users genuinely
    // want to interrupt, that goes through an explicit stop button, not
    // ambient mic noise.
    // Append the user's bubble IMMEDIATELY so they see their message land
    // before Aria starts thinking. Without this, the bubble only shows
    // up after the API responds — and on slow replies the user thinks
    // their text vanished. The assistant message is appended in the
    // response handler below (and the duplicate user-append there is
    // stripped to avoid double-rendering).
    // Skip when a referenceVideo is attached — the video path below
    // appends its own user message with hasVideo + videoName fields.
    if (!referenceVideo) {
      setMessages((m) => [...m, { role: 'user', content: text, mode: _inputMode }]);
    }
    // Sound: outgoing "send" chirp the instant the user fires off a text.
    // Then start the soft "..." thinking loop until Aria replies.
    if (_inputMode === 'text') {
      try { playSend(); } catch (_) {}
      try { startThinking(); } catch (_) {}
      // Stamp the send time so the minimum-think hold window can enforce a
      // ~1s "..." pause before Aria's reply bubble appears, even when the
      // backend responds faster than that.
      lastTextSendRef.current = Date.now();
    }
    // ASSIST MODE: EVERY TURN IS A COMMAND FOR THE SCREEN (Braeden 8/22: "if i
    // tell her to scroll down in assist mode, she doesnt do it ... she only
    // knows how to open up google tabs"). While she has the screen, what he
    // says goes to the screen engine and NOWHERE else - not the conversation
    // pipeline, not this window's voice. One listener, one executor, one
    // voice. The engine speaks its own result.
    const taskTurn=prepareTaskTurn(text,{inputMode:_inputMode});
    const appScopedRequest = isDesktopApp ? taskTurn.request : text;
    if (screenStream && typeof window !== 'undefined' && window.electronAPI) {
      // THE DOUBLE TEXT (8/25). This branch used to append the user's bubble a
      // SECOND time - the block ~20 lines above already appended it. One
      // utterance, two setMessages calls, two identical blue bubbles, on every
      // single assist turn. His screenshot showed "Open up settings." twice.
      //
      // It was never a double SEND. I widened a duplicate-turn guard from 1.5s
      // to 10s chasing this, and it could never have worked: the guard drops a
      // second CALL, and there was only ever one call. The turn reached the
      // backend once and did the work once - only the rendering doubled.
      //
      // The append above is skipped only when a referenceVideo is attached (that
      // path appends its own bubble with video fields), so cover that one case
      // and nothing else.
      if (referenceVideo) {
        const mode0 = (opts && opts.inputMode === 'voice') ? 'voice' : 'text';
        setMessages((m) => [...m, { role: 'user', content: text, mode: mode0 }]);
      }
      setInput('');
      const turn = voiceTurnRef.current.begin('assist', { timeoutMs: 240000 });
      voiceTrace.record('host.assist-handoff', { id: turn.id, owner: turn.owner });
      try { setInjectQuestion({ text: String(appScopedRequest), voiceTurn: turn, inputMode: opts?.inputMode === 'voice' ? 'voice' : 'text', diagnosticTest: opts?.diagnosticTest === true, capability:opts?.capability }); }
      catch (_) { turn.cancel(); }
      return;
    }

    // Preserve the original spoken task through permission/capture startup.
    // A conversational reply must not replace it with a generic screen check.
    if (isDesktopApp && (isAssistWorkRequest(appScopedRequest) || isAssistDiscoveryRequest(text) || isAssistInspectionRequest(text))) {
      setInput('');
      setAssistAsk({ task: String(appScopedRequest), inputMode: _inputMode, steps: [], diagnosticTest:opts?.diagnosticTest===true,capability:opts?.capability, reason: 'look at your screen and carry out your request' });
      return;
    }

    if (opts?.diagnosticTest === true) {
      const line='Transcript test: this request routes to conversation. An authenticated GPT response has not been requested because paid conversation tests are disabled.';
      setMessages(m=>[...m,{role:'assistant',content:line,mode:'text'}]);
      return;
    }

    setLoading(true);

    // ── SHE SAYS SHE IS WORKING (Braeden 8/22) ───────────────────────────
    //   "the verification taking time is fine, you just need to use other
    //    voice features to settle the time, like if it takes a minute for aria
    //    to find the answer, she just needs to say that — give me 5 seconds
    //    and ill find the real reason."
    //
    // Silence is what makes a wait feel broken, not the wait. Out loud, four
    // seconds of nothing reads as "she didn't hear me" and you talk over her.
    //
    // ON A TIMER, NEVER UNCONDITIONAL — the same rule the searching line
    // already follows further up this file: most turns answer in well under a
    // second, and announcing a delay that is not happening invents one. Voice
    // only; on the typed surface you can see the thinking dots.
    // ONLY WHEN SHE IS ACTUALLY DOING SOMETHING (Braeden 8/22): "i dont need
    // her to say hang on im looking that up, EVERY TIME only when shes
    // actually doing it, if its easy replies just wait". The timer used to arm
    // on every voice turn, and once the turn reader added a couple of seconds
    // to ordinary replies, every reply got a "hang on". So: the hold line is
    // armed only for turns that LOOK like work - a lookup, a make/open/set-up
    // ask, a screen task - and even then not before 3.5 seconds. An easy reply
    // that takes two seconds gets two seconds of quiet, which is fine.
    const _looksLikeWork = /\b(what(?:'s| is| are| does| do| did)|who(?:'s| is)|when (?:is|was|does|did)|where (?:is|are|can)|how (?:much|many|long|far|old|big|do i|does)|difference between|look ?up|search|find|google|research|check|help me (?:make|build|write|set up|create)|make (?:me |an? )|write (?:me |an? )|build|set up|open|go to|pull up|show me|calculate|price|quote|compare)\b/i
      .test(String(text || ''));
    let heldOn = false;
    // ── THE SECOND VOICE-ONLY INTERJECTION (8/25) ─────────────────────────
    // There were TWO of these, not one. The stutter filler fired at 2,500ms and
    // this hold line fired at 3,500ms, both gated on `_inputMode === 'voice'`
    // and both speaking a whole sentence before the answer. On a slow turn he
    // could get:
    //
    //   "Give me a second."      (2.5s)
    //   "Let me pull that up."   (3.5s)
    //   the actual answer
    //
    // Three spoken responses to one question, none of it possible while typing
    // - which is exactly why text felt fine and talking felt broken. Disabling
    // only the filler would have left this one still doubling him up.
    //
    // Both also set ariaSpeaking, and the echo gate drops every transcript
    // while that is set, so each interjection was a window where she could not
    // hear him at all.
    //
    // One turn, one spoken response. speakAriaLine and HOLD_LINES stay for a
    // deliberate bridge later; nothing schedules them behind his back.
    const holdTimer = null;
    const clearHold = () => { if (holdTimer) clearTimeout(holdTimer); };

    const isFirst = conversationHistory.filter((h) => h.role === 'user').length === 0;
    const queryToSend = initialQuery || text;
    if (!initialQuery) setInitialQuery(text);

    // The task decides which chain travels. A search turn carries the search
    // chain; a spoken turn carries the conversation. Same field either way, so
    // none of the ~98 lanes have to learn a new one — what changes is which
    // history they are reasoning over.
    const _isSearchTurn = _inputMode !== 'voice';
    const _track = _isSearchTurn ? searchTrack : conversationHistory;
    const historyForApi = _track.map((h) => ({ role: h.role, content: h.content }));
    const body = {
      // Identify the account on EVERY conversation turn (2026-07-15). Without
      // this the server resolved userId=null → plan 'free' → feature gates
      // walled even the owner ("that's a member feature" on his own machine).
      // Same source the transcribe/billing calls already use.
      userId: ensureAriaUserId() || 'default',
      // The desktop says so on every turn (9/26): the web-only first-time
      // onboarding lane refuses this marker, and machineContext alone is absent
      // until the first foreground poll lands.
      ...(isDesktopApp ? { clientPlatform: 'desktop' } : {}),
      // The tour rides along so the server can decide, per turn, whether to
      // keep showing her features or get out of the way (8/31).
      // `seed` picks the warm-up question from the server's pool — a different
      // one each visit (Braeden 9/2: "that's what makes her fun").
      ...(isOnboardingActive() ? { onboarding: true } : {}),   // web-only first-time flow in progress (onboardingRoute.js)
      ...(tourBeat ? { tour: { on: true, beat: tourBeat, seed: tourSeedRef.current, goal: tourGoalRef.current || '', lastQ: lastAskedRef.current || '', pace: (tourPaceRef.current && tourPaceRef.current.mode) || 'normal' } } : {}),
      // ── THE PROJECT IS HER MEMORY OF IT (Braeden 9/3) ────────────────────
      // "when the user talks she remembers what they say, by taking the notes
      //  for data... that's why there are different projects." The selected
      // project rides with every turn - its goal and every note so far - and
      // the other projects come along (names + notes) so she can spot when
      // something connects to earlier work.
      ...(selectedProject ? {
        project: {
          id: selectedProject.id, name: selectedProject.name, goal: tourGoalRef.current || selectedProject.goal || '',
          notes: (Array.isArray(selectedProject.notes) ? selectedProject.notes : []).slice(-24),
          others: (Array.isArray(userProjects) ? userProjects : []).filter((p) => p && p.id !== selectedProject.id).slice(0, 12)
            .map((p) => ({ id: p.id, name: p.name, notes: (Array.isArray(p.notes) ? p.notes : []).slice(-8) })),
        },
        _lastAsked: lastAskedRef.current || '',
      } : {}),
      // What they did on her screen since her last turn, plus where things
      // stand now (9/2). The server lane confirms, corrects, and remembers.
      uiTrail: (() => { try { return uiTrailRef.current.filter((e) => e.t > uiSentAtRef.current).slice(-12); } catch (_) { return []; } })(),
      uiState: { menuOpen: !!leftSidebarOpen, newProjectBox: !!personalProjOpen, project: (selectedProject && selectedProject.name) || null, view: effectiveSection || null },
      // What is in front of him on the desktop (9/5): the front window and
      // the last few he switched from. ariaSeesTheScreen turns it into the
      // same "[I can see it: ...]" note her own UI gets.
      ...(machineCtxRef.current && machineCtxRef.current.foreground && machineCtxRef.current.foreground.app ? {
        machineContext: {
          foreground: { app: machineCtxRef.current.foreground.app, title: machineCtxRef.current.foreground.title || '' },
          recentFocus: (Array.isArray(machineCtxRef.current.recentFocus) ? machineCtxRef.current.recentFocus : []).slice(0, 3)
            .map((r) => ({ app: r.app, title: r.title || '', secondsAgo: r.secondsAgo })),
        },
      } : {}),
      initialQuery: queryToSend,
      conversationHistory: historyForApi,
      // Which of the two things is running. The bar is a search engine; the
      // mic is the conversation that can operate it.
      track: _isSearchTurn ? 'search' : 'conversation',
      // SHE IS THE CENTRAL POINT, SO SHE KNOWS WHAT WAS LOOKED UP. Awareness,
      // not dialogue: the last few queries, never pasted in as things he said.
      ...(!_isSearchTurn && searchTrack.length
        ? { recentSearches: searchTrack.filter((h) => h.role === 'user').slice(-4).map((h) => h.content) }
        : {}),
      // ── WHAT SHE CAN SEE (Braeden 8/16) ─────────────────────────────────
      //   "she needs to be aware of whats happening everywhere ... whats on
      //    the screen, whats being typed"
      // Every turn used to ship the words and nothing else — no page, no
      // typed text, no idea a website was up on screen. "What am I looking
      // at?" was unanswerable and "open my leads" had nowhere to land.
      screen: ariaSnapshot({
        section: effectiveSection,
        typed: input,
        micOn, speaking: ariaSpeaking, loading,
        firstRunNode: firstRunRef.current ? firstRunRef.current.node : null,
        firstRunData: firstRunRef.current ? firstRunRef.current.data : null,
        sitePreview, bizPlusOpen, screenShareOn: !!screenStream,
        messages,
      }),
      language: 'en',
      prompt: text.trim(), // Voice or typed: relay this as the user prompt to the AI
      modelPref, // 'auto' | 'opus' | 'sonnet' — user preference from header dropdown
      personaPref, // 'personal' | 'work'
      stakesPref,  // 'auto' | 'low' | 'med' | 'high'
      approvalPref, // 'manual' | 'reversible' | 'trust'
      verifyPref,   // true | false
    };
    const preferredName = preferredNameRef.current || loadPreferredName();
    if (preferredName) {
      body.preferredName = preferredName;
      body.assistantStyle = {
        usePreferredName: true,
        // Use name strategically for attention or warmth, but avoid repetitive overuse.
        preferredNameUsage: 'occasional',
      };
    }
    if (!isFirst) body.currentAnswer = text;
    // Skip inline TTS so the backend returns text faster -- we'll fetch TTS in parallel
    body.skipAudio = true;
    // Tell the backend which input method was used. When 'text', the backend
    // skips the audio pipeline entirely (no TTS, no audioStreamUrl) so this
    // turn comes back as text-only. Mic submissions pass 'voice' for the
    // normal Shimmer audio reply.
    body.inputMode = _inputMode;
    if(isDesktopApp) {
      body.taskTurn=taskTurn;
      // Keep the actual spoken task and app in ordinary chat as well as Assist.
      if(body.prompt===text) body.prompt=appScopedRequest;
      if(body.currentAnswer===text) body.currentAnswer=appScopedRequest;
    }

    // Attach voice read (emotion/prosody/quality from Whisper) for voice turns.
    // Consumed once per turn; reset so stale reads never bleed into typed turns.
    if (_inputMode === 'voice' && lastVoiceReadRef.current) {
      body.voiceRead = lastVoiceReadRef.current;
      lastVoiceReadRef.current = null;
    }
    // the waterfall: lift the turn id and the ear's stamps out of the read into
    // clientTiming (what the lanes read is unchanged); this browser's later stamps
    // key on the same id
    if (_inputMode === 'voice') _voiceTurnTiming = null;   // a typed turn mid-playback must not blank the voice turn's id
    try {
      const vr = body.voiceRead;
      if (_inputMode === 'voice' && vr && vr.turnId) {
        _voiceTurnTiming = { turnId: String(vr.turnId) };
        body.clientTiming = { turnId: String(vr.turnId), earTiming: vr.earTiming || null, finalReceivedAt: Number(vr.finalReceivedAt) || 0, sentAt: 0 };
        delete vr.earTiming; delete vr.finalReceivedAt;
      }
    } catch (_) {}

    // Commit mode — send active task so Aria stays locked onto it until done.
    if (commitTaskRef.current) body.commitTask = commitTaskRef.current;

    // Include user preferences so AI knows what user likes
    const userPrefs = loadPreferences();
    if (userPrefs && Object.keys(userPrefs).length > 0) {
      body.userPreferences = userPrefs;
    }

    const userId = typeof localStorage !== 'undefined' ? localStorage.getItem('aria_user_id') : null;
    if (userId) body.userId = userId;
    // Pass session duration so backend can inject break/session context
    body.sessionMinutes = Math.floor((Date.now() - sessionStartRef.current) / 60000);
    // Tell backend if we're in an active conversation so Aria asks follow-ups after tasks
    if (ariaAttentionRef.current === 'attentive') body.activeConversation = true;

    const refImagesForApi = referenceImages.filter((r) => r.dataBase64).map((r) => ({ mimeType: r.mimeType || 'image/jpeg', dataBase64: r.dataBase64 }));
    if (refImagesForApi.length > 0) body.referenceImages = refImagesForApi;

    // Use getApiBase() at request time so Mac app always uses apiBase from URL query (injected by main process)
    const base = getApiBase() || (typeof window !== 'undefined' ? window.location.origin : '') || API_BASE;

    // ── Video analysis: if a video is attached, route to /api/ai/analyze-video instead ──
    if (referenceVideo && referenceVideo.dataBase64) {
      const videoUrl = base ? `${String(base).replace(/\/$/, '')}/api/ai/analyze-video` : '/api/ai/analyze-video';
      setMessages((m) => [...m, { role: 'user', content: text, hasVideo: true, videoName: referenceVideo.file?.name, mode: _inputMode }]);
      addConversationEntry('user', text);
      setReferenceVideo(null); // Clear after sending

      try {
        const videoBody = {
          videoBase64: referenceVideo.dataBase64,
          mimeType: referenceVideo.mimeType || 'video/mp4',
          question: text,
          skipAudio: true,
          activeConversation: ariaAttentionRef.current === 'attentive',
        };
        const userId = typeof localStorage !== 'undefined' ? localStorage.getItem('aria_user_id') : null;
        if (userId) videoBody.userId = userId;

        const resp = await fetchWithTimeout(videoUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(videoBody),
          timeout: 60000,
        });
        const data = await resp.json();
        if (data.ok) {
          const replyText = data.solution || data.reply || data.reasoning || 'I analyzed the video but could not produce a response.';
          setMessages((m) => [...m, { role: 'assistant', content: replyText, videoAnalysis: true, mode: _inputMode }]);
          addConversationEntry('aria', replyText);
          // Fetch TTS in parallel — spoken questions only; typed video
          // questions read the answer in chat.
          if (_inputMode === 'voice' && replyText.length > 0 && replyText.length < 5000) {
            const ttsUrl = base ? `${String(base).replace(/\/$/, '')}/tts` : '/tts';
            fetchWithTimeout(ttsUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: replyText, ...ariaUserIdJsonBody() }), timeout: 30000 })
              .then((r) => { if (!r.ok) throw new Error('TTS failed'); return r.arrayBuffer(); })
              .then((buf) => {
                if (!buf || buf.byteLength === 0) return;
                const bytes = new Uint8Array(buf);
                let binary = '';
                for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
                const dataUrl = `data:audio/wav;base64,${btoa(binary)}`;
                if (dataUrl.length > 100) {
                  unlockAudioForReply();
                  const audio = new Audio(dataUrl);
                  prepareAriaPlayback(audio);
                  activeReplyAudioRef.current = audio;
                  ariaSpeakingRef.current = true;
                  audio.onplay = () => setAriaSpeaking(true);
                  audio.onended = () => { if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null; ariaSpeakingRef.current = false; setAriaSpeaking(false); };
                  audio.onerror = () => { if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null; ariaSpeakingRef.current = false; setAriaSpeaking(false); };
                  audio.play().catch(() => setAriaSpeaking(false));
                }
              }).catch(() => {});
          }
        } else {
          const errMsg = data.message || 'Video analysis failed.';
          setMessages((m) => [...m, { role: 'assistant', content: errMsg, mode: _inputMode }]);
        }
      } catch (videoErr) {
        console.error('[Aria] Video analysis error:', videoErr);
        setMessages((m) => [...m, { role: 'assistant', content: 'Something went wrong analyzing the video. Please try again.', mode: _inputMode }]);
      }
      clearHold(); setLoading(false);
      if (!_willSpeak) { try { releaseFloorRef.current?.(); } catch (_) {} try { setPreparingSpeech(false); } catch (_) {} }
      return;
    }

    const conversationUrl = base ? `${String(base).replace(/\/$/, '')}/api/ai/conversation` : '/api/ai/conversation';
    const h = typeof window !== 'undefined' ? window.location?.hostname : '';
    const isLocalhost = h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h === '::1';
    const fallbackUrls = [
      conversationUrl,
      ...(isLocalhost
        ? ['http://127.0.0.1:3001/api/ai/conversation', 'http://localhost:3001/api/ai/conversation']
        : []),
    ].filter((u, i, arr) => arr.indexOf(u) === i);
    if (import.meta.env?.DEV || (typeof window !== 'undefined' && window.electronAPI)) console.log('[Aria] Sending to:', fallbackUrls[0]);

    // ── Unlock audio context NOW, inside the user-gesture handler ──────────
    // Previously the ack-audio play() did this implicitly. With ack disabled,
    // the reply audio arrives ~400ms later — past the gesture grace window —
    // so we play a silent WAV here while we're still in the user's click/keypress.
    unlockAudioForReply();

    // ── Acknowledgment removed ─────────────────────────────────────────
    // The real reply now arrives in <800ms, so the pre-reply "got it" audio
    // is no longer needed. Removing the fire-and-forget /api/ai/ack call
    // saves ~300ms of CPU contention on every conversation turn.
    let ackAudio = null;

    try {
      let res = null;
      let lastNetworkErr = null;
      // Attach this desktop's deviceId so the backend can route a headless Assist
      // task to THIS machine — this is what lets Aria actually take control and
      // DO a spoken/typed command ("organize my downloads", "clean up my desktop")
      // instead of only describing it. Fetched once and cached; no-op on web.
      try {
        if (typeof window !== 'undefined' && window.electronAPI?.getAgentConfig) {
          if (!window.__ariaDeviceId) {
            const _cfg = await window.electronAPI.getAgentConfig();
            window.__ariaDeviceId = _cfg?.deviceId || null;
          }
          if (window.__ariaDeviceId && body && typeof body === 'object') body.deviceId = window.__ariaDeviceId;
        }
      } catch (_) { /* deviceId is best-effort */ }
      // Carry the two-path help choice ('show' | 'do') so the server routes it.
      // Plus keep a TEACH session going across turns: once Aria is coaching, every
      // follow-up stays in teach mode (with recent context) until the user clearly
      // exits ("thanks", "got it", "just do it for me").
      if (body && typeof body === 'object') {
        const _exitTeach = /\b(thanks|thank you|got it|perfect|that worked|all set|never ?mind|do it for me|just do it|stop teaching|nvm)\b/i.test(String(text || ''));
        if (_exitTeach) teachActiveRef.current = false;
        const _helpMode = (opts && opts.helpMode) || (teachActiveRef.current && !_exitTeach ? 'show' : null);
        if (_helpMode) {
          body.helpMode = _helpMode;
          try {
            if (Array.isArray(messages)) {
              body.conversationHistory = messages.slice(-6).map((m) => ({
                role: m.role === 'assistant' ? 'assistant' : 'user',
                content: String(m.content || ''),
              }));
            }
          } catch (_) { /* history is best-effort */ }
        }
      }
      // Slow-answer cover: if this VOICE turn takes more than ~900ms, Aria
      // covers the gap so the user isn't left in silence while she reasons /
      // looks it up. Two flavors, cancelled the instant the real reply lands:
      //   • BRIDGE (preferred) — a specific, true line naming what she's doing
      //     ("Let me pull the HVAC pricing data — one sec"). Plays while the
      //     real answer generates; the reply WAITS for it (never cut).
      //   • FILLER (fallback) — a generic stutter when no bridge is warranted.
      fillerCancelRef.current = false;
      fillerTurnRef.current += 1;              // new turn — orphan any in-flight filler/bridge
      const _fillerTurn = fillerTurnRef.current;
      bridgeTextRef.current = null;
      bridgeAudioRef.current = null;
      bridgeDoneRef.current = null;
      let _replyArrived = false;
      // A pending answer is not proof that a lookup or tool action is running.
      // Keep acknowledgments generic; never echo the user's prompt as a task.
      // No filler on continuations/acks (Braeden 8/12: "she says let me double
      // check when it doesnt make sense"). "Let me check on that." after a
      // bare "yes"/"what"/"okay" is nonsense — there's nothing to check. Short
      // conversational turns get silence (the typing dots already show she's
      // thinking); fillers are for real questions that take real time.
      const _skipFiller = /^(y(es|ep|eah|ea|up)?|no|nope|nah|ok(ay)?|k|sure|right|correct|exactly|mhm|uh huh|what|huh|wha|hm+|thanks?|thank you|cool|nice|got it|i see|alright|aight)[\s.!?,]*$/i
        .test(String(text || '').trim()) || String(text || '').trim().split(/\s+/).length <= 2;
      // Give ordinary replies time to arrive without a second spoken response.
      // Short arithmetic questions need the answer, not an acknowledgment.
      const _simpleArithmetic = /(?:\d|zero|one|two|three|four|five|six|seven|eight|nine|ten)\s*(?:[+*/×÷-]|plus|minus|times|multiplied by|divided by)\s*(?:\d|zero|one|two|three|four|five|six|seven|eight|nine|ten)/i.test(String(text || '')) && String(text || '').trim().split(/\s+/).length <= 14;
      const BRIDGE_AFTER_MS = 4000;
      const _fillerTimer = setTimeout(() => {
        if (_inputMode !== 'voice' || _skipFiller || _simpleArithmetic) return;
        if (_replyArrived || fillerCancelRef.current) return;
        if (_fillerTurn !== fillerTurnRef.current || !loadingRef.current) return;
        speakBridge("Let me think for a moment.", _fillerTurn);
      }, BRIDGE_AFTER_MS);

      // ── NORMAL GPT-6 ASTRA CHAT (explicit opt-in only) ────────────────
      // This takes only ordinary text/voice conversation. Assist, project,
      // image/video, tour, and action paths stay on the existing JSON route.
      // With the client flag off, this block is never entered and normal chat
      // behaves byte-for-byte like the existing local Aria flow.
      const _useNormalChatGpt = normalChatGptStreamEnabled() &&
        !isOnboardingActive() &&   // onboarding turns run on the JSON route: no GPT, no guest turn
        !tourBeat && !screenStream && !referenceVideo &&
        !(Array.isArray(body.referenceImages) && body.referenceImages.length) &&
        !body.helpMode && !body.commitTask;
      // A normal voice turn must be diagnosable past `voice.sent`. These
      // markers report only route/playback state, not spoken content.
      const _reportNormalVoiceStage = (stage, meta = {}) => {
        if (_inputMode !== 'voice') return;
        try {
          const traceBase = String(base || '').replace(/\/$/, '');
          fetch(`${traceBase}/api/pipeline/event`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            // phraseId is random and contains no speech text. Reusing it as
            // reqId makes the browser and server halves of one clip joinable
            // in the pipeline trace without storing a transcript.
            body: JSON.stringify({ stage, level: 'ok', text: 'normal-voice', meta, reqId: meta?.phraseId || '' }),
          }).catch(() => {});
        } catch (_) {}
      };
      _reportNormalVoiceStage('normal.gpt.route', {
        clientBuild: 'voice-handoff-diagnostic-2',
        selected: _useNormalChatGpt,
        tour: !!tourBeat,
        screen: !!screenStream,
        video: !!referenceVideo,
        images: !!(Array.isArray(body.referenceImages) && body.referenceImages.length),
        help: !!body.helpMode,
        commit: !!body.commitTask,
      });
      if (_useNormalChatGpt) {
        const normalStreamUrl = base
          ? `${String(base).replace(/\/$/, '')}/api/ai/normal-chat-stream`
          : '/api/ai/normal-chat-stream';
        const _normalStreamId = `normal-gpt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const _now = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const _normalTurnStartedAt = _inputMode === 'voice' && lastVoiceFinalPerfRef.current
          ? lastVoiceFinalPerfRef.current : _now;
        lastVoiceFinalPerfRef.current = 0;
        let _normalFirstAudioStarted = false;
        let _normalReply = '';
        let _normalMessageAdded = false;
        let _speechQueue = Promise.resolve();
        let _speechQueued = false;
        let _speechStreamEnded = false;
        let _normalGeneration = 0;
        let _normalPhraseSequence = 0;
        let _normalPhraseOutstanding = 0;
        let _normalTtsFailed = false;
        const _normalReplyMode = () => (_inputMode === 'voice' && _normalTtsFailed ? 'text' : _inputMode);

        // Each phrase uses the existing Edge byte-stream endpoint. Queueing
        // keeps phrases ordered and holds the microphone floor until the whole
        // answer is done; no OpenAI audio endpoint is involved.
        const _queueEdgePhrase = (phrase, generation = _normalGeneration) => {
          if (_inputMode !== 'voice' || !String(phrase || '').trim()) return;
          const phraseId = `${_normalStreamId}-p${++_normalPhraseSequence}`;
          _normalPhraseOutstanding += 1;
          const _mediaMeta = (audio, extra = {}) => ({
            phraseId,
            readyState: Number(audio?.readyState) || 0,
            networkState: Number(audio?.networkState) || 0,
            paused: !!audio?.paused,
            currentTimeMs: Math.max(0, Math.round((Number(audio?.currentTime) || 0) * 1000)),
            durationMs: Number.isFinite(audio?.duration) ? Math.round(audio.duration * 1000) : null,
            muted: !!audio?.muted,
            volume: Number(audio?.volume),
            ...extra,
          });
          // Do not persist an exception message: Chromium may include the
          // media URL, whose query string contains the spoken phrase. These
          // structured fields preserve the actionable media failure details.
          const _errorMeta = (audio, error) => _mediaMeta(audio, {
            errorName: String(error?.name || audio?.error?.name || 'MediaError').slice(0, 80),
            mediaErrorCode: Number(error?.code || audio?.error?.code) || 0,
            hasErrorMessage: !!String(error?.message || audio?.error?.message || ''),
          });
          const _queuedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
          voiceTrace.record('turn.tts-queued', { id: _normalStreamId, delayMs: Math.max(0, Math.round(_queuedAt - _normalTurnStartedAt)) });
          _reportNormalVoiceStage('normal.tts.queued', { phraseId, turnId: _normalStreamId,
            elapsedMs: Math.max(0, Math.round(_queuedAt - _normalTurnStartedAt)), chars: String(phrase).trim().length });
          _willSpeak = true;
          _speechQueued = true;
          _speechQueue = _speechQueue.then(async () => {
            if (generation !== _normalGeneration) return;
            _vtStamp('tts.queued', { path: 'stream-phrase' });
            await waitForBridge();
            if (generation !== _normalGeneration) return;
            await new Promise((resolve) => {
              const _voiceBase = String(getApiBase() || base || '').replace(/\/$/, '');
              const _url = `${_voiceBase}/api/tts/stream?text=${encodeURIComponent(String(phrase).trim())}&traceId=${encodeURIComponent(phraseId)}${_vtParam()}`;
              const audio = new Audio();
              audio.crossOrigin = "anonymous";
              audio.src = _url;
              _vtStamp('tts.requested', { path: 'stream-phrase' });
              _vtWatchPlay(audio, { path: 'stream-phrase' });
              let settled = false;
              const finish = (failed = false, detail = {}) => {
                if (settled) return;
                settled = true;
                if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null;
                _normalPhraseOutstanding = Math.max(0, _normalPhraseOutstanding - 1);
                // Keep the orb speaking through queued phrase boundaries.
                // The turn completion handler clears it after the final phrase.
                if (!activeReplyAudioRef.current || activeReplyAudioRef.current === audio) {
                  ariaSpeakingRef.current = false;
                  setAriaSpeaking(false);
                }
                if (failed) {
                  const _failedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
                  _reportNormalVoiceStage('normal.tts.error', { ..._mediaMeta(audio, detail), turnId: _normalStreamId,
                    elapsedMs: Math.max(0, Math.round(_failedAt - _normalTurnStartedAt)) });
                } else {
                  const _finishedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
                  _reportNormalVoiceStage('normal.tts.ended', { ..._mediaMeta(audio), turnId: _normalStreamId,
                    elapsedMs: Math.max(0, Math.round(_finishedAt - _normalTurnStartedAt)) });
                  // Preserve the existing dashboard marker during the
                  // diagnostic rollout.
                  _reportNormalVoiceStage('normal.tts.done', { ..._mediaMeta(audio), turnId: _normalStreamId,
                    elapsedMs: Math.max(0, Math.round(_finishedAt - _normalTurnStartedAt)) });
                }
                if (failed) {
                  _normalTtsFailed = true;
                  setVoiceError('Voice playback failed. The written reply is shown in chat.');
                  setMessages((m) => m.map((message) => (
                    message?.role === 'assistant' && message?.data?._normalChatStreamId === _normalStreamId
                      ? { ...message, mode: _normalReplyMode(), data: { ...message.data, voicePlaybackFailed: true } }
                      : message
                  )));
                }
                resolve();
              };
              try {
                prepareAriaPlayback(audio);
                try { attachAriaAnalyser(audio); } catch (_) {}
                activeReplyAudioRef.current = audio;
                ariaSpeakingRef.current = true;
                audio.playbackRate = chooseSpeechRate(String(phrase));
                _reportNormalVoiceStage('normal.tts.request', { ..._mediaMeta(audio), turnId: _normalStreamId,
                  elapsedMs: Math.max(0, Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - _normalTurnStartedAt)) });
                audio.onloadstart = () => _reportNormalVoiceStage('normal.tts.loadstart', _mediaMeta(audio));
                audio.onloadedmetadata = () => _reportNormalVoiceStage('normal.tts.loadedmetadata', _mediaMeta(audio));
                audio.oncanplay = () => _reportNormalVoiceStage('normal.tts.canplay', _mediaMeta(audio));
                audio.onwaiting = () => { if (activeReplyAudioRef.current === audio) { setAriaSpeaking(false); setPreparingSpeech(true); } _reportNormalVoiceStage('normal.tts.waiting', _mediaMeta(audio)); };
                audio.onstalled = () => _reportNormalVoiceStage('normal.tts.stalled', _mediaMeta(audio));
                audio.onsuspend = () => _reportNormalVoiceStage('normal.tts.suspend', _mediaMeta(audio));
                audio.onabort = () => _reportNormalVoiceStage('normal.tts.abort', _mediaMeta(audio));
                audio.onplaying = () => {
                  setPreparingSpeech(false);
                  const _playedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
                  const _elapsedMs = Math.max(0, Math.round(_playedAt - _normalTurnStartedAt));
                  _reportNormalVoiceStage('normal.tts.playing', { ..._mediaMeta(audio), turnId: _normalStreamId, elapsedMs: _elapsedMs });
                  if (!_normalFirstAudioStarted) {
                    _normalFirstAudioStarted = true;
                    voiceTrace.record('turn.first-audio', { id: _normalStreamId, delayMs: _elapsedMs });
                  }
                  setAriaSpeaking(true);
                };
                audio.onended = () => finish(false);
                audio.onerror = () => finish(true, _errorMeta(audio));
                audio.play().catch((error) => finish(true, _errorMeta(audio, error)));
              } catch (error) { finish(true, _errorMeta(audio, error)); }
            });
          }).catch(() => {});
        };

        const _clearNormalAttempt = () => {
          _normalGeneration += 1;
          _normalReply = '';
          _normalMessageAdded = false;
          _normalTtsFailed = false;
          _speechQueue = Promise.resolve();
          _speechQueued = false;
          _normalPhraseOutstanding = 0;
          _speechStreamEnded = false;
          try {
            const audio = activeReplyAudioRef.current;
            if (audio) { audio.pause(); audio.currentTime = 0; }
            activeReplyAudioRef.current = null;
          } catch (_) {}
          setMessages((m) => m.filter((message) => message?.data?._normalChatStreamId !== _normalStreamId));
        };

        const _showNormalGptTechnicalError = (code) => {
          _replyArrived = true;
          if (_fillerTimer) clearTimeout(_fillerTimer);
          cancelFiller();
          _clearNormalAttempt();
          const _isGuestLimit = String(code || '').includes('429') || String(code || '').includes('GUEST_NORMAL_CHAT_QUOTA');
          const _normalDisabled = code === 'NORMAL_CHAT_GPT_DISABLED';
          const _needsSession = /^GUEST_SESSION_/.test(String(code || '')) || /HTTP_40[13]$/.test(String(code || ''));
          const _limitReply = "You've used the free conversations available without an account. Create a free Aria account or sign in to keep talking, then choose a paid plan when you want more usage.";
          setMessages((m) => [...m, {
            role: 'assistant',
            content: _normalDisabled ? 'Normal conversation is disabled in the current app configuration. Computer-control commands are still available.' : _needsSession ? 'Aria needs a valid sign-in in this app before she can answer. Open your account menu and sign in here; signing in on the website does not automatically sign in the Windows app. No GPT reply was generated.' : _isGuestLimit ? _limitReply : 'Aria could not complete this reply. Please try again in a moment.',
            error: !_isGuestLimit,
            mode: 'text',
            data: { source: 'gpt-6-astra', code: code || 'NORMAL_CHAT_GPT_RETRIES_EXHAUSTED', _normalChatStreamId: _normalStreamId },
          }]);
          setLastModelUsed('GPT unavailable');
          try { releaseFloorRef.current?.(); } catch (_) {}
          try { setPreparingSpeech(false); } catch (_) {}
          clearHold();
          setLoading(false);
        };

        try {
          const _streamResult = await streamNormalChat({
            onWorkspace: (taskWorkspace) => {
              if (!taskWorkspace) return;
              _normalMessageAdded = true;
              setMessages((items) => upsertStreamMessage(items, { streamId: _normalStreamId, mode: _inputMode, taskWorkspace }));
            },
            url: normalStreamUrl,
            body: { ...body, inputMode: _inputMode, assistOfferAvailable: isDesktopApp && !assistAsk, assistOfferPending: isDesktopApp && !!assistAsk },
            turnId: _normalStreamId,
            turnStartedAt: _normalTurnStartedAt,
            onDelta: async (delta, fullReply) => {
              _replyArrived = true;
              if (_fillerTimer) clearTimeout(_fillerTimer);
              cancelFiller();
              _normalReply = fullReply;
              _normalMessageAdded = true;
              setMessages((items) => upsertStreamMessage(items, { streamId: _normalStreamId, content: fullReply, mode: _normalReplyMode() }));
              setLoading(false);
            },
            // Queue one complete reply after the model finishes. This avoids a
            // renderer callback race that could throw during phrase streaming
            // and leave the turn thinking forever.
            onPhrase: async () => {},
            onStage: (stage, timing) => {
              const _event = stage === 'normal.gpt.first_delta' ? 'turn.ai-first-token'
                : stage === 'normal.gpt.done' ? 'turn.ai-done'
                : stage === 'normal.gpt.request' ? 'turn.ai-request'
                : stage === 'normal.gpt.headers' ? 'turn.ai-headers'
                : stage === 'normal.gpt.error' || stage === 'normal.gpt.rejected' || stage === 'normal.gpt.timeout' || stage === 'normal.gpt.transport_error' ? 'turn.ai-error'
                : null;
              if (_event) voiceTrace.record(_event, { id: _normalStreamId, delayMs: timing.elapsedMs, reason: stage });
            },
            onRetry: async () => {
              // The server is retrying GPT, never switching to local dialogue.
              // Remove any incomplete attempt so only a fresh GPT response stays.
              _clearNormalAttempt();
              setLoading(true);
            },
          });

          _reportNormalVoiceStage('normal.gpt.returned', { handled: !!_streamResult.handled, hasReply: !!_streamResult.reply });
          if (_streamResult.handled) {
            _replyArrived = true;
            if (_fillerTimer) clearTimeout(_fillerTimer);
            cancelFiller();
            if (_streamResult.error) {
              _showNormalGptTechnicalError(_streamResult.error.code);
              return;
            }
            const _finalReply = String(_streamResult.reply || _normalReply || '').trim();
            if (isDesktopApp && typeof _streamResult.assistOffer?.task === 'string') {
              setAssistAsk({ task: _streamResult.assistOffer.task.slice(0, 1000), reason: 'look at your screen and help with the task you described', steps: [], inspectFirst: true, inputMode: _inputMode });
            }
            if (_inputMode === 'voice' && _finalReply) _queueEdgePhrase(_finalReply);
            if (_finalReply || _streamResult.taskWorkspace) {
              _normalMessageAdded = true;
              setMessages((items) => upsertStreamMessage(items, { streamId: _normalStreamId, content: _finalReply,
                mode: _normalReplyMode(), source: _streamResult.source, taskWorkspace: _streamResult.taskWorkspace }));
            }

            if (_finalReply) {
              if (_inputMode !== 'voice') {
                setSearchTrack((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: _finalReply }].slice(-20));
              } else {
                setConversationHistory((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: _finalReply }]);
              }
              addConversationEntry('user', text);
              addConversationEntry('aria', _finalReply);
              setLastModelUsed(_streamResult.source || 'GPT');
            }
            // First-time onboarding (web only): the server's gate answered this turn
            // and told us the flow is on. From here the app routes turns to
            // /api/ai/conversation until a reply says _onboardingDone.
            try {
              const _sig = applyOnboardingSignal(_streamResult);
              if (_sig.openSignup) window.dispatchEvent(new CustomEvent('aria-open-signin', { detail: { mode: 'signup', from: 'onboarding' } }));
            } catch (_) { /* routing hint only; never the reply */ }

            if (_speechQueued) {
              _speechStreamEnded = true;
              _speechQueue.then(() => {
                const _completedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
                const _turnElapsed = Math.max(0, Math.round(_completedAt - _normalTurnStartedAt));
                voiceTrace.record('turn.audio-complete', { id: _normalStreamId, delayMs: _turnElapsed });
                _reportNormalVoiceStage('normal.turn.audio_complete', { turnId: _normalStreamId, elapsedMs: _turnElapsed });
                try { releaseFloorRef.current?.(); } catch (_) {}
                try { setPreparingSpeech(false); } catch (_) {}
              });
            } else {
              const _completedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
              voiceTrace.record('turn.text-complete', { id: _normalStreamId, delayMs: Math.max(0, Math.round(_completedAt - _normalTurnStartedAt)) });
              try { releaseFloorRef.current?.(); } catch (_) {}
              try { setPreparingSpeech(false); } catch (_) {}
            }
            return;
          }
        } catch (normalStreamErr) {
          _reportNormalVoiceStage('normal.reply.error', {
            errorName: String(normalStreamErr?.name || 'Error'),
            errorMessage: normalStreamErr instanceof ReferenceError ? String(normalStreamErr.message).slice(0, 120) : undefined,
          });
          // With normal GPT opt-in on, a network miss is a technical failure,
          // not permission to route the same turn to local/canned dialogue.
          if (import.meta.env?.DEV) console.warn('[Aria] normal GPT stream unavailable:', normalStreamErr?.message);
          if (normalStreamErr?.name === 'AbortError') {
            clearHold();
            setLoading(false);
            return;
          }
          _showNormalGptTechnicalError(normalStreamErr?.code || 'NORMAL_CHAT_GPT_NETWORK_ERROR');
          return;
        }
      }

      for (const url of fallbackUrls) {
        try {
          if (body.clientTiming) body.clientTiming.sentAt = Date.now();   // the waterfall's request.sent
          res = await fetchWithTimeout(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            // 45s ceiling (raised from 12s, 8/26). The old 12s was set when every
            // answer was a canonical lookup. She THINKS before she speaks now, and
            // the never-done-it path runs a real web search inside the turn, so a
            // good answer can legitimately take 15-20s. At 12s the client was
            // killing turns the backend was still working on and showing a canned
            // line - which is what produced the 21.0s screenshot.
            timeout: 45000,
          });
          if (import.meta.env?.DEV || (typeof window !== 'undefined' && window.electronAPI)) console.log('[Aria] Conversation URL used:', url);
          break;
        } catch (netErr) {
          lastNetworkErr = netErr;
        }
      }
      _replyArrived = true;
      _vtStamp('reply.received', { path: 'json' });
      if (_fillerTimer) clearTimeout(_fillerTimer);
      cancelFiller();
      if (!res) {
        // Specifically detect the timeout case so we can give a real message
        // instead of the generic "Can't reach Aria" the catch-all uses.
        const isTimeout = lastNetworkErr && (lastNetworkErr.name === 'AbortError' || /timeout|aborted/i.test(lastNetworkErr.message || ''));
        if (isTimeout) {
          setMessages((m) => [...m, {
            role: 'assistant',
            // NEVER BLAME HIM FOR HER FAILURE (8/26). This line was banned in the
            // backend on 8/25, but the ban lives in ariaBannedLines.js and this is
            // the CLIENT - so no backend guard could ever have caught it. It read
            // as "you were unclear" when the truth was "I timed out", and he saw
            // it on a sentence that was perfectly clear.
            content: "That one took me too long and timed out on my end - not you. Say it again and I will get it.",
            error: true,
            mode: _inputMode,
          }]);
          clearHold(); setLoading(false);
          return;
        }
        throw lastNetworkErr || new Error('No backend reachable');
      }

      let data = null;
      try {
        const responseText = await res.text();
        data = responseText ? JSON.parse(responseText) : {};
      } catch (parseErr) {
        setMessages((m) => [...m, { role: 'assistant', content: "Aria's reply was invalid. Try again.", error: true, mode: _inputMode }]);
        clearHold(); setLoading(false);
        return;
      }

      if (!res.ok) {
        const serverMsg = data?.message || data?.error || res.statusText;
        if (res.status === 402) {
          setMessages((m) => [...m, { role: 'assistant', content: serverMsg || "You've used your $1 free trial or monthly allowance. Go to Subscription for $20/month membership or wait for your next period.", error: true, mode: _inputMode }]);
          clearHold(); setLoading(false);
          return;
        }
        if (res.status === 503) {
          setMessages((m) => [...m, { role: 'assistant', content: serverMsg || (isDev ? "Aria's AI isn't configured. Set OPENAI_API_KEY or XAI_API_KEY in the backend .env." : "Aria isn't available right now."), error: true, mode: _inputMode }]);
          clearHold(); setLoading(false);
          return;
        }
        // PLAIN WORDS, NOT A STATUS CODE (9/2). The sweep caught "Error (502):
        // Bad Gateway" printed as her reply during a backend restart. Braeden:
        // "aria is designed to be simple for people ... connect to the average
        // person." A person needs to know one thing: try again in a second.
        // The code stays on the message for anyone debugging.
        setMessages((m) => [...m, {
          role: 'assistant',
          content: (res.status >= 500 || res.status === 0)
            ? "I lost my connection for a second. Give it a moment and send that again."
            : (serverMsg || "That one didn't go through. Try it again."),
          error: true, status: res.status, mode: _inputMode,
        }]);
        clearHold(); setLoading(false);
        return;
      }

      const summaryText = data?.summaryMessages?.length ? data.summaryMessages.join(' ').trim() : '';
      // Hand the whole payload to the workstation. It decides for itself
      // whether there is anything worth showing and renders nothing if not.
      clearHold();
      // On a tour turn the ring on the real button IS the visual; a scene
      // diagram of the same sentence ("Top left corner -> Three lines")
      // competes with it and covers her words (measured 9/3).
      try { setWsPayload(data ? ((data._fromTour || data.uiPoint) && data.scene ? { ...data, scene: undefined } : data) : null); } catch (_) {}
      // ── THE TOUR ANSWERS TO HER, NOT THE OTHER WAY ROUND (8/31) ────────
      // `_tourYield` means she decided this turn was a real request and let
      // the normal lanes answer it. The tour stops there and does not resume:
      // "ARIA NEEDS TO LISTEN TO THEM thats how the whole thing works."
      try { if (data && data.workspace) setWorkspace(data.workspace); } catch (_) {}
      // ── WHAT SHE FOUND GOES IN THE PROJECT (Braeden 9/3) ───────────────
      // The errand lane's list is filed under the selected project, in the
      // same store the drawer reads, so it is still there when they come
      // back. "That's saved in your project" has to be literally true.
      try {
        const pn = data && data.projectNote;
        if (pn && (pn.text || pn.a)) {
          // two shapes: a found list {title, text} or an intake answer {q, a}
          const note = pn.a
            ? { t: Date.now(), q: String(pn.q || '').slice(0, 200), a: String(pn.a).slice(0, 400) }
            : { t: Date.now(), title: String(pn.title || 'Found').slice(0, 80), text: String(pn.text).slice(0, 2000) };
          const linkId = data.projectLink && data.projectLink.id;
          setUserProjects((ps) => {
            const target = (selectedProject && selectedProject.id) || (ps[0] && ps[0].id);
            if (!target) return ps;
            return ps.map((p) => {
              if (!p) return p;
              if (p.id === target || (linkId && p.id === linkId)) return { ...p, notes: [...(Array.isArray(p.notes) ? p.notes : []), note].slice(-40) };
              return p;
            });
          });
          // keep the selected project's copy fresh too, so the next turn carries the note
          try { setSelectedProject((sp) => (sp && sp.id === ((selectedProject && selectedProject.id)) ? { ...sp, notes: [...(Array.isArray(sp.notes) ? sp.notes : []), note].slice(-40) } : sp)); } catch (_) {}
        }
        if (data && typeof data._lastAsked === 'string') lastAskedRef.current = data._lastAsked;
        else if (data && data._tourDone) lastAskedRef.current = '';
        // MAKING SENSE (Braeden 9/5): she said their goal back and asked if she
        // got it right; the goal rides back on every turn until they say yes,
        // and only then does she type. Nothing is named on a guess.
        if (data && typeof data._tourGoal === 'string' && data._tourGoal) tourGoalRef.current = data._tourGoal.slice(0, 160);
      } catch (_) { /* a note must never cost the turn */ }
      // ── WHAT SHE SAW, KEPT (9/2) ───────────────────────────────────────
      // The server turned the UI trail into a note; it goes into the history
      // she reasons over (not the rendered chat), so three turns later she
      // still knows the menu was opened and which project was made.
      try {
        uiSentAtRef.current = Date.now();
        if (data && typeof data._screenNote === 'string' && data._screenNote) {
          const note = data._screenNote;
          setConversationHistory((prev) => (prev.length && prev[prev.length - 1].content === note) ? prev : [...prev, { role: 'assistant', content: note }]);
        }
      } catch (_) { /* memory is a nicety; the turn is not */ }
      try {
        // They told her to slow down (the tour lane heard "too fast", "wait",
        // "one thing at a time"); her line already said so - this just makes
        // her hands keep the promise (9/5).
        try { if (data && data._tourPace === 'slow') _setPace('slow', 'they asked', { quiet: true }); } catch (_) {}
        // First-time onboarding (web only): a reply stamped _onboardingDone ends the
        // JSON-route detour; _openSignup opens the existing sign-up surface.
        try {
          const _osig = applyOnboardingSignal(data);
          if (_osig.openSignup) window.dispatchEvent(new CustomEvent('aria-open-signin', { detail: { mode: 'signup', from: 'onboarding' } }));
        } catch (_) { /* routing hint only; never the reply */ }
        if (data && data._tourYield) { setTourBeat(null); setTourPoint(null); setThreeThings(null); }
        else if (data && (data._tourBeat || data._tourDone)) {
          setTourBeat(data._tourDone ? null : (data._tourBeat || null));
          // She has just said who she is and handed the floor back. Hold the
          // pause here, and take the initiative only if nobody fills it.
          // Any other beat cancels a wait still standing from before.
          try {
            if (!data._tourDone && data._tourBeat === 'ai') _tourWaitThenOffer();
            // her first move just landed: let it sit, then ask if they want to keep going (9/5)
            else if (!data._tourDone && data._tourBeat === 'keep' && data._tourKeep) _tourWaitThenOffer('keep');
            else _tourWaitCancel();
          } catch (_) { /* the pause is a nicety; the turn is not */ }
          // ── ON THE WORD, NOT AT THE START (Braeden 9/2: "the timing has
          //  to be right") — the ring lands as she says the phrase the
          //  server names in `uiPoint.when`; the card lands on "three
          //  things"; the title starts typing on "naming". With no phrase
          //  (or no clock) they land at once, as before.
          const _spoken = String(data.reply || data.question || '');
          // the ear hears what she just said, so the answer decodes as an answer (9/2)
          try { streamingSessionRef.current?.sendContext?.(_spoken); } catch (_) {}
          if (data.uiPoint && data.uiPoint.when) whenSaid(_spoken, data.uiPoint.when, () => setTourPoint(data.uiPoint));
          else setTourPoint(data.uiPoint || null);
          // ── SHE TYPES THE TITLE FOR THEM (Braeden 9/2) ──────────────
          // "aria repeats it back and types in for them a simple title for
          //  the subject" — the server named it; this puts it in the real
          //  New-project box, letter by letter, and creates the project.
          if (data.tourAction === 'type_title' && data.title) {
            tourGoalRef.current = String(data.goal || text || '').slice(0, 160);   // for the errand at `scope` (9/3)
            whenSaid(_spoken, 'naming', () => runTitleShow(data.title), 0);
          }
          if (Array.isArray(data.threeThings) && data.threeThings.length) whenSaid(_spoken, 'three things', () => setThreeThings(data.threeThings), 0);
        }
      } catch (_) { /* the tour is a nicety; the turn is not */ }
      // ── visual doctrine dispatch (8/12) ─────────────────────────────────
      try {
        const vc = data && data.visual && data.visual.class;
        // LOOKUP: she visibly uses her search engine — the query she ran (or
        // his question) types into the bar while the results land.
        if (vc === 'lookup') {
          const q = (data.searchResult && (data.searchResult.query || data.searchResult.q)) || text;
          runSearchShow(q);
        }
        // TEACH: the turn is tagged instantly; the whiteboard lesson builds
        // async and joins this same payload when it lands. Identity check so
        // a lesson never attaches to a NEWER turn's workstation.
        if (vc === 'teach' && data && !data.lesson) {
          const snap = data;
          fetch('/api/lesson', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ question: text }),
          }).then((r) => r.json()).then((j) => {
            if (j && j.ok && j.lesson) {
              setWsPayload((p) => (p === snap ? { ...snap, lesson: j.lesson } : p));
            }
          }).catch(() => { /* no lesson beats a wrong lesson */ });
        }
        // SCENE (9/2): the backend compiles `scene` on the turn when her
        // reply has a shape (list, sequence, arithmetic, figure). Prose has
        // none, so the director model proposes one off the turn. It joins
        // THIS turn's payload in both places it renders — the timeline
        // message and the workstation — never a newer turn's (identity
        // check on `snap`). Lookups and screen turns already have their
        // visual; short replies are talk and get nothing (the endpoint
        // holds the same 20-word floor).
        if (data && !data.scene && typeof data.reply === 'string' && data.reply.split(/\s+/).length >= 20
            && !data._fromTour && !data.uiPoint && !data.threeThings   // the intro's ring/card IS the visual (9/3)
            && !data.lesson && vc !== 'lookup' && vc !== 'screen'
            && !(data.searchResult && ((data.searchResult.results && data.searchResult.results.length) || (data.searchResult.cards && data.searchResult.cards.length)))) {
          const snap = data;
          fetch(`${API_BASE}/api/scene`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reply: data.reply, question: text }),
          }).then((r) => r.json()).then((j) => {
            if (!(j && j.ok && j.scene)) return;
            const clock = { mode: 'read', startedAt: Date.now() };
            const next = { ...snap, scene: j.scene, _sceneClock: clock };
            setWsPayload((p) => (p === snap ? next : p));
            setMessages((ms) => ms.map((m) => (m && m.data === snap ? { ...m, data: next } : m)));
          }).catch(() => { /* no scene beats a wrong scene */ });
        }
        // SESSION: her case-file notes become the pinned notepad.
        if (data && data.caseFile) setNotepadFile(data.caseFile);
      } catch (_) { /* visuals may never cost the turn */ }
      let reply = (data && (data.reply || data.question || data.reasoning || summaryText)) || '';
      if (typeof reply === 'string') reply = reply.trim();

      // ── SHE ACTUALLY DOES IT (Braeden 8/16) ─────────────────────────────
      //   "the voice that im talking to needs to take control of the entire
      //    program if i ask it"
      // The server can return a uiAction alongside the words. This executes it,
      // so "open my leads" MOVES THE PAGE instead of describing where leads
      // live. Validated against the known vocabulary first — an unknown action
      // is dropped rather than silently doing nothing while she claims it is
      // done, which is the failure mode that makes an assistant untrustworthy.
      if (data && data.uiAction) {
        const act = data.uiAction;
        if (!validUiAction(act)) {
          console.warn('[aria-ui] refused unknown action', act);
        } else if (act.type === 'navigate') {
          // seen doing it (9/5): if the section's control is on screen her
          // cursor presses it first; otherwise the page just changes
          const _nav = () => { try { window.location.hash = act.section; } catch (_) {} setDashboardSection(act.section); };
          ariaHand.click(['.dashboard-menu-item.' + String(act.section || '').replace(/[^a-z0-9_-]/gi, ''), 'text:' + String(act.section || '')]).then(_nav, _nav);
        } else if (act.type === 'show_site') {
          setSitePreview({ url: act.url, loading: true });
          (async () => {
            try {
              const r = await fetch(`${API_BASE}/api/aria/site-preview`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url: act.url }),
              });
              const d = r.ok ? await r.json() : null;
              if (d && d.ok && d.shot) setSitePreview({ url: act.url, loading: false, ...d });
              else setSitePreview(null);
            } catch (_) { setSitePreview(null); }
          })();
        } else if (act.type === 'close_panel') {
          const _close = () => { setSitePreview(null); try { setBizPlusOpen(false); } catch (_) {} };
          ariaHand.click(['[aria-label="Close preview"]']).then(_close, _close);
        } else if (act.type === 'set_input') {
          // typed in front of them, key by key, not pasted (9/5)
          const _txt = String(act.text || '');
          if (_txt && ariaHand.startTyping(['input.input', '.input-row input'])) {
            for (let k = 1; k <= _txt.length; k++) setTimeout(() => { setInput(_txt.slice(0, k)); try { ariaHand.key(_txt.charAt(k - 1)); } catch (_) {} }, 120 + k * 22);
            setTimeout(() => { try { ariaHand.stopTyping(); } catch (_) {} }, 120 + _txt.length * 22 + 150);
          } else setInput(_txt);
        } else if (act.type === 'mic') {
          const on = !!act.on;
          if (!on) { try { stopAriaPlayback(); } catch (_) {} try { instantVoice.stop(); } catch (_) {} }
          micUserMutedRef.current = !on;
          try { persistDesktopMicUserMuted(!on); } catch (_) {}
          setMicOn(on);
        } else if (act.type === 'new_project') {
          // She opens it herself. The drawer first, then the box a beat later
          // so the movement reads as two steps rather than everything
          // appearing at once — the existing tour effects (tourBeat 'menu' →
          // 'project' → 'title') then narrate it exactly as before, except
          // nobody had to click anything.
          try { setTourPoint(null); } catch (_) {}
          // Both moves wait on her VOICE, not a timer (9/5, see tourMove):
          // the drawer opens once "I'm opening your dashboard" has actually
          // been said and a breath has passed; the drawer line then plays
          // (the 'menu' effect), and the box opens only after THAT line ends.
          // Before this the drawer opened in the same tick as the reply and
          // the drawer line cut her sentence off a word in; the box and its
          // line followed 1.5s later and cut the drawer line too.
          const _said = String((data && (data.reply || data.question)) || '');
          // her cursor presses the real buttons (9/5): the three lines, then New project
          tourMove('open_menu', () => { try { setLeftSidebarOpen(true); } catch (_) {} }, { after: _said, target: ['.aria-hamburger', '.sidebar-toggle'] });
          tourMove('open_box', () => { try { setPersonalProjOpen(true); } catch (_) {} }, { after: TOUR_LINE_MENU, target: ['.dash-project--new'] });
        } else if (act.type === 'open_account') {
          // ── STAGE THREE OF THE INTRO (Braeden 9/5) ─────────────────────
          // They said yes to keeping her. The sign-up modal opens (AccountMenu
          // listens for aria-open-signin), the subscribe intent is marked so
          // the payment box follows sign-up on its own, and her cursor presses
          // the Account control first when one is on screen. Payment success
          // fires aria-billing-identity, which the member-beat effect below
          // turns into the download line and the ring on the download pill.
          try { localStorage.setItem('aria_intent_subscribe', '1'); } catch (_) {}
          const _open = () => { try { window.dispatchEvent(new CustomEvent('aria-open-signin', { detail: { mode: 'signup', from: 'tour' } })); } catch (_) {} };
          ariaHand.click(['.app-dash-account-btn', 'text:Account', 'text:Sign in']).then(_open, _open);
        } else if (act.type === 'open_setup') {
          try { setBizText(''); setBizPlanBusy(false); setBizPlusOpen(true); } catch (_) {}
        } else if (act.type === 'assist_permission') {
          // SHE ASKS FIRST, WITH A BUTTON (Braeden 8/22): "can i go into assist
          // mode, with a pop up, that says allow, so aria takes control of the
          // computer and does what she needs to".
          //
          // The spoken "want me to start?" confirm was deleted earlier for a
          // good reason - people never said the second yes and got stranded. A
          // popup fixes that without giving up the permission: one click, and
          // it cannot be misheard.
          //
          // WEB HAS NO SCREEN TO TAKE. There is nothing to control in a browser
          // tab, so asking would be theatre - the link just opens.
          if (!window.electronAPI) {
            if (act.openUrl) { try { window.open(act.openUrl, '_blank', 'noopener'); } catch (_) {} }
          } else {
            setAssistAsk({
              reason: act.reason || 'take control of your computer',
              task: act.task || '',
              inputMode: _inputMode,
              openUrl: act.openUrl || '',
              steps: Array.isArray(act.steps) ? act.steps : [],
            });
          }
        } else if (act.type === 'start_assist') {
          // THE DOER (8/20): she asked, they said yes — take the screen and
          // work the problem. The task rides in the input so the assist loop
          // opens already aimed at it; her spoken line covers the handoff.
          try { onScreenShareClick(); } catch (_) {}
          if (act.task) { try { setInput(String(act.task)); } catch (_) {} }
        } else if (act.type === 'point') {
          // She POINTS (8/20: "she should be able to read the screen, point to
          // this"). Navigate first if the thing lives on another page — the cue
          // re-measures every 400ms, so it finds the target once it renders.
          if (act.section) {
            try { window.location.hash = act.section; } catch (_) {}
            setDashboardSection(act.section);
          }
          setAriaCue({ kind: 'point', at: act.at, label: act.label || null });
          setTimeout(() => setAriaCue((c) => (c && c.at === act.at ? null : c)), 10000);
        }
      }

      // SHE POINTS AT WHAT SHE IS TALKING ABOUT (Braeden 8/22): "is she
      // pointing to anything on the screen for the user to see what shes
      // talking about ... i didnt see it".
      //
      // The instruction names a control on someone else's page - Meta's Create
      // button, the Daily budget field - so the app cannot highlight it with a
      // CSS selector the way it points at its own UI. It goes to the fullscreen
      // click-through coach overlay instead, which sits over whatever window is
      // in front. The caption lands immediately; the circle needs the assist
      // loop to have located the control, so a caption with no coordinates
      // still puts her words on the screen rather than only in his ear.
      if (data?.pointAt?.label && window.electronAPI?.sendOnScreenAnnotations) {
        try {
          // NOT YET CIRCLED. Drawing a ring around the actual control needs its
          // coordinates, which only the assist loop's screen analysis can give -
          // it is someone else's page. Until that is wired, the caption alone
          // goes up: her words on the screen he is looking at, over Ads Manager,
          // instead of only in his ear. A caption with no circle is honest; a
          // circle drawn at a guessed coordinate would point at the wrong thing.
          window.electronAPI.sendOnScreenAnnotations({
            annotations: [],
            message: data.pointAt.caption || data.pointAt.label,
          });
        } catch (_) { /* an overlay that fails must never eat the reply */ }
      }

      // THE SCREEN SUPPLIES THE WORDS (Braeden 8/22: "make sure she acually
      // reads the screen right ... so how do we fix it?"). A walkthrough step
      // arrives with its GOAL. If this client has eyes — desktop, assist mode
      // on — it reads the screen right now and says what is actually there:
      // the control by its real name, pointed at where it really is, or "I
      // don't see it, I can see X, Y — what page are you on?" The fixed line
      // she already spoke was the no-eyes fallback; the screen's answer
      // replaces it in the transcript so the next turn builds on the truth.
      if (data?.screenStep?.stage && window.electronAPI?.captureScreen && screenStream) {
        (async () => {
          try {
            const cap = await window.electronAPI.captureScreen();
            const image = cap?.dataUrl || cap?.image || cap;
            if (!image) return;
            const r = await fetch(apiBase + '/api/screen/step', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ image, ...data.screenStep, deviceId: window.__ariaDeviceId || 'desktop' }),
            });
            const st = await r.json();
            if (!st || !st.ok || !st.reply) return;
            // Say the screen's version, and draw where it is.
            setMessages((m) => {
              const out = m.slice();
              for (let i = out.length - 1; i >= 0; i -= 1) {
                if (out[i].role === 'assistant') { out[i] = { ...out[i], content: st.reply, fromScreen: true }; break; }
              }
              return out;
            });
            if (Array.isArray(st.annotations) && st.annotations.length && window.electronAPI?.sendOnScreenAnnotations) {
              window.electronAPI.sendOnScreenAnnotations({ annotations: st.annotations, message: st.reply });
            }
            // Say it out loud the same way her bridge lines are said. The fixed
            // line may already be playing; the screen's version follows it.
            try {
              const base = getApiBase() || API_BASE;
              const tr = await fetch(`${String(base).replace(/\/$/, '')}/tts`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text: st.reply, ...ariaUserIdJsonBody() }),
              });
              if (tr.ok) {
                const buf = await tr.arrayBuffer();
                if (buf && buf.byteLength) {
                  const url = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
                  const a = new Audio(url);
                  a.onended = () => { try { URL.revokeObjectURL(url); } catch (_) {} };
                  a.play().catch(() => {});
                }
              }
            } catch (_) { /* a silent step still shows on screen */ }
          } catch (e) { console.warn('[screen-step] failed:', e?.message); }
        })();
      }

      // Commit mode — advance or clear the task based on Aria's signal tokens.
      if (data?.commitUpdate) {
        if (data.commitUpdate.done) {
          commitTaskRef.current = null;
        } else if (data.commitUpdate.nextStep != null && commitTaskRef.current) {
          commitTaskRef.current = { ...commitTaskRef.current, currentStep: data.commitUpdate.nextStep };
        }
      }

      // Capture which model actually answered (for the "Last reply by" badge
      // in the power-user controls dropdown). Backend may send modelUsed,
      // modelTier, model, or brainMeta.detectedMode depending on path.
      const _modelUsed = data?.modelUsed || data?.model || data?.brainMeta?.detectedMode || '';
      if (_modelUsed) setLastModelUsed(String(_modelUsed));

      // Two-path help offer → remember the task so the user's next "show me" /
      // "do it" routes correctly without them restating what they wanted.
      if (data && data._helpOffer && data._helpOffer.task) {
        pendingHelpRef.current = { task: data._helpOffer.task };
      }
      // Teach session is live → keep coaching on the next turns.
      if (data && (data._teach || data.source === 'aria_teach_mode')) {
        teachActiveRef.current = true;
      }

      // ── Voice routing — execute the action Aria committed to ────────────
      // If the backend's action-intent middleware matched, `data.action`
      // tells us what to actually DO (not just say). Aria already returned
      // a confirmation string in `reply`; we run the action alongside.
      if ((data?.source === 'aria_action_intent' || data?._runActions) && (data?.action?.type || Array.isArray(data?.actions))) {
        // Execute ONE action. Compound commands ("open chrome and type youtube")
        // arrive as data.actions[] and are run in order below (with a pause after
        // launching an app so the follow-on step lands in the app just opened).
        const runOne = async (a) => {
          if (!a || !a.type) return;
          // Assist mode = JUST GO. We no longer wait on a "want me to start?"
          // confirm (it stranded users who didn't say a second "yes"). The
          // assist_mode_on handler below launches the screen share directly — the
          // OS screen-permission prompt IS the permission. (confirmFirst from the
          // backend is intentionally ignored now.)
        try {
          // Surface navigation (web + desktop) — set hash and dashboard section.
          if (a.hash) {
            window.location.hash = a.hash;
            setDashboardSection(a.hash);
          }
          // External URL (web: new tab; desktop: native open).
          if (a.type === 'open_url' && a.url) {
            if (window.electronAPI?.openExternal) {
              try { window.electronAPI.openExternal(a.url); }
              catch { window.open(a.url, '_blank'); }
            } else {
              window.open(a.url, '_blank');
            }
          }
          // Launch a native app by name (desktop only) — uses agent.js's robust
          // Windows launcher (Get-StartApps → shell:appsfolder, with a start
          // fallback). e.g. "open chrome" → { type:'open_app', app:'chrome' }.
          if (a.type === 'open_app' && a.app) {
            if (window.electronAPI?.runComposeActions) {
              try { await window.electronAPI.runComposeActions([{ type: 'open_app', app: a.app }]); }
              catch (e) { console.warn('[action] open_app failed:', e?.message); }
            } else if (window.electronAPI?.openApp) {
              try { window.electronAPI.openApp(a.app); }
              catch (e) { console.warn('[action] open_app failed:', e?.message); }
            } else {
              // Plain web — no desktop bridge, so launching a program isn't
              // possible. Say so instead of silently doing nothing.
              setMessages((m) => [...m, { role: 'assistant', content: `I can open ${a.app} for you from the desktop app — on the web I can't launch programs. Want me to open its website instead?`, mode: _inputMode }]);
            }
          }
          // Trigger the business-profile modal.
          if (a.trigger === 'business_profile_modal') {
            setShowBusinessOnboarding(true);
          }
          // Assist mode — desktop only. The IPC normalizes truthy → 'assist'
          // and null → off. Don't pass the literal string 'off' (it's truthy
          // and gets normalized to 'assist' — opposite of what we want).
          // ANY OTHER AGENT ACTION (8/23): scroll / key / virus_scan / type ...
          // from the backend's basic-command table go straight to the agent,
          // tagged as user-typed by main.js (no consent ask, no screenshot).
          if (a.type && !['open_url', 'open_app', 'assist_mode_on', 'assist_mode_off', 'screen_action'].includes(a.type) && window.electronAPI?.runComposeActions) {
            let out = null;
            try { out = await window.electronAPI.runComposeActions([a]); } catch (e) { console.warn('[action] ' + a.type + ' failed:', e?.message); }
            // SAY WHAT CAME BACK. "Checking." is not an answer; Defender's
            // numbers are. And a failure is said, never swallowed.
            try {
              const r = out && Array.isArray(out.results) ? out.results[0] : null;
              let line = '';
              if (r && r.ok === false && r.error) line = `That didn't work: ${r.error}`;
              else if (r && a.type === 'virus_status') {
                const fmt = (v) => { const m = /\d{10,}/.exec(String(v || '')); const d = m ? new Date(+m[0]) : (v ? new Date(v) : null); return d && !isNaN(d) ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'never'; };
                const running = !!r.running;
                line = running
                  ? 'The scan is still running.'
                  : `${r.rtp ? 'Real-time protection is on.' : 'Real-time protection is OFF.'} Last quick scan ${fmt(r.quickStart)}${r.quickEnd ? `, finished ${fmt(r.quickEnd)}` : ''}. ${Number(r.threats) > 0 ? `${r.threats} threat${Number(r.threats) === 1 ? '' : 's'} found.` : 'No threats found.'}`;
              }
              if (line) {
                setMessages((m) => [...m, { role: 'assistant', content: line, mode: _inputMode === 'voice' ? 'voice' : 'text' }]);
                if (_inputMode === 'voice') { try { speakAriaLine(line); } catch (_) {} }
              }
            } catch (_) {}
            return;
          }
          if (a.type === 'assist_mode_on' && window.electronAPI?.sendOverlayAssistMode) {
            // No nav — the Assist page is never shown (owner rule 2026-07-15).
            // Assist lives in the floating overlay + hidden engine; the user
            // stays on whatever they were doing while Aria drives their screen.
            try { window.electronAPI.sendOverlayAssistMode('assist'); }
            catch (e) { console.warn('[action] assist_mode_on failed:', e?.message); }
            // JUST GO. We're now on the screen view; request the share.
            // getDisplayMedia triggers the OS screen-permission prompt the FIRST
            // time (that IS the permission) and goes straight into compact Assist
            // after (main.js shrinks + pins the window → support-box skin) — no
            // second "yes" needed.
            setTimeout(() => {
              try { if (typeof requestScreenShare === 'function') requestScreenShare(); }
              catch (e) { console.warn('[action] requestScreenShare failed:', e?.message); }
            }, 80);
          }
          if (a.type === 'assist_mode_off' && window.electronAPI?.sendOverlayAssistMode) {
            try { window.electronAPI.sendOverlayAssistMode(null); }
            catch (e) { console.warn('[action] assist_mode_off failed:', e?.message); }
          }
          // Screen action — hand off to /api/screen/analyze with a screenshot
          // (desktop only — the agent layer needs to see the screen to know
          // what to click). On web, log a clear message and bail.
          if (a.type === 'screen_action') {
            // Prefer the user's exact command (a.q, added 2026-07-10) — "point
            // at X" must reach the analyzer as-is, not re-labeled "Click X".
            const question = a.q ? a.q : (a.target ? `Click "${a.target}"` : a.text ? `Type "${a.text}"` : '');
            if (question) {
              await (async () => {
                try {
                  if (!window.electronAPI?.captureScreen) {
                    // Say it where the owner can SEE it — a silent console.warn
                    // here is exactly how "nothing happened" went undiagnosed.
                    try { setMessages((m) => [...m, { role: 'assistant', content: "I can only do that from the desktop app — it doesn't look like it's running right now.", mode: _inputMode }]); } catch (_) {}
                    return;
                  }
                  const cap = await window.electronAPI.captureScreen();
                  const image = cap?.dataUrl || cap?.image || cap; // shape varies
                  // Absolute API base (8/23): a relative /api/... URL is
                  // file:///api/... inside the desktop app and never reached
                  // the backend. os rides along so shortcuts map to Windows.
                  const r = await fetch((getApiBase() || '') + '/api/screen/analyze', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      userId,
                      question,
                      image,
                      desktopAssist: true,
                      clientPlatform: 'desktop',
                      os: (window.electronAPI && window.electronAPI.platform) || '',
                    }),
                  });
                  const plan = await r.json();
                  // If the analyzer returned action(s), hand them to the
                  // desktop agent via the existing runComposeActions IPC channel.
                  if (window.electronAPI?.runComposeActions && Array.isArray(plan?.actions) && plan.actions.length > 0) {
                    try { await window.electronAPI.runComposeActions(plan.actions); }
                    catch (e) { console.warn('[action] runComposeActions failed:', e?.message); }
                  }
                  // Don't drop the VISUALS (2026-07-10): a successful analyze
                  // often answers with annotations (glow circles) or assist
                  // cues instead of actions — forward both to the overlay so
                  // pointing is SEEN, not just spoken.
                  if (Array.isArray(plan?.annotations) && plan.annotations.length > 0 && window.electronAPI?.sendOnScreenAnnotations) {
                    try { await window.electronAPI.sendOnScreenAnnotations({ annotations: plan.annotations, message: plan.reply || '' }); } catch (_) {}
                  }
                  if (Array.isArray(plan?.assistCues) && plan.assistCues.length > 0 && window.electronAPI?.executeAssistCues) {
                    try { await window.electronAPI.executeAssistCues(plan.assistCues); } catch (_) {}
                  }
                  console.log('[action] screen_analyze plan:', plan);
                } catch (e) {
                  console.warn('[action] screen_action exec failed:', e?.message);
                }
              })();
            }
          }
          console.log('[Aria] action executed:', a.type, a);
        } catch (e) {
          console.warn('[Aria] action exec failed:', e?.message);
        }
        }; // end runOne

        // Run the action(s) in order. Compound payloads (data.actions[]) run
        // sequentially; a launched app gets a moment to come forward before the
        // next step so a follow-on type/click lands in it (not the old window).
        (async () => {
          const list = (Array.isArray(data.actions) && data.actions.length)
            ? data.actions
            : (data.action ? [data.action] : []);
          for (const act of list) {
            await runOne(act);
            if (act && act.type === 'open_app') await new Promise((r) => setTimeout(r, 2200));
            else if (list.length > 1) await new Promise((r) => setTimeout(r, 500));
          }
        })();
      }

      // Forward Aria's attention cues to the desktop overlay window so they
      // paint on top of whatever app the user is in (Chrome, Premiere, etc.).
      // No-op outside the desktop app.
      if (Array.isArray(data?.attention) && data.attention.length > 0 &&
          typeof window !== 'undefined' && window.electronAPI?.attention?.emit) {
        try { window.electronAPI.attention.emit(data.attention); } catch (_) {}
      }
      // "Ok." IS AN ANSWER WHEN SHE MEANT IT (8/22, live: "We're going to
      // write." -> backend "Ok." -> this turned it into "Aria couldn't
      // generate a reply"). The placeholder test is for a brain that emitted
      // nothing useful, not for a lane that answered on purpose - those mark
      // the reply as quoted / carry a turn kind.
      const _deliberate = !!(data && (data.quoted || data.turn || data.source === 'aria_turn_read'));
      const isPlaceholderReply = !_deliberate && /^(Done\.?|Got it\.?|OK\.?)$/i.test(reply);
      let replyText = (reply && !isPlaceholderReply) ? reply : null;
      // Backend may return cached guide with options but no reply text (old server); always show something
      if (!replyText && data?.options?.length) {
        const first = data.options[0];
        const title = first?.title || first?.description || (typeof first === 'string' ? first : null);
        replyText = title ? `Here are some options for you. For example: ${title}.` : "Here are some options for you.";
      }
      if (!replyText) replyText = "Aria couldn't generate a reply. Try again or rephrase.";
      // In chat show only the solution (one short attempt); full reply is spoken via audio/TTS
      const displayContent = (data?.solution != null && String(data.solution).trim() !== '') ? String(data.solution).trim() : replyText;
      if (import.meta.env?.DEV || (typeof window !== 'undefined' && window.electronAPI)) {
        console.log('[Aria] Response received:', { ok: res.ok, hasReply: !!reply, replyLen: reply?.length, replyPreview: (reply || replyText || '').toString().substring(0, 80) });
        if (!reply && data && typeof data === 'object') console.log('[Aria] Response keys (no reply):', Object.keys(data));
      }
      // Pass through optional citation + exhibit fields from the server.
      // citation: { source: 'Claude'|'Gemini'|'Grok'|'GPT', meta?: '4.5 · 200K ctx', body?: 'raw heavy-model answer' }
      // exhibit:  { type: 'llm'|'api'|'encryption', ...typeSpecificFields, caption?: string }
      // NOTE: the user bubble was already appended at the top of this
      // function (right before setLoading(true)), so only the assistant
      // message is added here — appending the user message again would
      // double-render their text.
      const _cite = data && (data.citation || data.source) ? (data.citation || data.source) : null;
      const _exhibit = data && data.exhibit ? data.exhibit : null;
      // surface: the wb-*.html panel the task router picked for this turn.
      // Embedded by the backend's taskOrchestrator. When present, the chat
      // bubble renders the wb-* template inline as the "Amazon for AI"
      // tailored layout for whatever the user is trying to do.
      const _surface = data && (data.surface || data.workbench?.surface) ? (data.surface || data.workbench?.surface) : null;
      // projectSuggestion: backend detected "I want to start X" — but
      // doesn't create yet. Aria OFFERS a dashboard via a chip; user
      // confirms ("Yes, build it") → POST creates the project and we
      // navigate. action='open' = dashboard already exists, just opens.
      const _projectSuggestion = data && data.projectSuggestion ? data.projectSuggestion : null;
      // Capture structured intent-offer payloads so the chat can render an
      // action card (hire_offer / parts_offer / dashboard_preview) instead
      // of just plain text.
      const _displayMode = data?.displayMode || null;
      const _hireDraft = data?.hireDraft || null;
      const _partsDraft = data?.partsDraft || null;
      const _dashboardPreview = data?.dashboardPreview || null;
      // SCENE CLOCK (9/2): a typed reply is read from the moment it lands,
      // so its scene's items land on the words as the eye reaches them. A
      // spoken reply waits for her audio; the voice-side workstation hands
      // the player her real speech clock (see sceneClock below).
      try {
        if (data && data.scene && !data._sceneClock) data._sceneClock = { mode: _inputMode === 'text' ? 'read' : 'wait', startedAt: Date.now() };
      } catch (_) { /* a clock is a nicety; the turn is not */ }
      // ── ONE SHEET, NOT ONE PER TURN (9/3) ────────────────────────────
      // The project sheet rides every intake turn so it can update, and each
      // turn in the typed thread renders its own Workstation — so three
      // questions put three sheets down the page, the first two frozen at
      // the state they were answered in. The newest sheet IS the sheet; the
      // older copies are dropped as it arrives. Everything else on those
      // turns (results, drafts, scenes) is a record of that turn and stays.
      setMessages((m) => [
        ...(data && data.sheet
          ? m.map((msg) => (msg && msg.data && msg.data.sheet ? { ...msg, data: { ...msg.data, sheet: undefined } } : msg))
          : m),
        { role: 'assistant', content: displayContent, data, mode: _inputMode, citation: _cite, exhibit: _exhibit, surface: _surface, projectSuggestion: _projectSuggestion, displayMode: _displayMode, hireDraft: _hireDraft, partsDraft: _partsDraft, dashboardPreview: _dashboardPreview }
      ]);
      // Capture turnState so the mic auto-close effect knows whether Aria
      // is done (close mic), expecting an answer (LISTENING — keep open),
      // or just waiting (close after silence). See post-Aria-speaking
      // effect for the actual close logic.
      if (data && typeof data.turnState === 'string') {
        lastTurnStateRef.current = data.turnState.toUpperCase();
      }

      // ── ARC ACTIONS — autonomous Assist Mode dispatch ─────────────
      // When Aria's response is from an arc with autonomous actions
      // (credit application walkthrough, etc.), the renderer:
      //   1. dispatches non-narrate / non-verify actions to the desktop
      //      agent via existing aria:runComposeActions IPC (nut.js does
      //      the actual clicking / typing / scrolling)
      //   2. fires verify actions against the existing /api/screen/verify
      //      endpoint so the vision model confirms the page state
      //   3. auto-advances the conversation with "next" so Aria moves to
      //      the next step without the user having to keep saying it
      //
      // Failsafe: any error here falls through silently. The user can
      // still progress manually with "done" / "next" / by clicking.
      try {
        if (data && data._fromArc && Array.isArray(data._arcActions) && data._arcActions.length > 0) {
          const arcActions = data._arcActions;
          const agentActions = arcActions.filter(a =>
            a && typeof a.type === 'string' &&
            a.type !== 'narrate' &&
            a.type !== 'verify' &&
            a.type !== 'screenshot_and_verify' &&
            a.type !== 'write_user_facts' &&
            a.type !== 'analyze_screen'
          );
          const verifyActions = arcActions.filter(a =>
            a && (a.type === 'verify' || a.type === 'screenshot_and_verify')
          );
          const analyzeScreenAction = arcActions.find(a => a && a.type === 'analyze_screen');

          // ── ANALYZE SCREEN action — "can you help me with this" ──
          // Aria captures the user's screen + sends it to the existing
          // /api/screen/analyze vision pipeline. Response gets appended to
          // chat + spoken via TTS, same shape as the original conversation reply.
          if (analyzeScreenAction) {
            try {
              if (typeof window !== 'undefined' && window.electronAPI?.captureScreen) {
                // Capture pixels + a11y context (active app, window title,
                // focused field, URL if browser). Vision model gets BOTH so
                // it can focus on what the user is actually working on
                // instead of describing the whole screen.
                const cap = await window.electronAPI.captureScreen({
                  includeMenuBar: false,
                  includeClipboard: false,
                });
                if (cap && cap.ok && cap.image) {
                  const analyzeUrl = (base || '') + '/api/screen/analyze';
                  const ar = await fetchWithTimeout(analyzeUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      image: cap.image,
                      question: text,
                      desktopAssist: true,
                      screenShareMode: false,
                      // screenContext steers the vision model toward the
                      // user's actual focus — most-recent activity, active
                      // window, focused field, etc. /api/screen/analyze
                      // already destructures this field on the backend.
                      screenContext: cap.screenContext || null,
                    }),
                    timeout: 60000,
                  });
                  const analysis = await ar.json().catch(() => ({}));
                  const visionReply = analysis?.reply || analysis?.solution || analysis?.quickReply || '';
                  if (visionReply) {
                    setMessages((m) => [...m, { role: 'assistant', content: visionReply, data: analysis, mode: _inputMode }]);
                    // Speak it if the response carried audio.
                    // HARD RULE: route through prepareAriaPlayback() so mic-mute fully gates voice.
                    // Aria never talks unless the mic is open AND the user spoke to her.
                    if (analysis.audioUrl) {
                      try {
                        const a = new Audio(analysis.audioUrl);
                        prepareAriaPlayback(a);
                        a.play().catch(()=>{});
                      } catch (_) {}
                    }
                    // Forward annotations to the blue-assist overlay
                    if (Array.isArray(analysis.annotations) && analysis.annotations.length > 0 &&
                        window.electronAPI?.attention?.emit) {
                      try { window.electronAPI.attention.emit(analysis.annotations); } catch (_) {}
                    }
                  } else {
                    setMessages((m) => [...m, { role: 'assistant', content: "I looked but couldn't make out what's going on. Can you tell me what you're trying to do?", mode: _inputMode }]);
                  }
                } else {
                  const errMsg = cap?.error === 'screen_permission_denied' || (cap?.hint && cap.hint.includes('Screen Recording'))
                    ? "I need Screen Recording permission to see your screen. Want me to open the Settings pane?"
                    : "I couldn't capture your screen. The desktop app might need to be restarted, or Screen Recording isn't granted.";
                  setMessages((m) => [...m, { role: 'assistant', content: errMsg, mode: _inputMode }]);
                }
              } else {
                // Web-only fallback
                setMessages((m) => [...m, { role: 'assistant', content: "I can only see your screen in the desktop app — I'm running in the browser right now. Open the Aria desktop app and ask again.", mode: _inputMode }]);
              }
            } catch (analyzeErr) {
              console.warn('[Aria arc] analyze_screen failed:', analyzeErr);
              setMessages((m) => [...m, { role: 'assistant', content: "Something hiccupped trying to read your screen. Try again in a second.", mode: _inputMode }]);
            }
          }

          let agentOk = true;
          let agentRan = 0;
          if (agentActions.length > 0 && typeof window !== 'undefined' && window.electronAPI?.runComposeActions) {
            try {
              const result = await window.electronAPI.runComposeActions(agentActions);
              agentOk = !!(result && result.ok !== false);
              agentRan = (result && result.ran) || 0;
              if (import.meta.env?.DEV || window.electronAPI) {
                console.log('[Aria arc] agent actions:', { ok: agentOk, ran: agentRan, requested: agentActions.length });
              }
            } catch (agentErr) {
              console.warn('[Aria arc] runComposeActions threw:', agentErr);
              agentOk = false;
            }
          } else if (agentActions.length > 0 && typeof window !== 'undefined' && !window.electronAPI) {
            // Web-only fallback: handle open_url client-side
            for (const a of agentActions) {
              if (a.type === 'open_url' && a.url) {
                try { window.open(a.url, '_blank'); } catch (_) {}
              }
            }
          }

          // Verify actions — call existing /api/screen/verify endpoint
          for (const v of verifyActions) {
            try {
              await fetchWithTimeout((base || '') + '/api/screen/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ prompt: v.prompt, arc_id: data._arcId, step: data._arcStep }),
                timeout: 15000,
              });
            } catch (verifyErr) {
              console.warn('[Aria arc] verify failed:', verifyErr);
            }
          }

          // Auto-advance: if agent ran any actions cleanly and the arc isn't
          // terminal, fire "next" after a delay so the user can interrupt
          // with "wait" or "stop". Skipped if no agent actions actually ran
          // (likely a guided step where Aria waits for user input).
          if (agentOk && agentRan > 0 && !data._arcTerminal) {
            setTimeout(() => {
              try { sendMessageWithText('next'); }
              catch (advErr) { console.warn('[Aria arc] auto-advance failed:', advErr); }
            }, 1800);
          }
        }
      } catch (arcDispatchErr) {
        console.warn('[Aria arc] dispatch error:', arcDispatchErr);
      }

      // ── On-screen highlight overlay (Assist Mode) ───────────────
      // When Aria's response includes annotations, push them to the
      // transparent click-through overlay window so the user sees
      // circles / rects directly on top of whatever app they're in.
      // Local rendering — zero cloud cost. No-op on web (no electronAPI).
      try {
        if (data && Array.isArray(data.annotations) && data.annotations.length > 0
            && typeof window !== 'undefined' && window.electronAPI?.sendOnScreenAnnotations) {
          window.electronAPI.sendOnScreenAnnotations({
            annotations: data.annotations,
            message: typeof data.annotationMessage === 'string'
              ? data.annotationMessage
              : (displayContent || '').toString().slice(0, 240),
          }).catch(() => {});
        }
      } catch (_) { /* annotations are best-effort */ }
      // A searched turn lands in the search chain; a spoken one in the
      // conversation. Never both — that merge is what made a typed query read
      // as something he had said.
      if (_inputMode !== 'voice') {
        setSearchTrack((prev) => [
          ...prev,
          { role: 'user', content: text },
          { role: 'assistant', content: displayContent },
        ].slice(-20));
      } else {
        setConversationHistory((prev) => [
          ...prev,
          { role: 'user', content: text },
          { role: 'assistant', content: displayContent },
        ]);
      }
      // Log to conversation bubble
      addConversationEntry('user', text);
      addConversationEntry('aria', displayContent);

      if (isDesktopApp && /^my name is\s+/i.test(String(text).trim())) {
        try {
          if (localStorage.getItem(DESKTOP_FIRST_RUN_UX_COMPLETE_KEY) !== '1') {
            firstRunResumeHandledRef.current = true;
            void runDesktopWakeAndExamplesFlow();
          }
        } catch (_) {}
      }

      if (activeReplyAudioRef.current) {
        try {
          activeReplyAudioRef.current.pause();
          activeReplyAudioRef.current.currentTime = 0;
        } catch (_) {}
        activeReplyAudioRef.current = null;
        setAriaSpeaking(false);
      }

      // Stop ack audio cleanly before full reply plays (no overlap)
      if (ackAudio) {
        try { ackAudio.pause(); ackAudio.currentTime = 0; } catch (_) {}
        ackAudio = null;
      }

      // Fire-and-forget POST to backend pipeline log so the dashboard pills
      // light up. Does NOT block the audio path on failure.
      const dashEmit = (stage, text, meta) => {
        try {
          fetch('http://127.0.0.1:3001/api/pipeline/event', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ stage, text: text || '', meta: meta || null, reqId: meta?.reqId || '' }),
          }).catch(() => {});
        } catch (_) {}
      };

      // Resolve inline audio source. Prefer Blob URL over data: URI because
      // Chromium handles Blob audio more reliably and avoids parsing 100kB+
      // base64 strings on the main thread.
      let inlineAudioSrc = null;
      let inlineBlobUrl = null;
      const tDecodeStart = performance.now();
      try {
        if (data?.audioUrl && data.audioUrl.startsWith('data:audio')) {
          const m = data.audioUrl.match(/^data:(audio\/[^;]+);base64,(.+)$/);
          if (m) {
            const bytes = atob(m[2]);
            const arr = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
            const blob = new Blob([arr], { type: m[1] });
            inlineBlobUrl = URL.createObjectURL(blob);
            inlineAudioSrc = inlineBlobUrl;
            dashEmit('audio.decode', `decoded ${(blob.size/1024).toFixed(1)}KB ${m[1]}`,
              { ms: Math.round(performance.now() - tDecodeStart), size: blob.size, mime: m[1] });
          } else {
            inlineAudioSrc = data.audioUrl;
            dashEmit('audio.decode', 'using data URI directly (no base64 match)',
              { ms: Math.round(performance.now() - tDecodeStart) });
          }
        } else if (data?.audioUrl) {
          inlineAudioSrc = data.audioUrl;
          dashEmit('audio.decode', 'streaming URL', { ms: Math.round(performance.now() - tDecodeStart) });
        } else if (data?.audioData) {
          const bytes = atob(data.audioData);
          const arr = new Uint8Array(bytes.length);
          for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
          const blob = new Blob([arr], { type: 'audio/wav' });
          inlineBlobUrl = URL.createObjectURL(blob);
          inlineAudioSrc = inlineBlobUrl;
          dashEmit('audio.decode', `decoded ${(blob.size/1024).toFixed(1)}KB audio/wav`,
            { ms: Math.round(performance.now() - tDecodeStart), size: blob.size });
        } else if (data?.audioStreamUrl) {
          // ── PLAY THE STREAM SHE ALREADY HANDED US (2026-09-08) ──────────
          // On an uncached line the backend attaches audioStreamUrl (a chunked
          // edge synth that starts in ~1s) AND this client ignored it, then
          // fired a SECOND blocking POST /tts below and waited for the whole
          // clip. That doubled the wait on exactly the lines that were already
          // the slowest. <audio src=streamUrl> plays the chunked response
          // natively — one clip, one onplay/onended, so the floor + echo gate
          // behave exactly as they do for a cached hit. Absolute base because
          // the desktop renderer runs on file://, where a relative URL would
          // resolve to the filesystem root.
          const _b = String(getApiBase() || '').replace(/\/$/, '');
          inlineAudioSrc = _b + data.audioStreamUrl + (data.audioStreamUrl.includes('?') ? _vtParam() : '');
          dashEmit('audio.decode', 'streaming URL (edge)', { ms: Math.round(performance.now() - tDecodeStart) });
        }
      } catch (decodeErr) {
        console.error('[aria-audio] failed to decode audio:', decodeErr);
        dashEmit('audio.decode', decodeErr.message, { level: 'err' });
      }
      // HARD MODALITY RULE: typed message → text-only reply, no matter what
      // audio the server attached (belt-and-suspenders — a canonical/cached
      // payload or future middleware must never make her talk over typing).
      // Local fallback responses may contain text only. Voice turns still need
      // to speak that completed text, so synthesize it here when no audio field
      // was attached by the legacy conversation route.
      if (!inlineAudioSrc && _inputMode === 'voice' && String(displayContent || '').trim()) {
        try {
          const _voiceBase = String(getApiBase() || base || '').replace(/\/$/, '');
          inlineAudioSrc = `${_voiceBase}/api/tts/stream?text=${encodeURIComponent(String(displayContent).trim())}${_vtParam()}`;
          dashEmit('audio.decode', 'fallback TTS stream', { ms: Math.round(performance.now() - tDecodeStart) });
        } catch (_) {}
      }
      if (inlineAudioSrc && _inputMode !== 'voice') {
        if (inlineBlobUrl) { try { URL.revokeObjectURL(inlineBlobUrl); } catch (_) {} }
        inlineAudioSrc = null;
        inlineBlobUrl = null;
      }
      if (inlineAudioSrc) {
        console.log('[aria-audio] playing', inlineBlobUrl ? 'blob' : 'data-uri', 'src');
        _vtStamp('tts.queued', { path: 'inline', src: inlineBlobUrl ? 'blob' : (/\/api\/tts\/stream/.test(inlineAudioSrc) ? 'stream' : 'other') });
        // Never cut a bridge mid-sentence — if Aria is mid "let me pull the…"
        // cover line, wait for it to finish, then speak the real reply.
        await waitForBridge();
        unlockAudioForReply();
        const audio = new Audio();
        audio.src = inlineAudioSrc;
        _vtStamp('tts.requested', { path: 'inline' });
        _vtWatchPlay(audio, { path: 'inline' });
        prepareAriaPlayback(audio);
        audio.playbackRate = chooseSpeechRate((data?.reply || replyText || displayContent || '').toString());
        activeReplyAudioRef.current = audio;
        ariaSpeakingRef.current = true;
        audio.onplay = () => {
          console.log('[aria-audio] onplay');
          setAriaSpeaking(true);
          dashEmit('audio.play', `speakers (duration ${audio.duration?.toFixed(2) || '?'}s)`,
            { duration: audio.duration });
        };
        audio.oncanplay = () => console.log('[aria-audio] oncanplay (duration='+audio.duration+'s)');
        audio.onloadedmetadata = () => console.log('[aria-audio] onloadedmetadata');
        audio.onended = () => {
          console.log('[aria-audio] onended');
          if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null;
          ariaSpeakingRef.current = false;
          setAriaSpeaking(false);
          // HER TURN IS OVER — LISTEN AGAIN (2026-09-08). This inline path (the
          // one that plays audioUrl / audioStreamUrl) never released the floor;
          // only the /tts refetch path below did. So after every cached-hit or
          // streamed reply the mic sat muted until the 12s ceiling — "she goes
          // deaf after i talk to her." One line, matching the /tts path.
          try { releaseFloorRef.current?.(); } catch (_) {} try { setPreparingSpeech(false); } catch (_) {}
          if (inlineBlobUrl) try { URL.revokeObjectURL(inlineBlobUrl); } catch (_) {}
        };
        audio.onerror = () => {
          const code = audio.error?.code; // 1=ABORT 2=NETWORK 3=DECODE 4=SRC_NOT_SUPPORTED
          console.error('[aria-audio] onerror code='+code+' message='+audio.error?.message);
          if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null;
          ariaSpeakingRef.current = false;
          setAriaSpeaking(false);
          // Audio failed — don't leave her deaf behind a held floor.
          try { releaseFloorRef.current?.(); } catch (_) {} try { setPreparingSpeech(false); } catch (_) {}
          setVoiceError('Audio playback failed (code '+code+'). Check Windows Volume Mixer.');
          if (inlineBlobUrl) try { URL.revokeObjectURL(inlineBlobUrl); } catch (_) {}
        };
        audio.play().then(() => {
          console.log('[aria-audio] play() promise resolved');
        }).catch((e) => {
          console.warn('[aria-audio] play() rejected, retry in 100ms:', e?.name, e?.message);
          setTimeout(() => audio.play().catch((e2) => {
            console.error('[aria-audio] retry play() failed:', e2?.name, e2?.message);
            ariaSpeakingRef.current = false;
            setAriaSpeaking(false);
            // Playback never started — release the floor or the mic is deaf.
            try { releaseFloorRef.current?.(); } catch (_) {} try { setPreparingSpeech(false); } catch (_) {}
            setVoiceError('Audio blocked: '+(e2?.message||e?.message||'unknown')+'. Click the app once and try again.');
          }), 100);
        });
      } else if (replyText && replyText.length > 0 && replyText.length < 5000 && data?._communicationMode !== 'text' && _inputMode !== 'text') {
        // No audio in response – TTS the full reply (spoken), not the short solution shown in chat.
        // Skip if backend told us this is a text-only turn OR the user typed (not mic).
        const textToSpeak = (data?.reply != null && String(data.reply).trim() !== '') ? String(data.reply).trim() : replyText;
        unlockAudioForReply();
        const ttsUrl = base ? `${String(base).replace(/\/$/, '')}/tts` : '/tts';
        _willSpeak = true;
        _vtStamp('tts.queued', { path: 'tts-post' });
        _vtStamp('tts.requested', { path: 'tts-post' });
        fetchWithTimeout(ttsUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // _ttsRole: server marks person-behavior turns (e.g. 'repeat' =
          // she's re-saying herself → slower edge prosody).
          body: JSON.stringify({ text: textToSpeak, ...ariaUserIdJsonBody(), ...(data?._ttsRole ? { role: data._ttsRole } : {}), ...((_voiceTurnTiming && _voiceTurnTiming.turnId) ? { turnId: _voiceTurnTiming.turnId } : {}) }),
          timeout: 30000,
        })
          .then(async (r) => {
            if (!r.ok) {
              let detail = '';
              try {
                const j = await r.json();
                detail = j?.message || j?.error || '';
              } catch (_) {}
              setVoiceError(
                detail || `Aria voice failed (${r.status}). Add your User ID under Subscription, or set ALLOW_ANONYMOUS_AI=1 on the backend for local dev.`,
              );
              _showWhatSheSaid(textToSpeak);
              try { releaseFloorRef.current?.(); } catch (_) {} try { setPreparingSpeech(false); } catch (_) {}
              return null;
            }
            return r.arrayBuffer().then((buffer) => ({
              buffer,
              contentType: r.headers.get('content-type'),
            }));
          })
          .then(async (ttsResponse) => {
            const buf = ttsResponse?.buffer;
            if (!buf || buf.byteLength === 0) return;
            const dataUrl = audioDataUrlFromArrayBuffer(buf, ttsResponse?.contentType);
            if (dataUrl.length > 100) {
              // Wait out any in-flight bridge cover line — never cut her off.
              await waitForBridge();
              const audio = new Audio(dataUrl);
              _vtWatchPlay(audio, { path: 'tts-post' });
              prepareAriaPlayback(audio);
              audio.playbackRate = chooseSpeechRate(textToSpeak);
              activeReplyAudioRef.current = audio;
              ariaSpeakingRef.current = true; // raise VAD threshold before play() fires
              audio.onplay = () => { setAriaSpeaking(true); try { setPreparingSpeech(false); } catch (_) {} };
              audio.onended = () => {
                if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null;
                ariaSpeakingRef.current = false;
                setAriaSpeaking(false);
                // Her turn is genuinely over - listen again (8/31).
                try { releaseFloorRef.current?.(); } catch (_) {} try { setPreparingSpeech(false); } catch (_) {}
              };
              audio.onerror = () => {
                if (activeReplyAudioRef.current === audio) activeReplyAudioRef.current = null;
                ariaSpeakingRef.current = false;
                setAriaSpeaking(false);
                setVoiceError('Aria voice playback failed. Please try again.');
                _showWhatSheSaid(textToSpeak);
                try { releaseFloorRef.current?.(); } catch (_) {} try { setPreparingSpeech(false); } catch (_) {}
              };
              audio.play().catch(() => {
                ariaSpeakingRef.current = false;
                setAriaSpeaking(false);
                setVoiceError('Aria voice playback failed. Please click once in the app, then try again.');
                _showWhatSheSaid(textToSpeak);
                try { releaseFloorRef.current?.(); } catch (_) {} try { setPreparingSpeech(false); } catch (_) {}
              });
            }
          })
          .catch((err) => {
            setVoiceError(
              err && (String(err.message || '').includes('fetch') || err.message === 'Failed to fetch')
                ? "Can't reach the server for Aria's voice. Is HowTo-Backend running on port 3001?"
                : "Aria voice request failed.",
            );
            _showWhatSheSaid(textToSpeak);
            try { releaseFloorRef.current?.(); } catch (_) {} try { setPreparingSpeech(false); } catch (_) {}
          });
      }
    } catch (err) {
      if (import.meta.env?.DEV) console.log('[Aria] Request failed:', err?.message);
      const isNetwork = err?.name === 'TypeError' && err?.message?.includes('fetch') || err?.message === 'Failed to fetch' || err?.code === 'ECONNREFUSED' || err?.name === 'AbortError';
      // NOT HIS INTERNET (Braeden 9/6). The old line here branched on the BUILD
      // MODE, and the desktop app ships a production build - so every time the
      // local backend on 127.0.0.1 restarted, the app told him to check his
      // internet connection. cannotReachMessage() looks at where the request
      // was actually going and says the true thing.
      const message = isNetwork
        ? (isDev ? "Can't reach Aria. Start the backend: cd HowTo-Backend && node server.js (port 3001). Open View → Toggle Developer Tools → Console and look for [Aria] Sending to: to see which URL was used." : cannotReachMessage('Aria'))
        : `Error: ${err.message}`;
      setMessages((m) => [...m, { role: 'assistant', content: message, error: true, mode: _inputMode }]);
    } finally {
      clearHold(); setLoading(false);
    }
  };

  useLayoutEffect(() => {
    sendMessageWithTextRef.current = sendMessageWithText;
  });
  // Keep ref current on every render so voice/async callbacks always see latest send
  sendMessageWithTextRef.current = sendMessageWithText;

  // Do not tie the chip to voiceRecording — that fires for the whole VAD capture window (noise/restarts too)
  // and reads as “stuck processing.” Capture state stays on the mic button only.
  const showAriaStatus =
    !(isDesktopApp && showFirstRunExamplesModal) &&
    (loading ||
      ariaSpeaking ||
      (effectiveSection === 'ask' && micOn && (voiceListening || micSpeaking)));
  const ariaStatusLabel = loading
    ? 'Aria is thinking...'
    : ariaSpeaking
      ? 'Aria is speaking...'
      : (effectiveSection === 'ask' && micOn && voiceListening)
        ? 'Listening...'
        : (effectiveSection === 'ask' && micOn && micSpeaking)
          ? 'Aria can hear you'
          : '';
  const ariaStatusClass = loading
    ? 'is-thinking'
    : ariaSpeaking
      ? 'is-speaking'
      : 'is-listening';

  const showAskHelpIdeas =
    effectiveSection === 'ask' &&
    messages.length === 0 &&
    !loading &&
    !introLocked &&
    (!isDesktopApp || hasSpokenDesktopIntro) &&
    !readAwaitingIntroName() &&
    (!isDesktopApp || firstRunUxComplete);

  const applyHelpIdeaPrompt = (prompt) => {
    setInput(prompt);
    requestAnimationFrame(() => {
      try {
        chatInputRef.current?.focus();
      } catch (_) {}
    });
  };

  // Listen for "go home and prefill" requests dispatched from ExploreView
  // (and anywhere else that wants to seed the input from a different surface).
  useEffect(() => {
    const onPrefillAndGoHome = (e) => {
      const prompt = e?.detail?.prompt;
      if (typeof prompt !== 'string' || !prompt) return;
      try { if (window.location.hash) window.location.hash = ''; } catch (_) {}
      applyHelpIdeaPrompt(prompt);
    };
    window.addEventListener('aria:prefillAndGoHome', onPrefillAndGoHome);
    return () => window.removeEventListener('aria:prefillAndGoHome', onPrefillAndGoHome);
  }, []);

  // Listen for "open this workbench" requests dispatched from ExploreView when
  // a tile's route is a static .html surface (e.g. wb-shopping.html). Stores
  // the src/title and switches to the 'workbench' section so WorkbenchFrame
  // renders it. Previously there was no listener, so those tiles opened
  // nothing (the "nothing pops up" bug).
  useEffect(() => {
    const onOpenWorkbench = (e) => {
      const src = e?.detail?.src;
      if (typeof src !== 'string' || !src) return;
      setOpenWorkbench({ src, title: e?.detail?.title || 'Workbench' });
      try { window.location.hash = 'workbench'; } catch (_) {}
      setDashboardSection('workbench');
    };
    window.addEventListener('aria:openWorkbench', onOpenWorkbench);
    return () => window.removeEventListener('aria:openWorkbench', onOpenWorkbench);
  }, []);

  // Assist mode from anywhere (e.g. the Aria box + menu → "Assist mode").
  React.useEffect(() => {
    const onOpenAssist = () => {
      // Same path as the working assist_mode_on action: nav to the screen view,
      // enter overlay assist, then request the share.
      // No nav — Assist page is never shown (owner rule 2026-07-15). The share
      // request below mounts the hidden engine; overlay is the visible surface.
      try { if (window.electronAPI?.sendOverlayAssistMode) window.electronAPI.sendOverlayAssistMode('assist'); } catch (_) {}
      setTimeout(() => { try { if (typeof requestScreenShare === 'function') requestScreenShare(); } catch (_) {} }, 80);
    };
    window.addEventListener('aria:openAssist', onOpenAssist);
    return () => window.removeEventListener('aria:openAssist', onOpenAssist);
  }, []);

  // When a text-mode send completes (loading goes true → false), restore
  // focus to the input AND flush any queued send the user fired while
  // we were still replying. Tracks a ref to detect the transition.
  // IMPORTANT: always update the ref at the end (regardless of early
  // return) so subsequent transitions are detected correctly. Previously
  // the ref was only updated on one path, which left it stuck and broke
  // multi-message flows after the first turn.
  const wasLoadingRef = useRef(false);
  useEffect(() => {
    const wasLoading = wasLoadingRef.current;
    wasLoadingRef.current = loading;
    if (wasLoading && !loading) {
      // Pull the next queued message (FIFO) and fire it once the current reply
      // finished. Sending re-sets loading, so the following queued message is
      // handled on the NEXT loading→done transition — one at a time.
      if (queuedMessagesRef.current.length > 0) {
        const next = queuedMessagesRef.current[0];
        setQueuedMessages((q) => q.slice(1));
        // Fire on next tick so React commits the unloaded state first.
        setTimeout(() => {
          try { sendMessageWithText(next.text || 'What is this and how can I fix it?'); } catch (_) {}
        }, 0);
        return;
      }
      if (lastInputMode === 'text') {
        // Small delay to let React commit the disabled→enabled state if
        // something else re-disables the input mid-frame.
        const t = setTimeout(() => {
          try { chatInputRef.current?.focus(); } catch (_) {}
        }, 30);
        return () => clearTimeout(t);
      }
    }
  }, [loading, lastInputMode]);

  // Loading-stage hint — feels like Aria narrating, not a generic spinner.
  // Phrasing is conversational + first-person so the bubble reads as her
  // talking. Timings match the conversation pipeline:
  //   < 0.4s  → "On it"               (canonical fast path)
  //   0.4-1s  → "Looking that up"      (deeper canonical / brain match)
  //   1-2s    → "Checking what I know" (intent / hot cache)
  //   2-3.5s  → "Thinking it through"  (Aria's own reasoning layer — gpt-4o)
  //   3.5-5.5s→ "Pulling more info"    (multi-model or RAG)
  //   5.5s+   → "One sec"              (almost done)
  // Never name a specific external model — Claude is BUILD-TIME ONLY per
  // ARIA_CONTEXT.md, so any "asking Claude" hint at runtime is a lie.
  // Reverts to empty as soon as loading clears so the bubble vanishes.
  const [loadingHint, setLoadingHint] = useState('');
  useEffect(() => {
    if (!loading) {
      setLoadingHint('');
      return;
    }
    setLoadingHint('On it');
    const t1 = setTimeout(() => setLoadingHint('Looking that up'), 400);
    const t2 = setTimeout(() => setLoadingHint('Checking what I know'), 1000);
    const t3 = setTimeout(() => setLoadingHint('Thinking it through'), 2000);
    const t4 = setTimeout(() => setLoadingHint('Pulling more info'), 3500);
    const t5 = setTimeout(() => setLoadingHint('One second'), 5500);
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      clearTimeout(t4); clearTimeout(t5);
    };
  }, [loading]);

  // The citation source line above the Aria bubble already shows which
  // model answered (orange "CLAUDE OPUS" / blue "GEMINI" / etc.) — so
  // the post-answer "got it from X" flash would be redundant noise.
  // Keep the loading-hint strictly for what Aria is doing right now.

  return (
    <>
    {/* Aria business onboarding: 5-question profile capture (trade · stage ·
        mindset · energy · comm style). Renders once per user; gated by
        /api/aria/onboarding/status. Skippable — but we'll ask again next session. */}
    {showBusinessOnboarding && (
      <AriaBusinessOnboarding
        onComplete={handleBusinessOnboardingComplete}
        onSkip={handleBusinessOnboardingSkip}
        allowSkip={true}
      />
    )}

    {/* Desktop onboarding: one Allow button — opens only panes that are still missing */}
    {isDesktopApp && onboardingStep >= 0 && needsDesktopPermissionOnboarding && (
      <div className="onboarding-overlay">
        <div className="onboarding-card">
          <h2 className="onboarding-title">Allow Aria to use your screen</h2>
          <p className="onboarding-text">
            macOS reports Screen Recording is <strong>off</strong> for this app. Click <strong>Allow</strong> to open System Settings, turn on <strong>Aria</strong> under <strong>Screen Recording</strong>, then return here and click <strong>Done</strong>. For click/scroll in Assist, also enable <strong>Accessibility</strong> (only if you use that feature).
          </p>
          <div className="onboarding-actions">
            <button
              type="button"
              className="onboarding-btn onboarding-btn--primary"
              onClick={() => {
                let delayMs = 0;
                if (!micAccessGranted) {
                  window.electronAPI?.requestMicrophoneAccess?.().catch(() => {});
                  setTimeout(() => { window.electronAPI?.openMicrophoneSettings?.(); }, 220);
                  delayMs = 520;
                }
                // Open Screen Recording settings if denied OR if capture failed but
                // status is still 'not-determined' (macOS 26 TCC race condition).
                // needsDesktopPermissionOnboarding already requires screenPermissionPromptNeeded,
                // so inside this overlay we know capture failed — always open settings when not granted.
                if (screenExplicitlyDenied || !screenGranted) {
                  setTimeout(() => { window.electronAPI?.openScreenRecordingSettings?.(); }, delayMs);
                  delayMs += 400;
                }
                if (!accessibilityGranted) {
                  setTimeout(() => { window.electronAPI?.openAccessibilitySettings?.(); }, delayMs);
                }
              }}
            >
              Allow Required Access
            </button>
            <button
              type="button"
              className="onboarding-btn onboarding-btn--secondary"
              onClick={() => {
                setPersistentUiFlag(ONBOARDING_DONE_KEY, true);
                setPersistentUiFlag(DESKTOP_UIFLAG_ONBOARDING_DONE, true);
                setScreenPermissionPromptNeeded(false);
                setOnboardingStep(-1);
              }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    )}
    {isDesktopApp && showFirstRunExamplesModal && (
      <div
        className="onboarding-overlay onboarding-overlay--first-run-examples"
        role="dialog"
        aria-modal="true"
        aria-labelledby="first-run-examples-title"
      >
        <div className="onboarding-card onboarding-card--first-run-examples">
          <h2 id="first-run-examples-title" className="onboarding-title">What Aria can help with</h2>
          <p className="onboarding-text">
            Here are a few common ways people use Aria. Pick one to drop into the box, or say &quot;Aria&quot; anytime you need help.
          </p>
          <ul className="ask-help-ideas-list first-run-examples-list">
            {ASK_HELP_EXAMPLE_PROMPTS.map((ex) => (
              <li key={ex.title} className="ask-help-ideas-item">
                <div className="ask-help-ideas-item-text">
                  <span className="ask-help-ideas-item-title">{ex.title}</span>
                  <span className="ask-help-ideas-item-desc">{ex.description}</span>
                </div>
                <button
                  type="button"
                  className="ask-help-ideas-use-btn"
                  onClick={() => applyHelpIdeaPrompt(ex.prompt)}
                >
                  Use this idea
                </button>
              </li>
            ))}
          </ul>
          <div className="onboarding-actions">
            <button type="button" className="onboarding-btn onboarding-btn--primary" onClick={completeDesktopFirstRunUx}>
              Continue
            </button>
          </div>
        </div>
      </div>
    )}
    <div
      className={`app app--section-${effectiveSection} ${backendConnected === false ? 'app--banner-visible' : ''} ${assistCompactLayout ? 'app--assist-compact' : ''}`}
    >
      {/* Live read-along subtitle — turns on whenever Aria is speaking. */}
      <AriaSubtitle messages={messages} ariaSpeaking={ariaSpeaking} audioRef={activeReplyAudioRef} />
      {/* Orb removed per owner request (2026-06-13): the full-screen Ask-page
          orb was the "old layout" — purely decorative dead space. The Ask page
          now lands clean (wordmark + input), and text mode's glass sheet simply
          blurs the dark app background. To restore, re-add an <AriaOrb fill />
          inside a `.aria-universe` wrapper gated on effectiveSection === 'ask'. */}
      {/* Live chat bubble overlay removed from main Ask page — full transcript still in Conversation History */}
      {backendConnected === false && (
        <div className="app-backend-banner" role="alert">
          <span className="app-backend-banner-dot" aria-hidden />
          Cannot reach server. Run <code>cd HowTo-Backend && npm start</code>, then open <strong>http://localhost:3001</strong> and click Retry — <strong>no need to refresh the page</strong> (only reload the browser if you're still offline after the server is up).
          <button
            type="button"
            className="app-backend-banner-retry"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      )}
      {isDesktopApp && desktopUpdateInfo && !updateDismissed && (
        <div className="app-update-toast" role="status">
          <span className="app-update-toast-dot" aria-hidden />
          <span className="app-update-toast-text">
            {updateDownloadReady
              ? `Aria v${desktopUpdateInfo.version} is ready to install`
              : `New update available — v${desktopUpdateInfo.version}`}
          </span>
          <button
            type="button"
            className="app-update-toast-later"
            onClick={() => setUpdateDismissed(true)}
          >
            Later
          </button>
          <button
            type="button"
            className="app-update-toast-install"
            disabled={updateInstalling}
            onClick={installUpdateFromSidebar}
          >
            {updateProgressStage === 'installing' ? 'Installing…' : updateInstalling ? 'Downloading…' : updateDownloadReady ? 'Restart now' : 'Install Now'}
          </button>
        </div>
      )}
      {/* Global decorative blurred grey orbs floating behind ALL content.
          position: fixed inset: 0 z-index: 0 — sits BENEATH every page,
          every workbench, every modal. aria-hidden, pointer-events: none. */}
      <div className="aria-bg-blobs" aria-hidden="true">
        <span className="aria-bg-blob aria-bg-blob--1" />
        <span className="aria-bg-blob aria-bg-blob--2" />
        <span className="aria-bg-blob aria-bg-blob--3" />
        <span className="aria-bg-blob aria-bg-blob--4" />
        <span className="aria-bg-blob aria-bg-blob--5" />
        <span className="aria-bg-blob aria-bg-blob--6" />
        <span className="aria-bg-blob aria-bg-blob--7" />
      </div>
      {/* Content always clickable: banner pushes down, never overlays */}
      <div className="app-content" style={{ pointerEvents: 'auto' }} onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
      {dragOver && (
        <div className="app-drop-overlay">
          <div className="app-drop-overlay-inner">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <span>Drop images here</span>
          </div>
        </div>
      )}
      <div className="app-content-inner">
      {/* Frameless title bar — spans the top edge; window controls + chrome live
          on it (drag region), and all app content sits below it. */}
      <div className="app-titlebar" aria-hidden="true" />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="input-file-hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={onFileInputChange}
      />
      {/* Hamburger trigger — pinned to the top-left corner. Click slides the
          sidebar in as an overlay drawer. Only thing visible when sidebar is
          closed. */}
      {!leftSidebarOpen && (
        <button
          type="button"
          className="aria-hamburger"
          onClick={() => setLeftSidebarOpen(true)}
          aria-label="Open menu"
          title="Menu"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="4" y1="7" x2="20" y2="7"/>
            <line x1="4" y1="12" x2="20" y2="12"/>
            <line x1="4" y1="17" x2="20" y2="17"/>
          </svg>
        </button>
      )}
      {/* UPDATE READY pill - one click reloads onto the newest build. Only on the
          desktop app; the website is always current by definition. */}
      {/* BUILD STATUS CHIP - always visible on the desktop app (8/22). Green =
          this window runs the newest bundle AND the server is on its newest
          code. Amber = a newer UI build exists (button restarts). Red = the
          server is unreachable or is serving code older than its files. */}
      {typeof window !== 'undefined' && window.electronAPI && buildStatus.checkedAt > 0 && (() => {
        // TWO STATES, NOT FOUR (Braeden 8/25: "it needs to say Up to Date, or
        // needs restart, next to the green button, red button when needs
        // restart for latest models and backends, so when you click it it
        // updates and restarts the app").
        //
        // It used to have four: current / behind / stale / down - amber for a
        // stale UI, red for a stale server, and the Update button ONLY on
        // 'behind'. So a backend running old code, or a brain that had trained
        // and never been served, showed a red dot he could not act on. R10, R11
        // and R12 all sat undeployed for days behind exactly that.
        //
        // Now: green Up to date, or red Needs restart with a button - and the
        // red covers all three reasons a restart would get him something he
        // does not have: a newer UI bundle, a backend behind its own code, or a
        // trained model that is not being served.
        const sv = buildStatus.server || {};
        const serverBad = !sv.ok;
        const modelsBehind = !!(sv.models && sv.models.behind);
        const serverStale = !!sv.stale;
        // 'hands' = shimmer-desktop/agent.js and friends, which load once at
        // app start. Named separately because the fix is different: a UI change
        // needs a reload, stale hands need a real relaunch.
        const handsBehind = !!buildStatus.handsBehind;
        const needsRestart = buildStatus.uiBehind || serverStale || modelsBehind || handsBehind;
        const state = serverBad ? 'down' : needsRestart ? 'restart' : 'current';
        const built = buildStatus.builtAt ? new Date(buildStatus.builtAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
        // Say WHICH thing is behind - "needs restart" with no reason is a chip
        // he has to come and ask me about.
        const why = [
          buildStatus.uiBehind ? 'app' : null,
          serverStale ? 'backend' : null,
          modelsBehind ? 'models' : null,
          handsBehind ? 'hands' : null,
        ].filter(Boolean).join(' + ');
        const label = state === 'down' ? 'Server unreachable'
          : state === 'restart' ? `Needs restart · ${why}`
          : `Up to date${built ? ` · ${built}` : ''}`;
        const tip = state === 'down'
          ? 'The backend is not answering on this machine.'
          : [
              `UI ${buildStatus.running || '?'}${buildStatus.latest ? ` / latest ${buildStatus.latest}` : ''}`,
              sv.bootedAt ? `server booted ${new Date(sv.bootedAt).toLocaleTimeString()}` : '',
              serverStale && sv.latestCodeFile ? `backend behind ${sv.latestCodeFile}` : '',
              modelsBehind && sv.models ? `${sv.models.newest} trained ${new Date(sv.models.trainedAt).toLocaleString()} — not being served` : '',
              handsBehind && sv.desktopHands ? `assist code (${sv.desktopHands.file}) changed after this app started — restart to pick it up` : '',
            ].filter(Boolean).join(' · ');
        return (
          <div className={`aria-build-chip aria-build-chip--${state}`} title={tip}>
            <span className="aria-build-chip__dot" />
            <span className="aria-build-chip__label">{label}</span>
            {state === 'restart' && (
              <button type="button" className="aria-build-chip__btn" onClick={restartApp}>Update &amp; restart</button>
            )}
          </div>
        );
      })()}
      {updateReady && (
        <button
          type="button"
          className="aria-update-pill"
          onClick={restartApp}
          title={`Built ${updateReady.builtAt ? new Date(updateReady.builtAt).toLocaleTimeString() : ''}`}
        >
          Update ready — restart
        </button>
      )}
      {/* BACK (Braeden 8/13: "add a back button so if they click into anything
          its easy to click a back button"). The trail and goBack() already
          existed above — navDepth was being computed and never rendered, so
          there was no way out of a page but the drawer. Sits beside the
          hamburger, and only appears once you have actually gone into
          something, so the home screen never shows a dead arrow. */}
      {navDepth > 0 && !leftSidebarOpen && (
        <button
          type="button"
          className="aria-back-btn"
          onClick={goBack}
          aria-label="Go back"
          title="Back"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}
      {/* Floating back button removed per owner request 2026-06-25. Navigate back
          via the HowTo wordmark (resets to home) or the dashboard menu. */}
      {/* No backdrop — user can still text Aria while the drawer is open. The
          chevron inside the drawer header closes it. */}
      <aside className={`app-sidebar-left ${!leftSidebarOpen ? 'app-sidebar-left--closed' : ''} ${ceoState?.active ? 'app-sidebar-left--ceo' : ''}`}>
        <div
          className="app-sidebar-left-header"
          onClick={() => setLeftSidebarOpen(false)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setLeftSidebarOpen(false); }}
          style={{ cursor: 'pointer' }}
        >
          {leftSidebarOpen && (
            ceoState?.active && ceoState.businessName ? (
              // CEO mode — show the business name + Aria orb-dot in place of "Dashboard"
              <button
                type="button"
                className="app-sidebar-business app-sidebar-business--btn"
                title={`${ceoState.businessName} — close menu`}
                onClick={() => setLeftSidebarOpen(false)}
              >
                <span className="app-sidebar-orb" aria-hidden />
                <div className="app-sidebar-business-text">
                  <div className="app-sidebar-business-name">{ceoState.businessName}</div>
                  {ceoState.businessType && (
                    <div className="app-sidebar-business-sub">{ceoState.businessType}</div>
                  )}
                </div>
              </button>
            ) : (
              // Dashboard header is now the entry point to the project canvas.
              // Click → see all your projects → click one → project + Aria chat.
              <button
                type="button"
                className="app-sidebar-left-title app-sidebar-left-title--btn"
                onClick={() => setLeftSidebarOpen(false)}
                title="Close menu"
              >Dashboard</button>
            )
          )}
          <button
            type="button"
            className="sidebar-toggle"
            onClick={() => setLeftSidebarOpen((o) => !o)}
            aria-label={leftSidebarOpen ? 'Close left sidebar' : 'Open left sidebar'}
            title={leftSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          >
            {leftSidebarOpen ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            )}
          </button>
        </div>
        {/* HOME SCREEN (Braeden 8/13): "add somethign on the very top that says
            Home screen ... so when they click on it it brings them back to the
            home screen." Sits above everything else in the drawer. */}
        {/* "Home screen" removed 8/22 — "get rid of everything else, only thing i
            want to see is the title of the project". It was navigation, not a
            project. Every other route still works by hash. */}
        {/* THE ICONS LIVE HERE NOW (Braeden 8/13): profile at the very bottom
            of the dashboard, day/night switch directly above it. Both used to
            float top-right; the floating copies are gone so there's one of
            each. */}
        {/* THE FOOTER IS ONE ROW NOW (Braeden 8/22): "get rid of the night and
            day mode, also add a account seciotn so when you click on it, its
            like a setting pop up almost."

            The Night/Day pill left the dashboard — the switch itself moved
            INTO the account panel rather than being deleted, so the capability
            survives where settings belong. The row used to be a 28px avatar
            next to a CSS ::after label, so only the avatar was clickable and
            the word "Account" was dead pixels. It is one full-width button. */}
        {leftSidebarOpen && (
          <div className="app-dash-footer">
            <button
              type="button"
              className="app-dash-account-btn"
              onClick={() => setAccountPanelOpen(true)}
              aria-haspopup="dialog"
            >
              <span className="app-dash-account-btn__icon" aria-hidden="true">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
              </span>
              <span className="app-dash-account-btn__label">Account</span>
              <span className="app-dash-account-btn__chev" aria-hidden="true">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
              </span>
            </button>
          </div>
        )}
        <div className="app-sidebar-left-inner">
          {/* THE DASHBOARD IS A LIST OF PROJECTS (Braeden 8/22) —
              "the only thing on dashbaord is going to be a list of projects,
               with account at the bottom, get rid of everything else, only
               thing i want to see is the title of the project and when you
               click on it, it brings you to the workstation of that project."

              Gone: the Business/Personal switch, per-project logos, tags, edit
              buttons, and the hover preview panel that rendered a mock agent
              board. What is left is the title, and clicking it opens that
              project. I first cut the HEADER DROPDOWN by mistake — that is a
              menu, this is the dashboard. Account stays at the bottom, in the
              footer above. */}
          <div className="dash-list">
            {(userProjects.length ? userProjects : DEFAULT_PROJECTS).map((proj, idx) => (
              <button
                key={proj.id || proj.name || idx}
                type="button"
                className="dash-project"
                title={proj.name}
                onClick={() => {
                  setSelectedProject(proj);
                  window.location.hash = 'company';
                  setDashboardSection('company');
                  setLeftSidebarOpen(false);
                }}
              >{proj.name}</button>
            ))}
            {!(userProjects.length ? userProjects : DEFAULT_PROJECTS).length && (
              <div className="dash-empty">No projects yet.</div>
            )}
            <button
              type="button"
              className="dash-project dash-project--new"
              onClick={() => { setPersonalProjName(''); setPersonalProjOpen(true); }}
            >+ New project</button>
          </div>
        </div>
      </aside>

      {/* ACCOUNT PANEL (Braeden 8/22) — "add a account seciotn so when you click
          on it, its like a setting pop up almost." Everything the old 4-item
          avatar dropdown reached, plus the Night/Day switch that used to sit
          loose on the dashboard, in one sheet. Backdrop click and Escape close
          it; the drawer stays open behind so closing returns you to the list. */}
      {accountPanelOpen && (
        <div
          className="acct-panel__backdrop"
          onClick={() => setAccountPanelOpen(false)}
          role="presentation"
        >
          <div
            className="acct-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Account"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="acct-panel__head">
              <div className="acct-panel__title">Account</div>
              <button
                type="button"
                className="acct-panel__close"
                onClick={() => setAccountPanelOpen(false)}
                aria-label="Close account panel"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="acct-panel__who">
              <div className="acct-panel__avatar" aria-hidden="true">
                {(accountIdentity.name || accountIdentity.email || '?').trim().charAt(0).toUpperCase()}
              </div>
              <div className="acct-panel__whotext">
                <div className="acct-panel__name">{accountIdentity.name || 'Not signed in'}</div>
                <div className="acct-panel__email">{accountIdentity.email || 'Sign in to sync across devices'}</div>
              </div>
            </div>

            {/* The theme switch lives here now, not on the dashboard. */}
            <div className="acct-panel__section">
              <div className="acct-panel__label">Appearance</div>
              <div className="acct-panel__theme" role="group" aria-label="App theme">
                <button
                  type="button"
                  className={`acct-theme-opt${appTheme === 'night' ? ' on' : ''}`}
                  onClick={() => switchTheme('night')}
                  aria-pressed={appTheme === 'night'}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3a9 9 0 1 0 9 9c0-.34-.02-.68-.06-1.01a5.5 5.5 0 0 1-7.93-7.93C12.68 3.02 12.34 3 12 3z" /></svg>
                  Night
                </button>
                <button
                  type="button"
                  className={`acct-theme-opt${appTheme === 'day' ? ' on' : ''}`}
                  onClick={() => switchTheme('day')}
                  aria-pressed={appTheme === 'day'}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.4 1.4M17.6 17.6L19 19M5 19l1.4-1.4M17.6 6.4L19 5" /></svg>
                  Day
                </button>
              </div>
            </div>

            {/* ── THREE THINGS (Braeden 9/4) ──────────────────────────────
                "i want to make the system so simple, there is sign out button,
                 subscription button and day and night mode"

                So this panel is those three and nothing else. Settings and
                Conversation history left from here — they were NOT deleted,
                both still open from the dashboard menu (top-left), which is
                where a list of pages belongs. "Plan & billing" is called
                Subscription because that is the word he uses for it.

                The desktop restart row stays only while an update is actually
                waiting: it is the one way he gets a new build, and hiding it
                permanently would strand that. With nothing to install it is
                clutter, so it is gone until there is. */}
            <div className="acct-panel__section">
              {/* Opens the pay sheet DIRECTLY (8/22). Routing through
                  #subscription re-mounted the component that listens for
                  'aria-open-checkout' while the event was in flight, so the
                  row did nothing - measured in the served bundle. */}
              <button type="button" className="acct-row" onClick={() => { setAccountPanelOpen(false); setLeftSidebarOpen(false); setTimeout(() => { try { window.dispatchEvent(new CustomEvent('aria-open-checkout', { detail: { plan: 'subscription' } })); } catch (_) {} }, 60); }}>
                <span>Subscription</span><span className="acct-row__chev">›</span>
              </button>
              {typeof window !== 'undefined' && window.electronAPI && updateReady && (
                <button type="button" className="acct-row" onClick={restartApp}>
                  <span>Update ready — restart now</span><span className="acct-row__chev">›</span>
                </button>
              )}
            </div>

            <div className="acct-panel__section">
                {isDesktopApp && <button type="button" className="acct-row" onClick={() => {
                  try { const key = assistPermissionKey(); if (key) localStorage.removeItem(key); } catch (_) {}
                  refreshAssistPolicy((n) => n + 1);
                  window.electronAPI?.assistConsent?.(false);
                }}>
                  Assist: {hasRememberedAssist() ? 'Always allow — click to reset' : 'Ask every time'}
                </button>}
              {accountIdentity.hasIdentity ? (

                <button
                  type="button"
                  className="acct-row acct-row--danger"
                  onClick={() => {
                    try {
                      // Everything that says WHO this device is. `aria_projects`
                      // is deliberately NOT here — that is his work, not his
                      // identity, and signing out must never delete it.
                      ['aria_session_token', 'aria_session_email', 'aria_user_email', 'aria_user_first',
                       'aria_user_id', 'aria_session_messages', 'aria_session_history',
                       'aria_user_plan', 'aria_intent_subscribe', 'howto_owner_id', 'howto_user_id',
                      ].forEach((k) => localStorage.removeItem(k));
                    } catch (_) {}
                    setAccountPanelOpen(false);
                    try { window.location.reload(); } catch (_) {}
                  }}
                >
                  <span>Sign out</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="acct-row acct-row--primary"
                  onClick={() => {
                    setAccountPanelOpen(false);
                    try { window.dispatchEvent(new CustomEvent('aria-open-signin')); } catch (_) {}
                  }}
                >
                  <span>Sign in</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ASSIST PERMISSION (Braeden 8/22) — "can i go into assist mode, with a
          pop up, that says allow, so aria takes control of the computer and
          does what she needs to ... and walks them through the steps, no
          matter where they are."

          Deliberately a POPUP and not a spoken confirm: the spoken one was
          removed because people never said the second yes and got stranded
          mid-task. One click cannot be misheard. Allow is not styled as the
          scary option — she asked for it, and the whole point is that she can
          actually do the work — but "Not now" is a real answer that leaves the
          link unopened and the screen untouched. */}
      {assistAsk && (
        <div className="assist-ask__backdrop" role="presentation">
          <div className="assist-ask" role="alertdialog" aria-modal="true" aria-labelledby="assist-ask-title">
            <div className="assist-ask__icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
            </div>
            <div id="assist-ask-title" className="assist-ask__title">Can I look at your screen?</div>
            <div className="assist-ask__body">
              Allow lets Aria view your screen and control your computer for your task. Always allow remembers this for your account on this device. You can stop Assist or reset permission in the account menu.
            </div>
            {assistAsk.steps.length > 0 && (
              <ol className="assist-ask__steps">
                {assistAsk.steps.slice(0, 4).map((st, i) => <li key={i}>{st}</li>)}
              </ol>
            )}
            <div className="assist-ask__actions">
              <button
                type="button"
                className="assist-ask__btn"
                onClick={() => {
                  setAssistAsk(null);
                  setConversationHistory((h) => [...h, { role: 'user', content: 'Not now. Continue without screen access.' }]);
                }}
              >Deny</button>
              <button
                type="button"
                className="assist-ask__btn assist-ask__btn--allow"
                autoFocus
                onClick={() => startApprovedAssist(assistAsk)}
              >Allow this time</button>
              {isDesktopApp && <button
                type="button"
                className="assist-ask__btn assist-ask__btn--allow"
                onClick={async () => {
                  try { const key = assistPermissionKey(); if (key) localStorage.setItem(key, 'allow'); } catch (_) {}
                  refreshAssistPolicy((n) => n + 1);
                  await startApprovedAssist(assistAsk);
                }}
              >Always allow</button>}
            </div>
          </div>
        </div>
      )}

      {newProjOpen && (
        <NewProjectModal
          onClose={() => setNewProjOpen(false)}
          onCreate={(p) => { setUserProjects((ps) => [p, ...ps]); setNewProjOpen(false); }}
        />
      )}

      {personalProjOpen && (
        <div className="account-modal__backdrop" onClick={() => setPersonalProjOpen(false)}>
          <div className="personal-proj-modal" onClick={(e) => e.stopPropagation()}>
            <h3>New project</h3>
            <p>Anything you are working on — a deal, an ad, a build, a business. It gets its own workstation.</p>
            <input
              autoFocus
              type="text"
              value={personalProjName}
              onChange={(e) => setPersonalProjName(e.target.value)}
              placeholder="What's the project?"
              maxLength={60}
              onKeyDown={(e) => { if (e.key === 'Enter' && personalProjName.trim()) {
                // ── DURING THE INTRO, THIS BOX IS HOW A TEXT USER ANSWERS HER (9/2) ──
                // She just asked "what's something you've been wanting to do?"
                // and this modal's backdrop covers the Ask bar. So what they type
                // here IS their answer: it goes out as a turn, she names it, and
                // runTitleShow types the real title back into this same box.
                if (tourBeat === 'title' || tourBeat === 'confirm') { const g = personalProjName.trim(); setPersonalProjName(''); try { sendMessageWithText(g, { inputMode: 'text' }); } catch (_) {} return; }
                const _p = { id: 'p' + Date.now().toString(36), name: personalProjName.trim(), status: 'Active', createdAt: new Date().toISOString() }; setUserProjects((ps) => [_p, ...ps]); setPersonalProjOpen(false); setSelectedProject(_p); setLeftSidebarOpen(false); window.location.hash = 'company'; setDashboardSection('company'); } }}
            />
            <div className="personal-proj-actions">
              <button type="button" className="personal-proj-cancel" onClick={() => setPersonalProjOpen(false)}>Cancel</button>
              <button
                type="button"
                className="personal-proj-add"
                disabled={!personalProjName.trim()}
                onClick={() => {
                  // Same as Enter above: during the intro this is their answer to her, not a bare create.
                  if ((tourBeat === 'title' || tourBeat === 'confirm') && personalProjName.trim()) { const g = personalProjName.trim(); setPersonalProjName(''); try { sendMessageWithText(g, { inputMode: 'text' }); } catch (_) {} return; }
                  const _p = { id: 'p' + Date.now().toString(36), name: personalProjName.trim(), status: 'Active', createdAt: new Date().toISOString() }; setUserProjects((ps) => [_p, ...ps]); setPersonalProjOpen(false); setSelectedProject(_p); setLeftSidebarOpen(false); window.location.hash = 'company'; setDashboardSection('company'); }}
              >Create project</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit project name — popup box in front to type a new name */}
      {editProj && (
        <div className="proj-modal-overlay" onClick={() => setEditProj(null)}>
          <div className="proj-modal" onClick={(e) => e.stopPropagation()}>
            <div className="proj-modal__title">Rename project</div>
            <input
              className="proj-modal__input"
              autoFocus
              value={editProj.name}
              placeholder="Project name"
              onChange={(e) => setEditProj((s) => ({ ...s, name: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveEditProject(); } else if (e.key === 'Escape') { setEditProj(null); } }}
            />
            <div className="proj-modal__actions">
              <button type="button" className="proj-modal__btn" onClick={() => setEditProj(null)}>Cancel</button>
              <button type="button" className="proj-modal__btn proj-modal__btn--primary" onClick={saveEditProject}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete project — "are you sure" confirm */}
      {deleteProj && (
        <div className="proj-modal-overlay" onClick={() => setDeleteProj(null)}>
          <div className="proj-modal" onClick={(e) => e.stopPropagation()}>
            <div className="proj-modal__title">Delete project?</div>
            <div className="proj-modal__text">Are you sure you want to delete <b>“{deleteProj.name}”</b>? This can't be undone.</div>
            <div className="proj-modal__actions">
              <button type="button" className="proj-modal__btn" onClick={() => setDeleteProj(null)}>Cancel</button>
              <button type="button" className="proj-modal__btn proj-modal__btn--danger" onClick={confirmDeleteProject}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* "Business +" — Aria asks what the business is, then generates the right
          agents + dashboard for that niche (Jobs → Appointments / Users / Orders…). */}
      <SetupHelpBox
        ref={bizBoxRef}
        pinTopRight={bizDock}
        open={bizPlusOpen}
        onClose={() => {
          if (bizPlanBusy) return;
          // Abandoning mid-interview resets the pipeline — reopening starts clean.
          bizPipelineRef.current = null;
          setBizReady(false);
          setBizPlusOpen(false);
        }}
        greeting={'I’ll look up your business and build your setup. What’s it called?'}
        agentTitle="your business"
        onUserMessage={(t) => { if (!bizPipelineRef.current) setBizText(t); }}
        inputPlaceholder="Your business name…"
        onAssist={onScreenShareClick}
        localResponder={bizLocalResponder}
        // The ✦ button doesn't EXIST until Aria has what she needs — she asks
        // the basics first (what you do, the name, how long, how many people),
        // researches the business online in between, THEN offers the button.
        previewNode={bizBuild.active ? (
          <div style={{ border: '1px solid rgba(0,0,0,.10)', borderRadius: 14, padding: '11px 13px', background: 'rgba(10,99,255,.04)' }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: '#0a63ff', marginBottom: 8 }}>
              {bizBuild.done ? 'Your setup — built' : 'Building your setup…'}
            </div>
            {bizBuild.steps.map((s) => (
              <div key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5, lineHeight: 1.45, marginBottom: 4, opacity: s.status === 'wait' ? 0.5 : 1 }}>
                <span style={{ flex: '0 0 auto', width: 14, color: s.status === 'done' ? '#12b76a' : s.status === 'fail' ? '#e5484d' : '#0a63ff' }}>
                  {s.status === 'done' ? '✓' : s.status === 'fail' ? '×' : '•'}
                </span>
                <span>{s.label}</span>
              </div>
            ))}
            {bizBuild.done && (
              <button type="button"
                onClick={() => { setBizPlusOpen(false); setBizDock(false); bizPipelineRef.current = null; setBizBuild({ active: false, steps: [], done: false, plan: null }); window.location.hash = 'company'; setDashboardSection('company'); setLeftSidebarOpen(false); }}
                style={{ marginTop: 8, width: '100%', border: 0, borderRadius: 10, padding: '9px 12px', background: '#0a63ff', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                {/* It's already on screen behind the chat — this just clears
                    the chat out of the way (8/13). */}
                Done — close this
              </button>
            )}
          </div>
        ) : null}
        readyAction={{
          label: '✦ Generate',
          busy: bizPlanBusy,
          busyLabel: 'Building your setup…',
          enabled: true,
          requireMessage: false,
          onClick: buildBizPlan,
        }}
        endpoint="/api/aria/intake"
        buildBody={(text, _history, uid) => ({ businessText: text, ...(uid ? { userId: uid } : {}) })}
      />


      <div className="app-center" key={effectiveSection}>
      {/* The cursive "Aria" wordmark that used to sit top-center during chat was
          removed (owner, 2026-06-19): Aria's identity now lives on her support
          box / dock, and the main chat stays clean. */}
      {/* "Aria" hero — ALWAYS visible at the top of the screen on every page
          (ask, history, settings, subscription, image-gen, dev, etc). Big +
          centered on the empty landing state, shrinks to a small header
          everywhere else. Click = back to home. */}
      {/* Frameless window: invisible strip in the empty center of the top edge,
          so the chromeless window can still be dragged to move it (Electron only). */}
      <div className="app-drag-strip" aria-hidden="true" />
      {/* Day mode: grey shade across the top edge, fading to transparent by the
          HowTo wordmark. Inline styles so it renders regardless of CSS cascade. */}
      {appTheme === 'day' && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, height: 82,
            background: 'linear-gradient(to bottom, #b4b7c1 0%, #babdc6 60%, rgba(255,255,255,0) 100%)',
            pointerEvents: 'none', zIndex: 3,
          }}
        />
      )}
      {/* Day mode: second grey shade — starts ~halfway down, deepens to the
          bottom of the screen, behind all content (z-index 0). */}
      {appTheme === 'day' && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed', inset: 0,
            background: 'linear-gradient(to bottom, rgba(150,152,162,0) 48%, rgba(150,152,162,0.16) 72%, rgba(150,152,162,0.46) 100%)',
            pointerEvents: 'none', zIndex: 0,
          }}
        />
      )}
      {/* Night/Day toggle now uses a View Transitions reveal-wipe (see App.css
          ::view-transition-old). This legacy node is kept hidden for compatibility. */}
      <div className="theme-flash" aria-hidden="true" />
      <button
        type="button"
        className={`aria-hero${(messages.length > 0 || effectiveSection !== 'ask') ? ' aria-hero--small' : ''}${effectiveSection === 'settings' ? ' aria-hero--corner' : ''}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Wherever the user is — drawer open, settings, history, image gen,
          // dev console — this is a hard reset back to the home/landing state.
          setMessages([]);
          setConversationHistory([]);
          setInput('');
          setLastInputMode('voice');
          lastTextSendRef.current = 0;
          lastAriaTextRef.current = 0;
          setLeftSidebarOpen(false);                  // close drawer if open
          try { setShowConversationOverlay(false); } catch (_) {}
          try { window.location.hash = 'ask'; } catch (_) {}
          setDashboardSection('ask');
          setView('ask');
        }}
        aria-label="HowTo — back to home"
      >
        HowTo
      </button>
      {/* Global day/night theme switch — sits just left of the HowTo wordmark, themes the whole app */}
      {/* The switch itself now lives at the bottom of the dashboard (8/13).
          This node stays ONLY to keep its <style> block — which defines the
          .app-theme-opt button styling the footer copy uses — and is hidden. */}
      {!assistCompactLayout && (
        <div className="app-theme-switch app-theme-switch--retired" role="group" aria-label="App theme" aria-hidden="true">
          <style>{`
            .app-theme-switch{position:fixed;top:11px;right:162px;z-index:211;-webkit-app-region:no-drag;app-region:no-drag;display:inline-flex;padding:3px;background:rgba(255,255,255,0.10);border:1px solid rgba(255,255,255,0.22);border-radius:999px;backdrop-filter:blur(14px) saturate(160%);-webkit-backdrop-filter:blur(14px) saturate(160%);box-shadow:0 2px 10px rgba(0,0,0,0.35);}
            html.app--web .app-theme-switch{right:64px;}
            .app-theme-opt{display:inline-flex;align-items:center;gap:6px;padding:6px 13px;background:transparent;border:none;border-radius:999px;font-size:12px;font-weight:700;color:rgba(255,255,255,0.62);letter-spacing:0.02em;cursor:pointer;font-family:inherit;transition:color .18s,background .18s;}
            .app-theme-opt svg{width:13px;height:13px;}
            .app-theme-opt:hover{color:#fff;}
            .app-theme-opt.on{background:rgba(255,255,255,0.92);color:#111;box-shadow:0 1px 3px rgba(0,0,0,0.3);}
            body.theme-light .app-theme-switch{background:rgba(15,22,32,0.42);border:1px solid rgba(255,255,255,0.18);box-shadow:0 2px 10px rgba(0,0,0,0.22);backdrop-filter:blur(14px) saturate(160%);-webkit-backdrop-filter:blur(14px) saturate(160%);}
            body.theme-light .app-theme-opt{color:rgba(255,255,255,0.66);}
            body.theme-light .app-theme-opt:hover{color:#fff;}
            body.theme-light .app-theme-opt.on{background:rgba(255,255,255,0.92);color:#111;box-shadow:0 1px 3px rgba(0,0,0,0.3);}
            /* Narrow windows: STAY far right (never mid-header — parking at
               left:104px overlapped the centered HowTo wordmark) and compact
               to icon-only so the pill fits beside the profile icon. */
            @media (max-width:760px){
              .app-theme-switch{right:170px;}
              html.app--web .app-theme-switch{right:64px;}
              .app-theme-opt{padding:6px 9px;font-size:0;gap:0;}
              .app-theme-opt svg{width:15px;height:15px;}
            }
          `}</style>
          <button type="button" className={`app-theme-opt${appTheme === 'night' ? ' on' : ''}`} onClick={() => switchTheme('night')} aria-pressed={appTheme === 'night'}>
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3a9 9 0 1 0 9 9c0-.34-.02-.68-.06-1.01a5.5 5.5 0 0 1-7.93-7.93C12.68 3.02 12.34 3 12 3z" /></svg>
            Night
          </button>
          <button type="button" className={`app-theme-opt${appTheme === 'day' ? ' on' : ''}`} onClick={() => switchTheme('day')} aria-pressed={appTheme === 'day'}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.4 1.4M17.6 17.6L19 19M5 19l1.4-1.4M17.6 6.4L19 5" /></svg>
            Day
          </button>
        </div>
      )}
      {/* Profile icon — always pinned top-right, on every section (Ask,
          Explore, ImageGen, Build, every workbench). The in-header
          AccountMenu inside the Ask branch was nuked so we don't double
          up. Lives next to the small HowTo wordmark; HowTo at right: 84px,
          profile at right: 24px → profile sits to the right of the
          wordmark, always. */}
      {/* Account-ready gate: blocks HowTo until email verified + (paid) payment. */}
      <AuthGate />
      {/* The sign-in modal, headless: the Account panel's "Sign in" row fires
          'aria-open-signin' and this is what answers it (8/22). */}
      <AccountMenu headless />
      {/* Global payment popup — the ONLY subscription surface; opens over any screen. */}
      <GlobalCheckout />
      {/* What's left, top-right, on every section (Braeden 9/6). Mounted HERE,
          beside AuthGate/GlobalCheckout, rather than inside the Ask header —
          the header's right cluster renders null outside Assist and only the
          Ask branch draws it at all, so a header mount would vanish on Explore,
          Shop and every workbench. Hidden in Assist compact, where the ring is
          the only thing that may be on screen. Its "become a member" button is
          the same 'aria-open-checkout' event GlobalCheckout above listens for,
          so there is still exactly ONE payment surface. */}
      <UsageMeter hidden={assistCompactLayout} ballLive={!orbCompact} busy={loading} guestRemaining={creditsRemaining >= 0 ? creditsRemaining : null} />
      {/* Only renders while ?firstrun=new is in effect — a real visitor never
          sees it. Restart replays the onboarding, Exit gives him his account
          back. */}
      <FirstRunPreviewBadge />
      {/* Profile moved into the dashboard footer (8/13) — it renders there, so
          the floating copy is gone rather than duplicated. When the drawer is
          CLOSED there's still a way in: the hamburger opens it. */}
      {/* Frameless window controls — minimize / maximize / close (desktop only) */}
      {typeof window !== 'undefined' && window.electronAPI && !assistCompactLayout && (
        <div className="app-win-controls" aria-label="Window controls">
          <button type="button" className="app-win-btn" title="Minimize" aria-label="Minimize" onClick={() => { try { window.electronAPI.windowMinimize(); } catch (_) {} }}>
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><line x1="2.5" y1="6" x2="9.5" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
          <button type="button" className="app-win-btn" title="Maximize" aria-label="Maximize" onClick={() => { try { window.electronAPI.windowToggleMaximize(); } catch (_) {} }}>
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><rect x="2.5" y="2.5" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5"/></svg>
          </button>
          <button type="button" className="app-win-btn app-win-btn--close" title="Close" aria-label="Close" onClick={() => { try { window.electronAPI.windowClose(); } catch (_) {} }}>
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><line x1="3" y1="3" x2="9" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><line x1="9" y1="3" x2="3" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
        </div>
      )}
      {/* Screen-share indicator — honest "Aria can see your screen" signal while
          Assist runs. No "Go to Assist" button: the Assist page is gone (owner
          rule 2026-07-15), so the only action is to stop sharing. */}
      {screenStream && (
        <div className="global-screen-share-indicator" role="status" aria-live="polite">
          <span className="global-screen-share-dot" aria-hidden />
          <span className="global-screen-share-label">Aria can see your screen</span>
          <button
            type="button"
            className="global-screen-share-btn global-screen-share-btn--stop"
            onClick={() => {
              if (screenStream) {
                screenStream.getTracks().forEach((t) => t.stop());
                setScreenStream(null);
              }
              setScreenShareError('');
            }}
          >
            Stop sharing
          </button>
        </div>
      )}
      {effectiveSection === 'home' ? (
        <div className="aria-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
          <HomeScreen onNavigate={(s) => { try { window.location.hash = s; } catch (_) {} setDashboardSection(s); }} />
        </div>
      ) : effectiveSection === 'company' ? (
        /* full-bleed: cancel .app-center's 16px gutters so the CEO photo banner
           touches both screen edges (width comes from flex-stretch, not 100%). */
        <div className="aria-scroll full-bleed" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          <CompanyView project={selectedProject} businessName={ceoState?.businessName} theme={appTheme} onNavigate={(s) => { try { window.location.hash = s; } catch (_) {} setDashboardSection(s); }} />
        </div>
      ) : effectiveSection === 'books' ? (
        /* BOOKS = THE RECORD, AND ONLY THE RECORD (8/13). It used to list the
           automations; that is the machinery, so it moved to Systems below. */
        <div className="aria-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
          <BooksView theme={appTheme} onNavigate={(s) => { try { window.location.hash = s; } catch (_) {} setDashboardSection(s); }} />
        </div>
      ) : effectiveSection === 'system' ? (
        /* SYSTEMS = the machinery: every automation Aria runs and whether it is
           on. Was an overlay off the company page; it is a page like Books now
           so the two read as the pair he asked for. */
        <div className="aria-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
          <SystemsView theme={appTheme} onNavigate={(s) => { try { window.location.hash = s; } catch (_) {} setDashboardSection(s); }} />
        </div>
      ) : effectiveSection === 'leads' ? (
        /* Braeden 8/10: this route used to mount <JobsView /> — the Leads nav
           item was showing the jobs page. Now it renders the real leads page:
           tracker on top, one tailored block per platform. */
        <div className="aria-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
          <LeadsView onNavigate={(s) => { try { window.location.hash = s; } catch (_) {} setDashboardSection(s); }} />
        </div>
      ) : effectiveSection === 'google' ? (
        /* Braeden 8/10: Google page — profile photo + live rating, then the
           manage / reviews / review-request-text tabs. */
        <div className="aria-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
          <GoogleView businessName={ceoState?.businessName} />
        </div>
      ) : effectiveSection === 'jobs' ? (
        /* Braeden 8/10: Jobs — management tracker, new-job form, then every job. */
        <div className="aria-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
          <JobsPage />
        </div>
      ) : effectiveSection === 'explore' ? (
        <ExploreView />
      ) : effectiveSection === 'shop' ? (
        // The ONLY physical workbench surface left. Aria is a helper, not
        // a builder — every other "do something" surface (image gen, website
        // builder, video maker, writer, workbench) was removed because chat
        // is the right place to guide a user to the real tool. Shopping is
        // the exception: finding-and-curating IS what a helper does.
        <WorkbenchFrame src="/wb-shopping.html" title="Shop" />
      ) : effectiveSection === 'canvas' ? (
        <CanvasSection userId={(typeof localStorage !== 'undefined' && localStorage.getItem('aria_user_id')) || 'default'} />
      ) : effectiveSection === 'trends' ? (
        <TrendsView />
      ) : effectiveSection === 'integrations' ? (
        <div className="app-integrations-section">
          <header className="header header--minimal">
            <h1 className="header-title">Integrations</h1>
          </header>
          <main className="main" style={{ padding: '24px', maxWidth: 720 }}>
            <p style={{ opacity: 0.7, fontSize: 14, marginBottom: 24 }}>
              Connect your tools so Aria can actually see your calendar, email,
              and books — not just talk about them.
            </p>
            <IntegrationsPanel userId={(typeof localStorage !== 'undefined' && localStorage.getItem('aria_user_id')) || 'default'} />
          </main>
        </div>
      ) : effectiveSection === 'dev' ? (
        <div className="app-dev-section">
          <header className="header header--minimal">
            <h1 className="header-title">Dev Console</h1>
          </header>
          <main className="main" style={{ padding: '24px', maxWidth: 1100 }}>
            <DevConsole userId={(typeof localStorage !== 'undefined' && localStorage.getItem('aria_user_id')) || 'default'} />
          </main>
        </div>
      ) : effectiveSection === 'live' ? (
        <div className="app-live-section">
          <header className="header header--minimal">
            <h1 className="header-title">Live voice monitor</h1>
          </header>
          <main className="main" style={{ padding: '20px', maxWidth: 1400 }}>
            <LiveVoiceMonitor userId={(typeof localStorage !== 'undefined' && localStorage.getItem('aria_user_id')) || 'default'} />
          </main>
        </div>
      ) : effectiveSection === 'whisper-test' ? (
        <div className="app-whisper-test-section">
          <header className="header header--minimal">
            <h1 className="header-title">🎤 Whisper mic test</h1>
          </header>
          <main className="main" style={{ padding: '24px', maxWidth: 1000 }}>
            <WhisperMicTest />
          </main>
        </div>
      ) : effectiveSection === 'review' ? (
        // Knowledge-review dashboard — Braeden's surface for approving
        // candidate claims before Aria teaches them to users. Admin-token
        // gated client-side (matching server-side STORAGE_AGENT_ADMIN_TOKEN).
        <ReviewQueue />
      ) : effectiveSection === 'settings' ? (
        <div
          className="settings-modal-backdrop"
          onClick={() => { try { window.location.hash = 'ask'; } catch (_) {} setDashboardSection('ask'); setView('ask'); setSettingsCat(null); }}
        >
          <div className="settings-modal settings-modal--big" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Settings">
            {(() => {
              const closeSettings = () => { try { window.location.hash = 'ask'; } catch (_) {} setDashboardSection('ask'); setView('ask'); setSettingsCat('account'); setForgetPhase('idle'); setForgetToken(''); setOverageInfoOpen(false); };
              // ── HowTo identity: gradient icon tile + colored GLOW per category.
              // (glow = an "r,g,b" string used by the dark-card-with-colored-glow
              // language from ARIA_CONTEXT.md — this is NOT iOS material.)
              const catMeta = {
                account: { color: 'linear-gradient(155deg,#fadd7f,#e0a828)', glow: '245,197,66', desc: 'Email, password and sign out', icon: (<svg viewBox="0 0 24 24"><circle cx="12" cy="8.2" r="3.5"/><path d="M5.5 19.5c0-3.4 3.1-5.3 6.5-5.3s6.5 1.9 6.5 5.3"/></svg>) },
                subscription: { color: 'linear-gradient(155deg,#42e2a0,#0d9c86)', glow: '52,211,153', desc: 'Plan, payment and billing', icon: (<svg viewBox="0 0 24 24"><rect x="3" y="5.5" width="18" height="13" rx="2.6"/><path d="M3 10h18M7 15h4"/></svg>) },
                appearance: { color: 'linear-gradient(155deg,#8fa0ff,#4f46e5)', glow: '129,140,248', desc: 'Theme and text size', icon: (<svg viewBox="0 0 24 24"><path d="M12 3a9 9 0 100 18 4.5 4.5 0 010-9 4.5 4.5 0 000-9z"/><circle cx="12" cy="7.5" r="0.6" fill="currentColor"/><circle cx="16" cy="10" r="0.6" fill="currentColor"/></svg>) },
                voice: { color: 'linear-gradient(155deg,#cf9bfb,#7c3aed)', glow: '167,110,240', desc: "Aria's voice, speed and mic", icon: (<svg viewBox="0 0 24 24"><rect x="9" y="3" width="6" height="10.5" rx="3"/><path d="M5.5 11a6.5 6.5 0 0013 0M12 17.5V21"/></svg>) },
                history: { color: 'linear-gradient(155deg,#aab3c2,#6a7382)', glow: '148,163,184', desc: 'History, export and delete', icon: (<svg viewBox="0 0 24 24"><path d="M3.5 12a8.5 8.5 0 105-7.7"/><path d="M3.5 4.3v3.4h3.4"/><path d="M12 7.6V12l3 1.9"/></svg>) },
                notifications: { color: 'linear-gradient(155deg,#ffb066,#f2621f)', glow: '245,130,60', desc: 'Sound and push alerts', icon: (<svg viewBox="0 0 24 24"><path d="M6 9.5a6 6 0 0112 0c0 4.6 2 5.8 2 5.8H4s2-1.2 2-5.8z"/><path d="M10 19.5a2 2 0 004 0"/></svg>) },
              };

              // ── Identity + small helpers ─────────────────────────────────────
              const acctId = () => { try { return localStorage.getItem(ARIA_USER_ID_KEY) || ''; } catch (_) { return ''; } };
              const acctEmail = () => { try { return localStorage.getItem('aria_session_email') || localStorage.getItem('aria_user_email') || ''; } catch (_) { return ''; } };
              const safeParse = (s) => { try { return s ? JSON.parse(s) : null; } catch (_) { return null; } };
              const isOn = (k, defOn) => { try { const v = localStorage.getItem(k); return v === '1' || (defOn && v !== '0'); } catch (_) { return !!defOn; } };

              // ── Real handlers (every one wired to something that works) ───────
              const doSignOut = () => {
                try { ['aria_session_token','aria_session_email','aria_session_messages','aria_session_history','aria_user_id','aria_user_email','aria_user_first'].forEach((k) => localStorage.removeItem(k)); } catch (_) {}
                try { window.location.reload(); } catch (_) {}
              };
              const doResetPassword = async () => {
                const email = acctEmail();
                if (!email) { flashSettingsToast('Sign in first to reset your password.'); return; }
                try {
                  const r = await fetch(`${API_BASE}/api/auth/request-reset`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
                  flashSettingsToast(r.ok ? `Reset link sent to ${email}.` : 'Could not send reset link — try again.');
                } catch (_) { flashSettingsToast('Could not send reset link — check your connection.'); }
              };
              const doExportData = () => {
                try {
                  const payload = { exportedAt: new Date().toISOString(), userId: acctId(), email: acctEmail(),
                    conversation: safeParse(localStorage.getItem('aria_conversation_log')),
                    archive: safeParse(localStorage.getItem('aria_conversation_archive')), messages };
                  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
                  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'aria-my-data.json';
                  document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => { try { URL.revokeObjectURL(a.href); } catch (_) {} }, 2000);
                  flashSettingsToast('Downloaded aria-my-data.json.');
                } catch (_) { flashSettingsToast('Could not export — try again.'); }
              };
              const postOverage = (allow) => { const id = acctId(); if (!id) return; try { fetch(`${API_BASE}/api/user/billing/overage`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: id, allow }) }); } catch (_) {} };
              const requestForget = async () => {
                const id = acctId();
                if (!id) { flashSettingsToast('Sign in first — nothing to forget for an anonymous session.'); return; }
                setForgetBusy(true);
                try {
                  const r = await fetch(`${API_BASE}/api/aria/advisor/forget/request`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId: id, email: acctEmail() || null }) });
                  const d = await r.json().catch(() => ({}));
                  if (r.ok) { if (d && d.token) setForgetToken(String(d.token)); setForgetPhase('sent'); flashSettingsToast(d && d.token ? 'Confirmation code ready below.' : 'Check your email for a confirmation code.'); }
                  else { flashSettingsToast(d && d.error ? `Couldn’t start: ${d.error}` : 'Could not start the delete — try again.'); }
                } catch (_) { flashSettingsToast('Could not reach the server.'); }
                setForgetBusy(false);
              };
              const confirmForget = async () => {
                const token = forgetToken.trim();
                if (!token) { flashSettingsToast('Enter the confirmation code first.'); return; }
                setForgetBusy(true);
                try {
                  const r = await fetch(`${API_BASE}/api/aria/advisor/forget/confirm`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) });
                  if (r.ok) { setForgetPhase('done'); flashSettingsToast('Your data has been deleted.'); }
                  else { const d = await r.json().catch(() => ({})); flashSettingsToast(d && d.error ? `Failed: ${d.error}` : 'Code invalid or expired.'); }
                } catch (_) { flashSettingsToast('Could not reach the server.'); }
                setForgetBusy(false);
              };
              const cancelForget = async () => { const token = forgetToken.trim(); try { if (token) await fetch(`${API_BASE}/api/aria/advisor/forget/cancel`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token }) }); } catch (_) {} setForgetPhase('idle'); setForgetToken(''); };
              const onPushToggle = async (e) => {
                const el = e.currentTarget; const parent = el.parentElement;
                if (el.checked) {
                  if (typeof Notification === 'undefined') { el.checked = false; parent.classList.remove('aria-toggle--on'); flashSettingsToast('This device doesn’t support notifications.'); return; }
                  let perm = Notification.permission;
                  if (perm === 'default') { try { perm = await Notification.requestPermission(); } catch (_) {} }
                  if (perm !== 'granted') { el.checked = false; parent.classList.remove('aria-toggle--on'); flashSettingsToast('Allow notifications in your browser to turn this on.'); return; }
                  try { localStorage.setItem('aria_notif_push', '1'); } catch (_) {}
                  parent.classList.add('aria-toggle--on');
                  try { const n = new Notification('Aria', { body: 'Notifications are on. I’ll ping you here when something needs you.' }); n.onclick = () => { try { window.focus(); n.close(); } catch (_) {} }; } catch (_) {}
                  flashSettingsToast('Push notifications on.');
                } else { try { localStorage.setItem('aria_notif_push', '0'); } catch (_) {} parent.classList.remove('aria-toggle--on'); flashSettingsToast('Push notifications off.'); }
              };
              const pickTheme = (v) => {
                try { localStorage.setItem('aria_theme_pref', v); } catch (_) {}
                setThemePrefState(v);
                if (v === 'light') switchTheme('day');
                else if (v === 'dark') switchTheme('night');
                else { const dark = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches; switchTheme(dark ? 'night' : 'day'); }
              };
              const pickTextSize = (v) => { try { localStorage.setItem('aria_text_size', v); } catch (_) {} setTextSizePref(v); document.body.classList.remove('aria-text--s','aria-text--m','aria-text--l'); document.body.classList.add('aria-text--' + v); };
              const pickSpeed = (v) => { try { localStorage.setItem('aria_voice_speed', v); } catch (_) {} setVoiceSpeedPref(v); };
              const doAction = (act) => {
                if (act === 'checkout') { try { window.dispatchEvent(new CustomEvent('aria-open-checkout', { detail: { plan: 'subscription' } })); } catch (_) {} return; }
                if (act === 'billing') { openBillingPortal(); return; }
                if (act === 'history') { try { window.location.hash = 'history'; } catch (_) {} setDashboardSection('history'); return; }
                if (act === 'signin') { try { window.dispatchEvent(new CustomEvent('aria-open-signin')); } catch (_) {} closeSettings(); return; }
                if (act === 'signout') { doSignOut(); return; }
                if (act === 'reset-password') { doResetPassword(); return; }
                if (act === 'export') { doExportData(); return; }
              };

              // ── Real display values from live state ──────────────────────────
              const emailNow = acctEmail();
              const planLabel = (() => { const s = billingSnapshot; if (s && (s.status === 'active' || s.active || s.subscribed)) return s.planName || s.tier || 'Pro'; return 'Free'; })();
              const cardLabel = (() => {
                const s = billingSnapshot; const c = s && (s.card || s.paymentMethod || s.defaultCard);
                if (c && (c.last4 || c.last_four)) { const b = (c.brand || 'Card'); return `${b.charAt(0).toUpperCase() + b.slice(1)} ···· ${c.last4 || c.last_four}`; }
                try { const raw = localStorage.getItem('aria_payment_method'); if (raw) { const pm = JSON.parse(raw); return `${(pm.brand || 'Card')} ···· ${pm.last4 || '••••'}`; } } catch (_) {}
                return 'Add a card';
              })();
              const themePref = themePrefState || (appTheme === 'day' ? 'light' : 'dark');

              // ── Category → control set (every control is real) ────────────────
              const accountItems = emailNow
                ? [ { type: 'value', label: 'Email', value: emailNow },
                    { type: 'action', label: 'Reset password', hint: 'We’ll email you a secure link', act: 'reset-password' },
                    { type: 'action', label: 'Sign out', danger: true, act: 'signout' } ]
                : [ { type: 'value', label: 'Account', value: 'Not signed in' },
                    { type: 'action', label: 'Sign in', act: 'signin' } ];
              const categories = [
                { key: 'account', label: 'Account', items: accountItems },
                { key: 'subscription', label: 'Subscription', items: [
                  { type: 'action', label: 'Plan', value: planLabel, act: 'checkout' },
                  { type: 'action', label: 'Payment method', value: cardLabel, act: 'checkout' },
                  { type: 'action', label: 'Manage billing', value: 'Open', act: 'billing' },
                  { type: 'overage', label: 'Allow extra usage' },
                ]},
                { key: 'appearance', label: 'Appearance', items: [
                  { type: 'theme', label: 'Theme' },
                  { type: 'textsize', label: 'Text size' },
                ]},
                { key: 'voice', label: 'Voice & Audio', items: [
                  { type: 'voiceid', label: 'Aria’s voice', value: 'Ava · warm female' },
                  { type: 'speed', label: 'Voice speed' },
                  { type: 'mic', label: 'Microphone' },
                ]},
                { key: 'history', label: 'History & Data', items: [
                  { type: 'action', label: 'Conversation history', value: (messages.length ? `${messages.length} messages` : 'Empty'), act: 'history' },
                  { type: 'action', label: 'Export my data', value: 'Download', act: 'export' },
                  { type: 'forget', label: 'Forget my data', danger: true },
                ]},
                { key: 'notifications', label: 'Notifications', items: [
                  { type: 'toggle', label: 'Sound effects', k: 'aria_sounds_enabled', defaultOn: true },
                  { type: 'push', label: 'Push notifications', k: 'aria_notif_push' },
                ]},
              ];
              const detailCat = categories.find((c) => c.key === settingsCat) || categories[0];
              const detailMeta = catMeta[detailCat.key] || {};

              // ── One row renderer per control type ─────────────────────────────
              const labelBlock = (it) => (
                <div className="set-row__label">
                  <span className="set-row__name">{it.label}</span>
                  {it.hint && <span className="set-row__hint">{it.hint}</span>}
                </div>
              );
              const seg = (opts, cur, onPick, extra) => (
                <div className={`set-seg ${extra || ''}`}>
                  {opts.map(([v, l]) => (
                    <button key={v} type="button" className={`set-seg__btn ${cur === v ? 'is-on' : ''}`} onClick={() => onPick(v)}>{l}</button>
                  ))}
                </div>
              );
              const renderRow = (it, i) => {
                if (it.type === 'action') {
                  return (
                    <button type="button" key={i} className={`set-row set-row--tap ${it.danger ? 'set-row--danger' : ''}`} onClick={() => doAction(it.act)}>
                      {labelBlock(it)}
                      <span className="set-row__right">{it.value && <span className="set-row__val">{it.value}</span>}<span className="set-row__chev" aria-hidden="true">›</span></span>
                    </button>
                  );
                }
                if (it.type === 'value') return (<div className="set-row" key={i}>{labelBlock(it)}<span className="set-row__val">{it.value}</span></div>);
                if (it.type === 'toggle') {
                  const on = isOn(it.k, it.defaultOn);
                  return (
                    <div className="set-row" key={i}>{labelBlock(it)}
                      <label className={`aria-toggle ${on ? 'aria-toggle--on' : ''}`}>
                        <input type="checkbox" defaultChecked={on} onChange={(e) => { try { localStorage.setItem(it.k, e.target.checked ? '1' : '0'); e.currentTarget.parentElement.classList.toggle('aria-toggle--on', e.target.checked); } catch (_) {} }} />
                        <span className="aria-toggle__thumb" />
                      </label>
                    </div>
                  );
                }
                if (it.type === 'push') {
                  const on = isOn(it.k, false);
                  return (
                    <div className="set-row" key={i}>{labelBlock(it)}
                      <label className={`aria-toggle ${on ? 'aria-toggle--on' : ''}`}>
                        <input type="checkbox" defaultChecked={on} onChange={onPushToggle} />
                        <span className="aria-toggle__thumb" />
                      </label>
                    </div>
                  );
                }
                if (it.type === 'overage') {
                  const on = isOn('aria_allow_overage', false);
                  return (
                    <React.Fragment key={i}>
                      <div className="set-row">
                        <div className="set-row__label"><span className="set-row__name">{it.label}</span>
                          <button type="button" className="set-row__seemore" onClick={() => setOverageInfoOpen((v) => !v)}>{overageInfoOpen ? 'Hide details' : 'What’s this?'}</button>
                        </div>
                        <label className={`aria-toggle ${on ? 'aria-toggle--on' : ''}`}>
                          <input type="checkbox" defaultChecked={on} onChange={(e) => {
                            if (e.target.checked) { e.target.checked = false; e.currentTarget.parentElement.classList.remove('aria-toggle--on'); setOverageConfirmOpen(true); return; }
                            try { localStorage.setItem('aria_allow_overage', '0'); e.currentTarget.parentElement.classList.remove('aria-toggle--on'); } catch (_) {}
                            postOverage(false); flashSettingsToast('Extra usage off — Aria stops at your plan.');
                          }} />
                          <span className="aria-toggle__thumb" />
                        </label>
                      </div>
                      {overageInfoOpen && <div className="set-note"><OverageNotice /></div>}
                      {overageConfirmOpen && (
                        <OverageConfirmModal onCancel={() => setOverageConfirmOpen(false)} onConfirm={() => {
                          try { localStorage.setItem('aria_allow_overage', '1'); } catch (_) {}
                          postOverage(true); setOverageConfirmOpen(false);
                          document.querySelectorAll('.set-row .aria-toggle input[type="checkbox"]').forEach((cb) => {
                            const row = cb.closest('.set-row');
                            if (row && /Allow extra usage/.test(row.textContent || '')) { cb.checked = true; cb.parentElement.classList.add('aria-toggle--on'); }
                          });
                          flashSettingsToast('Extra usage on — Aria can go past your plan when needed.');
                        }} />
                      )}
                    </React.Fragment>
                  );
                }
                if (it.type === 'theme') return (<div className="set-row set-row--stack" key={i}>{labelBlock(it)}{seg([['dark','Dark'],['light','Light'],['system','System']], themePref, pickTheme)}</div>);
                if (it.type === 'textsize') return (<div className="set-row set-row--stack" key={i}>{labelBlock(it)}{seg([['s','Small'],['m','Medium'],['l','Large']], textSizePref, pickTextSize)}</div>);
                if (it.type === 'speed') return (<div className="set-row set-row--stack" key={i}>{labelBlock(it)}{seg([['0.9','Slower'],['1.0','Natural'],['1.1','Faster']], voiceSpeedPref, pickSpeed)}</div>);
                if (it.type === 'mic') {
                  return (
                    <div className="set-row" key={i}>{labelBlock(it)}
                      {audioInputs.length > 0
                        ? (<select className="set-select" value={preferredMicId} onChange={(e) => setPreferredMicId(e.target.value)}>
                            {audioInputs.map((d, idx) => (<option key={`${d.deviceId || 'mic'}-${idx}`} value={d.deviceId || ''}>{d.label || (d.deviceId === 'default' ? 'Default microphone' : `Microphone ${idx + 1}`)}</option>))}
                          </select>)
                        : (<span className="set-row__val">{activeMicLabel || 'Default'}</span>)}
                    </div>
                  );
                }
                if (it.type === 'voiceid') {
                  return (
                    <div className="set-row" key={i}>{labelBlock(it)}
                      <span className="set-row__right"><span className="set-row__val">{it.value}</span>
                        <button type="button" className="set-mini-btn" onClick={() => speakAriaLine("Hey, this is Aria. This is how I sound — I'm here whenever you need me.")}>Preview</button>
                      </span>
                    </div>
                  );
                }
                if (it.type === 'forget') {
                  return (
                    <React.Fragment key={i}>
                      <button type="button" className="set-row set-row--tap set-row--danger" onClick={() => { if (forgetPhase === 'idle') requestForget(); }} disabled={forgetPhase !== 'idle'}>
                        {labelBlock(it)}
                        <span className="set-row__right"><span className="set-row__val">{forgetPhase === 'done' ? 'Deleted' : (forgetBusy ? 'Working…' : 'Delete')}</span>{forgetPhase === 'idle' && <span className="set-row__chev" aria-hidden="true">›</span>}</span>
                      </button>
                      {forgetPhase === 'sent' && (
                        <div className="set-forget">
                          <p className="set-forget__msg">This permanently deletes your conversations and memory. Enter the confirmation code to finish.</p>
                          <input className="set-forget__input" value={forgetToken} onChange={(e) => setForgetToken(e.target.value)} placeholder="Confirmation code" />
                          <div className="set-forget__row">
                            <button type="button" className="set-btn set-btn--ghost" onClick={cancelForget} disabled={forgetBusy}>Cancel</button>
                            <button type="button" className="set-btn set-btn--danger" onClick={confirmForget} disabled={forgetBusy}>{forgetBusy ? 'Working…' : 'Delete everything'}</button>
                          </div>
                        </div>
                      )}
                      {forgetPhase === 'done' && <div className="set-note">Your data has been deleted. Sign out to finish.</div>}
                    </React.Fragment>
                  );
                }
                return null;
              };

              return (
                <>
                <header className="set-head">
                  <div className="set-head__id">
                    <span className="set-brand">HowTo</span>
                    <span className="set-head__title">Settings</span>
                  </div>
                  <button type="button" className="set-done" onClick={closeSettings}>Done</button>
                </header>
                <div className="set-body">
                  <nav className="set-rail" aria-label="Settings categories">
                    {categories.map((cat) => {
                      const m = catMeta[cat.key] || {};
                      const on = cat.key === detailCat.key;
                      return (
                        <button key={cat.key} type="button" className={`set-rail__item ${on ? 'is-active' : ''}`} style={on ? { '--glow': m.glow } : undefined} onClick={() => setSettingsCat(cat.key)}>
                          <span className="set-rail__icon" style={{ background: m.color }}>{m.icon}</span>
                          <span className="set-rail__text">
                            <span className="set-rail__label">{cat.label}</span>
                            <span className="set-rail__desc">{m.desc}</span>
                          </span>
                        </button>
                      );
                    })}
                  </nav>
                  <section className="set-detail" key={detailCat.key}>
                    <div className="set-detail__head">
                      <span className="set-detail__icon" style={{ background: detailMeta.color }}>{detailMeta.icon}</span>
                      <div className="set-detail__titles">
                        <h2 className="set-detail__title">{detailCat.label}</h2>
                        <p className="set-detail__sub">{detailMeta.desc}</p>
                      </div>
                    </div>
                    <div className="set-card" style={{ '--glow': detailMeta.glow }}>
                      {detailCat.items.map((it, i) => renderRow(it, i))}
                    </div>
                    <div className={`set-toast ${settingsToast ? 'is-show' : ''}`} aria-live="polite">{settingsToast}</div>
                  </section>
                </div>
                </>
              );
            })()}
          </div>
        </div>
      ) : effectiveSection === 'history' ? (
        <div className="app-history-section">
          <header className="header header--minimal">
            <h1 className="header-title">Conversation History</h1>
          </header>
          <div className="history-messages">
            {messages.length === 0 ? (
              <div className="history-empty">
                <p>No conversations yet. Start talking to Aria!</p>
                <button type="button" className="history-go-ask" onClick={() => { window.location.hash = 'ask'; setDashboardSection('ask'); setView('ask'); }}>
                  Ask Aria
                </button>
              </div>
            ) : (
              messages.map((msg, i) => {
                const isUser = msg.role === 'user';
                const prevMsg = messages[i - 1];
                const showSender = !prevMsg || prevMsg.role !== msg.role;
                return (
                  <div key={i} className={`chat-msg ${isUser ? 'chat-msg--user' : 'chat-msg--ai'}`}>
                    {showSender && (
                      <span className="chat-msg-sender">{isUser ? 'You' : 'Aria'}</span>
                    )}
                    {/* SHOW IT, DON'T JUST SAY IT (8/14). When the turn earns a
                        picture — the actual unpaid invoices, what is switched
                        on, why reply speed decides a job — it renders under her
                        line. Null on most turns: a visual every time is
                        wallpaper and stops being read. */}
                    {!isUser && turnVisual && i === messages.map((x) => x.role).lastIndexOf('assistant')
                      && <AriaVisual spec={turnVisual} theme={appTheme} />}
                    <div className="chat-msg-bubble">
                      {typeof msg.content === 'string' ? msg.content : String(msg.content)}
                      {!isUser && (msg.data?.audioUrl || msg.data?.audioData) && (
                        <button
                          type="button"
                          className="chat-msg-play"
                          onClick={() => {
                            const src = msg.data.audioUrl || `data:audio/wav;base64,${msg.data.audioData}`;
                            const a = new Audio(src);
                            prepareAriaPlayback(a);
                            a.playbackRate = chooseSpeechRate((msg?.content || '').toString());
                            a.play().catch(() => {});
                          }}
                          aria-label="Play Aria's reply"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                          Play
                        </button>
                      )}
                    </div>
                    {/* Structured action cards for hire / parts offers. */}
                    {!isUser && msg.displayMode === 'hire_offer' && (
                      <HireOfferCard
                        draft={msg.hireDraft}
                        userId={(typeof localStorage !== 'undefined' && localStorage.getItem('aria_user_id')) || 'default'}
                      />
                    )}
                    {!isUser && msg.displayMode === 'parts_offer' && (
                      <PartsOfferCard
                        draft={msg.partsDraft}
                        userId={(typeof localStorage !== 'undefined' && localStorage.getItem('aria_user_id')) || 'default'}
                      />
                    )}
                    {!isUser && msg.displayMode === 'dashboard_preview' && msg.dashboardPreview && (
                      <DashboardPreviewCard dashboard={msg.dashboardPreview} />
                    )}
                    {!isUser && !msg.error && (
                      <div className="chat-msg-feedback">
                        <button
                          type="button"
                          className={`chat-msg-feedback-btn ${feedbackGiven[i] === 'up' ? 'chat-msg-feedback-btn--up-active' : ''}`}
                          onClick={() => sendFeedback(i, 'up')}
                          disabled={!!feedbackGiven[i]}
                          aria-label="Good response"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                        </button>
                        <button
                          type="button"
                          className={`chat-msg-feedback-btn ${feedbackGiven[i] === 'down' ? 'chat-msg-feedback-btn--down-active' : ''}`}
                          onClick={() => sendFeedback(i, 'down')}
                          disabled={!!feedbackGiven[i]}
                          aria-label="Poor response"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/><path d="M17 2h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3"/></svg>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : (
      <>
      {/* The ring she draws while she talks about a real control (8/31). */}
      {/* THE MEMBERS' DOWNLOAD, WHERE SHE SAYS IT IS (9/5). DesktopDownloadsSection
          was defined and rendered nowhere, so a new member had no link to find and
          her "top right" line would have pointed at nothing. Web only - the desktop
          app does not offer itself. Members only, by design (see the section). */}
      {!isDesktopApp && desktopMember && (
        // a viewport-fixed frame, so the banner's absolute top/right (App.css
        // .desktop-downloads--banner) measures from the screen, not from
        // whichever ancestor happens to be positioned (probed 9/5: none was)
        <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 30 }} aria-hidden={false}>
          <DesktopDownloadsSection info={desktopDownloadInfo} variant="banner" member={desktopMember} />
        </div>
      )}
      <TourSpotlight point={tourPoint} onDone={() => setTourPoint(null)} />
      {/* The "do these three" card (Braeden 9/2) — up while she names their
          project, so the generation is narrated, never a silent wait. */}
      <ThreeThings items={threeThings} title={madeTitle ? `Made "${madeTitle}" - what I need to know` : 'What I need to know'} onDone={() => { setThreeThings(null); setMadeTitle(null); }} />

      {/* Which job this is filed under (8/31). */}
      <JobChip
        workspace={workspace}
        onOpen={(w) => { try { window.location.hash = 'canvas/' + w.id; } catch (_) {} }}
      />

      {/* First Time? button — floats in the open dark space on the left for
          not-signed-in visitors. Fixed overlay so it never disturbs the locked
          header/orb/input. Tapping it asks Aria for a spoken overview, then it's
          dismissed for good. */}
      {/* `forceFirstRunButton` (?firstrun=1) shows this while SIGNED IN. Without
          it the button only renders when logged out, which is why the whole
          onboarding road was unreachable for the owner — he is always signed in,
          so he had never once seen the thing that starts it (8/16). It shows the
          button; it does not press it. */}
      {/* ── WHERE THIS BUTTON IS ALLOWED TO EXIST (Braeden 8/31) ──────────
          "somebodies first experiance, on the website, that is the only spot
           someone sees the first time button, should never be anywhere else.
           and its only for people who arnt logged in, and its probably there
           first time on the site."

          Three gates, all of them his:
            website   - never in the desktop app (window.electronAPI is the
                        app; it was showing there, which is the "never
                        anywhere else" he is talking about)
            logged out- an account means they are not new
            first visit- a returning anonymous visitor has seen it; the marker
                        is written on first load, so the SECOND visit is quiet
          ?firstrun=1 still forces it, because he is always signed in and
          otherwise could never see his own front door. */}
      {/* ── SHE PROVES IT BEFORE SHE EXPLAINS IT (Braeden 8/31) ───────────
          "when the user first gets on, i need to get them hooked with what
           makes aria valuable."

          What stood here was a button reading "First Time? Tap for a quick
          tour" with an emoji hand pointing at it. Everything a stranger could
          learn about her sat behind that one tap, and the tap led to her
          TALKING about a search bar. Measured the same day on a cleared
          browser: the page was a black void, her opening line was spoken only
          (so a denied mic or a muted laptop got a blank screen with the word
          "listening" on it), and the one thing that actually sells her — 24
          real trucks, real prices, live off the web in 2.3s — was never shown
          to anyone who did not already know to ask for it.

          FirstLook puts that proof ON the front door: three real sentences,
          each wired to the real pipeline, each probed against the live
          backend before it was allowed to stand here. Tapping one runs it for
          actual. The tour is still one click away for anyone who would rather
          be walked through it — it just no longer stands between a stranger
          and the demonstration.

          The gates underneath are unchanged and still his: website only,
          logged out only, first visit only. */}
      {((isNewVisitor || forceFirstRunButton)
        && !firstTimeDismissed && effectiveSection === 'ask'
        /* Once they have typed something, the button is in the way — it sat
           on top of the search results at 1280px (measured 9/2). The door is
           for the empty landing; a person mid-search has already come in. */
        && messages.length === 0) && (
        // ── THE OLD FIRST TIME BUTTON, BACK (Braeden 9/2) ──────────────────
        // "remember the old first time button, thats what i want to see, not
        //  that bullshit." The card door (FirstLook, 8/31) and the pill that
        // replaced it are gone. This is what stood here before them: a button
        // floating in the open dark space on the left, an emoji hand pointing
        // at it, reading "First Time? Tap for a quick tour". One tap starts her
        // spoken intro (the walked tour) and the button is dismissed for good.
        // ── THE ORIGINAL, FROM THE RECORDS (Braeden 9/2: "make it so the
        //    button is back to how it was, should be in records somewhere") ──
        // Recovered verbatim from src/App.jsx.bak-826 (the 8/26 backup): the
        // same card, the same three lines, the same 👈 hand nudging at it, the
        // same pulse. Only the tap changed - it starts the walked intro now.
        /* The class carries no desktop styling — the inline styles below are
           still the recovered 8/26 original, untouched. It exists so a PHONE
           can move this out of the way: at 375px the button floats dead
           centre and printed straight through two of the door's tiles
           (measured 9/3). See `.first-time-door` in Uses.css. */
        <div className="first-time-door" style={{ position:'fixed', left:'26px', top:'50%', transform:'translateY(-50%)', zIndex:45, display:'flex', alignItems:'center', gap:'12px', pointerEvents:'none' }}>
          <button
            type="button"
            aria-label="First time here? Get a quick overview from Aria"
            onClick={() => { setFirstTimeDismissed(true); if (FIRST_TIME_IS_VOICE_ONBOARDING) startOnboarding(); else startAriaTour(); }}
            style={{
              pointerEvents:'auto', cursor:'pointer', textAlign:'left',
              display:'flex', flexDirection:'column', alignItems:'flex-start', gap:'3px',
              padding:'16px 22px', borderRadius:'16px',
              background:'linear-gradient(135deg, rgba(20,28,48,.92), rgba(12,18,34,.94))',
              border:'1.5px solid rgba(34,211,238,.55)',
              boxShadow:'0 0 28px rgba(34,211,238,.35), 0 8px 30px rgba(0,0,0,.5)',
              color:'#eaf4ff', backdropFilter:'blur(6px)',
              animation:'firstTimePulse 2.4s ease-in-out infinite'
            }}
          >
            <span style={{ fontSize:'12px', letterSpacing:'.6px', textTransform:'uppercase', color:'#7fe6f5', fontWeight:700 }}>👋 New here?</span>
            <span style={{ fontSize:'21px', fontWeight:800, lineHeight:1.1 }}>First Time?</span>
            <span style={{ fontSize:'12.5px', color:'#a9bede' }}>Tap for a quick tour</span>
            {/* WHAT SHE JUST SAID, WHERE SHE IS POINTING (Braeden 9/6). The
                offer only appears after someone has sat there doing nothing,
                and it lands ON the thing she wants them to press, so the
                spoken line and the target are the same object. It cannot be
                appended to `messages` - the door's own gate is
                `messages.length === 0`, so her offer would delete the button
                she is offering. */}
          </button>
          <span aria-hidden="true" style={{ fontSize:'34px', color:'#22d3ee', filter:'drop-shadow(0 0 8px rgba(34,211,238,.6))', animation:'firstTimeArrow 1.2s ease-in-out infinite' }}>👈</span>
          {/* WHAT SHE SAID, WITHOUT MOVING THE DOOR (Braeden 9/6: "not sure what
              happened to the onboarding button but it doesnt seem to be working
              the way it did").
              My first cut put this line INSIDE the button. That grew it from
              107px to 236px wide the moment she offered, which pushed it into
              the door tiles and shoved the 👈 out - the exact 1280px collision
              the comment above this block already warned about. The offer must
              never change the door's footprint, so it is absolutely positioned
              UNDER the fixed container and takes no layout space at all. Fixed
              200px wide: the container sits at left:26, so it ends at 226 and
              the first tile starts at 280. */}
          {tourOffer ? (
            <span style={{
              position:'absolute', top:'100%', left:0, marginTop:'10px',
              width:'200px', boxSizing:'border-box', pointerEvents:'none',
              fontSize:'13px', lineHeight:1.4, color:'#d6ecff',
              background:'linear-gradient(135deg, rgba(20,28,48,.94), rgba(12,18,34,.96))',
              border:'1px solid rgba(34,211,238,.35)', borderRadius:'12px',
              padding:'10px 12px', boxShadow:'0 8px 24px rgba(0,0,0,.5)',
              animation:'firstTimeOffer 420ms ease-out both',
            }}>{tourOffer}</span>
          ) : null}
          <style>{`
            @keyframes firstTimePulse { 0%,100%{ box-shadow:0 0 24px rgba(34,211,238,.30), 0 8px 30px rgba(0,0,0,.5);} 50%{ box-shadow:0 0 42px rgba(34,211,238,.58), 0 8px 30px rgba(0,0,0,.5);} }
            @keyframes firstTimeArrow { 0%,100%{ transform:translateX(0);} 50%{ transform:translateX(-9px);} }
            /* Eased in, not popped - the offer is the one moment she speaks
               unprompted, and a hard appearance reads as an ad (9/6). */
            @keyframes firstTimeOffer { from{ opacity:0; transform:translateY(-4px);} to{ opacity:1; transform:none;} }
            @media (prefers-reduced-motion: reduce) { @keyframes firstTimeOffer { from{opacity:1} to{opacity:1} } }
          `}</style>
        </div>
      )}
      {/* THE TALKING BALL (8/14) — centre screen while she is in a voice
          conversation. Fixed overlay + pointerEvents:none so the locked
          header/orb/input layout underneath is untouched. It reads real
          amplitude off her audio, so it moves with the words, not on a timer. */}
      {/* ── THEIR WEBSITE, IN A BROWSER FRAME (Braeden 8/16) ────────────────
          "she finds it pulls it up in like a browser looking thing, so we can
           see the website and she can do her part"
          A real screenshot of their real homepage. The chrome around it (dots,
          address bar) is what makes it read as "my site is open" rather than
          "here is a picture" — which is the difference between her showing
          them something and her claiming something. */}
      {/* ── WATCHING HER BUILD IT (Braeden 8/16) ────────────────────────────
          "while its being made show the user whats being done in real time so
           they can see it, and whats happening"
          Every line here is a stage the build actually reported — never a timer.
          A bar that fills smoothly while nothing happens teaches people the bar
          means nothing, and a minute of spinner is indistinguishable from a
          hang, which is when someone closes the tab. */}
      {buildRun && effectiveSection === 'ask' && (
        <div style={{
          position: 'fixed', left: '50%', top: '46%', transform: 'translate(-50%,-50%)',
          width: 'min(520px, 88vw)', zIndex: 45,
          background: '#0d1424', border: '1px solid #223049', borderRadius: 12,
          boxShadow: '0 24px 70px rgba(0,0,0,.62)', overflow: 'hidden',
        }}>
          <div style={{ padding: '11px 14px', borderBottom: '1px solid #22304c',
                        display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ width: 9, height: 9, borderRadius: 99,
              background: buildRun.done ? '#34d399' : '#22d3ee',
              boxShadow: `0 0 10px ${buildRun.done ? '#34d399' : '#22d3ee'}`,
              animation: buildRun.done ? 'none' : 'ariaBuildPulse 1.1s ease-in-out infinite' }} />
            <b style={{ fontSize: 13 }}>{buildRun.done ? 'Your site is ready' : 'Building your site'}</b>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#7b8cad',
                           fontVariantNumeric: 'tabular-nums' }}>
              {Math.round((buildRun.elapsedMs || 0) / 1000)}s
            </span>
          </div>
          <div style={{ maxHeight: 260, overflow: 'auto', padding: '9px 14px' }}>
            {(buildRun.stages || []).map((st, i, arr) => (
              <div key={i} style={{
                display: 'flex', gap: 9, alignItems: 'baseline', padding: '4px 0',
                opacity: i === arr.length - 1 ? 1 : 0.62,
              }}>
                <span style={{ color: '#34d399', fontSize: 11 }}>
                  {i === arr.length - 1 && !buildRun.done ? '▸' : '✓'}
                </span>
                <span style={{ fontSize: 12.5, color: '#dceaff' }}>{st.label}</span>
                <span style={{ marginLeft: 'auto', fontSize: 10.5, color: '#7b8cad',
                               fontVariantNumeric: 'tabular-nums' }}>
                  {(st.ms / 1000).toFixed(1)}s
                </span>
              </div>
            ))}
            {!(buildRun.stages || []).length && (
              <div style={{ fontSize: 12, color: '#7b8cad', padding: '8px 0' }}>Starting…</div>
            )}
          </div>
          {buildRun.done && buildRun.result && buildRun.result.previewUrl && (
            <div style={{ padding: '10px 14px', borderTop: '1px solid #22304c', display: 'flex', gap: 8 }}>
              <a href={buildRun.result.previewUrl} target="_blank" rel="noreferrer"
                 style={{ background: '#3b6fd4', color: '#fff', textDecoration: 'none',
                          borderRadius: 8, padding: '7px 14px', fontSize: 12.5, fontWeight: 700 }}>
                Open it
              </a>
              <button onClick={() => setBuildRun(null)}
                      style={{ background: 'none', border: '1px solid #22304c', color: '#9fb4d4',
                               borderRadius: 8, padding: '7px 14px', fontSize: 12.5, cursor: 'pointer' }}>
                Close
              </button>
            </div>
          )}
          <style>{`@keyframes ariaBuildPulse{0%,100%{opacity:.4}50%{opacity:1}}`}</style>
        </div>
      )}

      <AriaCues cue={ariaCue} />
      {sitePreview && effectiveSection === 'ask' && (
        <div style={{
          position: 'fixed', left: '50%', top: '46%', transform: 'translate(-50%,-50%)',
          width: 'min(760px, 84vw)', zIndex: 44,
          borderRadius: 12, overflow: 'hidden',
          background: '#0d1424', border: '1px solid #223049',
          boxShadow: '0 24px 70px rgba(0,0,0,.62)',
        }} data-aria-cue="site">
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px',
            background: '#131c30', borderBottom: '1px solid #22304c',
          }}>
            <span style={{ width: 11, height: 11, borderRadius: 99, background: '#ff5f57' }} />
            <span style={{ width: 11, height: 11, borderRadius: 99, background: '#febc2e' }} />
            <span style={{ width: 11, height: 11, borderRadius: 99, background: '#28c840' }} />
            <div style={{
              flex: 1, marginLeft: 8, background: '#0b1220', border: '1px solid #22304c',
              borderRadius: 99, padding: '4px 12px', fontSize: 12, color: '#9fb4d4',
              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
            }}>{sitePreview.host || sitePreview.url}</div>
            <button
              onClick={() => setSitePreview(null)}
              style={{ background: 'none', border: 0, color: '#7b8cad', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
              aria-label="Close preview"
            >×</button>
          </div>
          <div style={{ background: '#0b1220', minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {sitePreview.loading
              ? <div style={{ color: '#7b8cad', fontSize: 13, padding: '70px 0' }}>loading {sitePreview.url}…</div>
              : sitePreview.shot
                ? <img src={sitePreview.shot} alt={sitePreview.host || 'site'}
                       style={{ width: '100%', display: 'block', maxHeight: '58vh', objectFit: 'cover', objectPosition: 'top' }} />
                : <div style={{ color: '#fbbf24', fontSize: 13, padding: '70px 0' }}>couldn't load that one</div>}
          </div>
          {!sitePreview.loading && (sitePreview.phone || (sitePreview.services || []).length > 0) && (
            <div style={{ padding: '9px 13px', borderTop: '1px solid #22304c', fontSize: 12, color: '#9fb4d4' }}>
              {sitePreview.phone ? <span>☎ {sitePreview.phone}</span> : null}
              {(sitePreview.services || []).length ? <span style={{ marginLeft: 12 }}>{sitePreview.services.slice(0, 4).join(' · ')}</span> : null}
            </div>
          )}
        </div>
      )}

      {/* ── THE RING IN ASSIST MODE (Braeden 8/21) ───────────────────────
          "add the talking circle we have in the website, so that way the user
           has a way of seeing that its talking to the ai"
          Assist collapses the app to a 400x540 corner box. The full-page ball
          below is positioned for the wide layout (right column, measured top,
          210px) — in the compact box it lands outside the visible area, which
          is why it looked missing in assist. This is the same component with
          assist-sized geometry, rendered ONLY in compact mode. */}
      {assistCompactLayout && !micOn && (
        <div style={{
          position: 'fixed', top: 10, left: 0, right: 0,
          display: 'flex', justifyContent: 'center',
          zIndex: 9999, pointerEvents: 'none',
        }}>
          {(() => {
            let force = null;
            try { force = new URLSearchParams(window.location.search).get('ball'); } catch (_) {}
            return (
              <TalkingBall
                speaking={force ? force === 'speaking' : ariaSpeaking}
                // Thinking spans the WHOLE wait - the model AND the voice
                // synthesis - so the ring never goes quiet before she talks.
                thinking={force ? force === 'thinking' : (!ariaSpeaking && (loading || preparingSpeech))}
                listening={force ? force === 'listening' : (!ariaSpeaking && !loading && !preparingSpeech && micOn)}
                muted={!micOn}
                onToggleMute={() => {
                  const next = !micOn;
                  if (!next) { try { stopAriaPlayback(); } catch (_) {} try { instantVoice.stop(); } catch (_) {} }
                  micUserMutedRef.current = !next;
                  try { persistDesktopMicUserMuted(!next); } catch (_) {}
                  setMicOn(next);
                }}
                size={132}
                theme={appTheme}
              />
            );
          })()}
        </div>
      )}

      {effectiveSection === 'ask' && !assistCompactLayout && (
        <div className={'aria-ball-dock' + (orbCompact ? ' aria-ball-dock--compact' : '')} style={{
          // ── ALWAYS ON, RIGHT SIDE, UNDER THE LIVE CHAT (Braeden 8/16) ────
          //   "on the right side i need to see the circle talking ball always
          //    vissable right under the text voice caht thing so we can always
          //    see it"
          // It rendered only while (firstRunOn || ariaSpeaking), centre screen —
          // so the one thing that shows what she is doing was absent exactly
          // when you were waiting to find out, and it sat over the orb when it
          // did appear.
          //
          // `top` is MEASURED off the live chat, never a guessed constant. That
          // chat is fixed at top:56 and holds the last two messages, so its
          // height changes with every reply; a hardcoded offset looks right
          // until a long sentence pushes a bubble down onto the ball.
          // ── THE CIRCLE GETS OUT OF THE WAY WHEN YOU TYPE (Braeden 8/31) ─
          //   "when the text box is being used the circle needs to go up to
          //    the top right of the screen so all the info in the text box
          //    can be centered, so it looks good"
          // Typing is the search engine; the circle is the conversation. While
          // the bar is the thing in use, she shrinks into the corner and hands
          // the middle of the screen to the results. It stays a live control —
          // still the mute switch, still shows listening/thinking/speaking —
          // just small, and no longer holding a 46vw column open beside the
          // work. On a voice turn it returns to full size, measured off the
          // live chat as before.
          position: 'fixed',
          // Compact: tucked INTO the corner (Braeden 9/2: "move the mute circle
          // more in the corner so it's not overlapping"). At 12/12 the circle
          // and its MUTED label sat across the title band and the top of the
          // live-chat column.
          //
          // ── UNDER THE TITLE BAR, NOT ON IT (Braeden 9/4) ────────────────
          //   "i need you to fix the microphone, its on the top right of the
          //    screen next to the x and it needs to not overlap anything"
          //
          // top:2 / right:4 put a 76px circle at x1200-1276, y2-100. The
          // window buttons are at top:11, right:14 and are 98px wide
          // (x1168-1266) — so in the desktop app the circle sat ON minimise,
          // maximise and CLOSE, and it wins at z-index 9999 against their 212.
          // The X was under the mic. It also covered the drag band, so the
          // window could not be moved by that corner.
          //
          // The title bar is 52px (`.app-titlebar`). Parking the circle at 58
          // clears the buttons, the drag band and the small wordmark (which is
          // centred), and still reads as the top-right corner.
          top: orbCompact ? 58 : talkingBallTop,
          right: orbCompact ? 8 : 14,
          width: orbCompact ? 'auto' : 'min(440px, 46vw)',
          display: 'flex', justifyContent: 'center',
          transition: 'top 280ms cubic-bezier(.16,1,.3,1), right 280ms cubic-bezier(.16,1,.3,1)',
          // ── OVER EVERYTHING (Braeden 8/16) ──────────────────────────────
          //   "if im talking to the ai and the mic is open than this always
          //    needs to be displayed over everything so i can always see it"
          // It sat at 34, under the live chat at 35, so a couple of long
          // messages buried the one thing that tells you whether she is
          // listening. Nothing on this page should ever cover it.
          zIndex: 9999,
          // The wrapper stays click-through so it never blocks the page; only
          // the circle itself takes clicks (it is the mute button now).
          pointerEvents: 'none',
        }}>
          {/* one state at a time, in priority order: her voice beats the
              spinner, the spinner beats the open mic (8/14).
              ?ball=speaking|thinking|listening forces one for design review —
              there is no other way to see all three, since two of them depend
              on a live mic and a live reply. */}
          {(() => {
            let force = null;
            try { force = new URLSearchParams(window.location.search).get('ball'); } catch (_) {}
            return (
              <TalkingBall
                speaking={force ? force === 'speaking' : ariaSpeaking}
                // Thinking spans the WHOLE wait - the model AND the voice
                // synthesis - so the ring never goes quiet before she talks.
                thinking={force ? force === 'thinking' : (!ariaSpeaking && (loading || preparingSpeech))}
                listening={force ? force === 'listening' : (!ariaSpeaking && !loading && !preparingSpeech && micOn)}
                // Muted is the absence of an open mic, not a separate flag —
                // one source of truth, so the ring can never disagree with
                // whether audio is actually being captured.
                muted={!micOn}
                onToggleMute={() => {
                  // Clicking the circle IS the mute switch. It also stops her
                  // mid-sentence: if you reach for mute while she is talking,
                  // you want quiet, not to wait for her to finish.
                  const next = !micOn;
                  if (!next) { try { stopAriaPlayback(); } catch (_) {} try { instantVoice.stop(); } catch (_) {} }
                  micUserMutedRef.current = !next;
                  try { persistDesktopMicUserMuted(!next); } catch (_) {}
                  setMicOn(next);
                }}
                // 340 was sized for the middle of the screen. In the right-hand
                // column it would be wider than the chat above it.
                size={orbCompact ? 76 : 210}
                theme={appTheme}
              />
            );
          })()}
        </div>
      )}

      {/* Center: clean header — hidden in desktop app for cleaner look */}
      <header className="header header--minimal" data-aria-layout="center-header">
        {/* Top-left gear icon → drops down the dashboard nav menu.
            Replaces the persistent left sidebar per user request. */}
        <div className="dashboard-menu-wrap" ref={dashboardMenuRef}>
          <button
            type="button"
            className={`dashboard-menu-btn ${dashboardMenuOpen ? 'dashboard-menu-btn--open' : ''}`}
            onClick={(e) => { e.stopPropagation(); setDashboardMenuOpen(v => !v); }}
            aria-label="Open dashboard menu"
            title="Dashboard"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
          {/* REVERTED 8/22. I changed THIS menu when he asked for the DASHBOARD
              to become a project list — wrong surface. The result was two
              identical project menus, this one and the sidebar. The dashboard
              is the project list; this stays navigation. */}
          {dashboardMenuOpen && (
            <div className="dashboard-menu-dropdown" role="menu">
              <button type="button" className={`dashboard-menu-item ${effectiveSection === 'ask' ? 'active' : ''}`} onClick={() => { window.location.hash = 'ask'; setDashboardSection('ask'); setView('ask'); setDashboardMenuOpen(false); }}>Ask Aria</button>
              <button type="button" className={`dashboard-menu-item ${effectiveSection === 'history' ? 'active' : ''}`} onClick={() => { window.location.hash = 'history'; setDashboardSection('history'); setDashboardMenuOpen(false); }}>Conversation History</button>
              <button type="button" className={`dashboard-menu-item ${effectiveSection === 'company' ? 'active' : ''}`} onClick={() => { window.location.hash = 'company'; setDashboardSection('company'); setDashboardMenuOpen(false); }}>Company</button>
              <button type="button" className={`dashboard-menu-item ${effectiveSection === 'leads' ? 'active' : ''}`} onClick={() => { window.location.hash = 'leads'; setDashboardSection('leads'); setDashboardMenuOpen(false); }}>Jobs</button>
              {/* Image Gen / Website / Video / Write / Workbench menu entries
                  were removed 2026-06-05 along with the wb-*.html surfaces.
                  Aria is a helper, not a builder — if a user wants to make
                  something, they ask in chat. Shopping is the lone surface
                  because finding-and-curating IS what a helper does. */}
              <button type="button" className={`dashboard-menu-item ${effectiveSection === 'shop' ? 'active' : ''}`} onClick={() => { window.location.hash = 'shop'; setDashboardSection('shop'); setDashboardMenuOpen(false); }}>Shop</button>
              <button type="button" className={`dashboard-menu-item ${effectiveSection === 'integrations' ? 'active' : ''}`} onClick={() => { window.location.hash = 'integrations'; setDashboardSection('integrations'); setDashboardMenuOpen(false); }}>Integrations</button>
              <button type="button" className="dashboard-menu-item" onClick={() => { setShowBusinessOnboarding(true); setDashboardMenuOpen(false); }}>Business Profile</button>
              <button type="button" className="dashboard-menu-item dashboard-menu-item--disabled" title="Coming soon" disabled>API Access</button>
              <button type="button" className={`dashboard-menu-item ${effectiveSection === 'review' ? 'active' : ''}`} onClick={() => { window.location.hash = 'review'; setDashboardSection('review'); setDashboardMenuOpen(false); }}>🛡️ Knowledge Review</button>
              {/* ── SIGN OUT IN TWO CLICKS (Braeden 9/4) ────────────────────
                  "can you ad an easy way to sign out". It was three: open the
                  sidebar, find Account at the very bottom of it, then the
                  panel. This menu button is on screen at all times, so Account
                  belongs in it — and Account is now only the three things he
                  asked for, sign out among them. */}
              <button type="button" className="dashboard-menu-item" onClick={() => { setDashboardMenuOpen(false); setAccountPanelOpen(true); }}>Account</button>
              <div className="dashboard-menu-divider" />
              <button type="button" className={`dashboard-menu-item ${effectiveSection === 'whisper-test' ? 'active' : ''}`} onClick={() => { window.location.hash = 'whisper-test'; setDashboardSection('whisper-test'); setDashboardMenuOpen(false); }}>🎤 Whisper mic test</button>
              <button type="button" className={`dashboard-menu-item ${effectiveSection === 'live' ? 'active' : ''}`} onClick={() => { window.location.hash = 'live'; setDashboardSection('live'); setDashboardMenuOpen(false); }}>Live voice monitor</button>
              <button type="button" className={`dashboard-menu-item ${effectiveSection === 'dev' ? 'active' : ''}`} onClick={() => { window.location.hash = 'dev'; setDashboardSection('dev'); setDashboardMenuOpen(false); }}>Dev Console</button>
              <button type="button" className={`dashboard-menu-item ${effectiveSection === 'settings' ? 'active' : ''}`} onClick={() => { window.location.hash = 'settings'; setDashboardSection('settings'); setDashboardMenuOpen(false); }}>Settings</button>
            </div>
          )}
        </div>
        <div className="header-title-row" onClick={() => setShowConversationOverlay(true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setShowConversationOverlay(true); }}>
          <span className="header-title-name">Aria</span>
          {/* Model dropdown + power-user controls REMOVED from main header
              (2026-06-02 per owner). State hooks + backend wiring still
              live above so a future Settings page can re-surface them.
              Body still sends modelPref / personaPref / stakesPref /
              approvalPref / verifyPref so the backend keeps honoring them. */}
          {(() => {
            // Live mic-status indicator. Priority order:
            //   1) Aria is speaking → "speaking..."
            //   2) Backend is working → "thinking..."
            //   3) Mic detects voice → "hearing you"
            //   4) Mic is on but quiet → "listening"
            //   5) Mic off → no label
            let label = '';
            let dotClass = '';
            if (ariaSpeaking) { label = 'speaking...'; dotClass = 'aria'; }
            else if (loading) { label = 'thinking...'; dotClass = 'load'; }
            else if (micOn && micSpeaking) { label = 'hearing you'; dotClass = 'live'; }
            else if (micOn) { label = 'listening'; dotClass = 'idle'; }
            if (!label) return null;
            return (
              <span className={`header-title-status header-title-status--${dotClass}`} style={{ marginLeft: 10, fontSize: 13, opacity: 0.75, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: dotClass === 'live' ? '#3aff7a' : dotClass === 'aria' ? '#3a9dff' : dotClass === 'load' ? '#ffaa3a' : '#888',
                  animation: dotClass === 'live' || dotClass === 'aria' ? 'pulse 1.2s ease-in-out infinite' : 'none',
                }} />
                {label}
              </span>
            );
          })()}
        </div>
        {/* Top-right header:
            - In Assist Mode (assistCompactLayout = true), the 3-button cluster
              collapses to a single small text bubble so Aria can text the user
              guidance while they work in another app. No downloads, no sign-in
              pill — just a persistent surface for her messages.
            - Outside Assist Mode, show the normal cluster (downloads on web,
              AccountMenu always). */}
        {assistCompactLayout ? (
          <AriaTopRightBubble
            messages={messages}
            loading={loading}
            ariaSpeaking={ariaSpeaking}
            onClick={() => setShowConversationOverlay(true)}
          />
        ) : (
          /* AccountMenu moved to a section-agnostic floating wrapper next
             to the .aria-hero (see top of .app-center).
             Top-right Mac/Windows download banner removed per design — only
             the HowTo brand + profile icon live in the top corners now. */
          null
        )}
      </header>

      {/* ── Top-right LIVE CHAT (LAYOUT.md canonical — restored 8/12) ──────
          The render was lost in a refactor; the CSS (.aria-live-chat, blue
          user / dark Aria bubbles, typing dots, mobile stretch) survived
          untouched. Braeden 8/12: "in a window how the ai responds back
          through text so i can just see what she says back" — every spoken
          reply STAYS readable here (last 2 messages per the locked spec),
          including the First-Time demo lines, instead of flashing and
          vanishing with the transient orb caption. */}
      {effectiveSection === 'ask' && (messages.length > 0 || loading || micOn) && (
        <div className="aria-live-chat" aria-live="polite">
          {/* VOICE TURNS ARE THE CIRCLE, NOT TEXT (Braeden 8/29): "to many
              text on the screen, there should only be the talking circle ...
              when shes communicating, its only that, text box is only for the
              text communicaiton, not the tts talking." This supersedes the
              8/12 "every spoken reply STAYS readable here" note above — the
              owner reversed it explicitly. A spoken exchange lives in her
              voice and the ball; a TYPED exchange still renders here. Filter
              at the RENDER, not at the 13 append sites — the fourteen-wrapper
              lesson. Untagged messages default to showing (hiding them would
              silently eat replies from any append site that forgot the tag). */}
          {/* ── THE SEARCH BAR IS NOT A TALKING COMMUNICATION (Braeden 8/31) ──
              "the search bar isnt a talking communication, so stop poping it
               up like one, its for the search engine, so treat it like a text
               chat, not a tts chat"

              Typed turns used to pop up HERE — iMessage bubbles floating
              beside the talking ball, which is the voice presentation. A
              person who types into a search engine is not being talked at.
              So the typed exchange moved into the centre column as a plain
              reading thread (`.ws-thread`, below), and this column is left to
              the voice surface only: the live mic transcript.

              Both branches of the split now hold: SPOKEN renders no text and
              lives in the circle (8/29); TYPED renders no bubble and lives in
              the reading column. Neither one borrows the other's furniture.
              The typing indicator moved with it, for the same reason. */}
          {/* THE WORKSTATION (8/22) — "its just where all of the info being
              worked on lives". Sits directly under her last line, inside the
              live chat, because that is the surface actually on screen for
              #ask. I first wired it to the `messages.map` block further down
              this file, which turned out to be a chat surface that is not
              rendered in this section — the answer appeared and the
              workstation did not. Verified in the running app, not assumed.
              Renders NOTHING when the payload has no artifact. */}
          {/* The workstation moved to the center sheet (8/22): this column is her words only. */}
          {/* WHAT HER EARS PICK UP (Braeden 8/12: "i want to see what the
              whisper picks up") — the raw live transcription, word by word,
              while the mic is hot. Dimmed + labeled so it reads as "being
              heard", then the blue user bubble takes over once it's sent. */}
          {/* GONE (Braeden 9/2): "i dont want to see the whisper bubbles
              anymore, get rid of it." The live 🎙 transcript bubble that
              flickered with half-heard words is no longer rendered; what
              she acts on still lands in the conversation as his turn. */}
          {/* Braeden 8/20: "show me her thinking progress when i send a
              message, and also when i send a stt" — what her ears locked in
              (the exact STT text she acted on) + each pipeline stage with
              timing, live. Self-contained; renders nothing when idle.
              HIDDEN BY DEFAULT SINCE 8/29 — "clean it all up too, to many
              text on the screen". The 8/20 ask was debugging STT; the strip
              stays one flip away instead of deleted: set
              localStorage.aria_debug_strip = '1' (or add ?debug=1 to the
              URL) and it renders exactly as before. */}
          {(() => {
            try {
              if (localStorage.getItem('aria_debug_strip') === '1') return <AriaThinkingStrip />;
              if (new URLSearchParams(window.location.search).get('debug') === '1') return <AriaThinkingStrip />;
            } catch (_) {}
            return null;
          })()}
        </div>
      )}

      {/* Conversation overlay -- full scrollable conversation log */}
      {showConversationOverlay && (
        <div className="aria-convo-overlay" onClick={() => { setShowConversationOverlay(false); setShowConversationHistory(false); setSelectedArchiveDay(null); }}>
          <div className="aria-convo-overlay__panel" onClick={(e) => e.stopPropagation()}>
            <div className="aria-convo-overlay__header">
              <h2>{showConversationHistory ? (selectedArchiveDay !== null ? conversationArchive[selectedArchiveDay]?.date : 'History') : 'Today'}</h2>
              <div className="aria-convo-overlay__actions">
                {!showConversationHistory && (
                  <button type="button" className="aria-convo-overlay__history-btn" onClick={() => { refreshArchive(); setShowConversationHistory(true); }}>
                    History
                  </button>
                )}
                {showConversationHistory && selectedArchiveDay !== null && (
                  <button type="button" className="aria-convo-overlay__history-btn" onClick={() => setSelectedArchiveDay(null)}>
                    Back
                  </button>
                )}
                {showConversationHistory && selectedArchiveDay === null && (
                  <button type="button" className="aria-convo-overlay__history-btn" onClick={() => setShowConversationHistory(false)}>
                    Today
                  </button>
                )}
                <button type="button" className="aria-convo-overlay__close" onClick={() => { setShowConversationOverlay(false); setShowConversationHistory(false); setSelectedArchiveDay(null); }}>
                  &times;
                </button>
              </div>
            </div>
            <div className="aria-convo-overlay__body">
              {showConversationHistory && selectedArchiveDay === null ? (
                // Show list of archived days
                conversationArchive.length === 0 ? (
                  <p className="aria-convo-overlay__empty">No conversation history yet.</p>
                ) : (
                  conversationArchive.map((day, i) => (
                    <button key={i} type="button" className="aria-convo-overlay__day-btn" onClick={() => setSelectedArchiveDay(i)}>
                      <span className="aria-convo-overlay__day-date">{day.date}</span>
                      <span className="aria-convo-overlay__day-count">{day.entries.length} messages</span>
                    </button>
                  ))
                )
              ) : (
                // Show conversation entries (today or selected archive day)
                (() => {
                  const entries = showConversationHistory && selectedArchiveDay !== null
                    ? (conversationArchive[selectedArchiveDay]?.entries || [])
                    : conversationLog;
                  return entries.length === 0 ? (
                    <p className="aria-convo-overlay__empty">No messages yet. Start talking to Aria!</p>
                  ) : (
                    entries.map((entry, i) => (
                      <div key={i} className={`aria-convo-msg ${entry.role === 'user' ? 'aria-convo-msg--user' : 'aria-convo-msg--aria'}`}>
                        <div className="aria-convo-msg__bubble">
                          <p>{entry.text}</p>
                          <span className="aria-convo-msg__time">
                            {new Date(entry.time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    ))
                  );
                })()
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Assist engine — MOUNTED BUT NEVER SEEN (owner rule 2026-07-15:
          "i never want the user to see it"). Assist operates the user's OTHER
          apps, so the surface is the floating overlay + glowing cursor, not a
          page in our app. This component is not a viewport — it owns the whole
          capture loop (video.srcObject -> drawImage -> canvas.toDataURL ->
          /api/screen/analyze), so it has to stay mounted for Assist to work.
          Hidden via opacity/offscreen and NOT display:none: display:none lets
          the browser stop decoding the video, which silently kills capture. */}
      {(screenStream || screenShareRequesting) && (
        <div
          aria-hidden="true"
          data-assist-engine=""
          style={{ position: 'fixed', left: -99999, top: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: -1 }}
        >
        <ScreenShareView
          initialStream={screenStream}
          screenShareError={screenShareError}
          shareRequesting={screenShareRequesting}
          onRequestShare={requestScreenShare}
          onStopShare={() => {
            try {
              window.electronAPI?.screenShareEnded?.();
            } catch (_) {}
            if (screenStream) {
              screenStream.getTracks().forEach((t) => t.stop());
              setScreenStream(null);
            }
            setScreenShareError('');
          }}
          onBack={() => { window.location.hash = 'ask'; setView('ask'); setDashboardSection('ask'); }}
          onPickFiles={() => fileInputRef.current?.click()}
          onTakePicture={() => setCameraOpen(true)}
          overlayMuted={overlayMuted}
          playbackMuted={!micOn || overlayMuted}
          injectQuestion={injectQuestion}
          onClearInjectQuestion={() => setInjectQuestion(null)}
          // HER SIDE OF THE CONVERSATION (8/25). In assist mode this component
          // owns the turn, so without this the panel showed his blue bubbles
          // and nothing back - the screenshot that started this. Her replies
          // now land in the same thread he is reading.
          conversationHistory={conversationHistory}
          onVisualState={(state) => { assistVisualRef.current = state; }}
          onReply={(t, metadata) => {
            const line = String(t || '').trim();
            if (!line) return;
            setMessages((m) => {
              const last = m[m.length - 1];
              if (last && last.role === 'assistant' && String(last.content).trim() === line) return m;
              // Her reply belongs to the channel the turn came in on (8/29):
              // a spoken ask gets a spoken answer — the ball — and no bubble;
              // a typed ask keeps its reply readable in the box.
              return [...m, { role: 'assistant', content: line, mode: metadata?.inputMode || lastInputMode }];
            });
          }}
          hostOwnsMic
          voiceTurns={voiceTurnRef.current}
        />
        </div>
      )}

      <main className="main">
        {isDesktopApp && missingForSection.length > 0 && (
          <div className="backend-unreachable-banner" role="alert">
            Missing access for this task: {missingForSection.join(', ')}.
            {' '}
            <button
              type="button"
              className="app-backend-banner-retry"
              onClick={() => {
                if (missingForSection.includes('microphone')) {
                  window.electronAPI?.requestMicrophoneAccess?.().catch(() => {});
                  setTimeout(() => window.electronAPI?.openMicrophoneSettings?.(), 220);
                }
                if (missingForSection.includes('screen')) {
                  setTimeout(() => window.electronAPI?.openScreenRecordingSettings?.(), 420);
                }
                if (missingForSection.includes('accessibility')) {
                  setTimeout(() => window.electronAPI?.openAccessibilitySettings?.(), 820);
                }
              }}
            >
              Allow Required Access
            </button>
            {missingForSection.includes('microphone') && window.electronAPI?.openMicrophoneSettings && (
              <> <button type="button" className="app-backend-banner-retry" onClick={() => { window.electronAPI.requestMicrophoneAccess?.().catch(() => {}); setTimeout(() => window.electronAPI.openMicrophoneSettings(), 180); }}>Microphone</button></>
            )}
            {missingForSection.includes('screen') && window.electronAPI?.openScreenRecordingSettings && (
              <> <button type="button" className="app-backend-banner-retry" onClick={() => window.electronAPI.openScreenRecordingSettings()}>Screen Recording</button></>
            )}
            {missingForSection.includes('accessibility') && window.electronAPI?.openAccessibilitySettings && (
              <> <button type="button" className="app-backend-banner-retry" onClick={() => window.electronAPI.openAccessibilitySettings()}>Accessibility</button></>
            )}
          </div>
        )}
        {backendReachable === false && (
          <div className="backend-unreachable-banner" role="alert">
            Can't reach Aria. Start the backend: <code>cd HowTo-Backend && node server.js</code> (port 3001). If using the app from a different host/port, set <code>VITE_API_BASE=http://localhost:3001</code> when building.
          </div>
        )}
        <div ref={bottomRef} />

        {referenceVideo && (
          <div className="reference-images-row" role="list" aria-label="Attached video">
            <div className="reference-image-thumb reference-video-thumb" role="listitem">
              {referenceVideo.dataUrl ? (
                <video src={referenceVideo.dataUrl} muted style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
              ) : (
                <span className="reference-image-loading">…</span>
              )}
              <div className="reference-video-badge">Video</div>
              <button type="button" className="reference-image-remove" onClick={() => setReferenceVideo(null)} aria-label="Remove video">×</button>
            </div>
            <span style={{ fontSize: 11, opacity: 0.6, alignSelf: 'center' }}>{referenceVideo.file?.name || 'Video attached'}</span>
          </div>
        )}
        {referenceImages.length > 0 && (
          <div className="reference-images-row" role="list" aria-label="Attached reference images">
            {referenceImages.map((ref) => (
              <div key={ref.id} className="reference-image-thumb" role="listitem">
                {ref.dataUrl ? (
                  <img src={ref.dataUrl} alt="" />
                ) : (
                  <span className="reference-image-loading">…</span>
                )}
                <button type="button" className="reference-image-remove" onClick={() => removeReferenceImage(ref.id)} aria-label="Remove">×</button>
              </div>
            ))}
            {referenceImages.length >= MAX_REFERENCE_IMAGES && (
              <span className="reference-images-cap">Max {MAX_REFERENCE_IMAGES} photos</span>
            )}
          </div>
        )}
        {/* THE CENTER IS INFORMATION, THE TOP-RIGHT IS COMMUNICATION (Braeden
            8/22, screenshot of the same sentence in both places): "one is a
            search engine and needs to be used like one ... one shows
            communication, one is information, whether its a list, picture,
            site, what ever is in the work station is seperate from what the
            talking chat is, and needs to be shown that way."
            This sheet used to be a second copy of the conversation (iMessage
            bubbles). Now it holds ONLY the workstation - results pages, deal
            sheets, records, drafts, checklists - and nothing at all when there
            is no artifact (LAYOUT.md: it never renders empty). Her words live
            in the top-right live chat and nowhere else. */}
        {(() => {
          const hasArtifact = wsHasArtifact(wsPayload);
          // ── THE TYPED THREAD (Braeden 8/31) ───────────────────────────
          // "its for the search engine, so treat it like a text chat, not a
          // tts chat." Typed turns read here, in the column the results are
          // in, as text — not as bubbles orbiting the voice ball. Voice turns
          // are excluded on purpose: a spoken exchange renders no text at all
          // (8/29), so this stays empty through a whole voice session.
          // POSITIVE TAG ONLY (Braeden 9/2): "the tts is a different
          // conversation than the text chat, which means nothing should be
          // displayed in the text chat." `!== 'voice'` let any append that
          // forgot its tag print here - and a spoken turn that forgot its tag
          // is exactly the leak he means. The search chat shows a turn only
          // when the turn says it was typed. (The top-right voice surface
          // keeps the permissive rule; a forgotten tag there costs nothing.)
          const typedThread = messages
            .filter((m) => (m.role === 'user' || m.role === 'assistant')
              && typeof m.content === 'string' && m.content.trim()
              && (m.mode === 'text' || m.data?.taskWorkspace));
          const latestResearchIndex = latestWorkspaceIndex(typedThread);
          // No tail cap: it is a timeline now (9/2) - "everything there after
          // goes after itself as you scroll." The column scrolls; the history
          // stays.
          // ── THE NOTEBOOK STAYS OPEN (Braeden 8/31) ────────────────────
          // The sheet's visibility was tied to the workstation alone, so the
          // moment a turn carried no artifact her notes vanished mid-job -
          // measured: picked Meta from the logo picker, the picker closed and
          // took the page with it. A notebook you are working in does not
          // close because nothing else is on the desk.
          // The sheet now opens for a typed turn even when nothing was built,
          // because otherwise a typed answer with no artifact would render
          // nowhere at all once the top-right bubbles went away.
          const glassActive = hasArtifact || !!notepadFile || typedThread.length > 0
            || (loading && lastInputMode !== 'voice');
          return (
            <div
              /* Compact is the WORKING layout (full-width column). The notepad
                 only ever appeared next to an artifact before, so it always
                 got it; now that the page can stand alone it still has to,
                 or the sheet falls back to the small top-right rail and the
                 notes land under the orb (measured 8/31). */
              /* --centred: the bar is the tool in use, so the circle has gone
                 to the corner and the work takes the middle of the screen. */
              className={`aria-glass-chat aria-glass-chat--workstation ${glassActive ? 'aria-glass-chat--active' : ''}${glassActive ? ' aria-glass-chat--compact' : ''}${textMode ? ' aria-glass-chat--centred' : ''}`}
              aria-hidden={!glassActive}
            >
              <div
                className="aria-glass-chat-inner"
                ref={glassInnerRef}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
                }}
              >
                {/* ── THE TIMELINE (Braeden 9/2) ──────────────────────────
                    "add everything in a timeline so every text or generated
                     response that pops up goes down the list, like a normal
                     ai, and everything there after goes after itself as you
                     scroll."
                    Before this, the column showed the last eight lines and ONE
                    set of cards - the latest - which replaced the previous set
                    on every turn. Now each of his questions is followed by her
                    answer and by THAT turn's cards, in order, all the way back.
                    Every assistant message already carries its response as
                    `m.data`, so the artifact belongs to the message that made
                    it; nothing is copied around. */}
                {typedThread.length > 0 && (
                  <div className="ws-thread ws-thread--timeline">
                    {typedThread.map((m, i) => (
                      <React.Fragment key={`${i}-${m.content.slice(0, 24)}`}>
                        <div className={m.role === 'user' ? 'ws-asked' : ('ws-said' + (m.error ? ' ws-said--error' : ''))}>
                          {m.content}
                        </div>
                        {m.role === 'assistant' && m.data?.taskWorkspace && i === latestResearchIndex && (isDesktopApp ? <div className="research-download-links"><button type="button" onClick={() => window.dispatchEvent(new CustomEvent('aria:openAssist'))}>Open Assist</button></div> : <DesktopDownloadsSection info={desktopDownloadInfo} variant="links" member={desktopMember} />)}
                        {m.role === 'assistant' && wsHasArtifact(m.data) && (
                          <Workstation
                            payload={m.data}
                            hideWords
                            inTimeline
                            onPick={(opt) => {
                              const send = sendMessageWithTextRef.current;
                              if (send && opt && opt.label) send(String(opt.label));
                            }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
                {loading && lastInputMode !== 'voice' && (
                  <div className="ws-thread ws-thread--working">
                    <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                  </div>
                )}
                {/* A VOICE lookup still lands here as information (8/21: she
                    uses the search bar as a tool for the person). The typed
                    timeline owns its own artifacts above; this single slot is
                    only for a turn the timeline is not showing. */}
                {hasArtifact && typedThread.length === 0 && (
                  <Workstation
                    payload={wsPayload}
                    hideWords={false}
                    /* SCENE CLOCK, VOICE SIDE (9/2): while she is speaking
                       this line, the player gets the real start and length
                       of her audio, so scene items land on her words. Until
                       the audio starts it waits (2.5 s cap, then it reads). */
                    sceneClock={(ariaSpeaking && speechClockRef.current && speechClockRef.current.startedAt)
                      ? { mode: 'audio', startedAt: speechClockRef.current.startedAt, duration: speechClockRef.current.ms || 0, words: speechClockRef.current.words || null }
                      : { mode: 'wait' }}
                    onClose={() => setWsPayload(null)}
                    /* A TAP IS AN ANSWER (Braeden 8/31). Picking a logo sends
                       the platform exactly as if he had typed it, so the same
                       lanes read it and nothing special-cases the click. */
                    onPick={(opt) => {
                      try { setWsPayload(null); } catch (_) {}
                      const send = sendMessageWithTextRef.current;
                      if (send && opt && opt.label) send(String(opt.label));
                    }}
                  />
                )}
                {notepadFile && <Notepad caseFile={notepadFile} onClose={() => setNotepadFile(null)} />}
                {/* Queued messages — sent while Aria was replying. Pending
                    input, not her words, so they may sit here with their ×. */}
                {queuedMessages.map((q) => (
                  <div key={q.id} className="aria-bubble-row aria-bubble-row--user aria-bubble-row--queued">
                    <div className="aria-bubble aria-bubble--user aria-bubble--queued" title="Queued — will send when Aria finishes">
                      <span className="aria-bubble-queued-text">{q.text}</span>
                      <button
                        type="button"
                        className="aria-bubble-queued-x"
                        onClick={() => removeQueued(q.id)}
                        aria-label="Cancel queued message"
                        title="Cancel"
                      >×</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        {/* ── THE DOOR (Braeden 9/3) ────────────────────────────────────
            He sent the survey of what people actually use AI for, and asked
            for "the most simple and attractive layout for these categories,
            than break down each of these categories into more detailed
            sectors so it niches to different people better." Eight tiles in
            the survey's own order, each opening into six real starter turns.
            It stands above the box on the EMPTY landing only — once there is
            a conversation, the conversation is the screen. */}
        {/* `orbCompact` (9/4): when a voice turn goes live the circle grows to
            210px in a 46vw column at top-right, and its canvas printed 59x34px
            across the "Pictures & video" tile — measured at 1280, and it gets
            worse at 900 where the column is narrower and the ball sits further
            left. Capping the door's width cannot clear it at every size, and
            the door is not what someone talking to her is looking at, so it
            stands down for the length of the voice turn and comes back after.*/}
        {messages.length === 0 && orbCompact && (
          <Uses onPick={(prompt) => { try { sendMessageWithText(prompt, { inputMode: 'text' }); } catch (_) {} }} />
        )}
        <div className={`input-row${messages.length === 0 ? ' input-row--empty' : ''}`}>
          <div className="input-add-wrap" ref={addMenuRef}>
            <button
              type="button"
              className="input-add-btn"
              aria-label="Add attachment"
              title="Add"
              onClick={openAddMenu}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
            {addMenuOpen && (
              <div className="add-menu">
                {/* ── SAME MENU ON BOTH SURFACES (Braeden 9/7) ──────────────
                    "all of the features in the website need to work the exact
                     same as the app too ... where things can be clicked at,
                     make it littereally all the same."

                    This used to hide Assist and Take a picture on the web, on
                    the stated grounds that both "require the desktop app".
                    Neither claim was true of the code under it:

                      * Take a picture -> setCameraOpen -> startCamera() is
                        navigator.mediaDevices.getUserMedia and capturePicture()
                        is a canvas drawImage. No Electron anywhere.
                      * Assist -> onScreenShareClick() calls the Electron IPC
                        only `if (window.electronAPI?.screenShareStarted)` and
                        then requestScreenShare(), which is getDisplayMedia in a
                        browser. ScreenShareView has a whole browser branch with
                        its own Allow card (ScreenShareView.jsx, `!isDesktopApp`).

                    It was also inconsistent WITHIN the web build: AriaDock
                    already passed onAssist straight through, so the same user
                    could reach Assist from the dock and not from the + menu.

                    What differs between surfaces is what she can DO once she is
                    looking, not whether the button exists - the backend already
                    models that (services/ariaSurface: app = DO, web = EXPLAIN).
                    That belongs in her answer, not in a hidden menu item. */}
                <button type="button" className="add-menu-item" onClick={onAddAssist}>
                  Assist
                </button>
                <button type="button" className="add-menu-item" onClick={onAddFiles}>
                  Upload file
                </button>
                <button type="button" className="add-menu-item" onClick={onAddTakePicture}>
                  Take a picture
                </button>
              </div>
            )}
          </div>
          {/* ── SHE POINTS AT IT (Braeden 8/16) ──────────────────────────────
              "when she says type in your business, she needs to use assist mode
               featrues to point to what shes talking about, type it in here and
               ill chack it out, she has to use her assets"
              When she asks for something typed, "here" has to MEAN somewhere.
              This is the same blue Assist-Mode outline she already uses to
              point at things on a screen — her asset, reused, rather than a
              new widget nobody recognises. */}
          {inputHint && (
            <span className="aria-highlight-label" style={{ top: -11, left: 18, zIndex: 3, pointerEvents: 'none' }}>
              {inputHint}
            </span>
          )}
          <span className="input" role="status" style={{ display: 'flex', alignItems: 'center' }}>
            {micOn ? 'Listening — speak to Aria' : 'Tap the microphone to speak to Aria'}
          </span>
          {/* THE ONLY MIC IS THE CIRCLE (Braeden 8/31) ──────────────────
              "there should be no more mic in the box, the only thing that has
               a mic is the circle thing"
              The box is the search engine; the circle is the conversation.
              A mic living in the search box said they were the same tool.
              The circle already IS the mute switch (onToggleMute) — tapping it
              opens and closes the mic, so nothing was lost by taking this out,
              only the ambiguity about which surface you were addressing. */}
        </div>
        {/* Showcase cards — 4 Apple-style square bubbles. Real photos load
            from /public/cards/. Drop your own photos there to swap them:
              public/cards/business.jpg   — HowTo logo / your storefront
              public/cards/money.jpg      — bank balance screenshot ($500)
              public/cards/shopping.jpg   — protein powder (your order history)
              public/cards/imagegen.jpg   — pink elephant on ice (AI demo)
            If a local file is missing, the Unsplash fallback URL kicks in so
            the page never looks broken. */}
        {messages.length === 0 && (
          <div className="aria-showcase">
            {[
              // ──────────────────────────────────────────────────────────
              //  Every card plays a REAL recording from inside HowTo. No
              //  stock photos. No slideshows. Each video is captured from
              //  Aria's actual output — that's how the product stays legit
              //  and on brand. Drop the files into /public/cards/videos/:
              //
              //    build-website.mp4   — screen recording of an Aria-built
              //                          site live on its URL
              //    make-video.mp4      — a video Aria generated (Veo / etc.)
              //    generate-image.mp4  — Ken-Burns pan over a real AI gen,
              //                          or the gen-in-progress recording
              //    shop-for-me.mp4     — actual screen recording of Aria
              //                          finding products
              //
              //  Each one should be ~6–10s, 1080×608 (16:9), <2MB, H.264+AAC.
              //  Until the file exists, the card shows a clean dark placeholder
              //  with the title in the center — no stock photo fallback.
              // ──────────────────────────────────────────────────────────
              // Showcase cards (2026-06-05 update): Aria pivoted from
              // "builder" to "helper." The Build/Make/Generate cards used
              // to route to dead workstations — they now route to chat with
              // a starter prompt so Aria *guides* the user instead. The
              // optional `prompt` field is read by the onClick handler.
              /* Showcase cards removed per owner request 2026-06-25 — empty list renders nothing. */
            ].map((card) => (
              <button
                key={card.title}
                type="button"
                className="aria-card"
                onClick={() => {
                  // After the workstation pivot (2026-06-05), Help-me-decide
                  // and Walk-me-through cards route to 'ask' with a starter
                  // prompt prefilled in the input. Shop cards route to the
                  // shopping surface. No more dead workstation routes.
                  try { window.location.hash = card.section; } catch (_) {}
                  setDashboardSection(card.section);
                  if (card.prompt && card.section === 'ask') {
                    try { setInput(card.prompt); } catch (_) {}
                  }
                }}
              >
                <div className="aria-card__title">{card.title}</div>
                <div className="aria-card__art">
                  <CardVideo src={card.video} poster={card.poster} label={card.title} />
                </div>
              </button>
            ))}
          </div>
        )}
        {/* Interest chips removed — the 4 showcase cards above cover the
            same quick-start role, and stacking both was too noisy. */}
        {/* Debug voice state — hidden by default, enable with ?debug=1 in URL or window.__ariaDebug=true */}
        {(typeof window !== 'undefined' && (window.__ariaDebug || new URLSearchParams(window.location.search).get('debug') === '1')) && (
        <VoiceDebugPanel
          micOn={micOn} hasSpokenDesktopIntro={hasSpokenDesktopIntro} introLocked={introLocked}
          voicePolicy={voicePolicy} effectiveSection={effectiveSection} voiceRecording={voiceRecording}
          ariaSpeaking={ariaSpeaking} desktopUiFlagsReady={desktopUiFlagsReady}
          micSpeaking={micSpeaking} vadCanRun={vadCanRun} voiceError={voiceError} liveTranscript={liveTranscript}
        />
        )}
        {voiceError && (
          <p className="input-voice-error" role="alert">
            {voiceError}
            {isDesktopApp && voiceError.includes('Microphone denied') && window.electronAPI?.openMicrophoneSettings && (
              <>
                {' '}
                {window.electronAPI?.requestMicrophoneAccess && (
                  <button
                    type="button"
                    className="input-voice-error-link"
                    onClick={() => window.electronAPI.requestMicrophoneAccess().catch(() => {})}
                  >
                    Request Microphone Access
                  </button>
                )}
                {' '}
                <button type="button" className="input-voice-error-link" onClick={() => window.electronAPI.openMicrophoneSettings()}>
                  Open Microphone Settings
                </button>
                {' '}
                <button
                  type="button"
                  className="input-voice-error-link"
                  onClick={() => { setVoiceError(''); setMicOn(false); setTimeout(() => setMicOn(true), 400); }}
                >
                  Retry Mic
                </button>
              </>
            )}
            {!isDesktopApp && (voiceError.includes('Microphone blocked') || voiceError.includes('toggle')) && (
              <>
                {' '}
                <button
                  type="button"
                  className="input-voice-error-link"
                  onClick={() => { setVoiceError(''); setMicOn(false); setTimeout(() => setMicOn(true), 400); }}
                >
                  Retry Mic
                </button>
              </>
            )}
          </p>
        )}
        {/* Mic status bar — hidden by default, enable with ?debug=1 or window.__ariaDebug=true */}
        {(typeof window !== 'undefined' && (window.__ariaDebug || new URLSearchParams(window.location.search).get('debug') === '1')) && effectiveSection === 'ask' && micOn && (
          <p className="input-voice-error" style={{ opacity: 0.85 }}>
            {isDesktopApp && deviceCaps
              ? `Mic: ${micAccessGranted ? 'granted' : String(deviceCaps.microphone || 'unknown')} (${permissionAppName}).`
              : activeMicLabel ? `Mic: ${activeMicLabel}.` : 'Mic: checking…'}
            {' '}
            <button type="button" className="input-voice-error-link" onClick={runMicDiagnostic}>
              Test Mic
            </button>
            {' '}
            {lastMicPeak != null ? `Peak: ${lastMicPeak}${lastMicPeak >= 0.6 ? ' ✓' : ' (too quiet — speak louder or check input)'}` : ''}
            {audioInputs.length > 0 && (
              <>
                {' | '}
                <select
                  className="input-voice-error-link"
                  value={preferredMicId}
                  onChange={(e) => setPreferredMicId(e.target.value)}
                  style={{ maxWidth: 260 }}
                  title="Choose which microphone Aria should use"
                >
                  {audioInputs.map((d, i) => (
                    <option key={`${d.deviceId || 'mic'}-${i}`} value={d.deviceId || ''}>
                      {d.label || (d.deviceId === 'default' ? 'Default microphone' : `Microphone ${i + 1}`)}
                    </option>
                  ))}
                </select>
              </>
            )}
          </p>
        )}
      </main>
      </>
      )}
      </div>
      {/* Trending-videos side panel removed — was bleeding "No videos yet."
          onto every section. Will re-enable as a dedicated discovery view if
          we want it back. */}
      </div>
      </div>
    </div>


    <AriaTranscriptTest enabled={isDesktopApp} onSubmit={async text=>{
      try { fetch('/api/pipeline/event',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({stage:'test.transcript',text,meta:{method:'controlled_text',microphone:false}})}).catch(()=>{}); } catch (_) {}
      try {
        const {capabilityTest}=await import('./assistCapabilityTest');
        const test=capabilityTest(text);
        sendMessageWithTextRef.current?.(test?.request||text,{inputMode:'text',diagnosticTest:true,capability:test?.capability});
      } catch(error) {setMessages(m=>[...m,{role:'assistant',mode:'text',content:`Action test rejected: ${error.message}`}]);}
    }} />
    <OutOfCreditsModal
      open={outOfCreditsOpen}
      onClose={() => setOutOfCreditsOpen(false)}
      onSignIn={() => {
        setOutOfCreditsOpen(false);
        // Trigger AccountMenu modal by simulating click on its sign-in pill.
        // Falls back to dispatching a custom event AccountMenu listens for.
        try { window.dispatchEvent(new CustomEvent('aria-open-signin')); } catch (_) {}
        const pill = document.querySelector('.account-pill--signin');
        if (pill && typeof pill.click === 'function') pill.click();
      }}
    />

    {cameraOpen && (
      <div className="camera-modal-overlay" role="dialog" aria-modal="true" aria-label="Camera">
        <div className="camera-modal">
          <video ref={cameraVideoRef} autoPlay playsInline muted className="camera-modal-video" />
          <div className="camera-modal-actions">
            <button type="button" className="camera-modal-btn camera-modal-btn--cancel" onClick={stopCamera}>
              Cancel
            </button>
            <button type="button" className="camera-modal-btn camera-modal-btn--capture" onClick={capturePicture}>
              Take picture
            </button>
          </div>
        </div>
      </div>
    )}

    {updateInstallMessage === 'dev' && (
      <div className="desktop-update-toast desktop-update-toast--info" role="status">
        You&apos;re running from source. To get &quot;click Update → app closes, installs, and reopens&quot;, install Aria from the .pkg (<code>npm run dist:mac</code> then install <code>out/Aria-*.pkg</code>) and use that app. The installed app will shut down, install the new version, and open back up automatically when you click Update.
      </div>
    )}
    {updateInstallMessage === 'failed' && (
      <div className="desktop-update-toast desktop-update-toast--error" role="alert">
        Update download failed. The download page was opened in your browser — you can install the new version from there.
      </div>
    )}

    {capturedImage && (
      <div className="captured-preview">
        <img src={capturedImage} alt="Captured" />
        <button type="button" className="captured-preview-close" onClick={() => setCapturedImage(null)} aria-label="Close">×</button>
      </div>
    )}

    {/* Assist box — skinned exactly like the Support chat, filling the compact
        Assist window. Wired to the real Assist pipeline (input → sendMessage so
        Aria controls the computer; the × stops Assist). Covers the app beneath
        it (max z-index). */}
    {/* ── THE RING, AND NOTHING ELSE (Braeden 9/5) ─────────────────────
        "i need to see this same thing when im IN assist mode ... thats the
         only thing i see, not the chat box, only that, if i mute that than
         the chat box pops up, but if i click on the mic in the chat box,
         than its only the circle IN ASSIST MODE"

        Mic on in Assist: the home-screen ring (LISTENING / THINKING /
        SPEAKING, the circle itself is the mute button) fills the Assist
        window and the chat box is gone. Mic off: the chat box, whose mic
        button brings the ring back. Nothing else is drawn in between. */}
    {/* ON THE WEB there is no compact window: Assist IS the screen share running
        under the Ask page (the 'screen' route is hidden by owner rule 7/15).
        So "in assist mode" on howtoaria.com means screenStream is live, and
        the ring rule applies there exactly the same. */}
    {!isDesktopApp && (assistCompactLayout || !!screenStream) && micOn && (
      <div
        className="assist-voice-only"
        role="region"
        aria-label="Aria is listening"
        style={{
          position: 'fixed', inset: 0, zIndex: 2147483647,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: appTheme === 'day' ? '#f4f6fb' : '#05070d',
        }}
      >
        {(() => {
          let force = null;
          try { force = new URLSearchParams(window.location.search).get('ball'); } catch (_) {}
          return (
            <TalkingBall
              speaking={force ? force === 'speaking' : ariaSpeaking}
              thinking={force ? force === 'thinking' : (!ariaSpeaking && (loading || preparingSpeech))}
              listening={force ? force === 'listening' : (!ariaSpeaking && !loading && !preparingSpeech)}
              muted={false}
              onToggleMute={() => {
                try { stopAriaPlayback(); } catch (_) {}
                try { instantVoice.stop(); } catch (_) {}
                setVoiceError('');
                micUserMutedRef.current = true;
                try { persistDesktopMicUserMuted(true); } catch (_) {}
                setMicOn(false);
              }}
              size={260}
              theme={appTheme}
            />
          );
        })()}
      </div>
    )}
    {assistCompactLayout && !assistAsk && (
      <AssistChatBox
        messages={messages}
        input={input}
        onInputChange={(v) => { setInput(v); if (v && lastInputMode !== 'text') setLastInputMode('text'); }}
        onSend={() => sendMessage()}
        busy={loading}
        status={ariaSpeaking ? 'Speaking…' : loading ? 'Working…' : micOn ? 'Listening…' : 'Assist · on'}
        onPlus={() => { try { if (typeof requestScreenShare === 'function') requestScreenShare(); } catch (_) {} }}
        micOn={micOn}
        onToggleMic={() => {
          if (micOn) setVoiceError('');
          setMicOn((prev) => {
            const next = !prev;
            if (isDesktopApp) { micUserMutedRef.current = !next; persistDesktopMicUserMuted(!next); }
            if (next) setLastInputMode('voice');
            return next;
          });
        }}
        onStop={() => {
          try { window.electronAPI?.screenShareEnded?.(); } catch (_) {}
          if (screenStream) {
            try { screenStream.getTracks().forEach((t) => t.stop()); } catch (_) {}
            setScreenStream(null);
          }
          setScreenShareError('');
        }}
      />
    )}
    {/* Always-present Aria — a glowing orb pinned bottom-right on every screen.
        Tap to open a compact chat popup and talk to Aria from anywhere. Hidden
        in Assist (the whole rail is already an Aria chat — the orb would just
        clutter the small box). */}
    {/* Aria conversation is voice-only; no secondary text-chat composer. */}
    </>
  );
}

export default App;

